import { reduceIdentityAsk, type IdentityAskState } from './identity-ask';
/**
 * AI Onboarding Agent SDK
 * Voice-first onboarding platform with WebRTC connectivity to Pipecat server
 */

import {
  LogLevel,
  PipecatClient,
  Transport
} from '@pipecat-ai/client-js';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { AgentUI } from './AgentUI';
import { drivesHostPage, isSupportMode, resolveNudgeAutoShow, runsOnMarketingSite } from './agent-mode';
import { surfaceDemo } from './host-cta';
import {
  ExitIntentDetector,
  normalizeExitIntentMessage,
  normalizeExitIntentOptions,
  resolveExitIntentConfig,
  surfaceExitIntent,
  type ExitIntentOptions,
  type ExitIntentSurfaceOptions,
} from './exit-intent';
import { readAvPrefs, writeMicPref, writeSpeakerPref } from './av-prefs';
import { resolveSupportMcpForSession } from './support-mcp';
import {
  getOrCreateAnonymousId,
  withAnonymousId,
} from './anonymous-id';
import { SessionIdSource } from './session-id';
import { hasCalendar } from './calendar-embed';
import { forceReleaseSqueeze } from './host-squeeze';
import {
  applySqueezeCompat,
  isCompatRequested,
  releaseSqueezeCompat,
  resolveSidebarCompat,
} from './squeeze-compat';
import { readConversationId, writeConversationId, clearForgottenConversation, readForgottenConversationId, type PersistedMessage } from './wa-continuity';
import {
  endConversation,
  openOrResumeConversation,
  reconcileRestoredMessages,
  resolveResumeOutcome,
  type ResumedConversation,
  type ResumeOutcome,
} from './wa-resume';
import {
  identityAllowed,
  nextConsentDecision,
  pageEngagementAllowed,
  resolveConsentMode,
  type ConsentDecision,
  type ConsentMode,
} from './consent-mode';
import { EventQueue } from './event-queue';
import { createEmailCheck } from './email-check';
import type { EmailCheck } from './email-check';
import { cancellableWait, throttledRetryDelayMs } from './validation-retry';
import {
  VisitorEventsClient,
  getStoredVisitorToken,
  inferBookingProvider,
  stripUrlForEvent,
} from './visitor-events';
import type { CurrentPageSummary } from './visitor-events';
import { clampSidebarWidth, resolveDisplayMode } from '../components/websiteAgentLayout';
import {
  parseDemoCapturePolicy,
  type DemoIntroPrefill,
  type DemoCapturePolicy,
} from '../components/demoLayout';
import {
  AccessibleSnapshotCapture,
  AccessibleSnapshot,
} from './accessible-snapshot';
import { collectDialogReadoutText } from './accessible-dialog-readout';
import type { WebsiteAgentStartOptions } from '../types/agent';
import {
  reduceWebsiteAgentMicIntent,
  websiteAgentShouldJoinWithMicrophone,
  type WebsiteAgentMicIntentEvent,
} from './website-agent-mic-intent';
import { FloeDailyTransport as DailyTransport } from './floe-daily-transport';
import { RtviHandshakeTimeoutError } from './rtvi-handshake-error';
import { voiceStartupTimeline } from './voice-startup-timing';
import {
  SdkDiagnosticsClient,
  startupStageForMark,
  type SdkDiagnosticAgentMode,
  type SdkDiagnosticReason,
} from './sdk-diagnostics';
// The ONE canonical `?cors=1` builder (AvatarVideo owns it). Imported rather
// than re-derived: a second copy is how the SDK would drift back into loading
// an avatar asset on the plain key — or, worse, the cors key without CORS.
import { corsCacheKeyUrl } from '../components/AvatarVideo';
import {
  preconnectVoiceOrigins,
  prefetchVoiceStartupAssets,
  scheduleVoiceStartupPrefetch,
  voiceStartupAssetsForAvatar,
  voiceStartupAssetsPrefetched,
} from './startup-prefetch';
import {
  MAX_NUDGE_LOAD_FAILURES,
  afterLoadWhenIdle,
  fetchAllNudges,
  nudgeRequestPath,
  usableNudges,
} from './website-agent-nudges';
import type { WebsiteAgentNudgeRow } from './website-agent-nudges';
import { releaseWarmedMedia } from './avatar-intro-preload';
import {
  extractEmailActionFragment,
  redeemEmailScheduleAction,
} from './email-action-fragment';
import {
  BOOKING_ATTRIBUTION_CACHE_TTL_MS,
  fetchAttributedBookingUrl,
  isBookingAttributionCacheFresh,
} from './booking-attribution';
import type { BookingUxHint } from './booking-ux-hint';
import { hasBookingEmailTemplate, resolveBookingUrl } from './booking-url';

// Import from extracted modules
import { DOMIntrospector } from './dom-capture';
import { HybridTargetResolver, ActionExecutor } from './action-execution';
import { OverlaySystem } from './ui-rendering';
import { CanvasScreenshotCapture } from './snapshot-processing';
import { PageTracker, NavigationCompleteDetector, BatchedActionExecutor } from './event-handling';
import {
  isDropdownCurrentlyOpen as _isDropdownCurrentlyOpen,
  findDropdownSearchInput as _findDropdownSearchInput,
  typeToFilterDropdown as _typeToFilterDropdown,
  findVisibleDropdownOption as _findVisibleDropdownOption,
} from './dropdown-helpers';
import {
  SDK_WIDGET_ATTR,
  FLOE_AVATAR_USER_NAME,
  isSDKWidget,
  getSanitizedUrl,
  isValidationResponse,
  sanitizeUrl,
  querySelectorWithAngularFallback,
  AudioAnalyzer,
  EventEmitter,
  SDKConfig,
  EndUserStatusRequest,
  EndUserStatus,
  ScreenshotOptions,
  PageStructure,
  ElementInfo,
  ContextData,
  UIAction,
  HybridTarget,
  DOMSnapshot,
  HybridUIAction,
  BatchStep,
} from './types';
import { logger as defaultLogger, Logger, createLogger } from './logger';
import {
  attemptSpaNavigation,
  createBrowserNavigationAdapter,
  resolveNavigationTarget,
  FULL_LOAD_GRACE_MS,
  type SpaNavigationResult,
} from './spa-navigation';

import {
  classifyAutoMicProbe,
  classifyMicError,
  micErrorName,
  micPrecheckFromState,
  MIC_AUDIO_CONSTRAINTS,
  MIC_RECOVERY_WINDOW_MS,
  type MicErrorKind,
  type MicPermissionStatus,
  type MicPrecheck,
  MicProbeResult,
  micStateForToggle,
  reconcileMicIntentOnConnect,
  shouldStartUserMicAnalysis,
} from './mic-permission';
import { appendCaptionFrame } from './caption-text';
import {
  claimUiStrings,
  getString,
  isSupportedLanguage,
  releaseUiStrings,
  setLanguage,
  setUiStrings,
  type SupportedLanguage,
} from './ui-strings';
import {
  applyLanguageChanged,
  applyLanguageChangeFailed,
  beginLanguageSwitch,
  expireLanguageSwitch,
  FIXED_ENGLISH_POLICY,
  LocalizationPolicyError,
  languagePreferenceSource,
  languagePreferenceKey,
  markWidgetLanguage,
  persistLanguagePreference,
  resolveUntaggedOperatorCopy,
  resolveInitialLanguage,
  resolveSurfacePolicy,
  shouldPersistLanguageSource,
  type AgentLanguageSurface,
  type LanguagePreferenceSource,
  type LanguageSwitchState,
  type LocalizationPolicy,
  type StorageLike,
} from './language-preference';

// Re-export everything for backwards compatibility
export { DOMIntrospector } from './dom-capture';
export { HybridTargetResolver, ActionExecutor } from './action-execution';
export { OverlaySystem } from './ui-rendering';
export { CanvasScreenshotCapture } from './snapshot-processing';
export { PageTracker, LoadingDetector, DOMStabilityChecker, NavigationCompleteDetector, BatchedActionExecutor } from './event-handling';
export type { PageTrackerSDK } from './event-handling';

// Declare build-time constant (replaced by Vite during build)
declare const __FLOE_API_URL__: string;

// Capture the SDK script's own origin at load time (available synchronously
// during IIFE execution). The recorder is co-deployed on the same CDN, so we
// derive its URL from this origin.
const _sdkScriptOrigin: string | null = (() => {
  try {
    const src = (document.currentScript as HTMLScriptElement | null)?.src;
    if (src) return new URL(src).origin;
  } catch { /* ESM or non-script context */ }
  return null;
})();

/** Maximum number of messages to keep in transcript history. Configurable via SDKConfig. */
const DEFAULT_MAX_TRANSCRIPT_MESSAGES = 50;

/**
 * Server switching waits for a safe turn/audio boundary (bounded at 60s), so
 * leave ten seconds of transport/provider margin before releasing the selector.
 * The matching request id remains recoverable for a late authoritative ack.
 */
const LANGUAGE_SWITCH_ACK_TIMEOUT_MS = 70_000;

// ─────────────────────────────────────────────────────────────────────────────
// Manual recording flow helpers
// ─────────────────────────────────────────────────────────────────────────────

const RECORDING_PARAMS_KEY = 'floe_recording_params';
const RECORDING_PARAMS_TTL_MS = 15 * 60 * 1000; // 15 minutes (matches token TTL)

interface RecordingParams {
  recordingId: string;
  siteId: string;
  recordingToken: string;
  storedAt: number;
}

function isValidRecordingParams(obj: unknown): obj is RecordingParams {
  if (typeof obj !== 'object' || obj === null) return false;
  const p = obj as Record<string, unknown>;
  return (
    typeof p.recordingId === 'string' && p.recordingId.length > 0 &&
    typeof p.siteId === 'string' && p.siteId.length > 0 &&
    typeof p.recordingToken === 'string' && p.recordingToken.length > 0 &&
    typeof p.storedAt === 'number' && Number.isFinite(p.storedAt)
  );
}

/**
 * Detect recording params from the current URL query string.
 * When found, removes them from the visible URL and persists them to
 * sessionStorage so they survive same-origin navigation (e.g. a login
 * redirect on the customer's site).
 */
function getManualRecordingParams(): RecordingParams | null {
  try {
    const url = new URL(window.location.href);

    // Check URL params first
    const floeRecord = url.searchParams.get('floe_record');

    if (floeRecord === 'true') {
      const recordingId = url.searchParams.get('floe_recording_id');
      const siteId = url.searchParams.get('floe_site_id');
      const recordingToken = url.searchParams.get('floe_recording_token');

      // Always scrub recording params from the URL to avoid leaking partial
      // tokens into analytics, bookmarks, or copy-paste.
      url.searchParams.delete('floe_record');
      url.searchParams.delete('floe_recording_id');
      url.searchParams.delete('floe_site_id');
      url.searchParams.delete('floe_recording_token');
      window.history.replaceState(null, '', url.toString());

      if (recordingId && siteId && recordingToken) {
        const params: RecordingParams = {
          recordingId,
          siteId,
          recordingToken,
          storedAt: Date.now(),
        };

        try {
          sessionStorage.setItem(RECORDING_PARAMS_KEY, JSON.stringify(params));
        } catch { /* quota / private-browsing – best effort */ }

        return params;
      }
    }

    // Fall back to sessionStorage (params survived a same-origin redirect)
    const stored = sessionStorage.getItem(RECORDING_PARAMS_KEY);
    if (stored) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(stored);
      } catch {
        sessionStorage.removeItem(RECORDING_PARAMS_KEY);
        return null;
      }
      if (!isValidRecordingParams(parsed)) {
        sessionStorage.removeItem(RECORDING_PARAMS_KEY);
        return null;
      }
      if (Date.now() - parsed.storedAt < RECORDING_PARAMS_TTL_MS) {
        return parsed;
      }
      // Expired – clean up
      sessionStorage.removeItem(RECORDING_PARAMS_KEY);
    }
  } catch { /* URL/sessionStorage unavailable */ }

  return null;
}

/** Remove persisted recording params once recording has started. */
function clearManualRecordingParams(): void {
  try { sessionStorage.removeItem(RECORDING_PARAMS_KEY); } catch { /* noop */ }
}

/**
 * Dynamically inject floe-recorder.js into the page, wait for it to load,
 * then configure and start recording.
 */
async function initManualRecording(
  params: RecordingParams,
  apiUrl: string,
): Promise<void> {
  defaultLogger.log('[OnboardingSDK] 🎬 Manual recording mode detected – loading recorder…');

  // Determine the recorder script URL.
  // The recorder is co-deployed with the SDK on the same CDN, so prefer the
  // SDK script's own origin (captured at load time). Fall back to the API URL
  // origin (which may also serve static assets in dev), then to a relative path.
  const recorderUrl = (() => {
    if (_sdkScriptOrigin) {
      return `${_sdkScriptOrigin}/floe-recorder.js`;
    }
    try {
      const base = new URL(apiUrl);
      return `${base.origin}/floe-recorder.js`;
    } catch {
      return '/floe-recorder.js';
    }
  })();

  await new Promise<void>((resolve, reject) => {
    if ((window as any).FloeRecorder) {
      resolve();
      return;
    }

    const LOAD_TIMEOUT_MS = 10_000;
    const script = document.createElement('script');
    script.src = recorderUrl;
    script.async = true;

    const timer = setTimeout(() => {
      script.onload = null;
      script.onerror = null;
      script.remove();
      reject(new Error(`FloeRecorder script load timed out after ${LOAD_TIMEOUT_MS}ms: ${recorderUrl}`));
    }, LOAD_TIMEOUT_MS);

    script.onload = () => { clearTimeout(timer); resolve(); };
    script.onerror = () => {
      clearTimeout(timer);
      script.remove();
      reject(new Error(`Failed to load FloeRecorder from ${recorderUrl}`));
    };
    document.head.appendChild(script);
  });

  const FloeRecorder = (window as any).FloeRecorder;
  if (!FloeRecorder) {
    throw new Error('FloeRecorder not found after script load');
  }

  // Configure recorder for the dashboard flow:
  //  - Use the pre-created recording ID (skip creation step)
  //  - Authenticate with the short-lived recording token
  FloeRecorder.configure({
    apiEndpoint: apiUrl,
    siteId: params.siteId,
    recordingId: params.recordingId,
    recordingToken: params.recordingToken,
    autoUpload: true,
  });

  // Clear persisted params before starting so a page reload doesn't
  // accidentally restart recording.
  clearManualRecordingParams();

  await FloeRecorder.start();
  defaultLogger.log('[OnboardingSDK] ✅ Recording started');
}

/** Messages from the same role within this window (ms) are candidates for merging. */
const MERGE_WINDOW_MS = 30_000; // 30 seconds

/**
 * Main Onboarding SDK
 */
export class OnboardingSDK extends EventEmitter {
  private readonly logger: Logger;
  private config: SDKConfig;
  /**
   * FLO-451: does this agent act on the HOST page? Onboarding only — the demo
   * agent drives a server-side Playwright browser, the website agent drives
   * nothing. Gates every host-page capture (DOM + accessible snapshots,
   * interaction listeners). Derived once in the constructor from the same
   * predicate `index.ts` documents; see agent-mode.ts for why it is positive
   * rather than `!demoMode`.
   */
  private readonly drivesHostPage: boolean;
  /** Automatic desktop exit intent; absent until the marketing UI is ready. */
  private exitIntentDetector: ExitIntentDetector | null = null;
  /** One prompt per SDK/page instance, including manual `showExitIntent()`. */
  private exitIntentShown = false;
  /** Memoized so the intro form's check effect sees one stable function. */
  private emailCheck: EmailCheck | null = null;
  /** Any real marketing-session start suppresses a later exit prompt. */
  private exitIntentEngaged = false;
  /** Mints ONE id per session START — see session-id.ts (PR #620 r6, C1). */
  private readonly sessionIdSource = new SessionIdSource();
  /** The CURRENT session's browser writer-correlation id. Refreshed at
   *  every session start; before the first start it already holds the id that
   *  start will use, so pre-start reads (getSessionId, logs) stay truthful. */
  private sessionId: string;
  private overlay: OverlaySystem;
  private capture: CanvasScreenshotCapture;
  private actionExecutor: ActionExecutor;
  private pipecatClient: PipecatClient | null = null;
  private pipecatCallbackGeneration = 0;
  private transport: Transport | null = null;
  private isInitialized = false;
  /** Ends init()'s wait before it retries a throttled validate-client-key. */
  private cancelValidationRetry: (() => void) | null = null;
  /** How many times disconnect() has run: init() stops if it changes under it. */
  private teardownCount = 0;
  private ownsUiStrings = false;
  private reactRoot: ReactDOM.Root | null = null;
  private reactContainer: HTMLDivElement | null = null;
  /** Unbinds the one-shot voice-asset prefetch listeners (see armVoiceStartupPrefetch). */
  private voiceStartupPrefetchAbort: AbortController | null = null;
  /** Cancels the boot-time (idle-deferred) prefetch if the SDK is torn down first. */
  private voiceStartupPrefetchCancel: (() => void) | null = null;
  /** Cancels the SCHEDULED load of the site's whole nudge list (see armRemainingNudgesLoad). */
  private remainingNudgesLoadCancel: (() => void) | null = null;
  /** A load of that list is in flight: a navigation must not start a second. */
  private remainingNudgesLoading = false;
  /** Bumped on teardown, so a load that answers afterwards is ignored. */
  private remainingNudgesRun = 0;
  /** Loads of that list that have failed since it was armed (see MAX_NUDGE_LOAD_FAILURES). */
  private remainingNudgesFailures = 0;
  /** The visitor moved to another page while a load was out: if it fails, ask again. */
  private remainingNudgesAskedAgain = false;
  private agentState: 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'guiding' | 'paused' | 'minimized' = 'idle';
  private fatalError: string | null = null; // Set when the server reports init_error (e.g. site context failed to load) — shown instead of a bot greeting
  private pausedSessionId: string | null = null; // UI state only - tracks which session is paused
  private pendingResumeSessionId: string | null = null; // Explicit resume intent - set only by resumeSession(), cleared after use
  private botTranscript: string = '';
  private userTranscript: string = '';
  private transcriptHistory: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: number;
    isPartial?: boolean;
    sources?: Array<{
      name: string;
      snippet?: string | null;
      url?: string | null;
      confidence: number;
      source_type: 'content' | 'knowledge' | 'plan';
    }>;
  }> = [];
  /**
   * Sources from a `bot_citation` RTVI message that arrived BEFORE the
   * corresponding assistant transcript was finalized. Drained into the next
   * assistant message that lands in transcriptHistory. Rare in practice
   * (citation emits AFTER TTSStopped) but the buffer prevents lost chips.
   */
  private pendingCitationSources: Array<{
    name: string;
    snippet?: string | null;
    url?: string | null;
    confidence: number;
    source_type: 'content' | 'knowledge' | 'plan';
  }> | null = null;
  private lastUserTranscript: string = ''; // Track final user transcript for history
  private isConnected: boolean = false;
  private connectInFlight: boolean = false; // Guard against concurrent connect/cancel races
  private micEnabled: boolean = true; // Track actual mute state
  private userIsSpeaking: boolean = false; // Track if user is actively speaking
  private demoIntroFormSubmitted: boolean = false; // Track if the demo intro form has been submitted
  private botIsSpeaking: boolean = false; // Track if bot is actively speaking
  // The LLM and audio lifecycles overlap in avatar mode: RTVI can report the
  // completed text before Anam has finished playing it. Keep the physical audio
  // edge separate from botIsSpeaking, which also drives the visible transcript.
  private botAudioSpeaking: boolean = false;
  private botSpeakingEventOpen: boolean = false;
  private botTurnInterrupted: boolean = false;
  private llmTranscriptPending: boolean = false;
  private audioLevels: number[] = []; // Real-time audio levels for visualization
  private _discoveryPopupShown: boolean = false; // Track if discovery popup was shown this session
  private botAudioAnalyzer: AudioAnalyzer | null = null; // Audio analyzer for bot FFT
  private userAudioAnalyzer: AudioAnalyzer | null = null; // Audio analyzer for user mic FFT
  private userMicStream: MediaStream | null = null; // User's mic stream for analysis
  private userMicStreamRequestId: number = 0; // Guards async getUserMedia against disconnect races
  // Mic stream acquired during the user gesture by ensureMicPermission() (FLO-109).
  // Reused by setupUserMicAnalysis() so we never fire a second getUserMedia (and
  // thus never risk a second permission prompt) for the same session.
  private primedMicStream: MediaStream | null = null;
  // FLO-109: true while a demo has connected with the mic DEFERRED — i.e. the
  // session was started OUTSIDE a user gesture, where calling getUserMedia
  // would never surface the iOS Safari mic prompt and would silently strand
  // the prospect in text mode. Such a session connects mic-less and acquires
  // the mic from the next real tap (primeMicFromGesture via
  // markDemoIntroFormSubmitted).
  //
  // No shipped path sets this any more. Its one caller was the prefilled-email
  // auto-start in init(), removed when the prefilled path moved to tap-to-start
  // — the tap now starts the session AND acquires the mic on the same gesture,
  // which is what deferral was working around. Retained because
  // startDemoSessionAfterEmail({ deferMic: true }) is public surface an
  // embedder can still drive from a non-gesture context.
  private voicePendingGesture: boolean = false;
  private audioElements: HTMLAudioElement[] = [];
  private botVideoStream: MediaStream | null = null; // Bot's video track (demo mode screencast)
  /**
   * Avatar video track — the AI presenter's lip-synced face video. Populated
   * when the avatar publisher joins the Daily room as its own participant
   * (under the known user_name FLOE_AVATAR_USER_NAME) and its video track
   * arrives. Null when the agent has no avatar enabled (no avatar in the
   * room) or before the avatar's track arrives.
   */
  private avatarVideoStream: MediaStream | null = null;
  /**
   * True once Pipecat fires onBotReady (transport reached "ready" state).
   * Different from isConnected (set on transport "connected") — bot-ready can
   * trail the connection by a beat or two while the server finishes pipeline
   * setup (and, when an avatar is enabled, its session spin-up).
   * sendClientMessage() throws if called before this is true, so any
   * client→server message must gate on it.
   */
  private isBotReady: boolean = false;
  /**
   * Outbound client messages queued because the transport wasn't ready yet.
   * Flushed in order by onBotReady. Bounded by the natural rate of user input
   * (intro form + first text message) — no explicit max length needed for the
   * demo flow.
   */
  private pendingClientMessages: Array<{ type: string; data: any }> = [];
  private demoError: string | null = null; // Error message for demo mode fallback UI

  /**
   * End-of-demo wrap-up payload, set by the `demo_ended` RTVI server message
   * the bot emits when its `end_demo` tool runs. Powers the post-demo screen
   * in DemoView (features list, summary, next-step CTA). Null until the bot
   * ends the session normally. On error/disconnect this stays null and the
   * error path takes over instead.
   */
  private demoEnded: boolean = false;
  private demoSummary: string | null = null;
  private demoFeaturesShown: string[] = [];
  private demoNextStep: string | null = null;

  /**
   * FLO-307: deterministic opener chips, pushed by the server via a
   * `suggested_options` RTVI message at kickoff (never model-emitted, so
   * they're guaranteed present on turn 1). Rendered as tappable coral chips
   * above the demo text input. Transient: cleared on the next user turn
   * (typed, spoken, or a chip tap) and on `demo_ended`, so a stale chip can
   * never fire a second, unwanted turn mid-demo.
   */
  private suggestedOptions: string[] = [];
  /**
   * Optional wire ownership for the current row. Direct-demo deterministic
   * rows without matching language metadata are not safe outside English;
   * generated website follow-ups remain session-authored.
   */
  private suggestedOptionsMetadata: {
    language?: SupportedLanguage;
    source?: string;
  } | null = null;

  /**
   * FLO-618: how many times the bot has asked the website agent to put the
   * booking calendar on screen (`show_calendar`). A COUNTER rather than a
   * boolean so a second ask, after the visitor closed the first calendar,
   * is still a state change the view can react to. Never reset — the view
   * only ever compares it against the previous render's value.
   */
  private websiteAgentCalendarRequestId: number = 0;

  /**
   * The website→demo hand-over, as the server brackets it: `demo_starting` is
   * the START edge, `demo_visible` the END one (see sdk/demo-handover.ts).
   *
   * COUNTERS, like `websiteAgentCalendarRequestId` above and for the same
   * reason — a boolean already `true` is not a state change the view can react
   * to, and a visitor can be handed to a demo twice in one page session. Unlike
   * that one these DO reset, because the view's card state is scoped to a single
   * transition; `resetWebsiteAgentDemoHandover` owns every reset site.
   *
   * Both stay 0 for the demo and onboarding agents: the branches that raise them
   * are gated on `config.websiteAgent`, so nothing here can leak into a surface
   * that never has a hand-over to narrate.
   */
  private websiteAgentDemoStartingId: number = 0;
  private websiteAgentDemoVisibleId: number = 0;

  // Customer brand info — populated by validateClientKeyAndBotStatus when the
  // API returns siteDomain / siteLogoUrl. Drives the "demoing <Product>"
  // sidebar header + the branded loading screen in DemoView. Empty strings
  // (not null) so JSX can simply check truthiness without `??`.
  private siteName: string = '';
  private siteDomain: string = '';
  private siteLogoUrl: string = '';
  // FLO-296: self-hosted favicon (R2 URL) from validate-client-key. DemoView
  // renders siteLogoUrl → siteFaviconUrl → letter-mark, so the overlay no
  // longer builds a live Google s2/favicons URL. Empty = "not resolved yet".
  private siteFaviconUrl: string = '';
  // Demo overlay branding (FLO-64). Per-site values from validate-client-key,
  // overridable at init via config.demoAccentColor / config.demoCtaLabel.
  // Empty string = "not set" → DemoView falls back to Floe defaults.
  private siteAccentColor: string = '';
  private siteCtaLabel: string = '';
  /**
   * FLO-322: server truth — a live demo is BOTH entitled AND runnable. Gates the
   * push-to-demo CTA so we never show a dead "see it live" that spins a paid
   * session against an unrunnable site. Fail-safe default false: an absent field
   * never lights the CTA or auto-spins a demo we could not verify.
   */
  private demoAvailable: boolean = false;
  /**
   * Is the website agent entitled to start a session? (validate-client-key.)
   *
   * Distinct from `demoAvailable`, which is a DEMO-agent fact
   * (`demoAgentEnabled && isDemoReady`). A site entitled to the demo agent but
   * not the website agent passes `demoAvailable` while `/start-session`
   * (`entryPhase: 'q_and_a'`) throws Forbidden — so the host CTA must consult
   * this too or it green-lights a session that 403s.
   *
   * Defaults TRUE: it ships with `demoAvailable` from the same floe-api
   * version, and that one already fails closed, so "not reported" can only ever
   * mean an older server — never an unentitled site whose CTA should go dark.
   */
  private websiteAgentEnabled: boolean = true;
  /**
   * Visitor language policy and committed locale. All language packs arrive in
   * the validation response and stay instance-owned; only this active SDK's
   * selected pack is installed in the page-global string resolver.
   */
  private localizationPolicy: LocalizationPolicy = FIXED_ENGLISH_POLICY;
  private selectedLanguage: SupportedLanguage = 'en';
  private languageStorageKey: string = '';
  private languageSwitchState: LanguageSwitchState = { pending: null, error: null };
  private languageSwitchAckTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * FLO-318/349: resolved per-site website-agent config (opener chips, section
   * nudges, optional avatar, push-to-demo CTA) served by validateClientKey.
   * Null until validation succeeds → the SDK uses inert defaults (no chips
   * override, no nudges, no avatar).
   */
  private websiteAgentConfig: {
    openerChips?: string[];
    nudges?: {
      enabled?: boolean;
      contentMap?: WebsiteAgentNudgeRow[];
      /** floe-api left rows out of this response: load the whole list. */
      hasMore?: boolean;
    };
    // FLO-520/FLO-753: `enabled` gates every avatar surface. The media URLs stay
    // in config so re-enabling restores them, while the render layer suppresses
    // them whenever the switch is off. Absent on older floe-api builds → the SDK
    // renders the "Ask {agent}" pill.
    avatar?: {
      enabled?: boolean;
      idleLoopUrl?: string;
      posterUrl?: string;
      // The approach intro (floe-api AvatarIntroFiles). Untyped on purpose: it
      // is validated field by field in `resolveAvatarMedia`, the one place that
      // decides whether an intro is usable. Absent/null on older floe-api
      // builds and on avatars without one → the launcher rests on the loop.
      intro?: unknown;
    };
    pushToDemo?: { enabled?: boolean; ctaLabel?: string };
    // FLO-514/519: docked-sidebar display mode. Absent on older floe-api builds
    // → the render layer falls back to 'floating' / 400px.
    displayMode?: 'floating' | 'sidebar';
    sidebarWidth?: number;
    // FLO-523: zero-cooperation squeeze hardening, per site. BOTH default false
    // (absent facet = off) — these rewrite the host's document scroller and
    // patch its globals, so they only ever run where an operator asked for them.
    sidebarCompat?: { containment?: boolean; viewportFacade?: boolean };
    identityCapture?: {
      enabled?: boolean;
      timing?: 'never' | 'on_open' | 'after_n_questions' | 'on_demo_intent';
      afterNQuestions?: number;
      fields?: Array<'email' | 'name' | 'company' | 'role' | 'phone'>;
      askFields?: Array<'email' | 'name' | 'company' | 'role' | 'phone'>;
      requiredFields?: Array<'email' | 'name' | 'company' | 'role' | 'phone'>;
      phoneRegion?: string;
    };
    // Per-site default for the visitor-consent mode (additive — absent on
    // older floe-api builds, and junk is folded to 'not-required' by
    // resolveConsentMode). An explicit `config.consentMode` wins over it,
    // same precedence as displayMode/sidebarWidth above.
    consentModeDefault?: 'not-required' | 'pending';
  } | null = null;
  /**
   * The DEMO agent's identity-capture policy, resolved site-level by floe-api's
   * `validateClientKey`. Null before validation, which `captureIsDeferred`
   * reads as today's gating intro form — the fail-safe direction, since the
   * alternative is silently not capturing leads.
   *
   * `config.demoIdentityCapture` (set by the /d/{token} launch shell, which
   * carries the already-merged site ← link answer) WINS over this. Same
   * precedence as `websiteAgentDisplayMode` and friends: an explicit init
   * option beats the fetched site config.
   */
  private demoCaptureConfig: DemoCapturePolicy | null = null;
  /**
   * FLO — deferred capture: the server asked for the identity card this session
   * (the demo Shepherd pushed `capture_identity` at the configured arc point).
   * Session-scoped, one-way: it never un-sets, so a late frame can't retract a
   * card the prospect is already typing into.
   */
  private identityAsk: IdentityAskState | null = null;
  private identitySubmissionPending: string | null = null;
  private websiteIdentityAskRequested = false;
  /** Submitted or dismissed. One ask per session, either way. */

  /**
   * HTTPS booking URL configured on this Site in the Floe dashboard.
   * Falls back here if the customer didn't pass demoCalendarLink explicitly
   * in their Floe init snippet. Drives the post-demo iframe in DemoView.
   * Empty string when no URL is configured.
   */
  private siteCalBookingUrl: string = '';
  /** Current session's stated/confirmed email; never persisted for booking. */
  private bookingEmail: string | undefined;
  /** Provider URL decorated with a short-lived opaque source correlation. */
  private attributedBookingUrl: string = '';
  private attributedBookingUrlExpiresAtMs = 0;
  private bookingAttributionRequest: Promise<string> | null = null;
  private bookingAttributionGeneration = 0;

  // Deferred-start state for demo mode. We intentionally do NOT call
  // pipecatClient.startBotAndConnect at init time anymore — that hits
  // /sites/demo/:siteId/start which spawns a (paid) pipecat + avatar
  // session. Instead we render the SDK overlay with the intro form,
  // wait for the prospect to enter an email (Stage 1 advance), and
  // only THEN fire startBotAndConnect via startDemoSessionAfterEmail.
  //
  // demoSessionStarted is idempotency on that path (autoSubmit + manual
  // submit + duplicate ready event must not race two /demo/start calls).
  private demoSessionStarted: boolean = false;

  // FLO-320: idempotency for the website-agent session. The launcher pill's
  // click fires startWebsiteAgentSession(); a double-tap (or a re-open after a
  // collapse) must not spawn a second /sites/start-session.
  private websiteAgentSessionStarted: boolean = false;
  // FLO-390: monotonic session token, bumped each time a website-agent session
  // starts. endWebsiteAgentSession() captures it before its async disconnect and
  // bails on the post-await teardown if a fresh session started meanwhile — so a
  // re-tap during the async close can't have its transport/mic/audio clobbered.
  private websiteAgentGeneration: number = 0;
  // FLO-544: the FLO-329 conversation this session belongs to, as returned by
  // POST /sites/web-agent/session. Held IN MEMORY for exactly this session and
  // never read back from storage: only an id the server just handed us — under
  // this origin's validated clientKey — is fit to forward to /sites/start-session
  // (a stale or hand-edited one would be a request to write into some other
  // thread, which floe-api then has to reject).
  private websiteAgentConversationId: string | null = null;
  // FLO-557: the thread this visitor ENDED, until an open comes back with a
  // different one. The persistent half of this lives in sessionStorage
  // (`floe_wa_forgotten`) and survives a reload — but it is only written in
  // SIDEBAR mode, because the view gates the whole continuity module on it.
  // This field is the same fact for a floating-card session, where "forget this
  // chat" has to hold for the rest of the page load at least. Both are read as
  // one: {@link forgottenConversationId}.
  private websiteAgentEndedConversationId: string | null = null;
  // The resumed conversation's turns, once the server has told us it has any.
  // Handed to WebsiteAgentView as a render prop, where they REPLACE the
  // sessionStorage-restored history. Null = nothing to reconcile (no resume, a
  // failed one, or a resumed-but-empty thread — see reconcileRestoredMessages).
  private websiteAgentResumedMessages: PersistedMessage[] | null = null;

  // ── CDP Phase 1 (FLO-547): visitor behavioral events ──────────────────────
  /** Batched emitter for the C1 ingest endpoint. Created in the constructor;
   *  armed by bootstrap() only after validate-client-key succeeded. */
  private visitorEvents!: VisitorEventsClient;
  /** C5 consent kill switch from validate-client-key. Absent (older floe-api)
   *  → stays true; the server 200-drops batches when actually disabled. */
  private eventCollectionEnabled: boolean = true;
  /** FLO-960 deployment handshake from validate-client-key. Absent (older
   *  floe-api, or telemetry not deployed) → stays FALSE: no SDK should POST to
   *  an endpoint that may not exist. */
  private sdkDiagnosticsEnabled: boolean = false;
  // ── FLO-960: SDK lifecycle/failure diagnostics ────────────────────────────
  /** Batched emitter for POST /sites/telemetry. Created in the constructor
   *  (inert), armed after validate-client-key alongside visitorEvents, and
   *  turned off by the same C5 switch and by consent('denied'). Own vocabulary
   *  and allowlist live in sdk-diagnostics.ts — see the contract doc there. */
  private sdkDiagnostics!: SdkDiagnosticsClient;
  /** Set for the span of a teardown the SDK ITSELF asked for, so
   *  onDisconnected can report `client_requested` instead of a bare
   *  `transport_closed`. Cleared once the teardown settles. */
  private sdkRequestedTeardown = false;
  /** The client a teardown is disconnecting, for as long as that disconnect is
   *  awaited: a connect on THIS client that rejects meanwhile was ended by the
   *  teardown, not by a failure. Scoped to the client, unlike the flag above —
   *  a visitor who re-taps while the last session is still leaving has a new
   *  client, and its failures are real. */
  private clientBeingTornDown: PipecatClient | null = null;
  /** Detaches the startup-mark observer from the module-level timeline, so a
   *  torn-down SDK is not retained by a closure it left behind. */
  private voiceStartupMarkUnsubscribe: (() => void) | null = null;
  /**
   * FLO-960: the pending `first_audio` candidate — the bot's audio element for
   * this run plus whether it has begun playing. Readiness is RECORDED, not
   * reported, because a playing element can still be muted or silent.
   */
  private firstAudioCandidate: {
    audio: HTMLAudioElement;
    track: MediaStreamTrack;
    callbackGeneration: number;
    websiteAgentGeneration: number;
    playbackActive: boolean;
    detach: () => void;
  } | null = null;
  // ── Per-visitor consent mode ──────────────────────────────────────────────
  // Distinct from C5 above: that is the OPERATOR's site-wide switch; this is
  // the VISITOR's. Resolved at init (config.consentMode → the per-site
  // `websiteAgent.consentModeDefault` → 'not-required'); every identity call
  // site asks `identityConsented()` — see consent-mode.ts for the contract.
  /** Effective mode. 'not-required' until init resolves it, which is safe:
   *  nothing identity-bearing runs before validate-client-key anyway. */
  private consentMode: ConsentMode = 'not-required';
  /** The visitor's decision via `consent()`. 'denied' is sticky for the load. */
  private consentDecision: ConsentDecision | null = null;
  /** Init reached (and deferred) the visitor-events bootstrap point, so a
   *  later `consent('granted')` must run it. False before validation — a
   *  grant landing that early is simply recorded; init picks it up. */
  private consentBootstrapDeferred: boolean = false;
  /** FLO-544 honesty: how THIS session's open/resume ended. Null = no session
   *  (a dormant restored rail implies nothing about memory). Drives the
   *  "starting fresh" divider over a restored transcript the bot cannot see. */
  private websiteAgentResumeOutcome: ResumeOutcome | null = null;
  /** Epoch ms of the WIDGET_OPENED this page load, null while closed. Also the
   *  guard that keeps a restore-painted panel's later close from emitting a
   *  WIDGET_CLOSED with no matching open (the restore is NOT an open). */
  private widgetOpenedAtMs: number | null = null;
  /** Epoch ms of the VOICE_STARTED span in flight, null while the mic is off. */
  private voiceStartedAtMs: number | null = null;

  // Speaker mute (bot audio output). Pipecat client-js doesn't ship a
  // `setSpeakerMute` primitive — enableMic/enableCam/enableScreenShare is
  // the whole abstract API. So we own it here. Two layers of belt:
  //   (1) Walk `this.audioElements` (the <audio> elements we mounted in
  //       trackStarted) and toggle `.muted`. This is the same primitive
  //       the user would reach for; we just keep it scoped to OUR elements
  //       instead of querying the host page DOM.
  //   (2) Toggle `enabled` on `this.pipecatClient.tracks().bot?.audio` —
  //       on a received track this silences local rendering at the
  //       MediaStreamTrack layer (browsers gate playback on the receiver's
  //       enabled bit). Belt-and-suspenders for hosts that route audio
  //       through Web Audio rather than <audio> elements.
  // Tracks that arrive AFTER the user mutes are handled inside the
  // trackStarted handler by reading `this.speakerMuted` at mount time.
  private speakerMuted: boolean = false;
  private pageTracker: PageTracker;
  private screenShareEnabled: boolean = false;
  private domIntrospector: DOMIntrospector;
  private accessibleSnapshot: AccessibleSnapshotCapture;

  // Fast Execution System
  private fastExecutionMode: boolean = false;
  private batchedActionExecutor: BatchedActionExecutor | null = null;
  private navigationCompleteDetector: NavigationCompleteDetector | null = null;

  // Bot Action Tracking - counter for nested/concurrent bot actions
  // Use counter instead of boolean to handle nested actions correctly
  private _botActionDepth: number = 0;
  private pendingTypeAction: AbortController | null = null;
  private activePlanRunId: string | null = null;
  private retiredPlanRunIds = new Set<string>();
  private identifiedPlanRuns = false;
  private legacyPlanActionsAllowed = true;
  private planSessionOpen = true;

  // EndUser status from API (for skip onboarding logic)
  private endUserStatus: EndUserStatus | null = null;

  // UX Polish - Connection progress and returning user tracking
  private connectionProgress: { stage: 'initializing' | 'authenticating' | 'connecting' | 'joining' | 'ready' | 'error'; message: string; progress: number } | null = null;
  private isReturningUser: boolean = false; // True only for actual returning users (detected via API or localStorage)
  private _isMinimized: boolean = false;

  // Text mode state

  /**
   * FLO-379: which stream drives the on-screen bot transcript.
   *  - 'llm': render each LLM token the instant it arrives (fast, decoupled from
   *    speech) — text mode AND the website-agent.
   *  - 'tts': render at spoken (TTS) rate — the voice/demo default, and the basis
   *    for the opt-in closed-caption mode.
   * Derived so onboarding's runtime text-mode toggle keeps working; the ONLY new
   * behavior vs today is the website-agent flipping 'tts'→'llm'. Demo + onboarding
   * voice stay 'tts' (byte-for-byte unchanged). FLO-379b will opt the demo into
   * 'llm' behind marker-leak verification.
   */
  private get captionSource(): 'llm' | 'tts' {
    // While a LIVE DEMO is on screen (botVideoStream set — the website-agent's
    // driving phase, or a demo), the bot narrates with deixis/executor markers
    // ([[point:...]], [B5]) that are stripped ONLY on the TTS path, so render
    // TTS-paced there. FLO-379b will make the demo LLM-paced once the post-strip
    // text is verified. Q&A answers (no demo on screen) are marker-free → 'llm'.
    if (this.botVideoStream) return 'tts';
    // Speaker muted ⇒ no TTS is generated at all, so there is no TTS cadence
    // to pace captions from — they must be LLM-paced.
    return this.speakerMuted || this.config.websiteAgent ? 'llm' : 'tts';
  }
  /**
   * FLO-379 (#371 review): caption ownership LATCHED at turn start. captionSource
   * is derived from botVideoStream, which can flip mid-turn — a Q&A answer is
   * still streaming LLM-paced when start_demo attaches the demo video, so the
   * getter flips 'llm'→'tts'. Re-deriving the getter per event would then strand
   * the buffered LLM transcript (onBotLlmStopped early-returns; the TTS path never
   * held the text). onBotLlmStarted latches this and the LLM lifecycle uses it.
   */
  private llmCaptionTurn: boolean = false;
  // Keep caption ownership through delayed playback, even after the LLM has
  // finished. Attaching the demo video must not replay an LLM-rendered answer
  // from its TTS frames, or mix both streams into the same live caption.
  private captionTurnSource: 'llm' | 'tts' | null = null;
  // A muted Q&A reply has no playback to wait for. Keep this on the response,
  // so a later mute toggle or demo handoff cannot change its ownership.
  private captionTurnPlaybackExpected: boolean = false;
  /**
   * The assistant entry the last finalized caption turn produced, while it is
   * still the newest ASSISTANT entry — the visitor's own next message may
   * already sit below it.
   *
   * The word-frame stream and the audio lifecycle are independent: a TTS frame
   * can land AFTER its own segment's bot-stopped-speaking. Those words used to
   * open a live caption that no edge owned — the next segment's start either
   * wiped them or, while a deferred LLM turn still held llmTranscriptPending,
   * prepended them to the NEXT answer. That is why every answer on floe.so read
   * one clause behind the question and only completed when the visitor sent
   * their next message. A late frame now finishes the entry it belongs to, by
   * id, wherever the entry has ended up — a position lookup breaks the moment
   * the visitor replies before the tail lands, which is the common case for the
   * very words this exists to save.
   */
  private openAssistantMessageId: string | null = null;
  /**
   * The entry {@link openAssistantMessageId} named until a newer assistant turn
   * took over.
   *
   * A word frame trailing THAT turn can no longer be filed anywhere: the answer
   * it belongs to is closed, and the live buffer now belongs to a different
   * answer — which is exactly how these words used to surface inside the next
   * reply, or get wiped by its first segment. They are dropped instead. Losing
   * a word the visitor has already heard beats putting it in another turn's
   * mouth.
   */
  private supersededAssistantMessageId: string | null = null;
  // `assistant-<epoch>` alone collides when two turns finalize in the same
  // millisecond, and the late-frame lookup above needs the id to identify ONE
  // entry. See withRestoredIds() in wa-continuity.ts for the same concern.
  private assistantMessageSeq: number = 0;
  // Where a deferred transcript belongs in the history. A reply held for its
  // playback can outlive the visitor's next message; appending it on release
  // would file the answer BELOW the question it answered.
  private llmTranscriptPendingIndex: number | null = null;
  /**
   * Text already committed from LLM tokens whose spoken echo must stay silent.
   *
   * FLO-841 keeps caption ownership latched to 'llm' after such a turn so the
   * demo handoff cannot render the same answer a second time from its TTS
   * frames. The latch had no other release than that playback's own stop edge,
   * so a turn whose expected audio never arrived swallowed the NEXT spoken
   * utterance whole. Suppression now lasts exactly as long as the words keep
   * matching what was already rendered.
   */
  private captionEchoText: string | null = null;
  private captionEchoHeard: string = '';

  private get activeCaptionSource(): 'llm' | 'tts' {
    return this.captionTurnSource ?? this.captionSource;
  }
  private micPermissionDenied: boolean = false; // True when browser denied/cancelled mic access
  // True when the last mic probe produced no usable mic for ANY reason (denial,
  // no device, unreadable device, no mediaDevices). Superset of
  // micPermissionDenied — see applyMicProbeResult().
  private micProbeFailed: boolean = false;
  // FLO-706: Website Agent text sessions deliberately skip getUserMedia. This
  // distinguishes that unprobed posture from a real probe failure, so the first
  // explicit mic click can request permission and enable voice in one action.
  private websiteAgentMicPermissionReady: boolean = false;
  // Coalesce repeated mic clicks while a browser permission sheet is open. The
  // probe itself is origin-scoped, so a replacement Website Agent session may
  // safely consume the same result if its own explicit mic action joins it.
  private websiteAgentMicRequestInFlight: Promise<MicProbeResult> | null = null;
  // FLO-786: this session already made its ONE automatic (non-gesture) mic
  // request. The surface can remount around the post-welcome handoff — the
  // compact dock and the full conversation are different layout trees — and a
  // second unrequested prompt is exactly what browsers punish (Chrome starts
  // auto-blocking after dismissals). Tap-driven requests are unaffected.
  private websiteAgentAutoMicRequested: boolean = false;
  // FLO-786: live subscription to this origin's microphone permission. The SDK
  // used to READ that state twice and never listen, which made a blocked mic a
  // dead end inside the widget: the visitor could fix it in the address bar and
  // the panel would still be explaining the block, waiting for a tap it no
  // longer needed. Attached the first time a session has a mic verdict worth
  // watching, torn down with the session.
  private micPermissionWatch: {
    status: MicPermissionStatus;
    handler: () => void;
  } | null = null;
  // Bumped by every teardown. `watchMicPermission()` attaches its listener
  // AFTER an awaited query, so without this a teardown that lands during that
  // query cancels nothing: the listener attaches to a session that has already
  // ended, nothing ever removes it, and the browser's EventTarget then pins
  // this whole SDK instance in memory and re-renders a dormant launcher on
  // somebody else's permission change.
  private micPermissionWatchToken: number = 0;
  // FLO-786: when the automatic recovery above stops applying.
  //
  // `navigator.permissions` is scoped to the ORIGIN, not to the script that
  // asked: a change event cannot tell "the visitor went and unblocked us" from
  // "the visitor granted a mic to some other widget on the customer's page".
  // Attributing it to us is only honest close to our own request — the visitor
  // story this exists for is fixing the block in the address bar and coming
  // straight back. After that, a grant is somebody else's business and the mic
  // waits for a tap. A REVOCATION is never time-bounded; that always applies.
  private micRecoveryDeadlineMs: number | null = null;

  // Plan progress state (sent from bot during guided plan execution)
  private planProgress: {
    planName: string;
    currentStep: number;
    totalSteps: number;
    progressPercent: number;
    currentTaskTitle: string;
  } | null = null;

  constructor(config: SDKConfig, logger?: Logger) {
    super();

    // Validate config
    if (!config.clientKey) {
      throw new Error('Client key is required');
    }

    // Set defaults
    // Use baked-in API URL from build (__FLOE_API_URL__) or fallback to localhost
    const defaultApiUrl = typeof __FLOE_API_URL__ !== 'undefined'
      ? __FLOE_API_URL__
      : 'http://localhost:3001';

    this.config = {
      apiUrl: defaultApiUrl,  // Floe API URL (baked in at build time based on environment)
      enableVideo: false,
      enableAudio: true,  // Default to audio enabled for voice
      enableScreenCapture: true,
      debug: false,
      ...config
    };

    // Use the injected logger if provided; otherwise create one scoped to this instance.
    this.logger = logger ?? createLogger(this.config.debug ?? false);

    // FLO-451: derive the host-page capability once, from the mode flags, so no
    // call site re-derives it (and gets it wrong when a fourth mode arrives).
    this.drivesHostPage = drivesHostPage(this.config);

    // FLO-51: mic and speaker are two independent, independently-persisted
    // preferences. The config supplies the defaults; a stored preference (or a
    // migrated `floe_text_mode`) overrides them. The onboarding agent defaults
    // mic-ON — unlike the website agent, which joins muted because a marketing
    // page must not listen to strangers. This one runs post-signup and is
    // voice-first; joining muted would quietly retire that.
    //
    // Scoped to the ONBOARDING agent (drivesHostPage). The website and demo
    // agents each own their opening A/V posture deliberately — the website
    // agent joins mic-muted/speaker-on, the demo joins both on — and they
    // typically run on the marketing site while onboarding runs in the product.
    // Reading a shared key here would let a mute chosen in one agent silently
    // follow the visitor into another, which nobody asked for.
    if (this.drivesHostPage) {
      const avPrefs = readAvPrefs({
        micEnabled: this.config.enableAudio !== false,
        speakerMuted: false,
      });
      this.micEnabled = avPrefs.micEnabled;
      this.speakerMuted = avPrefs.speakerMuted;
    } else {
      this.micEnabled = this.config.enableAudio !== false;
    }

    // The id the FIRST session will run under (minted for real at each session
    // START — see beginPipecatSession).
    this.sessionId = this.sessionIdSource.current();

    // Initialize components
    this.overlay = new OverlaySystem(this.logger);
    this.capture = new CanvasScreenshotCapture(this.config.redactionPatterns, this.logger);
    // ActionExecutor reports React onClicks, which never bubble to PageTracker.
    // Hand it a private capability object rather than parking hooks on `window`,
    // where any script on the customer's page could read or replace them.
    this.actionExecutor = new ActionExecutor(this.logger, {
      isBotActive: () => this._botActionDepth > 0,
      sendClickMetadata: (data) => this.sendMetadata(data),
      // This reads only one visible active dialog. It is called synchronously by
      // the guarded VERIFY after exact identity checks, never as a generic page fallback.
      captureSupportRecordReadEvidence: () => collectDialogReadoutText(el =>
        el.isConnected && el.getClientRects().length > 0 &&
        getComputedStyle(el).visibility !== 'hidden' && !el.closest('[aria-hidden="true"], [hidden], [inert], [data-floe-widget]')
      ),
    });
    this.pageTracker = new PageTracker(this as any, this.logger);

    // CDP Phase 1 (FLO-547): visitor behavioral events. Constructed here but
    // inert until init() arms it via bootstrap() (post validate-client-key),
    // so nothing is emitted for an invalid key or a consent-off site (C5).
    this.visitorEvents = new VisitorEventsClient({
      apiUrl: () => this.config.apiUrl || '',
      clientKey: () => this.config.clientKey,
      logger: this.logger,
      // FLO-1096: read live, so a consent withdrawal or the site switch going
      // off stops the page being watched before the next summary is built.
      // Never for the in-product agent: only the two marketing-site agents.
      pageEngagementAllowed: () =>
        pageEngagementAllowed(
          this.eventCollectionEnabled,
          runsOnMarketingSite(this.config),
          this.consentMode,
          this.consentDecision,
        ),
    });
    // PAGE_VIEWED rides the existing URL-change poll (no second poller) —
    // consecutive same-path reports are deduped inside the client.
    this.pageTracker.onPageView((path) => this.visitorEvents.pageViewed(path));

    // FLO-960: SDK lifecycle/failure diagnostics. Inert until init() validates
    // the key AND the collection/consent gates allow it; the consent getter is
    // live so a withdrawal mid-session silences an already-queued batch.
    this.sdkDiagnostics = new SdkDiagnosticsClient({
      apiUrl: () => this.config.apiUrl || '',
      clientKey: () => this.config.clientKey,
      agentMode: () => this.diagnosticAgentMode(),
      consent: () =>
        this.identityConsented()
          ? this.consentDecision === 'granted'
            ? 'granted'
            : 'not-required'
          : null,
      // A pre-start mic denial still names the session ABOUT to start.
      fallbackSessionId: () => this.sessionIdSource.current(),
      logger: this.logger,
    });

    // Initialize DOM Introspection Layer
    this.domIntrospector = new DOMIntrospector(this.logger);

    // Initialize Accessible Snapshot capture (improved LLM-friendly DOM)
    this.accessibleSnapshot = new AccessibleSnapshotCapture(this.logger);

    // Initialize Fast Execution System
    this.batchedActionExecutor = new BatchedActionExecutor(this.overlay, this.actionExecutor, this.logger);
    this.batchedActionExecutor.setDOMIntrospector(this.domIntrospector);  // For DOM captures after each step
    // Note: navigationCompleteDetector is created in setupAudioPlayback() to centralize initialization

    // Claim page-global ownership before the constructor installs its shared
    // target resolver. Floe's fixed mount ids, page squeeze, and locale pack
    // are intentionally single-instance resources; a second instance must
    // fail before it can disturb the one already running.
    this.claimUiStringsOwnership();
    HybridTargetResolver.setDOMIntrospector(this.domIntrospector);

    // Never dump SDKConfig here. It may contain auth-provider functions, user
    // metadata, and future secret-bearing options. Log only a fixed safe
    // summary; notably, the MCP callback itself is never inspected or invoked.
    this.logger.log('[OnboardingSDK] Initialized', {
      mode: this.config.demoMode
        ? 'demo'
        : this.config.websiteAgent
          ? 'website_agent'
          : this.runsInSupportMode()
            ? 'support'
            : 'onboarding',
      mcpConfigured: Boolean(this.config.mcp),
    });
  }

  private claimUiStringsOwnership(): void {
    if (this.ownsUiStrings) return;
    claimUiStrings(this);
    this.ownsUiStrings = true;
  }

  /**
   * C1 (PR #620 r6): mint THIS session's Conversation writer id.
   *
   * Called at every session START seam — the onboarding connect in init(), the
   * demo start, the website-agent start, and the post-onboarding reconnect —
   * and nowhere else. The old constructor-scoped id was reused across restarts,
   * which made independent writers indistinguishable. Pipecat now owns the
   * separate server run id used for DemoSession, recording, and billing. The
   * first session keeps
   * the constructor-era value (SessionIdSource.begin()), so anything that read
   * getSessionId() before the start still names the session that then starts.
   *
   * Conversation continuity is untouched by the refresh: it rides the
   * conversationId + visitor token (FLO-524/544), never this writer id
   * — the only session-id storage write was the FLO-524 placeholder stamp,
   * removed for being wrong in exactly this direction.
   */
  private beginPipecatSession(): string {
    this.sessionId = this.sessionIdSource.begin();
    return this.sessionId;
  }

  /**
   * FLO-960: the surface this SDK instance reports as. Derived from the SAME
   * facts the constructor's init log prints — the config mode flags plus the
   * support-mode posture — so a diagnostics row and the SDK's own log line can
   * never disagree about which agent was running.
   *
   * Support mode is its own value rather than 'onboarding': it is the dormant,
   * user-initiated posture (FLO-534), and a support-mode start that never
   * happens fails for different reasons than a proactive onboarding one.
   */
  private diagnosticAgentMode(): SdkDiagnosticAgentMode {
    if (this.config.demoMode) return 'demo';
    if (this.config.websiteAgent) return 'website';
    return this.runsInSupportMode() ? 'support' : 'onboarding';
  }

  /**
   * FLO-960: the reason code for a thrown start, when the call site can do
   * better than the stage-derived default.
   *
   * A start that died because the visitor refused the microphone is
   * `permission_denied`, not a generic spawn failure — the two lead to
   * different investigations. So is one where the bot was in the room and
   * never answered `client-ready` (FLO-1140): that is `bot_ready_timeout`, the
   * same fact the 120s startup watchdog reports, only at the moment the
   * transport gives up instead of two minutes in. Everything else returns
   * undefined and lets `noteConnectFailed` classify by how far startup got.
   * Deliberately reads the error's CLASSIFIER, never its message: no exception
   * text is ever forwarded.
   */
  private diagnosticFailureReason(error: unknown): SdkDiagnosticReason | undefined {
    if (this.isMicPermissionError(error)) {
      return 'permission_denied';
    }
    return error instanceof RtviHandshakeTimeoutError ? 'bot_ready_timeout' : undefined;
  }

  /**
   * FLO-1140: was this client's rejected connect the SDK's own doing?
   *
   * A deliberate teardown awaits the client's disconnect, which aborts a
   * handshake still in flight — so the pending connect rejects INSIDE that
   * await. That rejection is the teardown working, not a start that failed: the
   * teardown already owns the cleanup and reports the ending as
   * `client_requested`, and a second report here would file a visitor closing
   * the widget as a `connect_failed` and hand the host an `error` for it.
   */
  private connectEndedByOwnTeardown(client: PipecatClient | null): boolean {
    return client !== null && client === this.clientBeingTornDown;
  }

  /**
   * FLO-960: the diagnostics reason for a microphone failure, or null when the
   * classifier could not tell us anything nameable.
   *
   * The classifier's `no-device` bucket is deliberately wide (NotFound,
   * NotReadable, Overconstrained, and no `mediaDevices` at all). Intake cares
   * about the split inside it, because a device another app is HOLDING is a
   * different report from a machine that has none — so the raw error name
   * decides, and it is never forwarded itself.
   */
  private diagnosticMicReason(
    kind: MicErrorKind,
    errorName?: string | null,
  ): 'permission_denied' | 'device_missing' | 'device_busy' | 'no_device' | null {
    if (kind === 'permission-denied') return 'permission_denied';
    if (kind === 'other') return null;
    if (errorName === 'NotReadableError') return 'device_busy';
    if (errorName) return 'device_missing';
    return 'no_device';
  }

  /**
   * FLO-960: mint this start's writer id AND open the diagnostics thread on it.
   *
   * ONE seam, called wherever a session start mints its id, so the id on the
   * telemetry row is the same one the API validates and forwards as
   * writerCorrelationId. A second call site that minted its own id (or reused
   * the constructor's) is exactly how a diagnostics thread would stop joining
   * to the conversation it describes.
   */
  private beginDiagnosticsTrackedSession(options?: { prefetched?: boolean }): string {
    const sessionId = this.beginPipecatSession();
    this.bindVoiceStartupMarks();
    this.sdkDiagnostics.startSession({
      correlationId: sessionId,
      prefetched: options?.prefetched,
    });
    return sessionId;
  }

  /**
   * FLO-960: mirror the existing click → greeting measurement onto the
   * diagnostics stream as `startup_stage` events.
   *
   * Reuses `voiceStartupTimeline` rather than timing anything a second time —
   * the marks (including the two taken from resource timing inside
   * FloeDailyTransport) already exist, and a parallel clock would drift from
   * the numbers VOICE_READY reports. Idempotent: the timeline is a module
   * singleton, so a re-init after disconnect() must not stack observers.
   */
  private bindVoiceStartupMarks(): void {
    if (this.voiceStartupMarkUnsubscribe) return;
    this.voiceStartupMarkUnsubscribe = voiceStartupTimeline.onMark((mark, msFromClick) => {
      const stage = startupStageForMark(mark);
      if (stage) this.sdkDiagnostics.noteStartupStage(stage, msFromClick);
    });
  }

  private languageSurface(): AgentLanguageSurface {
    if (this.config.demoMode) return 'demo';
    if (this.config.websiteAgent) return 'website';
    return 'onboarding';
  }

  private browserStorage(): StorageLike | undefined {
    try {
      return typeof window !== 'undefined' ? window.localStorage : undefined;
    } catch {
      return undefined;
    }
  }

  private configureLocalization(result: Record<string, any>): void {
    this.localizationPolicy = resolveSurfacePolicy(
      result.localizationPolicies,
      this.languageSurface(),
    );
    this.languageStorageKey = languagePreferenceKey(result.siteId, this.config.clientKey);
    const browserLanguages =
      typeof navigator === 'undefined'
        ? []
        : navigator.languages?.length
          ? navigator.languages
          : navigator.language
            ? [navigator.language]
            : [];
    const initial = resolveInitialLanguage({
      policy: this.localizationPolicy,
      storage: this.browserStorage(),
      storageKey: this.languageStorageKey,
      navigatorLanguages: browserLanguages,
    });
    this.activateLanguage(initial, 'initial');
  }

  private clearLanguageSwitchAckTimer(): void {
    if (!this.languageSwitchAckTimer) return;
    clearTimeout(this.languageSwitchAckTimer);
    this.languageSwitchAckTimer = null;
  }

  private resetLanguageSwitchState(): void {
    this.clearLanguageSwitchAckTimer();
    this.languageSwitchState = { pending: null, error: null };
  }

  private armLanguageSwitchAckTimeout(requestId: string): void {
    this.clearLanguageSwitchAckTimer();
    this.languageSwitchAckTimer = setTimeout(() => {
      this.languageSwitchAckTimer = null;
      const result = expireLanguageSwitch(
        this.languageSwitchState,
        requestId,
        getString('language.switchFailed'),
      );
      if (result.state === this.languageSwitchState) return;
      this.languageSwitchState = result.state;
      this.logger.warn(
        `[OnboardingSDK] Language switch acknowledgement timed out (${requestId})`,
      );
      this.renderReactUI();
    }, LANGUAGE_SWITCH_ACK_TIMEOUT_MS);
  }

  private activateLanguage(
    language: SupportedLanguage,
    preferenceSource: LanguagePreferenceSource,
  ): void {
    const option = this.localizationPolicy.languages.find((item) => item.code === language);
    if (!option) {
      throw new LocalizationPolicyError(
        `Cannot activate disabled language ${JSON.stringify(language)}.`,
      );
    }

    const previous = this.selectedLanguage;
    this.selectedLanguage = setLanguage(option.code);
    markWidgetLanguage(this.reactContainer, this.selectedLanguage);
    const packSize = setUiStrings(option.uiStrings);
    if (this.connectionProgress) {
      const progressKeys = {
        initializing: 'connection.initializing',
        authenticating: 'connection.authenticating',
        connecting: 'connection.connecting',
        joining: 'connection.joining',
        ready: 'connection.ready',
        error: 'connection.failed',
      } as const;
      this.connectionProgress = {
        ...this.connectionProgress,
        message: getString(progressKeys[this.connectionProgress.stage]),
      };
    }
    if (shouldPersistLanguageSource(preferenceSource) && this.languageStorageKey) {
      persistLanguagePreference(
        this.localizationPolicy,
        this.browserStorage(),
        this.languageStorageKey,
        this.selectedLanguage,
      );
    }

    if (previous !== this.selectedLanguage) {
      // Suggestions are authored in the previous language. Server-driven chips
      // will repopulate after the switch. Configured openers/nudges are gated at
      // render time to their known English authoring language, so switching back can
      // restore them without keeping old-language chips on another locale.
      this.suggestedOptions = [];
      this.suggestedOptionsMetadata = null;
    }
    this.logger.log(
      `[OnboardingSDK] 🌐 language=${this.selectedLanguage} uiStrings=${packSize} keys`,
    );
  }

  private visibleSuggestedOptions(): string[] {
    if (this.suggestedOptions.length === 0) return [];

    // Website Q&A rows are model-generated follow-ups. Direct-demo rows are a
    // mix: model-generated answers and deterministic openers/seams assembled
    // from untagged dashboard copy. Outside English, only a matching, explicitly
    // model-authored row is safe; malformed/old untagged website rows disappear,
    // while demo rows use local pack defaults.
    if (this.selectedLanguage === 'en') {
      return [...this.suggestedOptions];
    }
    if (
      this.suggestedOptionsMetadata?.language === this.selectedLanguage &&
      (this.suggestedOptionsMetadata.source === 'model' ||
        this.suggestedOptionsMetadata.source === 'followup')
    ) {
      return [...this.suggestedOptions];
    }
    if (!this.config.demoMode) return [];
    return [
      getString('demo.starter1'),
      getString('demo.starter2'),
      getString('demo.starter3'),
    ];
  }

  private isSessionStarted(): boolean {
    return this.config.demoMode
      ? this.demoSessionStarted && !this.demoEnded
      : this.isConnected || this.websiteAgentSessionStarted;
  }

  private requestLanguageChange(language: SupportedLanguage): void {
    if (
      language === this.selectedLanguage ||
      this.languageSwitchState.pending ||
      !this.localizationPolicy.languages.some((option) => option.code === language)
    ) {
      return;
    }

    if (!this.isSessionStarted()) {
      this.resetLanguageSwitchState();
      this.activateLanguage(language, 'ui');
      this.renderReactUI();
      return;
    }

    if (!this.localizationPolicy.midSessionSwitch) return;
    const requestId = crypto.randomUUID();
    this.languageSwitchState = beginLanguageSwitch(language, requestId);
    this.armLanguageSwitchAckTimeout(requestId);
    this.sendOrQueueClientMessage('set_language', { language, requestId });
    this.renderReactUI();
  }

  private handleLanguageChanged(payload: Record<string, unknown>): void {
    const pending = this.languageSwitchState.pending;
    const result = applyLanguageChanged(
      this.languageSwitchState,
      payload,
      this.localizationPolicy,
    );
    if (result.state === this.languageSwitchState && !result.commitLanguage) return;
    this.clearLanguageSwitchAckTimer();
    this.languageSwitchState = result.state;
    if (result.commitLanguage) {
      this.activateLanguage(
        result.commitLanguage,
        languagePreferenceSource(payload, pending),
      );
    }
    this.renderReactUI();
    this.emit('languageChanged', {
      language: result.commitLanguage,
      source: payload.source,
      changed: payload.changed,
    });
  }

  private handleLanguageChangeFailed(payload: Record<string, unknown>): void {
    const result = applyLanguageChangeFailed(
      this.languageSwitchState,
      payload,
      getString('language.switchFailed'),
    );
    if (result.state === this.languageSwitchState) return;
    this.clearLanguageSwitchAckTimer();
    this.languageSwitchState = result.state;
    this.renderReactUI();
  }

  /**
   * Validate client key and check if bot is enabled for this site.
   * This is called before showing any UI to ensure the bot should be displayed.
   */
  private async validateClientKeyAndBotStatus(
    retryWhenThrottled = true,
  ): Promise<{ valid: boolean; error?: string }> {
    // disconnect() while a request or the wait below is in flight: this
    // instance must not ask again, nor apply an answer to the page-global
    // language pack it has released (another instance may own it by now).
    const teardownsBefore = this.teardownCount;
    const tornDown = { error: 'Disconnected before validation finished', valid: false };
    try {
      let response = await this.requestClientKeyValidation();
      if (teardownsBefore !== this.teardownCount) {
        return tornDown;
      }

      // A 429 is "no answer yet", not "no" (see validation-retry.ts): wait out
      // the time floe-api names and ask once more, rather than leave this page
      // with no widget. A second 429 is refused below like any other non-OK
      // answer, on every surface: it must not fail open the way an unreachable
      // floe-api does for onboarding, because anyone can cause a 429.
      if (response.status === 429 && retryWhenThrottled) {
        const delayMs = throttledRetryDelayMs(response.headers.get('Retry-After'));
        this.logger.warn(`[OnboardingSDK] Client key validation was rate limited, retrying once in ${delayMs}ms`);
        const wait = cancellableWait(delayMs);
        this.cancelValidationRetry = wait.cancel;
        await wait.elapsed;
        // Only this wait's handle: a later init() may have set its own.
        if (this.cancelValidationRetry === wait.cancel) {
          this.cancelValidationRetry = null;
        }
        if (teardownsBefore !== this.teardownCount) {
          return tornDown;
        }
        response = await this.requestClientKeyValidation();
        if (teardownsBefore !== this.teardownCount) {
          return tornDown;
        }
      }

      // Check response.ok before parsing JSON - HTTP 4xx/5xx should reject initialization
      if (!response.ok) {
        let result: any = null;
        try {
          result = await response.json();
        } catch {
          // JSON parse failed, use statusText
        }
        return { valid: false, error: result?.error || response.statusText || `HTTP ${response.status}` };
      }

      const result = await response.json();

      // Reject a malformed body BEFORE touching `result.valid` — see
      // isValidationResponse for why each rejected shape would otherwise fail OPEN.
      if (!isValidationResponse(result)) {
        return { valid: false, error: 'Malformed validation response' };
      }

      if (!result.valid) {
        return { valid: false, error: result.error || 'Validation failed' };
      }

      // Capture customer brand for DemoView chrome. Optional fields, so this
      // is safe against older floe-api builds that haven't been redeployed
      // with the brand additions yet (they just stay empty).
      if (typeof result.siteName === 'string') this.siteName = result.siteName;
      if (typeof result.siteDomain === 'string') this.siteDomain = result.siteDomain;
      if (typeof result.siteLogoUrl === 'string') this.siteLogoUrl = result.siteLogoUrl;
      if (typeof result.siteFaviconUrl === 'string') this.siteFaviconUrl = result.siteFaviconUrl;
      if (typeof result.calBookingUrl === 'string') this.siteCalBookingUrl = result.calBookingUrl;
      // Branding (FLO-64). config.demoAccentColor / demoCtaLabel take precedence
      // — resolved at render time (renderReactUI), so init overrides always win.
      if (typeof result.siteAccentColor === 'string') this.siteAccentColor = result.siteAccentColor;
      if (typeof result.siteCtaLabel === 'string') this.siteCtaLabel = result.siteCtaLabel;
      // Resolve the visitor's language before React mounts, using all packs in
      // the surface policy: saved choice → browser preference → site default.
      // Onboarding is intentionally fixed-English.
      this.configureLocalization(result);
      // FLO-322: demo-readiness signal. Absent on older floe-api builds → keeps
      // its fail-safe default (demoAvailable=false so no dead CTA / no unverified
      // auto-spin). The network-fail-open branch below also leaves it false.
      if (typeof result.demoAvailable === 'boolean') this.demoAvailable = result.demoAvailable;
      // Same defensive read, opposite default — see the field's doc comment.
      if (typeof result.websiteAgentEnabled === 'boolean') {
        this.websiteAgentEnabled = result.websiteAgentEnabled;
      }
      // CDP Phase 1 (C5): consent kill switch for visitor-event collection.
      // Absent on older floe-api builds → stays true; the ingest endpoint
      // independently drops batches when the switch is actually off, so this
      // default can't leak events past an operator's choice.
      if (typeof result.eventCollectionEnabled === 'boolean') {
        this.eventCollectionEnabled = result.eventCollectionEnabled;
      }
      // FLO-960: the deployment handshake for the diagnostics endpoint. Absent
      // (an older floe-api, or a deployment that has not enabled telemetry) →
      // stays FALSE, so this SDK does not POST to an endpoint that may not
      // exist yet. Collection therefore needs all three: this flag, the site
      // switch above, and the visitor's consent.
      if (typeof result.sdkDiagnosticsEnabled === 'boolean') {
        this.sdkDiagnosticsEnabled = result.sdkDiagnosticsEnabled;
      }
      // FLO-318/349: resolved website-agent config (opener chips, section nudges,
      // avatar, push-to-demo). Server-capped by resolveWebsiteAgent, but validate
      // defensively (mirrors suggested_options / bot_citation) — a malformed
      // payload must never blow the launcher. Keep only contentMap entries with a
      // usable id + copy; absent/invalid config → stays null.
      if (
        result.websiteAgent &&
        typeof result.websiteAgent === 'object' &&
        !Array.isArray(result.websiteAgent)
      ) {
        const wa = result.websiteAgent;
        const rawNudges =
          wa.nudges && typeof wa.nudges === 'object' && !Array.isArray(wa.nudges)
            ? wa.nudges
            : undefined;
        const contentMap = usableNudges(rawNudges?.contentMap);
        this.websiteAgentConfig = rawNudges
          ? { ...wa, nudges: { ...rawNudges, contentMap } }
          : { ...wa };
      }
      // The demo agent's identity-capture policy (site level). Server-resolved
      // and whitelisted by resolveDiscovery, but re-validated here for the same
      // reason as websiteAgent above — and with a sharper consequence: an
      // unrecognized timing that slipped through would defer the ask, which
      // means it would also replace the form with a fieldless Start card and
      // stop capturing leads. An older floe-api serves nothing → stays null →
      // `captureIsDeferred` reads that as today's gating form.
      this.demoCaptureConfig = parseDemoCapturePolicy(result.demoIdentityCapture);

      return { valid: true };
    } catch (error) {
      // Only true network/offline errors (TypeError, AbortError) may fail open,
      // and only for fixed-English onboarding. Website/demo require the served
      // localization policy before they can choose a visitor language or voice;
      // proceeding without it would silently synthesize English.
      // The authorization check itself can still fail open for onboarding: it
      // is a client-side gate and floe-api re-validates the client key on every
      // subsequent call. Website/demo are different because this same response
      // is the only authoritative source for their localization contract.
      if (error instanceof TypeError || (error instanceof DOMException && error.name === 'AbortError')) {
        const surface = this.languageSurface();
        if (surface !== 'onboarding') {
          const message = `Unable to load required ${surface} localization policy.`;
          this.logger.warn(`[OnboardingSDK] ${message}`, error);
          return { valid: false, error: message };
        }
        this.logger.warn('[OnboardingSDK] Client key validation failed due to network/timeout, proceeding anyway:', error);
        return { valid: true };
      }

      // Other errors (unexpected) - fail closed to respect bot disablement
      this.logger.warn('[OnboardingSDK] Unexpected error during client key validation:', error);
      return { valid: false };
    }
  }

  /** One POST to /sites/validate-client-key, abandoned after 4 seconds. */
  private async requestClientKeyValidation(): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);
    try {
      return await fetch(`${this.config.apiUrl}/sites/validate-client-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientKey: this.config.clientKey,
          origin: window.location.origin,
          // Website agent only: floe-api answers with this page's nudges and
          // the SDK loads the rest later (website-agent-nudges.ts). Undefined
          // for every other agent, and JSON.stringify then drops the key.
          path: nudgeRequestPath(this.config, window.location.pathname),
        }),
        // Anonymous (clientKey + Origin) — no cookies on the public SDK bucket.
        credentials: 'omit',
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
 * Start screen sharing: sends the tab/window as a WebRTC video track to Pipecat.
 */
  private async startScreenShare() {
    try {
      // Enable screen share through Pipecat client
      if (this.pipecatClient) {
        await this.pipecatClient.enableScreenShare(true);
        this.screenShareEnabled = true;
        this.logger.log("[OnboardingSDK] Screen share enabled");
        
        // Notify server that screen share is active
        // This allows the vision scheduler and pipeline to activate
        this.sendMetadata({ type: 'screen_share_status', active: true });
        
        this.renderReactUI();
      }
    } catch (error) {
      this.logger.error("[OnboardingSDK] Screen share failed:", error);
      this.screenShareEnabled = false;
      throw error;
    }
  }


  /**
   * Initialize the SDK and connect to servers
   */
  async init(): Promise<void> {
    if (this.isInitialized) {
      this.logger.warn('[OnboardingSDK] Already initialized');
      return;
    }

    const teardownsBefore = this.teardownCount;
    try {
      // FLO-728: an email-action capability lives only in the fragment. Remove
      // it before validation, telemetry bootstrap, or any SDK-rendered asset
      // can observe the address bar. Redemption waits until the site consent
      // mode has resolved, so a pending/denied visitor never sends a stored
      // visitor token merely by opening the recap link.
      const pendingEmailActionToken =
        typeof window === 'undefined'
          ? null
          : extractEmailActionFragment({
              location: window.location,
              replaceUrl: (url) => window.history.replaceState(null, '', url),
            });

      // ── Manual recording mode ───────────────────────────────────────────────
      // When the dashboard redirects the user to their site with recording
      // params, we intercept here and start the recorder instead of the normal
      // onboarding flow. This must run before any Pipecat / UI initialisation.
      const recordingParams = getManualRecordingParams();
      if (recordingParams) {
        const apiUrl = this.config.apiUrl ?? 'http://localhost:3001';
        try {
          await initManualRecording(recordingParams, apiUrl);
          this.isInitialized = true;
          return;
        } catch (err) {
          this.logger.error('[OnboardingSDK] Failed to initialise manual recording:', err);
          clearManualRecordingParams();
          this.emit('error', { type: 'manual_recording_init_failed', error: err });
          // Fall through to normal onboarding init
        }
      }

      // This SDK's DOM mounts and scroll/squeeze resources are page-global, as
      // is the lightweight locale store. Claim it before validation can write a
      // language pack so a second active Floe instance cannot relabel the first.
      this.claimUiStringsOwnership();

      // Pre-check: Validate client key and check if bot is enabled
      // If bot is disabled, don't show any UI
      // A recap-email action is not made to wait out a throttle: its fragment
      // is already gone from the address bar, so a reload during the wait
      // would lose it.
      const validationResult = await this.validateClientKeyAndBotStatus(
        !pendingEmailActionToken,
      );
      // disconnect() ran since init() started (a throttled validation can
      // take a minute): the host has moved on, so nothing below may redeem,
      // navigate or mount.
      if (teardownsBefore !== this.teardownCount) {
        return;
      }

      // Resolve the per-site consent default before redeeming an email action.
      // This is intentionally earlier than visitor-event bootstrap below.
      this.consentMode = resolveConsentMode(
        this.config.consentMode,
        this.websiteAgentConfig?.consentModeDefault,
      );

      if (pendingEmailActionToken && typeof window !== 'undefined') {
        const actionResult = await redeemEmailScheduleAction({
          actionToken: pendingEmailActionToken,
          apiUrl: this.config.apiUrl || '',
          clientKey: this.config.clientKey,
          visitorToken: this.identityConsented()
            ? getStoredVisitorToken()
            : null,
          navigate: (url) => window.location.assign(url),
          onFailure: (kind) =>
            this.logger.warn(
              `[OnboardingSDK] Email action could not be completed (${kind})`,
            ),
        });
        if (actionResult === 'handled') {
          this.isInitialized = true;
          return;
        }
      }

      if (!validationResult.valid) {
        if (this.config.debug) {
          this.logger.log('[OnboardingSDK] Bot is disabled or invalid:', validationResult.error);
        }
        // Don't show any UI - bot is disabled for this site
        releaseUiStrings(this);
        this.ownsUiStrings = false;
        return;
      }

      // CDP Phase 1 (FLO-547): arm the visitor-event stream. Runs in EVERY
      // mode (WIDGET_LOADED/DEMO_* matter on demo-only sites too) and only
      // after validation, so an invalid key never emits. bootstrap() sends
      // WIDGET_LOADED + the first PAGE_VIEWED — as an explicit token-minting
      // fetch on the visitor's first ever boot (contract C1). Fire-and-forget:
      // analytics must never gate init.
      this.visitorEvents.setCollectionEnabled(this.eventCollectionEnabled);
      // FLO-960: armed only after validation, and only when the deployment
      // says the endpoint exists AND the site's collection switch is on. The
      // visitor's consent gate is read live inside the client.
      this.sdkDiagnostics.setCollectionEnabled(
        this.sdkDiagnosticsEnabled && this.eventCollectionEnabled,
      );
      // Nudge telemetry honors the SAME C5 kill switch: recordNudgeEvent() is
      // gated on the flag, and anything queued before validation settled is
      // dropped UNSENT — clear() then dispose(), mirroring
      // VisitorEventsClient.setCollectionEnabled (no goodbye flush, and the
      // pagehide/visibility listeners unbind so nothing beacons later; the
      // enqueue gate keeps them from re-binding). Nudges themselves still
      // render — only their telemetry stops, matching the server 200-dropping
      // the batches when the switch is off.
      if (!this.eventCollectionEnabled) {
        this.nudgeEventQueue.clear();
        this.nudgeEventQueue.dispose();
      }
      // Per-visitor consent mode, resolved AFTER validation so the per-site
      // default (websiteAgent.consentModeDefault — additive, absent on older
      // floe-api builds) can apply when the init snippet said nothing.
      if (this.identityConsented()) {
        // Today's behavior — including a pre-init `consent('granted')` under
        // 'pending' — bootstrap() sends WIDGET_LOADED + the first PAGE_VIEWED,
        // as an explicit token-minting fetch on the visitor's first ever boot
        // (contract C1). Fire-and-forget: analytics must never gate init.
        void this.visitorEvents.bootstrap();
        // Always-on SPA route watcher (popstate + 2s pathname poll). PageTracker
        // only starts in the bot-ready handler, so without this a non-engaged
        // visitor's client-side route changes would emit no PAGE_VIEWED at all.
        // pageViewed's consecutive same-path dedupe keeps the two sources from
        // double-reporting once a session connects. Stopped by dispose()/C5-off.
        this.visitorEvents.startUrlWatch();
      } else {
        // 'pending' with no grant yet: the queues stay INERT (bootstrap never
        // armed them → track()/pageViewed() no-op), no token is minted, and no
        // anonymousId is written. A later consent('granted') runs this exact
        // bootstrap; 'denied' leaves it parked forever (sticky for the load).
        this.consentBootstrapDeferred = true;
        this.logger.log(
          '[OnboardingSDK] Consent mode: pending — identity + visitor events deferred until consent("granted")',
        );
      }

      // Finalize any pending bot navigation from a previous full-page load.
      // Must run early so the metadata is queued before the transport connects.
      this._finalizePendingNavigation();

      // Inject vanilla JS overlay
      this.overlay.inject();

      // Initialize React UI components
      this.initReactUI();

      // ───── Website-agent mode: render launcher pill, defer connect ─────
      // FLO-320/FLO-706: do not spend a session on a page impression. The
      // explicit launcher click opens the chat and immediately warms Pipecat,
      // mic-disabled and without initializing local devices. A later mic click
      // owns the iOS permission gesture; opening alone never does. There is no
      // email gate — the pill opens straight into the chat window.
      if (this.config.websiteAgent) {
        this.logger.log('[OnboardingSDK] 💬 Website-agent mode — rendering launcher, deferring connect until pill click');
        this.agentState = 'idle';
        this.isInitialized = true;
        // FLO-523: the zero-cooperation squeeze harness, if this site asked for
        // it. Applied ONCE here (never per open/close — see squeeze-compat) and
        // released in disconnect(). A no-op for every site whose config has no
        // sidebarCompat facet, which is all of them by default.
        this.applyHostSqueezeCompat();
        this.renderReactUI();
        this.armConfiguredExitIntent();
        // Take the ~1MB Daily call-machine bundle (and the avatar's static
        // media) off the click → greeting path. Website agent only, and asked
        // positively — see startup-prefetch.ts.
        this.armVoiceStartupPrefetch();
        this.armRemainingNudgesLoad();
        this.emit('ready');
        return;
      }

      // ───── Support mode: dormant until the user asks ─────
      // FLO-534: `activation: 'on_demand'` makes the onboarding agent reactive.
      // It never opens a popup, never greets, and — the part that matters
      // commercially — never spends a (paid) pipecat session on a visitor who
      // didn't ask for one. We render the launcher and stop; the user's click
      // runs connectWithProgress(), the same path a returning user takes.
      //
      // Deliberately placed BEFORE checkEndUserStatus(): new-vs-returning only
      // picks WHICH proactive opening to use, and support mode has none, so
      // that lookup is a network round-trip whose answer we would discard.
      //
      // The server half is in the /sites/start-session body (see
      // connectWithProgress) — without it the agent would still open the
      // session with an onboarding pitch the moment the user clicked.
      if (this.runsInSupportMode()) {
        this.logger.log('[OnboardingSDK] 🛟 Support mode — dormant until the user opens it');
        this._discoveryPopupShown = true;
        this._isMinimized = true;
        this.agentState = 'idle';
        this.isInitialized = true;
        this.renderReactUI();
        this.emit('ready');
        return;
      }

      // Public bootstrap is scoped to THIS browser continuity anchor. Never
      // select by unsigned email/externalId, and never mint a WebVisitor merely
      // to decide which onboarding UI to show. Under pending/denied consent an
      // older persisted token must not leave the browser either.
      const bootstrapVisitorToken = this.identityConsented()
        ? getStoredVisitorToken()
        : null;
      if (bootstrapVisitorToken) {
        await this.checkEndUserStatus(bootstrapVisitorToken);
      }

      // Determine if we should skip the onboarding modal
      const shouldSkipModal = this.shouldSkipOnboardingModal();

      // Track whether we should connect the bot immediately
      // Default to true, but set to false if user skips or is a returning user
      let shouldConnectNow = true;

      if (shouldSkipModal && this.config.demoMode) {
        // Demo mode: skip modal AND connect immediately
        this.logger.log('[OnboardingSDK] 🎬 Demo mode — skipping modal, connecting immediately');
        this._discoveryPopupShown = true;
        shouldConnectNow = true;
      } else if (shouldSkipModal) {
        // Returning onboarding user: skip modal, show minimized, wait for click
        this.logger.log('[OnboardingSDK] 🔄 Skipping onboarding modal for returning user');
        this._discoveryPopupShown = true;
        this.isReturningUser = true;
        shouldConnectNow = false;
        this.logger.log('[OnboardingSDK] Bot will stay closed - user clicks to start');
      } else {
        // FLO-51: no page-load modal. It was the ONE mandatory click on the
        // first-time path (and unsuppressible — convertFloeToSDKConfig hardcoded
        // enableDiscoveryPopup: true), and it bought nothing the orb + nudge
        // don't: the visitor still has to click something, so make that click
        // the thing that actually starts the session.
        //
        // It also has to work this way for the mic. `getUserMedia` only prompts
        // inside a transient user activation on iOS Safari (FLO-109), so
        // auto-connecting at page load would join mic-less and silently drop
        // into the no-mic path. Every start is now gesture-rooted by
        // construction. `showWelcomePopupAsync()` stays on the public API for
        // hosts that drive it themselves.
        this.logger.log('[OnboardingSDK] 👋 Rendering launcher — the first click starts the session');
        this._discoveryPopupShown = true;
        this.markUserVisited();
        shouldConnectNow = false;
      }

      // If user skipped or is returning, show minimized state and don't connect yet
      if (!shouldConnectNow) {
        this.logger.log('[OnboardingSDK] 📍 Initialized in minimized state - user clicks to connect');
        this._isMinimized = true;
        this.agentState = 'idle';
        this.isInitialized = true;
        this.renderReactUI(); // Show the minimized UI
        this.emit('ready');
        return; // Exit early - don't connect the bot
      }

      // Create Daily transport for WebRTC
      this.transport = new DailyTransport();

      // Initialize Pipecat client with shared callbacks.
      // In text mode, disable mic so initDevices() won't prompt for mic permission.
      this.pipecatClient = new PipecatClient({
        transport: this.transport,
        enableMic: this.micEnabled,
        enableCam: this.config.enableVideo || false,
        enableScreenShare: this.config.enableScreenCapture || false,
        callbacks: this.createPipecatCallbacks(),
      });
      if (!this.config.debug) {
        this.pipecatClient.setLogLevel(LogLevel.WARN);
      }

      // Navigation detection — skip in demo mode (Playwright handles the product page)
      if (!this.config.demoMode) {
        this.setupNavigationDetection();
      }

      // Device init runs here for onboarding mode only. In demo mode it's
      // deferred to startDemoSessionAfterEmail() so the mic prompt fires from
      // the intro-form-submit gesture rather than at page load — iOS Safari
      // won't show the prompt outside a transient user activation (FLO-109).
      // Onboarding paths reach init() from a gesture (discovery-popup start) or
      // connect from a later tap, so prompting here is gesture-rooted.
      if (!this.config.demoMode) {
        // Always initialize devices — required for transport to reach ready state.
        // initDevicesWithMicFallback primes the mic from the gesture and falls
        // back to text mode automatically if the browser denies mic access.
        this.pipecatClient = await this.initDevicesWithMicFallback(this.pipecatClient);

        // Setup audio playback (bot audio tracks, analyzers).
        // In text mode, setupUserMicAnalysis() inside will be skipped.
        this.setupAudioPlayback();
      }

      // ───── Demo mode: defer pipecat connect until the prospect acts ─────
      // In demo mode we render the SDK overlay (intro form) WITHOUT calling
      // startBotAndConnect. That keeps the paid pipecat + avatar session from
      // spawning when a prospect lands and bounces. The session starts inside
      // startDemoSessionAfterEmail(), fired from the form.
      //
      // NOTHING starts here, not even when config.prospectEmail is supplied.
      // A prefilled email skips the form's email STAGE; it is not itself the
      // start signal. Two reasons it can't be:
      //
      //   1. Cost. init() runs on page load, so auto-starting here bills a
      //      session for anything that renders the page — headless link
      //      scanners (Proofpoint, Defender Safe Links) execute JS and would
      //      each spawn one before a human ever saw it. A gesture can't be
      //      emitted by accident; a page load can.
      //   2. The mic. Starting outside a user gesture means connecting
      //      mic-less (iOS Safari only surfaces the getUserMedia prompt inside
      //      a transient user activation — FLO-109), then re-acquiring the mic
      //      from a later tap. Starting FROM the tap does both at once.
      //
      // The warm-up this used to buy is small: the prefilled path puts the
      // prospect on a one-tap card, so it bought seconds of a 30-60s browser
      // boot, not the 15-30s of form-typing the original two-stage flow had.
      if (this.config.demoMode) {
        this.isInitialized = true;
        this.renderReactUI();
        this.armConfiguredExitIntent();
        this.emit('ready');
        return;
      }

      // ───── Onboarding mode: connect immediately ─────
      const proxyEndpoint = `${this.config.apiUrl}/sites/start-session`;

      this.logger.log(`[OnboardingSDK] Starting onboarding session via:`, proxyEndpoint);

      // Build request data as plain serializable object.
      // clientKey is required post 2026-06-02 (see CSO finding). Origin
      // still sent in body for backward compat with older floe-api builds;
      // new floe-api reads the trusted origin from the HTTP Origin header.
      const requestData: Record<string, any> = {
        clientKey: this.config.clientKey,
        origin: window.location.origin,
        // FLO-534: the server half of support mode. Sent from both session
        // builders so the two request shapes stay identical — this one is the
        // proactive path (support mode returns from init() before reaching it),
        // but a shape that differs by path is how the entryPhase stamp got
        // dropped once already.
        activation: this.activationForSession(),
        sessionData: {
          sessionId: this.beginDiagnosticsTrackedSession(),
          createDailyRoom: true,
          dailyRoomProperties: { start_video_off: true },
        },
      };

      // Add user info if provided. Consent-gated (C3): the configured
      // name/email ARE identity keys, so pre-grant under 'pending' (or after
      // an explicit denial) the spawn carries no userInfo at all — not merely
      // no anonymousId. The server tolerates its absence.
      if (this.config.userInfo && this.identityConsented()) {
        requestData.sessionData.userInfo = this.config.userInfo;
      }

      // CDP Phase 1 (contract C2): attach the server-minted visitor token when
      // the events bootstrap has one — floe-api forwards it to pipecat and
      // builds visitorProfile off it. Absent on a first-ever boot = cold open.
      // Consent-gated: pre-grant under 'pending' no token was minted this load,
      // and one persisted by an EARLIER load must not ride either.
      const onboardingVisitorToken = this.identityConsented() ? getStoredVisitorToken() : null;
      if (onboardingVisitorToken) {
        requestData.sessionData.visitorToken = onboardingVisitorToken;
      }

      await this.pipecatClient.startBotAndConnect({
        endpoint: proxyEndpoint,
        requestData: requestData as any,
      });

      // Show screen share consent modal if enableScreenCapture is true
      if (this.config.enableScreenCapture) {
        const userConsent = await this.overlay.showScreenShareModal();
        if (userConsent) {
          try {
            await this.startScreenShare();
          } catch (err) {
            this.logger.error("[OnboardingSDK] Screen share failed:", err);
          }
        } else {
          this.logger.log('[OnboardingSDK] Screen share declined by user');
        }
      }

      this.isInitialized = true;
      this.emit('ready');

    } catch (error) {
      // Validation/setup failures that never mounted UI leave nothing which
      // needs this locale. Release immediately so a corrected init can replace
      // the failed instance without requiring a cleanup call it cannot know it
      // needs. A mounted error surface keeps ownership until disconnect().
      if (!this.reactRoot && this.ownsUiStrings) {
        releaseUiStrings(this);
        this.ownsUiStrings = false;
      }
      this.logger.error('[OnboardingSDK] Initialization failed:', error);
      // FLO-960: how far the start got; the reason derives from that stage,
      // never from error.message.
      this.sdkDiagnostics.noteConnectFailed(this.diagnosticFailureReason(error));
      if (this.config.demoMode) {
        this.demoError = 'Could not start the demo. Please try again.';
        this.renderReactUI();
      }
      this.emit('error', error);
      throw error;
    }
  }

  /**
   * Start the deferred pipecat session for demo mode. Every caller is rooted in
   * a user gesture — the intro form's Stage 1 advance, the docked pill's email
   * submit, or the prefilled card's "Start demo" tap. That is deliberate: it is
   * what keeps a page load (or a headless link scanner) from billing a session,
   * and it is what lets the mic be acquired inline instead of deferred.
   *
   * Idempotent: subsequent calls are no-ops. /sites/demo/:siteId/start
   * spawns a paid pipecat + avatar session, so a duplicate call would burn
   * credits AND get a different daily room URL than the SDK is already
   * waiting on. The form leans on this — it fires the callback on submit
   * without tracking whether an earlier stage already did.
   *
   * `deferMic` exists for a caller that genuinely cannot be gesture-rooted. No
   * shipped path passes it any more (the prefilled-email auto-start at init
   * was its only user); it survives because startDemoSessionAfterEmail is a
   * public method an embedder can drive directly.
   *
   * The session_id/origin/clientKey shape mirrors what init() used to
   * send synchronously — the only difference is timing.
   */
  async startDemoSessionAfterEmail(email: string, opts?: { deferMic?: boolean }): Promise<void> {
    return this.startDemoSession({ email, deferMic: opts?.deferMic });
  }

  /**
   * The cost gate itself, with the email made OPTIONAL.
   *
   * `startDemoSessionAfterEmail` is public surface an embedder can drive, so its
   * signature (and its email-first semantics) stay exactly as they were; this is
   * the shape the deferred-capture path needs, where the Start tap happens
   * BEFORE anyone has been asked for an email. Everything else — idempotency,
   * the mic-on-gesture ordering, the request body — is identical.
   */
  private prospectEmailCheck(): EmailCheck {
    this.emailCheck ??= createEmailCheck(this.config.apiUrl || '', this.config.clientKey);
    return this.emailCheck;
  }

  private async startDemoSession(opts?: {
    email?: string;
    deferMic?: boolean;
  }): Promise<void> {
    const email = (opts?.email || '').trim();
    if (!this.config.demoMode) {
      this.logger.warn(
        '[OnboardingSDK] startDemoSession called outside demo mode — ignoring',
      );
      return;
    }
    if (this.demoSessionStarted) {
      this.logger.log(
        '[OnboardingSDK] startDemoSession: already started, no-op',
      );
      return;
    }
    this.markExitIntentEngaged();
    if (!this.pipecatClient) {
      this.logger.error(
        '[OnboardingSDK] startDemoSession: pipecatClient missing — init() must run first',
      );
      throw new Error('SDK not initialized');
    }
    this.demoSessionStarted = true;
    this.bookingEmail = email || undefined;
    // Only overwrite when we actually have one. Under a deferred policy this
    // fires with no email and must not clobber a value the host already knew.

    const proxyEndpoint = `${this.config.apiUrl}/sites/demo/${this.config.demoSiteId}/start`;
    this.logger.log(
      email
        ? `[OnboardingSDK] 📧 Email captured (${email.slice(0, 3)}…) — starting demo session via: ${proxyEndpoint}`
        : `[OnboardingSDK] ▶️ Start tapped (identity capture deferred) — starting demo session via: ${proxyEndpoint}`,
    );

    // Spreading `email: ''` over a
    // userInfo that carries a real address would blank it, and userInfo.email is
    // what floe-api dedupes sessions and maps EndUsers on.
    //
    // C3: the CONFIGURED userInfo (+ the anonymousId mint) is identity and is
    // consent-gated via consentedUserInfo(). The TYPED email stays either way —
    // the prospect submitting the form is the visitor volunteering that datum,
    // which no consent gate should un-ask.
    const userInfo = {
      ...(this.consentedUserInfo() || {}),
      ...(email ? { email } : {}),
    };
    const requestData: Record<string, any> = {
      clientKey: this.config.clientKey,
      origin: window.location.origin,
      // Visitor-selected demo language. The server validates it against the
      // demo localization policy before choosing STT/TTS/voice services.
      language: this.selectedLanguage,
      // FLO-150: forward the DemoLink id (set when launched from /d/[token]) so
      // floe-api can apply this link's persona override. Omitted when absent.
      ...(this.config.demoLinkId && { demoLinkId: this.config.demoLinkId }),
      sessionData: {
        sessionId: this.beginDiagnosticsTrackedSession(),
        createDailyRoom: true,
        // FLO-727: source lineage for a direct demo. pathname is deliberately
        // used instead of href so query parameters and fragments never leave
        // the host page through this field.
        sourcePath: window.location.pathname,
        // CDP Phase 1 (contract C2): cross-surface continuity — a returning
        // website-agent visitor's stored token keeps the demo on the SAME
        // WebVisitor instead of cold-opening a fresh identity. Absent on a
        // first-ever boot (mirrors the start-session paths). Consent-gated:
        // pre-grant the session runs WITHOUT identity keys (both optional).
        ...(this.identityConsented() && getStoredVisitorToken()
          ? { visitorToken: getStoredVisitorToken() }
          : {}),
        // FLO-1096: what the visitor did on this page before opening the demo.
        ...this.currentPageForSession(),
        // Forward the email upfront so floe-api can log / dedupe sessions per
        // prospect even before site_init lands. The anonymousId (the stable
        // client-side EndUser-resolve hint, FLO-325) rides inside
        // consentedUserInfo() above. C3: when consent is pending/denied and no
        // email was typed there is NO identity to send — omit the key entirely.
        ...(Object.keys(userInfo).length ? { userInfo } : {}),
      },
    };

    // A failed demo start can be retried through this public entry point. Give
    // that attempt fresh callbacks so old traffic cannot reopen or stop it.
    if (!this.planSessionOpen) {
      this.pipecatClient = new PipecatClient({
        transport: this.transport!,
        enableMic: this.micEnabled,
        enableCam: this.config.enableVideo || false,
        enableScreenShare: this.config.enableScreenCapture || false,
        callbacks: this.createPipecatCallbacks(),
      });
      if (!this.config.debug) this.pipecatClient.setLogLevel(LogLevel.WARN);
    }
    let connectingClient = this.pipecatClient;
    try {
      // FLO-109: device init was deferred from init() to this gesture path so
      // the mic prompt fires from the prospect's tap. ensureMicPermission()
      // (inside initDevicesWithMicFallback) primes the mic FIRST — before the
      // /demo/start round-trip below — so iOS Safari surfaces the prompt while
      // the gesture's transient activation is still valid. setupAudioPlayback()
      // wires bot-audio playback + analyzers (and reuses the primed mic stream
      // for visualization unless we fell back to text mode).
      //
      // deferMic (prefilled-email auto-start, which runs at init — NOT a
      // gesture): connect mic-less so we never fire getUserMedia outside a user
      // activation. The mic is acquired later from the "Start demo" tap
      // (primeMicFromGesture). This is "voice pending", NOT a permanent text-mode
      // fallback, so setupAudioPlayback() skips user-mic analysis here too.
      this.voicePendingGesture = !!opts?.deferMic;
      const initializedClient = await this.initDevicesWithMicFallback(
        this.pipecatClient,
        { deferMic: !!opts?.deferMic },
      );
      if (this.pipecatClient !== connectingClient) return;
      connectingClient = this.pipecatClient = initializedClient;
      this.setupAudioPlayback();

      await this.pipecatClient.startBotAndConnect({
        endpoint: proxyEndpoint,
        requestData: requestData as any,
      });
    } catch (error) {
      if (this.pipecatClient !== connectingClient) throw error;
      this.retirePlanRun();
      // Reset the flags so the user can retry from the form.
      this.demoSessionStarted = false;
      this.voicePendingGesture = false;
      // Release the mic acquired by setupAudioPlayback()→setupUserMicAnalysis()
      // before startBotAndConnect threw. Without this the OS recording
      // indicator stays lit (and the track stays open) while the error/retry UI
      // is shown. Bump the request id first so any in-flight getUserMedia from
      // setupUserMicAnalysis() invalidates itself and stops its own tracks.
      this.userMicStreamRequestId++;
      if (this.userMicStream) {
        this.userMicStream.getTracks().forEach((track) => track.stop());
        this.userMicStream = null;
      }
      this.stopPrimedMicStream();
      if (this.userAudioAnalyzer) {
        this.userAudioAnalyzer.destroy();
        this.userAudioAnalyzer = null;
      }
      if (this.connectEndedByOwnTeardown(connectingClient)) {
        throw error;
      }
      this.logger.error(
        '[OnboardingSDK] demo session start failed:',
        error,
      );
      this.sdkDiagnostics.noteConnectFailed(this.diagnosticFailureReason(error));
      this.demoError = 'Could not start the demo. Please try again.';
      this.renderReactUI();
      this.emit('error', error);
      throw error;
    }
  }

  /**
   * Side-effect-free microphone probe for an explicit Website Agent voice opt-in.
   *
   * Unlike ensureMicPermission() (FLO-109), this does NOT flip the session to
   * text mode on denial and does NOT retain the stream. It is called only from a
   * mic control gesture; text, chip, and panel-open paths never reach it. The
   * caller invokes it before its first await so iOS retains transient activation.
   *
   * On grant we immediately stop the probe track. The permission is now banked
   * for Pipecat's enabled join (or enableMic(true) on an existing text session),
   * while the short probe itself never leaves a stray capture track behind.
   *
   * Note this is about the MIC only — the bot's voice plays from turn 1 either
   * way, so a failure is non-fatal: the visitor listens and types instead of
   * talking. Never fall back to text mode here; text mode ALSO suppresses bot
   * audio, which we want on.
   *
   * PURE: it reports what it found and mutates no session state. The caller owns
   * that (applyMicProbeResult), so a probe whose prompt outlived its session can
   * be discarded wholesale rather than writing a dead session's verdict over a
   * live one's.
   */
  private async probeMicPermission(): Promise<MicProbeResult> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.logger.warn('[OnboardingSDK] 🎤 Mic request skipped — mediaDevices unavailable (Website Agent stays text-safe)');
      return { granted: false, denied: false };
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: MIC_AUDIO_CONSTRAINTS });
      // Release the short probe immediately; Pipecat owns the enabled session track.
      stream.getTracks().forEach((t) => t.stop());
      this.logger.log('[OnboardingSDK] 🎤 Mic permission granted for explicit Website Agent voice opt-in');
      return { granted: true, denied: false };
    } catch (error) {
      // Deliberately no text-mode fallback (that would suppress bot audio, which
      // we want ON). A failure just means the user can't unmute; text + bot audio
      // still work. `denied` drives user-facing copy and is permission-denial
      // ONLY — missing hardware is a failure too, but not the visitor's doing.
      const kind = classifyMicError(error);
      this.logger.warn('[OnboardingSDK] 🎤 Mic probe denied/unavailable (website-agent) — text input only:', error);
      return { granted: false, denied: kind === 'permission-denied', kind };
    }
  }

  /**
   * Read the browser's recorded microphone permission.
   *
   * `'prompt'` is the fallback for everything unknown — an unsupported
   * Permissions API, a 'microphone' name older Safari refuses to query, a
   * throw — because `getUserMedia` is the source of truth and this is only ever
   * used to recognise a hard `'denied'`.
   */
  private async queryMicPermissionState(): Promise<MicPrecheck> {
    return micPrecheckFromState((await this.queryMicPermissionStatus())?.state);
  }

  /**
   * The live `PermissionStatus` object, or null where it cannot be had.
   *
   * `'microphone'` is not a queryable name everywhere — Firefox rejects it
   * outright — and TypeScript's `PermissionName` union does not include it, so
   * the navigator is read through a structural cast exactly as the FLO-109
   * pre-check has always done.
   */
  private async queryMicPermissionStatus(): Promise<MicPermissionStatus | null> {
    try {
      const perms = (navigator as unknown as {
        permissions?: { query?: (d: { name: string }) => Promise<MicPermissionStatus> };
      }).permissions;
      if (!perms?.query) return null;
      return await perms.query({ name: 'microphone' });
    } catch {
      return null;
    }
  }

  /**
   * FLO-786: watch this origin's microphone permission for the life of the
   * session.
   *
   * The point is the recovery Google Meet has and we did not: a visitor who
   * blocked the mic and then changes their mind fixes it where browsers put
   * that control — the address bar — and expects the product to notice. Without
   * this they also have to come back and press our button, and until they do,
   * the panel is explaining a block that no longer exists.
   *
   * Idempotent, and silent where it cannot run (Firefox cannot query
   * `'microphone'`; the retry tap remains the way back there).
   */
  private watchMicPermission(): void {
    if (this.micPermissionWatch) return;
    const token = this.micPermissionWatchToken;
    void this.queryMicPermissionStatus().then((status) => {
      // Two verdicts can land while this query is in flight; keep the first
      // subscription rather than stacking a second handler on the same status.
      if (!status || this.micPermissionWatch) return;
      // …and the session can END while it is in flight, which is the case
      // stopWatchingMicPermission() cannot reach on its own.
      if (token !== this.micPermissionWatchToken) {
        this.logger.log('[OnboardingSDK] 🎤 Permission watch abandoned — its session ended while the query was in flight');
        return;
      }
      if (typeof status.addEventListener !== 'function') return;
      const handler = () => this.handleMicPermissionChange(micPrecheckFromState(status.state));
      status.addEventListener('change', handler);
      this.micPermissionWatch = { status, handler };
      this.logger.log('[OnboardingSDK] 🎤 Watching microphone permission for changes');
    });
  }

  private stopWatchingMicPermission(): void {
    // First, so an attach still queued behind the awaited query aborts too.
    this.micPermissionWatchToken++;
    const watch = this.micPermissionWatch;
    if (!watch) return;
    this.micPermissionWatch = null;
    watch.status.removeEventListener?.('change', watch.handler);
  }

  /**
   * The visitor changed this site's microphone permission in the browser.
   *
   * A grant is only acted on when THIS session asked for the mic and did not
   * get it. Some other widget on the customer's page can win a microphone
   * permission at any moment, and a text-first visitor who never asked to talk
   * must not have a live microphone handed to them by a decision they made
   * about something else.
   */
  private handleMicPermissionChange(state: MicPrecheck): void {
    // Read before the flags below are cleared: this is the record of having
    // asked, which is what makes a later grant an answer rather than a surprise.
    // The deadline bounds it — see micRecoveryDeadlineMs. Without that, a
    // visitor who was refused once would have Floe's mic armed by ANY later
    // origin-level grant, including one they gave to a different widget.
    const askedAndStillWaiting =
      (this.websiteAgentAutoMicRequested || this.micProbeFailed || this.micPermissionDenied)
      && this.micRecoveryDeadlineMs !== null
      && Date.now() <= this.micRecoveryDeadlineMs;

    if (state === 'denied') {
      // Revoked mid-session. The browser has already killed the track; the only
      // question is whether the UI keeps claiming to listen.
      this.logger.log('[OnboardingSDK] 🎤 Microphone permission revoked by the visitor — muting');
      // Read BEFORE the intent update below, which zeroes micEnabled itself:
      // deriving the transport mute from it afterwards silently skipped the
      // enableMic(false) and left the transport believing it still had a mic.
      const wasLive = this.micEnabled;
      this.micProbeFailed = true;
      this.micPermissionDenied = true;
      // Restart the clock: this refusal is as fresh as one from a probe, and
      // without it a second block→unblock cycle in one session would clear the
      // UI but never bring the mic back.
      this.micRecoveryDeadlineMs = Date.now() + MIC_RECOVERY_WINDOW_MS;
      if (this.config.websiteAgent) {
        this.updateWebsiteAgentMicIntent({ type: 'permission-resolved', granted: false });
      }
      if (wasLive) {
        this.pipecatClient?.enableMic(false);
        this.trackVoiceEnded();
      }
      this.micEnabled = false;
      this.renderReactUI();
      return;
    }

    // 'granted' or 'prompt' (the visitor reset the site to "ask"): either way
    // nothing is refused any more, so the control must stop explaining one.
    if (!this.micProbeFailed && !this.micPermissionDenied && state !== 'granted') return;
    this.micProbeFailed = false;
    this.micPermissionDenied = false;
    this.micRecoveryDeadlineMs = null;

    if (state === 'granted' && askedAndStillWaiting && this.config.websiteAgent) {
      this.logger.log('[OnboardingSDK] 🎤 Microphone unblocked by the visitor — enabling the voice they asked for');
      this.updateWebsiteAgentMicIntent({ type: 'permission-resolved', granted: true });
      if (this.pipecatClient) {
        this.pipecatClient.enableMic(true);
        if (this.isConnected) this.trackVoiceStarted();
      }
    }
    this.renderReactUI();
  }

  /**
   * Commit a mic-probe verdict to session state.
   *
   * `micProbeFailed` deliberately tracks EVERY unusable outcome — denial, absent
   * hardware, an unreadable device, no mediaDevices at all — while
   * `micPermissionDenied` keeps its narrower "the visitor said no" meaning for
   * the UI copy. toggleMute() gates on the former: gating on denial alone let a
   * NotFoundError through, and enableMic(true) then rendered a live-looking mic
   * button over a device that was never going to produce audio.
   */
  private applyMicProbeResult(result: MicProbeResult): void {
    this.micProbeFailed = !result.granted;
    this.micPermissionDenied = result.denied;
    // FLO-960: the explicit opt-in paths commit their verdict HERE, not through
    // handleMicUnavailable(), so a refusal would otherwise go unreported.
    // Callers filter inconclusive auto-probes out before reaching this.
    if (!result.granted) {
      // `kind` is absent only for the no-mediaDevices short-circuit, which
      // handleMicUnavailable() also classifies as 'no-device'; a plain denial
      // without a kind is still a permission denial.
      const micKind: MicErrorKind =
        result.kind ?? (result.denied ? 'permission-denied' : 'no-device');
      const micReason = this.diagnosticMicReason(micKind);
      if (micReason) this.sdkDiagnostics.noteMicDenied(micReason);
    }
    // FLO-786: from here on, the visitor can change this decision in the
    // browser rather than in our UI. Watch for it, and start the clock on how
    // long a grant may still be read as an answer to THIS request.
    this.micRecoveryDeadlineMs = result.granted ? null : Date.now() + MIC_RECOVERY_WINDOW_MS;
    this.watchMicPermission();
  }

  /** Keep pre-client permission and desired mute state in one transition. */
  private updateWebsiteAgentMicIntent(event: WebsiteAgentMicIntentEvent): void {
    const next = reduceWebsiteAgentMicIntent(
      {
        permissionReady: this.websiteAgentMicPermissionReady,
        desiredEnabled: this.micEnabled,
      },
      event,
    );
    this.websiteAgentMicPermissionReady = next.permissionReady;
    this.micEnabled = next.desiredEnabled;
  }

  /**
   * Start (or join) the origin-scoped Website Agent permission request.
   *
   * `probeMicPermission()` reaches getUserMedia before its first await, so
   * calling this synchronously from a mic control preserves the browser's
   * transient user activation. Repeated clicks share the pending browser sheet
   * instead of launching competing prompts.
   */
  private beginWebsiteAgentMicRequest(): Promise<MicProbeResult> {
    if (this.websiteAgentMicRequestInFlight) {
      return this.websiteAgentMicRequestInFlight;
    }
    const request = this.probeMicPermission();
    this.websiteAgentMicRequestInFlight = request;
    void request.finally(() => {
      if (this.websiteAgentMicRequestInFlight === request) {
        this.websiteAgentMicRequestInFlight = null;
      }
    });
    return request;
  }

  /**
   * FLO-544: open or resume this visitor's server-side conversation (FLO-329).
   *
   * Runs for BOTH display modes. The rail is what gets a resumed transcript
   * painted back into it, but the conversation itself is the website agent's
   * record of the chat — the dashboard's Conversations view, retention, and the
   * turns pipecat will write — and a floating-card site deserves that record as
   * much as a docked one. Never throws: the driver folds every failure into
   * `null`.
   *
   * FLO-557: this open also RE-SENDS the end signal for a thread this tab ended
   * whose marker is still standing. The standalone signal fired on End is
   * best-effort by design (offline, a 4xx, the 2s abort, or an End immediately
   * followed by "Ask another question" that races it), and the driver settles
   * the marker on the DEDICATED end endpoint, awaited BEFORE its open request
   * fires (PR #620 r6) — so carrying it here is what makes "forget this chat"
   * converge instead of depending on one fire-and-forget request landing. It
   * matters more since FLO-556: an id that comes back linked no longer just
   * routes future turns, it ships the thread's words to the bot, which is then
   * told to open by naming them.
   */
  private openWebAgentConversation(): Promise<ResumedConversation | null> {
    // Per-visitor consent: pre-grant this call is BOTH a token mint and a
    // durable server-side record of the visitor, so it must not fire. `null`
    // is the established degradation path — a fresh, unlinked session with
    // the local transcript intact (and the FLO-544 honesty divider over it).
    if (!this.identityConsented()) {
      this.logger.log(
        '[OnboardingSDK] Consent pending/denied — skipping conversation open (unlinked session)',
      );
      return Promise.resolve(null);
    }
    return openOrResumeConversation({
      apiUrl: this.config.apiUrl,
      clientKey: this.config.clientKey,
      // Stamped on the conversation server-side, so a pipecat session in the
      // logs can be traced back to the thread it was serving.
      pipecatSessionId: this.sessionId,
      // Idempotent server-side (`status: { not: ENDED }`), so re-sending it on
      // every open until the marker is spent costs nothing.
      endedConversationId: this.forgottenConversationId(),
    }).catch(() => null);
    // The .catch is a belt, not the seatbelt: the driver already folds every
    // failure into null. It matters because this promise is created BEFORE the
    // mic probe and may be abandoned unawaited (the cancelled/superseded return),
    // and an abandoned rejected promise is an unhandled rejection on the
    // customer's page — which some hosts escalate to a visible error.
  }

  /**
   * FLO-557: tell the server the visitor ENDED this conversation.
   *
   * Called from the EXPLICIT End only (the `onEndWebsiteAgent` prop — the chat
   * header's End button), never from `endWebsiteAgentSession()`, which also
   * serves the failed-connect cleanup path: a boot that never reached the bot
   * must not retire a thread the visitor is still reading, and neither must a
   * collapse (minimize keeps the thread resumable BY DESIGN).
   *
   * Which id: this session's, when it has one. A restored rail is dormant — no
   * session, no open call this pageview — and End is reachable there, so the
   * FLO-524 key is the fallback. That key now only ever holds a real FLO-329
   * conversation (it used to be stamped with the pipecat session id, which made
   * this name a row that cannot exist — see `startWebsiteAgentSession`). Read
   * SYNCHRONOUSLY, ahead of both teardowns that follow: `endWebsiteAgentSession()`
   * nulls the in-memory linkage before its first await, and the view's
   * `clearConversationContinuity()` empties the storage key the moment this
   * returns.
   *
   * Nothing persisted and nothing in memory → no call at all; there is no thread
   * to name, and a request without one would only mint server-side noise.
   * Fire-and-forget: the driver swallows every failure — which is why the id is
   * also REMEMBERED here, so the next open re-sends it as `endedConversationId`
   * and the refusal in `applyOpenedConversation()` holds in the meantime.
   */
  private endWebAgentConversation(): void {
    // Per-visitor consent: pre-grant no conversation was opened this load, and
    // the call would present a token persisted by an EARLIER load — an
    // identity-bearing request the visitor hasn't authorized. Nothing is lost:
    // the signal exists to retire threads, and an unconsented load has none.
    if (!this.identityConsented()) return;
    const conversationId = this.websiteAgentConversationId ?? readConversationId();
    if (!conversationId) return;
    this.websiteAgentEndedConversationId = conversationId;
    void endConversation({
      apiUrl: this.config.apiUrl,
      clientKey: this.config.clientKey,
      conversationId,
    })
      .then((sent) =>
        this.logger.log(
          `[OnboardingSDK] FLO-557: end signal for ${conversationId} ` +
            `${sent ? 'accepted' : 'not delivered (the forgotten marker still applies)'}`,
        ),
      )
      .catch(() => {
        // Unreachable — endConversation() resolves false rather than rejecting —
        // but this promise is deliberately unawaited, and an unhandled rejection
        // on the customer's page is a visible error on some hosts.
      });
  }

  /**
   * FLO-557: the thread this visitor asked to forget, if there is one.
   *
   * Two stores, one fact. The sessionStorage marker is the durable half and is
   * what survives a reload — but the view only writes it in sidebar mode (the
   * whole continuity module is gated there), so a floating-card session would
   * otherwise have no memory of an End at all. The in-memory field covers that
   * for the page load, which is the entire life of a floating card's chat.
   */
  private forgottenConversationId(): string | null {
    return readForgottenConversationId() ?? this.websiteAgentEndedConversationId;
  }

  /**
   * FLO-544: adopt what the open/resume call came back with.
   *
   * Three effects, in order of how much they matter:
   *   1. the conversation id becomes this session's linkage — it rides
   *      /sites/start-session and reaches the bot;
   *   2. it replaces the pipecat session id under the FLO-524 continuity key, so
   *      the persisted thread identifier is a real conversation;
   *   3. when the server resumed a thread that HAS turns, those turns are handed
   *      to the view to replace the sessionStorage-restored ones (sidebar only —
   *      the floating card has no restored history to reconcile with, and must
   *      not suddenly open onto a two-hour-old conversation).
   *
   * …with ONE exception to ALL THREE: a thread this tab ENDED. `openOrResume`
   * resolves a conversation from the visitor, not from anything the browser
   * sends, so a restart (or a reload) inside the 120-minute window can get back
   * the exact thread the visitor asked to forget. Two things now stop that, in
   * this order:
   *
   *   - the open driver settles the forgotten id on the dedicated end endpoint
   *     BEFORE its resume request fires ({@link openWebAgentConversation} passes
   *     it as `endedConversationId`) — so in the normal case this branch is
   *     unreachable;
   *   - and if it comes back anyway (an end write the server dropped, a floe-api
   *     that whitelists the field away instead of honouring it), the result is
   *     REFUSED WHOLESALE: not linked, not persisted, not painted.
   *
   * That refusal reverses FLO-544's original call, which kept the linkage so the
   * visitor's next turns still landed in the operator's record. FLO-556 is what
   * flipped it: the id stopped being a write-only routing token and became a
   * content READ — floe-api now ships the thread's tail in the spawn body and
   * the Shepherd is instructed to open by naming what was last discussed. Keeping
   * the linkage would therefore have the bot recite a deleted chat back to the
   * person who deleted it. An unlinked session is a recoverable loss (one
   * conversation the dashboard doesn't get); that is not.
   *
   * `null` in — the resume failed or there was nothing to resume — leaves every
   * one of those at its FLO-524 value. That is the degradation path, and it is
   * the same code path as a site that never had a conversation.
   */
  private applyOpenedConversation(result: ResumedConversation | null): void {
    if (!result) {
      // Resume honesty: the open failed (or was skipped) while a restored
      // transcript may still be painted — the bot starts COLD, and the view
      // must say so instead of implying memory. renderReactUI() so the
      // divider paints even when nothing else about this branch re-renders.
      this.websiteAgentResumeOutcome = 'fresh';
      this.renderReactUI();
      return;
    }
    if (this.forgottenConversationId() === result.conversationId) {
      // The marker STAYS: the next open re-sends it as `endedConversationId`
      // until a different thread comes back, which is what makes the end signal
      // converge rather than depend on one fire-and-forget request.
      this.logger.warn(
        `[OnboardingSDK] FLO-557: the server handed back ${result.conversationId}, which this ` +
          `tab ended — refusing it entirely (unlinked session, no turns, no history to the bot)`,
      );
      // Refused wholesale ⇒ the bot sees none of it: honesty-wise identical
      // to a failed open (resolveResumeOutcome's contract: refused = null).
      this.websiteAgentResumeOutcome = resolveResumeOutcome(null);
      this.renderReactUI();
      return;
    }
    this.websiteAgentResumeOutcome = resolveResumeOutcome(result);
    this.websiteAgentConversationId = result.conversationId;
    // A different thread: the End is spent, in BOTH stores. Cleared outside the
    // sidebar branch because the refusal above is mode-independent — leaving
    // either half set would keep re-sending a dead id on every later open.
    this.websiteAgentEndedConversationId = null;
    if (readForgottenConversationId()) clearForgottenConversation();
    if (this.isWebsiteAgentSidebarMode()) {
      writeConversationId(result.conversationId);
      const replacement = reconcileRestoredMessages(result);
      if (replacement) {
        // Identity IS the signal downstream: the view's effect keys on this
        // array, so a new one only arrives when the server actually resumed
        // turns. (Never mutated in place for the same reason.)
        this.websiteAgentResumedMessages = replacement;
        this.renderReactUI();
      }
    }
    this.logger.log(
      `[OnboardingSDK] FLO-544: ${result.resumed ? 'resumed' : 'opened'} conversation ` +
        `${result.conversationId} (${result.messages.length} prior turns from the server)`,
    );
  }

  /**
   * validate-client-key carried only this page's nudges. When floe-api said
   * there are more, load the site's whole list once the host page is loaded
   * and idle, and swap it in — so a visitor who moves to another page without
   * a reload still has its pills. A failed load keeps this page's rows.
   */
  private armRemainingNudgesLoad(): void {
    this.cancelRemainingNudgesLoad();
    if (!this.wantsWholeNudgeList()) {
      return;
    }
    this.remainingNudgesLoadCancel = afterLoadWhenIdle(() => {
      void this.loadRemainingNudges();
    });
  }

  /**
   * The visitor moved to another page without a reload, and that page's nudges
   * are only in the whole list: load it now, not at idle. This is also the
   * retry a load that failed gets, since nothing else asks again; it stops
   * after MAX_NUDGE_LOAD_FAILURES, so an endpoint that keeps refusing is not
   * asked on every page the visitor opens.
   */
  private loadRemainingNudgesOnNavigation(): void {
    if (
      !this.wantsWholeNudgeList() ||
      this.remainingNudgesFailures >= MAX_NUDGE_LOAD_FAILURES
    ) {
      return;
    }
    this.remainingNudgesLoadCancel?.();
    this.remainingNudgesLoadCancel = null;
    void this.loadRemainingNudges();
  }

  /** True while the SDK holds only the nudges of the page it loaded on. */
  private wantsWholeNudgeList(): boolean {
    const nudges = this.websiteAgentConfig?.nudges;
    return nudges?.enabled === true && nudges.hasMore === true;
  }

  private async loadRemainingNudges(): Promise<void> {
    if (this.remainingNudgesLoading) {
      // The load that is out will serve this move too, unless it fails.
      this.remainingNudgesAskedAgain = true;
      return;
    }
    this.remainingNudgesLoading = true;
    this.remainingNudgesAskedAgain = false;
    const run = this.remainingNudgesRun;
    const result = await fetchAllNudges(this.config.apiUrl || '', this.config.clientKey);
    // Torn down (or re-armed) while the request was out: this answer is stale.
    if (run !== this.remainingNudgesRun) {
      return;
    }
    this.remainingNudgesLoading = false;
    const current = this.websiteAgentConfig?.nudges;
    if (!current) {
      return;
    }
    if (!result.ok) {
      this.remainingNudgesFailures += 1;
      // Debug builds only: the SDK logger is silent on a customer's page.
      this.logger.warn(
        `[OnboardingSDK] Could not load the site's other nudges (${result.reason}); ` +
          'a page reached without a reload shows none until a later load succeeds',
      );
      // A move to another page arrived while this load was out, and its retry
      // was folded into it: make that retry now.
      if (this.remainingNudgesAskedAgain) {
        this.loadRemainingNudgesOnNavigation();
      }
      return;
    }
    this.websiteAgentConfig = {
      ...this.websiteAgentConfig,
      nudges: { ...current, contentMap: result.rows, hasMore: false },
    };
    this.renderReactUI();
  }

  /** Drop a scheduled load, and disown one already in flight. */
  private cancelRemainingNudgesLoad(): void {
    this.remainingNudgesLoadCancel?.();
    this.remainingNudgesLoadCancel = null;
    this.remainingNudgesRun += 1;
    this.remainingNudgesLoading = false;
    this.remainingNudgesFailures = 0;
    this.remainingNudgesAskedAgain = false;
  }

  /**
   * Warm the voice session's static assets before the visitor commits.
   *
   * Two stages, deliberately: DNS+TLS to Daily's CDN right away (cheap, and the
   * handshake is the one part that cannot be overlapped once the click lands),
   * then the bundle + avatar media on the FIRST intent signal inside the
   * widget's own container — hover, pointer-down or keyboard focus. Doing the
   * megabyte at page load instead would spend a customer's visitor's bandwidth
   * on a widget most of them never open, and would leave an unused-preload
   * warning in their console.
   *
   * The listeners live on `reactContainer`, which is the shadow HOST: events
   * from inside the shadow root retarget to it and still bubble, so one listener
   * covers every surface without the view components knowing anything about it.
   */
  private armVoiceStartupPrefetch(): void {
    if (!this.config.websiteAgent) return;
    try {
      preconnectVoiceOrigins();
    } catch {
      /* preconnect is an optimization, never a precondition */
    }
    if (voiceStartupAssetsPrefetched()) return;
    // Boot-time warm for every visitor, idle-deferred and after the host
    // page's `load` — so the click feels fast whether or not it was preceded
    // by a hover. The assets are read when the warm RUNS, so the avatar
    // URLs are whatever validate-client-key returned by then.
    this.voiceStartupPrefetchCancel?.();
    this.voiceStartupPrefetchCancel = scheduleVoiceStartupPrefetch(
      () => this.voiceStartupPrefetchAssets(),
      this.logger,
    );
    // Intent path: the fallback for a visitor the boot-time warm skipped
    // (constrained connection) or has not reached yet. A no-op once the
    // page-scoped latch is set, so nothing is ever fetched twice.
    const container = this.reactContainer;
    if (!container) return;
    this.voiceStartupPrefetchAbort?.abort();
    const abort = new AbortController();
    this.voiceStartupPrefetchAbort = abort;
    const run = () => {
      abort.abort();
      this.voiceStartupPrefetchAbort = null;
      prefetchVoiceStartupAssets(this.voiceStartupPrefetchAssets(), this.logger);
    };
    for (const type of ['pointerover', 'pointerdown', 'focusin'] as const) {
      container.addEventListener(type, run, {
        once: true,
        passive: true,
        signal: abort.signal,
      });
    }
  }

  /**
   * The media the prefetch may warm: the avatar loop + poster on their
   * `?cors=1` cache key, and ONLY for an enabled avatar — the same gate the
   * render layer applies. A disabled avatar warms nothing, whatever URLs the
   * config carries beside it (see voiceStartupAssetsForAvatar).
   */
  private voiceStartupPrefetchAssets() {
    return voiceStartupAssetsForAvatar(
      this.websiteAgentConfig?.avatar,
      corsCacheKeyUrl,
    );
  }

  /**
   * Start a Website Agent Q&A session after the visitor commits to an action.
   *
   * FLO-706 splits text from voice at the permission boundary:
   *   - typed questions, chips, demo requests, and capture submits omit
   *     `requestMicrophone`; they never call getUserMedia and join mic-muted;
   *   - an explicit mic control supplies `requestMicrophone:true`; its probe is
   *     invoked synchronously in that gesture and the client joins mic-enabled
   *     when permission is granted.
   *
   * A deliberate launcher open may also queue an agent-first turn. Ordinary
   * opens speak it; the keyboard path sets `speakerMuted` so the same turn is
   * rendered as text without TTS. `entryPhase: "q_and_a"` keeps the merged
   * pipecat session in the Website Agent Q&A phase. Idempotent: a second start
   * (or a re-open after collapse) is a no-op.
   */
  async startWebsiteAgentSession(options?: WebsiteAgentStartOptions): Promise<void> {
    if (!this.config.websiteAgent) {
      this.logger.warn('[OnboardingSDK] startWebsiteAgentSession called outside website-agent mode — ignoring');
      return;
    }
    if (this.websiteAgentSessionStarted) {
      this.logger.log('[OnboardingSDK] startWebsiteAgentSession: already started, no-op');
      return;
    }
    this.markExitIntentEngaged();
    this.websiteAgentSessionStarted = true;
    this.bookingEmail = undefined;
    const myGeneration = ++this.websiteAgentGeneration; // FLO-390: mark + remember this session

    // t0 for the click → greeting measurement. Armed before anything awaits so
    // the two serialized API round trips are inside the window, and completed by
    // the first bot speech (see voice-startup-timing.ts). One VOICE_READY per
    // session; the visitor-events client is already C5/consent gated, so a site
    // with collection off simply drops it.
    voiceStartupTimeline.start({ prefetched: voiceStartupAssetsPrefetched() });
    voiceStartupTimeline.onReady((props) =>
      this.visitorEvents.track('VOICE_READY', props),
    );

    // C1: mint THIS session's writer-correlation id BEFORE the open stamps it on
    // conversation (pipecatSessionId) — the same id then rides the
    // /sites/start-session body, so the two always agree. The API forwards it
    // only as a writer fence; Pipecat separately owns the DemoSession/billing
    // run id.
    //
    // FLO-960: the same seam opens the diagnostics thread (connect_started),
    // and carries the prefetch flag so a fast startup can be told apart from a
    // visitor who simply had the bundle warm.
    this.beginDiagnosticsTrackedSession({
      prefetched: voiceStartupAssetsPrefetched(),
    });

    const requestMicrophone = options?.requestMicrophone === true;
    // A deliberate launcher open is an invitation for the agent to take turn
    // zero, whether it will be spoken or rendered silently in keyboard mode.
    // Queue a non-user control while the client is still cold;
    // onBotReady flushes it only after site_init, and the server owns the real
    // greeting/watchdog path. This must never masquerade as a visitor turn.
    if (options?.agentFirst === true) {
      this.sendOrQueueClientMessage('website_agent_greet', {});
    }
    // Reset stale permission UI from a prior ended/failed session. An omitted
    // voice request is an UNPROBED text posture, not a denial or missing device.
    this.updateWebsiteAgentMicIntent({ type: 'reset' });
    this.micPermissionDenied = false;
    this.micProbeFailed = false;
    this.websiteAgentAutoMicRequested = false;

    // Begin getUserMedia before the first await, while the explicit mic click's
    // transient user activation is intact. Text starts leave this null and never
    // touch mediaDevices. Conversation resume starts immediately afterwards so
    // the two independent operations still overlap.
    const micProbePromise = requestMicrophone
      ? this.beginWebsiteAgentMicRequest()
      : null;

    // FLO-544: open (or resume) the server-side conversation this session belongs
    // to. Fired before either promise is awaited: the id has to be in hand before
    // /sites/start-session is built, and an opted-in mic probe can sit on an iOS
    // permission prompt for seconds, so the visitor pays max(probe, resume)
    // instead of their sum. Never blocks longer than WA_RESUME_TIMEOUT_MS; a
    // failure just leaves this a fresh, unlinked session (the FLO-524 behaviour).
    const conversationPromise = this.openWebAgentConversation();

    // FLO-524: the continuity key holds a CONVERSATION id and nothing else.
    //
    // It used to be stamped with `this.sessionId` here as a placeholder until the
    // open resolved, on the theory that a session id was "the best thread
    // identifier we have". It was worse than nothing: both readers of this key
    // require a real FLO-329 id — FLO-557's end signal names it to the server,
    // and `clearConversationContinuity()` records it as the forgotten thread —
    // so a stamped session id turned End into a request that can only match zero
    // rows AND a marker that can never match a resumed conversation, i.e. "forget
    // this chat" failing on both halves at once. And it did that by CLOBBERING a
    // real id restored from the previous page load, which is precisely the id
    // both readers wanted. So: leave the key alone; `applyOpenedConversation()`
    // writes the server's id at (1b) when there is one.
    if (this.isWebsiteAgentSidebarMode()) {
      const priorConversation = readConversationId();
      if (priorConversation) {
        this.logger.log(
          `[OnboardingSDK] FLO-524: continuing after a page load — prior thread ` +
            `${priorConversation} → new session ${this.sessionId}`,
        );
      }
    }

    // (0) Bot audio is ON for the ordinary launcher, but the dedicated keyboard
    // entry is an explicit silent-text choice. Set the requested posture BEFORE
    // setupAudioPlayback/connect so the first remote track and FLO-348's
    // bot-ready sync both observe it; changing this after start would let the
    // first queued answer speak before the mute reached the server.
    this.speakerMuted = options?.speakerMuted === true;

    // Clear any fatal error from a PRIOR session so a restart starts clean —
    // otherwise the previous session's init_error would surface in the fresh
    // chat during connect (it's only otherwise cleared once a full connect
    // succeeds, which a re-tap may not reach before rendering).
    this.fatalError = null;

    // Show the connecting state immediately so the chat reflects "Connecting…"
    // (and the FLO-382 thinking indicator) while either permission or resume is
    // pending. `micEnabled` is already false, so the control never claims an
    // unprobed text session is listening.
    this.agentState = 'connecting';
    this.setConnectionProgress('connecting', getString('connection.connecting'), 50);

    // (1) Voice starts collect the probe they launched above. Denial is non-fatal:
    // the visitor still gets text input + bot audio. Text starts skip this block
    // entirely, including its getUserMedia call and await.
    if (micProbePromise) {
      const micResult = await micProbePromise;
      // A permission sheet can stay open for seconds. Discard a verdict that
      // outlived its session rather than mutating a replacement session's UI.
      if (!this.websiteAgentSessionStarted || this.websiteAgentGeneration !== myGeneration) {
        this.logger.log('[OnboardingSDK] startWebsiteAgentSession: cancelled/superseded during mic request — aborting before connect');
        voiceStartupTimeline.abandon();
        return;
      }
      this.applyMicProbeResult(micResult);
      this.updateWebsiteAgentMicIntent({ type: 'permission-resolved', granted: micResult.granted });
    }

    // (1b) Collect the conversation opened at the top. In the common case this
    // resolved while the probe was up and costs nothing; worst case it is the
    // resume timeout, and `null` (offline, 4xx, junk, timed out) simply means
    // this session runs unlinked with the visitor's local transcript intact.
    const opened = await conversationPromise;
    // The first of the two serialized round trips is done. start-session cannot
    // be built until this resolves (it carries the conversation id), which is
    // exactly the serialization this mark exists to size.
    voiceStartupTimeline.mark('conversationOpened');

    // …and re-run the cancelled/superseded check, because that await is a SECOND
    // suspension point (up to WA_RESUME_TIMEOUT_MS) on the same path the guard
    // above exists to protect. The probe often resolves instantly — a previously
    // granted or denied mic never prompts — so on a slow API this is the window
    // the visitor is actually looking at "Connecting…" in, and the End (X) is
    // live throughout it. Without this, End would flip the guard, find no client
    // to disconnect, and the code below would then build one and connect: a paid
    // session behind a dormant UI that endWebsiteAgentSession() now no-ops on.
    // Checked BEFORE applyOpenedConversation so an abandoned session also cannot
    // write its conversation id into storage the End handler just cleared, or
    // repaint a transcript the visitor just deleted.
    if (!this.websiteAgentSessionStarted || this.websiteAgentGeneration !== myGeneration) {
      this.logger.log('[OnboardingSDK] startWebsiteAgentSession: cancelled/superseded during the conversation resume — aborting before connect');
      voiceStartupTimeline.abandon();
      return;
    }
    this.applyOpenedConversation(opened);

    // (2) A text session joins muted. An explicitly requested, granted voice
    // session joins enabled. A mic request made while this text boot was waiting
    // for conversation resume also sets websiteAgentMicPermissionReady and the
    // desired mic state. Gate on both so a second click that mutes before client
    // construction is preserved instead of being overwritten at join.
    const joinWithMicrophone = websiteAgentShouldJoinWithMicrophone({
      permissionReady: this.websiteAgentMicPermissionReady,
      desiredEnabled: this.micEnabled,
    });
    this.transport = new DailyTransport();
    this.micEnabled = joinWithMicrophone;
    this.pipecatClient = new PipecatClient({
      transport: this.transport,
      enableMic: joinWithMicrophone,
      enableCam: false,
      enableScreenShare: false,
      callbacks: this.createPipecatCallbacks(),
    });
    if (!this.config.debug) {
      this.pipecatClient.setLogLevel(LogLevel.WARN);
    }

    const proxyEndpoint = `${this.config.apiUrl}/sites/start-session`;
    this.logger.log('[OnboardingSDK] 💬 Starting website-agent (Q&A) session via:', proxyEndpoint);

    // Same body as the onboarding connect, plus the entryPhase contract field.
    const requestData: Record<string, any> = {
      clientKey: this.config.clientKey,
      origin: window.location.origin,
      // Website-agent Q&A and its in-session demo handoff share this visitor
      // choice for the life of the merged session.
      language: this.selectedLanguage,
      // FLO-320: the ONE server-facing field the client sends — the merged
      // pipecat session reads it to enter the Q&A phase.
      entryPhase: 'q_and_a',
      sessionData: {
        sessionId: this.sessionId,
        createDailyRoom: true,
        dailyRoomProperties: { start_video_off: true },
        // FLO-727: the website-agent and its in-session demo handoff retain the
        // page that opened this shared session. pathname contains neither the
        // query string nor fragment.
        sourcePath: window.location.pathname,
        // ─── Opening posture, at SPAWN time (greeting critical path) ───
        //
        // These three used to reach the bot only over the RTVI data channel,
        // which cannot open until the transport is ready: `website_agent_greet`
        // is queued at the click and flushed by onBotReady, *after* site_init.
        // Prod (2026-09-12) put client-ready ~1.8s after the server already saw
        // the transport connect, so the greeting inference could not start until
        // ~7.6s after the click even though everything it reads (language, the
        // resumed thread, the visitor profile) was in the spawn body all along.
        //
        // Carrying them here lets pipecat start the opening on its own
        // `on_client_connected`. The queued RTVI control is still sent and is
        // still the trigger for any older bot build — the kickoff is claimed by
        // a single latch server-side, so whichever arrives first wins and the
        // other is a no-op. Nothing here is identity-bearing.
        ...(options?.agentFirst === true ? { agentFirst: true } : {}),
        // Keyboard entry is an explicit silent-text choice. Without this the
        // early greeting would speak before `set_audio_output` (sent at
        // bot-ready) could arrive — the ordering that guarantee rests on today.
        audioOutputEnabled: !this.speakerMuted,
        // Same value site_init carries. An opening that can now precede site_init
        // must not be able to ask for a phone number the visitor has not
        // consented to be asked for.
        consent: this.consentDecision ?? this.consentMode,
        // CDP Phase 1 / contract C2: OPTIONAL continuity key — the stored
        // WebVisitor token, when the events bootstrap (or a prior open) minted
        // one. floe-api's spawn personalization (FLO-549) reads it to build the
        // visitorProfile; absence is the cold-open contract (C3) — which is
        // also why the consent gate can simply omit it pre-grant.
        ...(this.identityConsented() && getStoredVisitorToken()
          ? { visitorToken: getStoredVisitorToken() }
          : {}),
        // FLO-1096: what the visitor did on this page before opening the agent.
        ...this.currentPageForSession(),
      },
    };
    // FLO-325: forward the stable anonymous visitor id (+ any configured
    // userInfo) so the website-agent session is attributable from turn 1 — the
    // server resolves/creates an anonymous EndUser keyed on it, later merged into
    // an emailed EndUser when identity is captured. Consent-gated (C3): pre-grant
    // the session must carry no identity keys at all — the configured name/email
    // included, not merely the anonymousId — so the userInfo key itself is
    // omitted (the resolve path tolerates its absence).
    const waUserInfo = this.consentedUserInfo();
    if (waUserInfo) {
      requestData.sessionData.userInfo = waUserInfo;
    }
    // FLO-544: the conversation this session continues. floe-api verifies the id
    // belongs to this clientKey's site and forwards it to the spawned bot, so the
    // turns land in the resumed thread instead of a new orphan one. Omitted when
    // the open call failed — an absent field is exactly today's request. The id
    // is the in-memory one THIS session was just issued (never storage — a
    // stale/hand-edited id is a request to write into someone else's thread).
    if (this.websiteAgentConversationId) {
      requestData.conversationId = this.websiteAgentConversationId;
    }

    let connectingClient = this.pipecatClient;
    try {
      if (joinWithMicrophone) {
        // A granted permission probe can still be followed by a Daily device
        // failure (the mic disappears, is claimed by another app, or constraints
        // change). Degrade that case to a mic-free bot-audio + text session; do
        // not turn an optional voice opt-in into a fatal Q&A startup. The probe
        // already ran above, so skip re-priming it.
        const initializedClient = await this.initDevicesWithMicFallback(
          this.pipecatClient,
          {
            micAlreadyGranted: true,
          },
        );
        if (this.websiteAgentGeneration !== myGeneration || this.pipecatClient !== connectingClient) return;
        connectingClient = this.pipecatClient = initializedClient;
      } else {
        // Do NOT call PipecatClient.initDevices() for a warm muted start. Daily's
        // transport implements that call with startCamera(), which can acquire a
        // microphone even when enableMic/startAudioOff says not to. With both mic
        // and cam disabled, Pipecat's needsInit() is false and connect joins the
        // room muted without touching getUserMedia. Permission remains reserved
        // for the visitor's later, explicit mic click.
        this.logger.log('[OnboardingSDK] Skipping local device init for mic-muted website agent');
      }
      // Wire bot-audio playback + analyzers. The muted join skips USER-mic
      // analysis (nothing to analyze, and opening a stream would light the OS
      // recording indicator); toggleMute() does not start it either, because it
      // only feeds waveform visuals that this agent's chat card doesn't render.
      // Bot-audio analysis + playback are unaffected.
      this.setupAudioPlayback();
      // Track host-page navigation so the server keeps page context (like onboarding).
      this.setupNavigationDetection();

      await this.pipecatClient.startBotAndConnect({
        endpoint: proxyEndpoint,
        requestData: requestData as any,
      });
    } catch (error) {
      if (this.websiteAgentGeneration !== myGeneration || this.pipecatClient !== connectingClient) throw error;
      if (this.connectEndedByOwnTeardown(connectingClient)) {
        // disconnect() resets the session state itself; the startup timeline is
        // the one thing only this catch lets go of.
        voiceStartupTimeline.abandon();
        throw error;
      }
      // Release the half-built session's resources before retry. The client /
      // transport are already constructed, and initDevices / setupAudioPlayback /
      // setupNavigationDetection may have run — a connect failure may never fire
      // onDisconnected, so without an explicit teardown the pipecatClient,
      // transport, mic streams, audio elements + analyzers, and page/nav trackers
      // all leak, and a retry's new client overwrites the un-disconnected old one.
      // The guard is still set here, so endWebsiteAgentSession() runs its full
      // teardown; wrap it so a teardown error can't mask the original init error.
      // FLO-960: report BEFORE the teardown — it closes the diagnostics thread,
      // which would drop the failure report entirely.
      this.sdkDiagnostics.noteConnectFailed(this.diagnosticFailureReason(error));
      try {
        await this.endWebsiteAgentSession();
      } catch {
        /* best-effort teardown of the failed boot */
      }
      if (this.websiteAgentGeneration !== myGeneration || this.pipecatClient !== null) throw error;
      // Allow a retry: reset the guard and surface an idle state. Also DROP any
      // messages queued during this failed boot — FLO-382 queues sends (e.g. a
      // tapped opener) while websiteAgentSessionStarted is set, and a connect
      // failure may never fire onDisconnected (the other place the queue is
      // purged), so without this a retry would flush stale input into the fresh
      // session. Reset isBotReady too so nothing flushes until the next bot-ready.
      this.websiteAgentSessionStarted = false;
      this.pendingClientMessages = [];
      this.isBotReady = false;
      this.clearConnectionProgress();
      this.agentState = 'idle';
      voiceStartupTimeline.abandon();
      this.logger.error('[OnboardingSDK] Website-agent session start failed:', error);
      this.renderReactUI();
      this.emit('error', error);
      throw error;
    }
  }

  /**
   * FLO-320: end the muted website-agent Q&A session from its call controls.
   *
   * Unlike disconnect() (which unmounts the overlay + emits 'closed'), this KEEPS
   * the widget mounted and returns it to its dormant launcher pill — a persistent
   * site widget should still be re-openable after the visitor ends a chat. It
   * ends the paid pipecat session, releases mic/audio, and resets the
   * single-session guard so a later tap spins up a FRESH session. Modeled on
   * endDemoForWrapUp (overlay kept mounted). No-op if no session is active.
   */
  async endWebsiteAgentSession(): Promise<void> {
    if (!this.config.websiteAgent) {
      this.logger.warn('[OnboardingSDK] endWebsiteAgentSession called outside website-agent mode — ignoring');
      return;
    }
    if (!this.websiteAgentSessionStarted) {
      this.logger.log('[OnboardingSDK] endWebsiteAgentSession: no active session, no-op');
      return;
    }
    this.logger.log('[OnboardingSDK] Ending website-agent session');
    this.retirePlanRun();
    this.resetIdentityAskSession();

    // (1) Flip back to the dormant state and re-render FIRST so the launcher pill
    // reappears instantly (WebsiteAgentView renders the pill when connected is
    // false). Reset the guard so re-tapping starts a fresh session; clear the
    // transcript + speaker-mute so the next chat opens clean.
    this.isConnected = false;
    this.isBotReady = false;
    this.resetLanguageSwitchState();
    this.agentState = 'idle';
    this.resetBotSpeakingState();
    this.botTranscript = '';
    this.userTranscript = '';
    this.transcriptHistory = [];
    this.pendingCitationSources = null;
    this.speakerMuted = false;
    this.micEnabled = false;
    this.websiteAgentMicPermissionReady = false;
    this.micPermissionDenied = false;
    this.micProbeFailed = false;
    this.websiteAgentAutoMicRequested = false;
    this.micRecoveryDeadlineMs = null;
    this.stopWatchingMicPermission();
    // Drop this session's queued sends + bot-ready flag so a restart can't flush
    // stale input or believe the (old) bot is ready (FLO-382 review).
    this.pendingClientMessages = [];
    // FLO-385: clear the live-demo screencast so WebsiteAgentView's `demoActive`
    // flips false — otherwise the demo pane latches with a
    // frozen last frame after End, and a restart reopens straight into it (the
    // stream is only ever nulled in retryDemo otherwise). Runs solely on explicit
    // End, so the live driving/screencast path is untouched.
    this.botVideoStream = null;
    // The other half of that predicate since the hand-over card landed: a START
    // edge with no stream behind it holds the stage open on its own, so End has
    // to retire the edges as well as the picture.
    this.resetWebsiteAgentDemoHandover();
    this.avatarVideoStream = null;
    this.clearConnectionProgress();
    this.websiteAgentSessionStarted = false;
    // CDP Phase 1: close out an open VOICE span — the mic dies with the session.
    this.trackVoiceEnded();
    // FLO-544: drop this session's conversation LINKAGE, so nothing here can
    // forward a thread the next session never opened for itself — the restart's
    // own open call is what decides which conversation it belongs to. Harmless
    // on the failed-connect path this method also serves: the retry re-opens and
    // gets the id back.
    //
    // What this does NOT do, deliberately: end the conversation server-side.
    // This method is not the visitor's intent — it also runs on a FAILED connect
    // and (via End) on a rail with no session at all, so it must stay a pure
    // teardown. The server resolves a thread from the VISITOR (newest
    // ACTIVE/IDLE inside the 120-minute window), so absent any signal a restart
    // inside that window resumes the SAME conversation and its turns continue
    // there — which is right for a failed boot and wrong for an explicit End.
    // FLO-557 draws that line one level up: the `onEndWebsiteAgent` prop (the
    // header's End button, and only that) fires endWebAgentConversation() before
    // calling this. Its local twin is the forgotten-thread marker the view
    // writes on End and applyOpenedConversation() reads.
    this.websiteAgentConversationId = null;
    this.websiteAgentResumedMessages = null;
    // The honesty divider is a statement about THIS session's memory; with the
    // session gone it is a statement about nothing. The next session's open
    // recomputes it.
    this.websiteAgentResumeOutcome = null;
    // (FLO-524 continuity is NOT cleared here: this method is also the cleanup
    // path for a FAILED connect, and a boot that never reached the bot must not
    // wipe the transcript the visitor can still see. "Forget this chat" is a
    // user intent, so WebsiteAgentView's End handler owns it.)
    this.renderReactUI();

    // (2) Tear down the live session behind the pill. Capture the client and null
    // the SDK reference SYNCHRONOUSLY first, so if the visitor re-taps and starts
    // a fresh session during the async disconnect below, this teardown can't null
    // the NEW session's client (FLO-382 review). Disconnect pipecat (frees the
    // paid agent), stop the page/DOM/navigation trackers so no stale events fire
    // into a dead pipeline, and release audio elements + mic.
    const endingClient = this.pipecatClient;
    this.pipecatClient = null;
    // FLO-390: snapshot the session token. If a re-tap starts a fresh session
    // during the async disconnect below, its startWebsiteAgentSession() bumps the
    // generation — so we skip the rest of the teardown rather than tear down the
    // NEW session's transport / trackers / audio / mic.
    const gen = this.websiteAgentGeneration;
    // FLO-960: so a mid-teardown onDisconnected reports `client_requested`.
    this.sdkRequestedTeardown = true;
    try {
      if (endingClient) {
        await endingClient.disconnect();
      }
    } catch (err) {
      this.logger.warn('[OnboardingSDK] endWebsiteAgentSession: pipecat disconnect failed', err);
    }
    if (gen !== this.websiteAgentGeneration) {
      // A re-tap started a fresh session during the await. Its own start owns
      // the diagnostics thread now — closing it here would end the NEW
      // session's thread with the OLD session's teardown.
      this.sdkRequestedTeardown = false;
      this.logger.log('[OnboardingSDK] endWebsiteAgentSession: a newer session started during disconnect — leaving it intact');
      return;
    }
    // Close the diagnostics thread here rather than in dispose(): the widget
    // stays mounted and re-openable, so its queue must survive into the next
    // session. A no-op when onDisconnected already ended the thread.
    this.sdkDiagnostics.endSession('client_requested');
    this.sdkRequestedTeardown = false;
    this.transport = null;

    this.pageTracker.stop();
    this.domIntrospector.stopCapturing();
    if (this.navigationCompleteDetector) {
      this.navigationCompleteDetector.stop();
    }

    if (this.audioElements) {
      this.audioElements.forEach((audio) => { audio.pause(); audio.remove(); });
      this.audioElements = [];
    }
    this.userMicStreamRequestId++;
    if (this.userMicStream) {
      this.userMicStream.getTracks().forEach((track) => track.stop());
      this.userMicStream = null;
    }
    this.stopPrimedMicStream();
    if (this.botAudioAnalyzer) { this.botAudioAnalyzer.destroy(); this.botAudioAnalyzer = null; }
    if (this.userAudioAnalyzer) { this.userAudioAnalyzer.destroy(); this.userAudioAnalyzer = null; }
    this.audioLevels = [];
  }

  /**
   * Setup navigation detection for page navigation events.
   * Ensures navigationCompleteDetector is instantiated and started regardless
   * of text/voice mode — navigation tracking is needed in both modes so the
   * server always knows the current page context.
   */
  private setupNavigationDetection() {
    // Create navigation complete detector (only if not already created)
    if (!this.navigationCompleteDetector) {
      this.navigationCompleteDetector = new NavigationCompleteDetector(this.logger);
    }
    // Start navigation detection (start() handles duplicates internally)
    this.navigationCompleteDetector.start((url) => {
      this.logger.log('[OnboardingSDK] Navigation complete detected:', url);
      // Clear all overlay artifacts on navigation - stale highlights/tooltips from previous page
      this.overlay.cleanup();
      this.sendMetadata({
        type: 'navigation_complete',
        url,
        timestamp: Date.now(),
      });
    });
    this.logger.log('[OnboardingSDK] NavigationCompleteDetector started');
  }

  /**
   * Setup audio playback for bot audio tracks with real-time level analysis.
   * Centralizes all audio element handling and analyzer instantiation.
   * This is the single source of truth for audio initialization to prevent duplicates.
   *
   * Note: Navigation detection is handled separately by setupNavigationDetection()
   * so it works in both voice and text modes.
   */
  private setupAudioPlayback() {
    if (!this.pipecatClient) return;

    // Create audio analyzers for visualization (only if not already created)
    if (!this.botAudioAnalyzer) {
      this.botAudioAnalyzer = new AudioAnalyzer();
    }
    if (!this.userAudioAnalyzer && !this.config.websiteAgent) {
      this.userAudioAnalyzer = new AudioAnalyzer();
    }

    // Capture a separate user stream only where the UI renders its waveform.
    // Website Agent never does, so even a voice-first start must rely solely on
    // Daily's call track; opening another stream creates needless mobile-browser
    // contention and a second recording source. Text mode and gesture-deferred
    // voice remain gated as before for onboarding/demo callers.
    if (shouldStartUserMicAnalysis({
      websiteAgent: !!this.config.websiteAgent,
      voicePendingGesture: this.voicePendingGesture,
      micEnabled: this.micEnabled,
    })) {
      this.setupUserMicAnalysis();
    } else {
      const reason = this.config.websiteAgent
        ? '[OnboardingSDK] 🎤 Website Agent uses the Daily mic track — skipping waveform capture'
        : this.voicePendingGesture
          ? '[OnboardingSDK] 🎤 Voice pending gesture — deferring user mic analysis'
          : '[OnboardingSDK] 🔇 Mic muted — deferring user mic analysis until unmute';
      this.logger.log(reason);
    }

    // Idempotent registration: drop any track listeners left over from a prior
    // setupAudioPlayback() call on this same client (e.g. a demo retry that
    // re-runs this after a failed /demo/start) so bot-track events don't fire
    // multiple times. Only this method registers these two events, so clearing
    // all listeners for them is safe.
    this.pipecatClient.removeAllListeners('trackStarted');
    this.pipecatClient.removeAllListeners('trackStopped');

    // FLO-960: capture the run these listeners belong to. The Pipecat callbacks
    // are generation-fenced by createPipecatCallbacks(); these raw track
    // listeners were not, so a superseded client's late track could still reach
    // the live session's diagnostics.
    const capturedCallbackGeneration = this.pipecatCallbackGeneration;
    const capturedWebsiteAgentGeneration = this.websiteAgentGeneration;

    // Listen for audio and video tracks from the bot
    this.pipecatClient.on('trackStarted', (track: MediaStreamTrack, participant?: any) => {
      // Only handle remote (bot) tracks
      if (participant?.local) return;

      if (track.kind === 'audio') {
        this.logger.log('[OnboardingSDK] 🔊 Bot audio track started');
        const stream = new MediaStream([track]);
        const audio = document.createElement('audio');
        audio.autoplay = true;
        audio.srcObject = stream;
        // Apply current speaker-mute state to this fresh element +
        // track. Without this, late-arriving tracks (e.g., after a
        // renegotiation) would un-mute themselves on attach even though
        // the user had hit the speaker button.
        audio.muted = this.speakerMuted;
        try { track.enabled = !this.speakerMuted; } catch { /* readonly on some tracks */ }
        document.body.appendChild(audio);

        // FLO-960: first_audio means the browser actually began playing an
        // unmuted bot track. `trackStarted` is only availability — a track
        // commonly exists while still silent — so the report waits for real
        // playback evidence.
        this.armFirstAudioEvidence(audio, track, {
          callbackGeneration: capturedCallbackGeneration,
          websiteAgentGeneration: capturedWebsiteAgentGeneration,
        });

        // Start audio analysis for visualization
        if (this.botAudioAnalyzer) {
          this.botAudioAnalyzer.start(stream, (levels) => {
            // Only update levels when bot is actually speaking
            if (this.botIsSpeaking) {
              this.audioLevels = levels;
              this.renderReactUI();
            }
          });
        }

        // Store reference for cleanup
        if (!this.audioElements) {
          this.audioElements = [];
        }
        this.audioElements.push(audio);
      }

      // Remote video arrives from up to two Daily participants: the bot
      // publishes its screencast (participant.name set by us server-side to
      // "AI Demo Agent" / "AI Onboarding Assistant"), and — when the agent has
      // an avatar enabled — the avatar service publishes face video as its
      // own participant under the known user_name FLOE_AVATAR_USER_NAME (set
      // by pipecat-server when it points the avatar session's egress at the
      // bot's room). Pipecat's Participant type is {id, name, local} — match
      // on `name` (NOT userName/user_name; those don't exist on this type).
      if (track.kind === 'video') {
        const participantName: string = (participant?.name || '').toLowerCase();

        // Avatar: POSITIVE identity match on the known user_name (FLO-545).
        // Applies in every mode — demo, website-agent, AND onboarding — since
        // any agent can have an avatar now. Replaces the old "any named
        // non-bot participant" heuristic, which misclassified tracks whenever
        // the avatar vendor's display name drifted.
        if (participantName === FLOE_AVATAR_USER_NAME) {
          this.logger.log(`[OnboardingSDK] 🎭 Avatar video track started (participant=${participantName})`);
          this.avatarVideoStream = new MediaStream([track]);
          this.syncAudioOutput(); // FLO-348/FLO-51: TTS-output sync applies on every surface, onboarding included
          this.renderReactUI();
        } else if (this.config.demoMode || this.config.websiteAgent) {
          // Screencast classification — only the demo and website-agent
          // surfaces render the bot's screencast.
          // FLO-323: accept it in website-agent mode too — the website agent
          // hands off to a live demo (start_demo → driving), and its
          // screencast arrives on this same track. Without this the handoff
          // streams into a chat window with no <video> ("sorry I can't see
          // it"); AgentUI's demoHandoff branch then swaps to the DemoView
          // video surface once botVideoStream is set.
          // The bot is whoever WE named — guaranteed to contain "demo agent" /
          // "onboarding assistant" from the bot_name we set server-side.
          const isBot = participantName.includes('demo agent') || participantName.includes('onboarding assistant');

          if (isBot || !participantName) {
            // Named bot participant, or the no-name single-publisher fallback
            // — both are the bot's screencast.
            this.logger.log(`[OnboardingSDK] 🎬 Bot video track started (screencast, participant=${participantName || 'unnamed'})`);
            this.botVideoStream = new MediaStream([track]);
            // FLO-348: a live demo just came on screen — resync so the server
            // keeps TTS on (the demo's captions are sourced from TTS text)
            // even if the visitor muted the speaker during the Q&A phase.
            this.syncAudioOutput();
            this.renderReactUI();
          } else {
            // Named participant that is neither the bot nor the avatar
            // constant. Don't guess: assigning it to botVideoStream could
            // clobber a real screencast.
            this.logger.warn(`[OnboardingSDK] Unrecognized remote video participant "${participantName}" — ignoring track`);
          }
        }
      }
    });

    // Clean up audio elements when tracks stop
    this.pipecatClient.on('trackStopped', (track: MediaStreamTrack, participant?: any) => {
      if (!participant?.local && track.kind === 'audio') {
        this.logger.log('[OnboardingSDK] 🔇 Bot audio track stopped');
        // Stop bot audio analyzer
        if (this.botAudioAnalyzer) {
          this.botAudioAnalyzer.stop();
        }
        this.audioLevels = [];
        // FLO-960: the element behind this track can never become audible, so
        // drop the pending first-audio candidate (and its stream reference)
        // rather than leaving it armed for a track that is already over.
        if (this.firstAudioCandidate?.track === track) this.clearFirstAudioCandidate();
      }

      // FLO-545: avatar teardown — when the avatar's video track ends (session
      // closed server-side, or the avatar participant left mid-session), drop
      // the stream so the PiP unmounts instead of latching a frozen last
      // frame. Matched by track identity: avatarVideoStream wraps exactly the
      // track trackStarted received.
      if (
        !participant?.local &&
        track.kind === 'video' &&
        this.avatarVideoStream?.getTracks().includes(track)
      ) {
        this.logger.log('[OnboardingSDK] 🎭 Avatar video track stopped');
        this.avatarVideoStream = null;
        this.renderReactUI();
      }

      // Detect screen share stopped via browser's native "Stop sharing" button.
      // When the user clicks the browser chrome stop button, the video track ends
      // but the SDK's screenShareEnabled flag isn't updated. We catch it here to
      // notify the server so the vision scheduler stops.
      if (participant?.local && track.kind === 'video' && this.screenShareEnabled) {
        this.logger.log('[OnboardingSDK] 🖥️ Screen share track stopped (browser stop button)');
        this.screenShareEnabled = false;
        this.sendMetadata({ type: 'screen_share_status', active: false });
        this.renderReactUI();
      }
    });
  }

  /**
   * FLO-960: begin collecting evidence for `first_audio` on one bot audio
   * element. Nothing is reported from here — `maybeReportFirstAudio()` decides,
   * and it needs playback active, the bot's audio audible, AND the bot
   * speaking.
   *
   * Playback readiness is event-driven, because the media events ARE the media
   * state: `playing` means the browser is rendering, while `pause`, `waiting`,
   * `stalled` and `ended` mean the visitor is hearing nothing right now — a
   * track that played, stalled, and only then saw the bot speak is not a
   * first-audio moment. A rejected `play()` (autoplay blocked) reports nothing.
   *
   * The run generations are captured by the caller so a superseded client's
   * late track can never stamp the live session's diagnostics thread.
   */
  private armFirstAudioEvidence(
    audio: HTMLAudioElement,
    track: MediaStreamTrack,
    run: { callbackGeneration: number; websiteAgentGeneration: number },
  ): void {
    // One candidate at a time: retiring the previous one detaches ITS listeners,
    // so a re-armed element cannot accumulate handlers.
    this.clearFirstAudioCandidate();

    // `playing` (or a resolved play()) means the browser is rendering; every
    // other media event means it is not. Readiness is a live fact, not a latch:
    // a track that stalls before the bot speaks must not count as audible audio.
    const markPlaying = () => {
      if (this.firstAudioCandidate !== candidate) return;
      candidate.playbackActive = true;
      this.maybeReportFirstAudio();
    };
    const markNotPlaying = () => {
      if (this.firstAudioCandidate !== candidate) return;
      candidate.playbackActive = false;
    };

    const candidate = {
      audio,
      track,
      callbackGeneration: run.callbackGeneration,
      websiteAgentGeneration: run.websiteAgentGeneration,
      playbackActive: false,
      detach: () => {
        audio.removeEventListener('playing', markPlaying);
        audio.removeEventListener('pause', markNotPlaying);
        audio.removeEventListener('waiting', markNotPlaying);
        audio.removeEventListener('stalled', markNotPlaying);
        audio.removeEventListener('ended', markNotPlaying);
      },
    };
    this.firstAudioCandidate = candidate;

    try {
      // `playing` fires when playback begins after a stall/seek too, so this
      // also covers a UA whose play() promise resolves early.
      audio.addEventListener('playing', markPlaying);
      audio.addEventListener('pause', markNotPlaying);
      audio.addEventListener('waiting', markNotPlaying);
      audio.addEventListener('stalled', markNotPlaying);
      audio.addEventListener('ended', markNotPlaying);
      const started = audio.play?.();
      if (started && typeof started.then === 'function') {
        started.then(markPlaying, () => {
          // Autoplay blocked (or the element was torn down mid-request): the
          // visitor heard nothing, so nothing is reported.
        });
      }
    } catch {
      /* a UA without play() simply records readiness on `playing`, or not at all */
    }
  }

  /** Retire the pending first-audio candidate and detach its media listeners. */
  private clearFirstAudioCandidate(): void {
    const candidate = this.firstAudioCandidate;
    this.firstAudioCandidate = null;
    try {
      candidate?.detach();
    } catch {
      /* the element is gone; nothing left to detach */
    }
  }

  /**
   * FLO-960: report `first_audio` once playback is active, the bot's audio is
   * audible (element and speaker unmuted, track enabled) AND the bot is
   * speaking. Called from each edge that can complete that set: playback
   * starts, the bot starts speaking, and the visitor unmutes.
   */
  private maybeReportFirstAudio(): void {
    const candidate = this.firstAudioCandidate;
    if (!candidate || !candidate.playbackActive) return;
    try {
      if (!this.isCurrentPipecatRun(candidate.callbackGeneration, candidate.websiteAgentGeneration)) {
        this.clearFirstAudioCandidate();
        return;
      }
      // `ended` is the one media state worth re-reading: the events above are
      // the signal for pause/stall, but an element that finished while we were
      // checking must not be reported as live audio.
      if (candidate.audio.ended) return;
      if (candidate.audio.muted || this.speakerMuted) return;
      if (!candidate.track.enabled) return;
      if (!this.botAudioSpeaking) return;
      this.clearFirstAudioCandidate();
      this.sdkDiagnostics.noteFirstAudio();
    } catch {
      /* reporting must never touch playback */
    }
  }

  /**
   * True while the captured generations still identify the live run.
   *
   * ONE predicate shared by the Pipecat callback wrapper and the raw track
   * listeners, so a future edit cannot fence one and forget the other. FLO-390:
   * a late frame from a superseded website-agent client must not act on the
   * session that replaced it.
   */
  private isCurrentPipecatRun(
    capturedCallbackGeneration: number,
    capturedWebsiteAgentGeneration: number,
  ): boolean {
    if (capturedCallbackGeneration !== this.pipecatCallbackGeneration) return false;
    if (this.config.websiteAgent && capturedWebsiteAgentGeneration !== this.websiteAgentGeneration) {
      return false;
    }
    return true;
  }

  /**
   * Initialize devices on `client`, retrying with mic disabled if microphone
   * acquisition is denied or unavailable. On that device error the method:
   *   - sets micPermissionDenied/micEnabled. FLO-51: it does NOT persist a
   *     preference — a browser refusing the mic is not the user choosing to
   *     turn it off, and treating it as one used to silence the agent forever.
   *   - disables the mic on the existing client and retries initDevices
   * Keep one client per transport: reconstructing PipecatClient on the same
   * Daily transport registers duplicate listeners and doubles caption events.
   * Any non-device/permission error is rethrown unchanged.
   */
  private async initDevicesWithMicFallback(
    client: PipecatClient,
    opts?: {
      deferMic?: boolean;
      /** The caller already completed a gesture-bound permission probe. */
      micAlreadyGranted?: boolean;
    },
  ): Promise<PipecatClient> {
    const owningClient = this.pipecatClient;
    // FLO-109 (deferMic): the caller is starting the session OUTSIDE a user
    // gesture (prefilled-email auto-start at init). Disable the mic on this client
    // so initDevices() never reaches getUserMedia — there's no activation to
    // surface the iOS prompt, so attempting it would just fail silently. This is
    // NOT a mic-off preference: micEnabled stays true and the mic is
    // acquired later from a real tap (primeMicFromGesture).
    if (opts?.deferMic) {
      client.enableMic(false);
      await client.initDevices();
      this.logger.log('[OnboardingSDK] Devices initialized (mic deferred to gesture)');
      return client;
    }

    // FLO-109: prime the mic permission from the caller's user gesture BEFORE
    // initDevices(). All callers run from a tap/click (demo intro-form submit,
    // returning-user connect, discovery-popup start), so this is where iOS
    // Safari gets its chance to show the prompt. On denial/no-mic this flips us
    // to text mode (enableMic:false), so the initDevices() below won't prompt.
    if (this.micEnabled && !opts?.micAlreadyGranted) {
      const granted = await this.ensureMicPermission();
      if (this.pipecatClient !== owningClient) throw new Error('Device initialization belongs to a replaced client');
      if (!granted) {
        // Preserve the original client/transport ownership and camera settings.
        client.enableMic(false);
      }
    }

    try {
      await client.initDevices();
      this.logger.log('[OnboardingSDK] Devices initialized');
      return client;
    } catch (devError) {
      if (this.pipecatClient !== owningClient) throw devError;
      // Any device-acquisition failure degrades to text mode: permission denied
      // (iOS NotAllowed/Security/Abort) OR no usable mic (Android NotFound /
      // NotReadable / Overconstrained — e.g. no hardware, or the device is busy
      // because the primed stream is holding it). Only a genuinely unexpected
      // error ('other', e.g. a transport bug) is fatal.
      if (classifyMicError(devError) === 'other') throw devError;

      // FLO-51: join mic-less for THIS session, and persist NOTHING. The old
      // code wrote `floe_text_mode: 'true'` here, so one transient failure —
      // NotReadableError because another tab or Zoom held the mic — permanently
      // converted a voice-first agent into a silent text bot on that origin,
      // with no way back except finding a toggle. A failure is not a preference.
      this.logger.warn('[OnboardingSDK] Device init failed — joining without a mic:', devError);
      this.micProbeFailed = true;
      this.micPermissionDenied = classifyMicError(devError) === 'permission-denied';
      this.micEnabled = false;
      if (this.config.websiteAgent) this.websiteAgentMicPermissionReady = false;
      // FLO-960: this blocks voice before any spawn — a failed start.
      const devMicReason = this.diagnosticMicReason(
        classifyMicError(devError),
        micErrorName(devError),
      );
      if (devMicReason) this.sdkDiagnostics.noteMicDenied(devMicReason);

      client.enableMic(false);
      await client.initDevices();
      this.logger.log('[OnboardingSDK] Devices initialized (text mode fallback)');
      return client;
    }
  }

  /**
   * Returns true when the error represents a mic permission denial or cancellation.
   * Covers NotAllowedError (explicit deny), SecurityError (policy block), and
   * AbortError (user dismissed/cancelled the browser prompt). Delegates to the
   * shared classifier in ./mic-permission (also handles wrapped errors).
   */
  private isMicPermissionError(error: unknown): boolean {
    return classifyMicError(error) === 'permission-denied';
  }

  /**
   * Acquire microphone permission from within a direct user gesture (FLO-109).
   *
   * iOS Safari only surfaces the mic prompt when getUserMedia() is reached
   * during a transient user activation, so this MUST be called from a real
   * tap/click path (demo intro-form submit, connect tap, voice toggle) and
   * BEFORE any network round-trip that could outlive the activation window.
   *
   * Behaviour:
   *   - No-op (returns false) when already in text mode — never prompt then.
   *   - Permissions API pre-check: a definitive 'denied' short-circuits to text
   *     mode (the browser would never show a prompt anyway). 'granted'/'prompt'/
   *     unsupported fall through to getUserMedia, which is the source of truth.
   *   - On success the stream is retained as `primedMicStream` so
   *     setupUserMicAnalysis() can reuse it instead of issuing a second
   *     getUserMedia (no double-prompt, no duplicate capture).
   *   - On denial (NotAllowedError/SecurityError/AbortError) or missing hardware
   *     (NotFoundError/NotReadableError/OverconstrainedError) it falls back to
   *     text mode.
   *
   * Returns true when the mic is available for voice, false when we fell back
   * to text mode (or had no mediaDevices at all).
   */
  private async ensureMicPermission(skipPermissionPrecheck = false): Promise<boolean> {
    // The user's preference is mic-off (persisted, or an earlier denial) —
    // never prompt then. FLO-51: this is now the mic preference alone; it says
    // nothing about whether they want to hear the bot.
    if (!this.micEnabled) return false;

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.logger.warn('[OnboardingSDK] mediaDevices unavailable — joining without a mic');
      this.handleMicUnavailable('no-device');
      return false;
    }

    // Permissions API pre-check (Chrome/Android/desktop). Used ONLY to
    // short-circuit a hard 'denied' — never to gate the 'prompt' path, because
    // we still want getUserMedia to fire from the gesture below. iOS Safari
    // often can't query 'microphone' and throws here; we swallow that and fall
    // through to getUserMedia. The query resolves in ~ms, well within the
    // gesture's transient-activation window.
    //
    // skipPermissionPrecheck: callers already deep inside a user gesture
    // (primeMicFromGesture) skip this so the only thing between the tap and
    // getUserMedia is synchronous — no awaited query that could, on some
    // browsers, outlive the transient activation. getUserMedia then handles a
    // prior denial itself (rejects → text-mode fallback below), so nothing is lost.
    if (!skipPermissionPrecheck) {
      if ((await this.queryMicPermissionState()) === 'denied') {
        this.logger.warn('[OnboardingSDK] Mic permission already denied (Permissions API)');
        this.handleMicUnavailable('permission-denied');
        return false;
      }
    }

    // Snapshot the race guard so we can detect a disconnect that happens while
    // the permission prompt is open (disconnect() bumps userMicStreamRequestId).
    const requestId = this.userMicStreamRequestId;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: MIC_AUDIO_CONSTRAINTS });

      // Disconnected/superseded while the prompt was open — don't leak the capture.
      if (requestId !== this.userMicStreamRequestId) {
        this.logger.log('[OnboardingSDK] 🎤 Primed mic invalidated (disconnect during prompt) — stopping tracks');
        stream.getTracks().forEach((t) => t.stop());
        return false;
      }

      // Retain for visualization reuse; stop any prior primed stream first.
      if (this.primedMicStream) {
        this.primedMicStream.getTracks().forEach((t) => t.stop());
      }
      this.primedMicStream = stream;
      this.micPermissionDenied = false;
      this.logger.log('[OnboardingSDK] 🎤 Mic permission granted (primed on gesture)');
      return true;
    } catch (error) {
      const kind = classifyMicError(error);
      if (kind === 'no-device') {
        this.logger.warn('[OnboardingSDK] No usable microphone — text mode:', error);
      } else if (kind === 'permission-denied') {
        this.logger.warn('[OnboardingSDK] Mic permission denied on gesture — text mode');
      } else {
        this.logger.warn('[OnboardingSDK] getUserMedia failed — text mode:', error);
      }
      this.handleMicUnavailable(kind, micErrorName(error));
      return false;
    }
  }

  /**
   * The microphone is unavailable — denied, cancelled, or no usable device.
   *
   * FLO-51: this mutes the mic for THIS SESSION and nothing else. It does not
   * persist a preference (a browser refusing the mic is not the user choosing
   * anything — see the device-init catch above), and it emphatically does not
   * silence the bot: the user can still hear the agent and type back, which is
   * the whole point of decoupling the two. Everything they lose is the mic.
   */
  private handleMicUnavailable(kind: MicErrorKind = 'other', errorName?: string | null): void {
    const denied = kind === 'permission-denied';
    if (!this.micEnabled && this.micProbeFailed) return; // already handled

    this.logger.log(
      denied
        ? '[OnboardingSDK] 🎤 Mic refused — continuing without it (voice replies unaffected)'
        : '[OnboardingSDK] 🎤 No usable mic — continuing without it (voice replies unaffected)',
    );
    // FLO-960: report only a category we can actually name — the reason code,
    // never the DOMException's own name or message.
    const micReason = this.diagnosticMicReason(kind, errorName);
    if (micReason) this.sdkDiagnostics.noteMicDenied(micReason);
    // Same split applyMicProbeResult() uses: micProbeFailed covers EVERY
    // unusable outcome (so toggleMute() re-requests rather than lighting a
    // live-looking button over a dead device), while micPermissionDenied keeps
    // its narrow "the visitor said no" meaning. That distinction became
    // user-visible when the mic control started explaining itself — telling
    // someone with no microphone to "allow it in your browser settings" is
    // advice they cannot act on.
    this.micProbeFailed = true;
    this.micPermissionDenied = denied;
    this.micEnabled = false;
    // FLO-786: a refusal is not necessarily final — the visitor can lift it in
    // the browser, and this is how we find out without asking them to tap.
    this.micRecoveryDeadlineMs = Date.now() + MIC_RECOVERY_WINDOW_MS;
    this.watchMicPermission();

    // Mute mic in Pipecat if the client already exists
    if (this.pipecatClient) {
      this.pipecatClient.enableMic(false);
    }

    this.renderReactUI();
  }

  /**
   * Consume the mic stream primed on the user gesture (ensureMicPermission),
   * if it's still usable. Returns null when there's no primed stream or its
   * track has already ended (stopping the dead stream on the way out). Single-
   * use: clears `primedMicStream` so a later call falls back to getUserMedia.
   */
  private takePrimedMicStream(): MediaStream | null {
    const stream = this.primedMicStream;
    this.primedMicStream = null;
    if (!stream) return null;
    const live = stream.getAudioTracks().some((t) => t.readyState === 'live');
    if (!live) {
      stream.getTracks().forEach((t) => t.stop());
      return null;
    }
    return stream;
  }

  /**
   * Stop and clear a primed-but-unconsumed mic stream. Covers the small window
   * where ensureMicPermission() acquired the mic but the session tore down
   * before setupUserMicAnalysis() consumed it — without this it would keep the
   * microphone (and the OS recording indicator) live after disconnect.
   *
   * Also clears the voice-pending-gesture flag (FLO-109): this runs on every
   * teardown path, so a deferred-mic demo that's torn down before its "Start
   * demo" tap doesn't carry a stale "pending" state into a retry/re-init.
   */
  private stopPrimedMicStream(): void {
    this.voicePendingGesture = false;
    if (this.primedMicStream) {
      this.primedMicStream.getTracks().forEach((track) => track.stop());
      this.primedMicStream = null;
    }
  }

  /**
   * Setup user microphone analysis for waveform visualization
   */
  private async setupUserMicAnalysis() {
    // Capture request ID before async operation to detect race conditions
    const requestId = ++this.userMicStreamRequestId;

    try {
      // Get user's microphone stream for analysis (separate from the Pipecat mic
      // — just for visualization). FLO-109: reuse the stream already primed on
      // the user gesture by ensureMicPermission() when it's still live, so we
      // never fire a second getUserMedia (and never risk a second prompt). Fall
      // back to a fresh request when there's no primed stream — e.g. toggling
      // back to voice mid-session, where permission is already granted so this
      // won't prompt.
      let stream = this.takePrimedMicStream();
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: MIC_AUDIO_CONSTRAINTS,
        });
      }

      // Check if disconnected or superseded while permission prompt was open
      if (requestId !== this.userMicStreamRequestId || !this.userAudioAnalyzer) {
        this.logger.log('[OnboardingSDK] 🎤 Mic stream request invalidated (disconnect/reconnect occurred), stopping tracks');
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      // If we already had a stream (reconnect), stop it before replacing
      if (this.userMicStream) {
        this.userMicStream.getTracks().forEach(track => track.stop());
      }
      this.userMicStream = stream;

      // Mic access succeeded — this is proof of a usable device, so clear BOTH
      // failure flags (a stale micProbeFailed would route every later unmute
      // through a redundant re-probe) and update UI.
      if (this.micPermissionDenied || this.micProbeFailed) {
        this.micPermissionDenied = false;
        this.micProbeFailed = false;
        this.renderReactUI();
      }

      this.logger.log('[OnboardingSDK] 🎤 User mic stream captured for visualization');

      // Start analyzing user mic
      if (this.userAudioAnalyzer) {
        this.userAudioAnalyzer.start(stream, (levels) => {
          // Only update levels when user is actually speaking
          if (this.userIsSpeaking) {
            this.audioLevels = levels;
            this.renderReactUI();
          }
        });
      }
    } catch (error) {
      // Ignore rejections from superseded getUserMedia attempts (stale request IDs).
      // This guards against a race where a previous denied prompt resolves after
      // disconnect/reconnect and the new active attempt is already in progress.
      if (requestId !== this.userMicStreamRequestId) return;

      if (this.isMicPermissionError(error)) {
        // Guarded by isMicPermissionError, so this branch IS a real denial.
        this.handleMicUnavailable('permission-denied');
      } else {
        this.logger.warn('[OnboardingSDK] Could not capture mic for visualization:', error);
        // This is non-critical - visualization will just not work for user audio
      }
    }
  }

  /**
   * Is this a website-agent embed rendering as a DOCKED RAIL?
   *
   * The gate for FLO-524's continuity writes: the floating card opens and closes
   * within one page load, so persisting its state would be storage churn with no
   * reader. Same resolution order as the render props (init-time override first,
   * then the per-site facet) and the same defensive resolver, so a stale or
   * hand-edited config can never flip it on.
   */
  private isWebsiteAgentSidebarMode(): boolean {
    if (!this.config.websiteAgent) return false;
    return (
      resolveDisplayMode(
        this.config.websiteAgentDisplayMode ?? this.websiteAgentConfig?.displayMode,
      ) === 'sidebar'
    );
  }

  /**
   * FLO-523 — install the zero-cooperation squeeze harness for this site.
   *
   * Gated three ways, all of which must hold: website-agent mode (the only mode
   * that docks a rail), `displayMode: 'sidebar'`, and an explicit per-site
   * `sidebarCompat` flag. With the facet absent — every site today — this
   * resolves to both-false and returns before touching anything, so the flags-off
   * page is byte-identical to the v1 tree.
   *
   * Applied here rather than in WebsiteAgentView because the harness is STATIC
   * for the session: `container-type` swaps the containing block of every fixed
   * element on the page, so toggling it per open/close would relayout the whole
   * host page twice per interaction. Released by releaseSqueezeCompat() in
   * disconnect(); squeeze-compat also refuses to double-apply.
   */
  private applyHostSqueezeCompat(): void {
    const compat = resolveSidebarCompat(this.websiteAgentConfig?.sidebarCompat);
    if (!isCompatRequested(compat)) return;
    // One resolver for "docked rail?" — this also carries the websiteAgent gate
    // the docblock promises (a demo-mode embed with a stray sidebarCompat facet
    // must never install a page-wide harness).
    if (!this.isWebsiteAgentSidebarMode()) return;
    // The return value is intentionally dropped: disconnect() calls
    // releaseSqueezeCompat() unconditionally (the same belt forceReleaseSqueeze
    // is for v1), and the module keeps the single active harness itself.
    applySqueezeCompat({
      compat,
      root: this.reactContainer,
      dockWidth: clampSidebarWidth(
        this.config.websiteAgentSidebarWidth ?? this.websiteAgentConfig?.sidebarWidth,
      ),
    });
  }

  /**
   * Initialize React UI components
   */
  private initReactUI() {
    // Inject Floe's design-system fonts (Plus Jakarta Sans + DM Serif
    // Display) if they aren't already present. On floe-site the Next/Font
    // setup already defines --font-jakarta / --font-dm-serif on <html>;
    // on customer sites this preconnect+stylesheet ensures the demo form
    // and any other SDK chrome renders with the right typography.
    // Idempotent — guards against double-inject if the SDK is reinitialized.
    // FLO-531: Noto Sans Devanagari rides along because neither Latin face
    // ships Devanagari glyphs — without it a Hindi session falls to whatever
    // the OS picks (usually a serif), so headings and body render in two
    // unrelated faces. It sits AFTER the Latin faces in every stack, so Latin
    // text keeps coming from Jakarta/DM Serif and only Devanagari falls through.
    if (!document.getElementById('floe-sdk-fonts')) {
      const fragment = document.createDocumentFragment();
      const preconnect1 = document.createElement('link');
      preconnect1.rel = 'preconnect';
      preconnect1.href = 'https://fonts.googleapis.com';
      fragment.appendChild(preconnect1);
      const preconnect2 = document.createElement('link');
      preconnect2.rel = 'preconnect';
      preconnect2.href = 'https://fonts.gstatic.com';
      preconnect2.crossOrigin = '';
      fragment.appendChild(preconnect2);
      const fontsLink = document.createElement('link');
      fontsLink.id = 'floe-sdk-fonts';
      fontsLink.rel = 'stylesheet';
      fontsLink.href =
        'https://fonts.googleapis.com/css2?' +
        'family=Plus+Jakarta+Sans:wght@400;500;600;700&' +
        'family=DM+Serif+Display:ital@0;1&' +
        'family=Noto+Sans+Devanagari:wght@400;500;600;700&' +
        'display=swap';
      fragment.appendChild(fontsLink);
      document.head.appendChild(fragment);
    }

    // Create container for React components
    this.reactContainer = document.createElement('div');
    this.reactContainer.id = 'onboarding-sdk-react-ui';
    this.reactContainer.setAttribute(SDK_WIDGET_ATTR, 'react-ui');
    this.reactContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 2147483646;
    `;
    document.body.appendChild(this.reactContainer);

    // FLO-371: isolate the website-agent overlay (launcher + nudge pills + chat)
    // from host-page CSS by rendering the React tree inside a Shadow Root. Host
    // author styles — resets, `* {}`, `button {}`, aggressive frameworks — cannot
    // cross the shadow boundary; a base style neutralizes the inherited properties
    // that DO (font, color) and sets box-sizing deterministically. Component-local
    // <style> blocks (markdown resets, keyframes) render INSIDE the shadow, so they
    // scope naturally with no host bleed. Demo + onboarding keep rendering in the
    // light DOM (unchanged). DOM *reads* — the nudge heading scan — still query the
    // host document, so section anchoring is unaffected.
    // Guard on !demoMode to match AgentUI's render branch (it checks demoMode
    // FIRST): only shadow-mount when the website-agent UI is what actually renders,
    // so a hypothetical both-flags config still renders the demo in the light DOM.
    let mountNode: HTMLElement = this.reactContainer;
    if (this.config.websiteAgent && !this.config.demoMode) {
      const shadow = this.reactContainer.attachShadow({ mode: 'open' });
      const base = document.createElement('style');
      base.textContent =
        '*,*::before,*::after{box-sizing:border-box;}' +
        '.floe-sdk-shadow-mount{width:100%;height:100%;pointer-events:none;' +
        "font-family:'Plus Jakarta Sans','Noto Sans Devanagari',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;" +
        'color:#1C1917;line-height:1.4;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;}';
      shadow.appendChild(base);
      const inner = document.createElement('div');
      inner.className = 'floe-sdk-shadow-mount';
      shadow.appendChild(inner);
      mountNode = inner;
    }

    // The language was chosen before this container existed.
    markWidgetLanguage(this.reactContainer, this.selectedLanguage);

    // Create React root and render UI
    this.reactRoot = ReactDOM.createRoot(mountNode);
    this.renderReactUI();
  }

  /**
   * What the host page already knows about the prospect, in the shape the demo
   * intro form seeds its fields from. Sourced from `config.userInfo` — the same
   * object a /d/[token] demo link fills from its stored prospect context.
   *
   * Built once and cached: renderReactUI() runs on every state change (every
   * transcript delta while the bot speaks), and a fresh object each time would
   * churn the prop identity the form's prefill memo keys on.
   *
   * `email` is intentionally NOT included — see prefilledEmail at the call site.
   */
  private demoIntroPrefillCache?: DemoIntroPrefill;

  private demoIntroPrefill(): DemoIntroPrefill | undefined {
    if (this.demoIntroPrefillCache) return this.demoIntroPrefillCache;
    const info = this.config.userInfo;
    if (!info) return undefined;
    const prefill: DemoIntroPrefill = {
      phone: info.phone,
      name: info.name,
      company: info.company,
      // The SDK's public UserInfo calls it `designation`; the form calls it
      // `role`. Translate here so neither side has to know about the other.
      role: info.designation,
    };
    if (!prefill.name && !prefill.phone && !prefill.company && !prefill.role) return undefined;
    this.demoIntroPrefillCache = prefill;
    return prefill;
  }

  /**
   * The identity-capture policy in force: the explicit init option (a
   * /d/{token} link's already-merged site ← link answer) over the site policy
   * from validateClientKey, over nothing at all.
   *
   * `undefined` is meaningful and must survive — `captureIsDeferred(undefined)`
   * is `false`, i.e. today's gating intro form. Coercing it to a default here
   * would work but would put the fail-safe in two places.
   */
  private resolvedDemoCapture(): DemoCapturePolicy | undefined {
    // The init option goes through the SAME parser as the served policy. It
    // arrives from a host page — floe-site's launch shell validates it, but an
    // embedder can pass anything — and an unrecognized value here would read as
    // "not before_demo", i.e. deferred, i.e. a site that quietly stops
    // capturing leads. Unknown is never deferred, on every input path.
    return (
      parseDemoCapturePolicy(this.config.demoIdentityCapture) ??
      this.demoCaptureConfig ??
      undefined
    );
  }

  /**
   * Render React UI components
   */
  private renderReactUI() {
    if (!this.reactRoot) return;

    if (this.config.demoMode) {
      this.logger.log(`[OnboardingSDK] 🎨 Render — speaking=${this.botIsSpeaking}, transcript=${this.botTranscript.length}chars, history=${this.transcriptHistory.length}msgs`);
    }

    this.reactRoot.render(
      React.createElement(AgentUI, {
        connected: this.isConnected,
        voiceEnabled: this.micEnabled,
        agentState: this.agentState,
        botTranscript: this.botTranscript,
        userTranscript: this.userTranscript,
        transcriptHistory: [...this.transcriptHistory],
        screenShareEnabled: this.screenShareEnabled,
        userIsSpeaking: this.userIsSpeaking,
        botIsSpeaking: this.botIsSpeaking,
        audioLevels: [...this.audioLevels],
        // FLO-51: START only. It must never be handleToggleConnection(), which
        // flips on the current state — the orb calls this every time the panel
        // opens, so re-expanding a MINIMIZED live session would have torn it
        // down. Ending is a separate, explicitly-labelled action below.
        onToggleConnection: () => this.startSessionFromGesture(),
        onToggleMute: () => this.toggleMute(),
        micPermissionDenied: this.micPermissionDenied,
        micUnavailable: this.micProbeFailed && !this.micPermissionDenied,
        // FLO-51: only offer screen share when the site turned it on. The old
        // panel rendered the button unconditionally, including for the many
        // embeds that never enable screen capture.
        onToggleScreenShare: this.config.enableScreenCapture
          ? () => this.handleToggleScreenShare()
          : undefined,
        onResume: this.pausedSessionId ? () => this.resumeSession(this.pausedSessionId!) : undefined,
        pausedSessionId: this.pausedSessionId || undefined,
        connectionProgress: this.connectionProgress || undefined,
        fatalError: this.fatalError || undefined,
        isReturningUser: this.isReturningUser,
        // FLO-534: a third, independent reason to start collapsed. Kept
        // separate from the two above rather than folded into them: those
        // describe the USER (been here before / dismissed the welcome), this
        // describes the SITE's configuration. Reusing isReturningUser would
        // greet a brand-new visitor with "Welcome back".
        supportMode: this.runsInSupportMode(),
        onMinimize: () => this.handleMinimize(),
        onExpand: () => this.handleExpand(),
        onSendText: (text: string, options?: { messageKind?: 'intro_form' }) =>
          this.sendTextMessage(text, options),
        // FLO-51: end the onboarding session from the panel header. Distinct
        // from onMinimize (collapse, session keeps running) — the two used to be
        // adjacent look-alike buttons where only one stopped the billing.
        onEndSession: () => {
          void this.disconnectAndMinimize().catch((error) => {
            this.logger.error('[OnboardingSDK] Error ending session:', error);
            this.emit('error', error);
          });
        },
        // FLO-307: deterministic opener chips + their tap handler. Copy the
        // array so a later mutation (clearing on the next turn) can't alias
        // React's captured prop. onSelectOption clears first (immediate visual
        // feedback), then sends the chip text as a normal user turn.
        suggestedOptions: this.visibleSuggestedOptions(),
        onSelectOption: (text: string) => {
          this.suggestedOptions = [];
          this.suggestedOptionsMetadata = null;
          this.sendTextMessage(text);
        },
        nudgeText: this.config.nudge?.text,
        nudgeAutoHideDelay: this.config.nudge?.autoHideDelay,
        nudgeAutoShow: this.nudgeAutoShow(),
        planProgress: this.planProgress || undefined,
        // Every allowed pack is already local. Pre-session changes commit
        // synchronously; live-session changes stay pending until the server
        // confirms its STT/TTS/voice pipeline has switched.
        languageOptions: this.localizationPolicy.languages,
        selectedLanguage: this.selectedLanguage,
        pendingLanguageSwitch: this.languageSwitchState.pending,
        languageSwitchError: this.languageSwitchState.error,
        languageSwitchDisabled:
          this.isSessionStarted() && !this.localizationPolicy.midSessionSwitch,
        onLanguageChange: (language: SupportedLanguage) =>
          this.requestLanguageChange(language),
        // Demo mode props
        demoMode: this.config.demoMode || false,
        // FLO-706: Website Agent starts are intent-aware. Text/chip starts omit
        // options and stay mic-free; only the mic control supplies
        // requestMicrophone:true from its user gesture.
        websiteAgent: this.config.websiteAgent || false,
        // Return the promise so WebsiteAgentView can reconcile its optimistic
        // `sessionStarted` latch after a failed warm boot and allow a retry.
        // startWebsiteAgentSession already logs/emits before rejecting (a
        // rejection caused by the SDK's own teardown is rethrown quietly).
        onStartWebsiteAgent: (options?: WebsiteAgentStartOptions) =>
          this.startWebsiteAgentSession(options),
        // FLO-786: the post-welcome handoff for a session the visitor opened
        // with a voice launcher. Resolves false when the surface still has to
        // ask with its own (tap-driven) invitation.
        onRequestMicAfterWelcome: () => this.requestWebsiteAgentMicAfterWelcome(),
        onOpenWebsiteAgentPreview: () => this.markExitIntentEngaged(),
        // FLO-320: end the muted Q&A session from the bottom call rail. Tears down
        // pipecat (frees the paid agent) and resets the start guard so a later
        // pill tap spins up a fresh session.
        onEndWebsiteAgent: () => {
          // FLO-557: this prop is the visitor's EXPLICIT End — the one intent
          // that means "retire this thread", as opposed to a collapse (still
          // resumable, by design) or the failed-connect cleanup that shares
          // endWebsiteAgentSession(). So the server signal is fired HERE, and
          // FIRST: the teardown below nulls the conversation linkage
          // synchronously, and the view clears the storage key right after.
          this.endWebAgentConversation();
          this.endWebsiteAgentSession().catch((err) =>
            this.logger.error('[OnboardingSDK] endWebsiteAgentSession failed:', err),
          );
        },
        // FLO-285: 'docked' (bottom pill → full-bleed on start) vs 'fullscreen'
        // (form full-screen immediately). Defaults to docked for marketing embeds.
        embedMode: this.config.embedMode ?? 'docked',
        demoIntroFormSubmitted: this.demoIntroFormSubmitted,
        onMarkIntroFormSubmitted: () => this.markDemoIntroFormSubmitted(),
        botVideoStream: this.botVideoStream,
        avatarVideoStream: this.avatarVideoStream,
        onEndDemo: () => this.endDemoForWrapUp(),
        // The configurable "close" action: full teardown + emit 'closed', which
        // the host page handles (currently router.push('/') → landing). Used by
        // the End Call button and the post-demo overlay's close (X) button. Host
        // wrappers reconfigure the destination via sdk.on('closed', ...).
        onCloseDemo: async () => {
          // Wrap like handleToggleConnection: a disconnect() rejection (e.g.
          // pipecatClient.disconnect() failing) is logged + emitted, never left
          // as an unhandled promise from a React event handler.
          try {
            await this.disconnect();
          } catch (error) {
            this.logger.error('[OnboardingSDK] Error during demo close (disconnect):', error);
            this.emit('error', error);
          }
        },
        demoError: this.demoError,
        onDemoRetry: this.config.demoMode ? () => this.retryDemo() : undefined,
        // Resolved with priority: explicit config → dashboard-configured
        // Site.calBookingUrl → empty. Personal details are absent unless the
        // operator explicitly configured an {email} query value.
        demoCalendarLink: this.getResolvedCalendarUrl(),
        onSendProspectPrefill: (data: Record<string, string>) => this.sendProspectPrefill(data),
        // End-of-demo wrap-up (populated by the bot's `demo_ended` message)
        demoEnded: this.demoEnded,
        demoSummary: this.demoSummary,
        demoFeaturesShown: this.demoFeaturesShown,
        demoNextStep: this.demoNextStep,
        // Customer brand — populated by validateClientKeyAndBotStatus from
        // /sites/validate-client-key. Drives the sidebar header and the
        // loading screen so the prospect sees the demoed product's identity.
        siteName: this.siteName,
        siteDomain: this.siteDomain,
        siteLogoUrl: this.siteLogoUrl,
        siteFaviconUrl: this.siteFaviconUrl,
        // Demo overlay branding (FLO-64). Init-time override wins over the
        // per-site value from validate-client-key; empty → undefined so
        // DemoView falls back to the Floe coral / "Book a call" defaults.
        // Trim before the || chain so a whitespace-only override (e.g. "  ")
        // falls through to the next precedence level instead of winning.
        accentColor: this.config.demoAccentColor?.trim() || this.siteAccentColor.trim() || undefined,
        ctaLabel: resolveUntaggedOperatorCopy(
          this.config.demoCtaLabel?.trim() || this.siteCtaLabel.trim() || undefined,
          this.selectedLanguage,
        ),
        // FLO-585: shared launcher corner/inset from init config. Passed RAW —
        // each render surface resolves absent/junk to 'bottom-right' / 24px, so
        // a hand-edited config can never push the launcher off-screen.
        launcherPosition: this.config.launcher?.position,
        launcherInset: this.config.launcher?.inset,
        // FLO-322: server truth (demo entitled AND runnable) for the render
        // layer to gate the push-to-demo CTA. Fail-safe false on older API.
        demoAvailable: this.demoAvailable,
        // Entitlement to run a website-agent session at all. The host-page CTA
        // needs BOTH: a demo to show, and permission to start the session that
        // shows it.
        websiteAgentEnabled: this.websiteAgentEnabled,
        // FLO-318/349: resolved website-agent config → opener chips, section
        // nudge pills, optional avatar, and the push-to-demo CTA. Section nudges
        // only surface when the operator enabled that facet.
        websiteAgentOpenerChips:
          resolveUntaggedOperatorCopy(
            this.websiteAgentConfig?.openerChips,
            this.selectedLanguage,
          ) ?? [],
        websiteAgentNudges: this.websiteAgentConfig?.nudges?.enabled
          ? resolveUntaggedOperatorCopy(
              this.websiteAgentConfig.nudges.contentMap,
              this.selectedLanguage,
            )
          : undefined,
        websiteAgentAvatarEnabled: !!this.websiteAgentConfig?.avatar?.enabled,
        // FLO-520/FLO-753: avatar media stays in config for re-enabling. Passed
        // RAW — the render layer applies the enabled gate and URL whitelist, so
        // disabled, stale, or hand-edited media never reaches a video surface.
        avatarIdleAfterMs: this.config.avatarIdleAfterMs,
        websiteAgentAvatarIdleLoopUrl: this.websiteAgentConfig?.avatar?.idleLoopUrl,
        websiteAgentAvatarPosterUrl: this.websiteAgentConfig?.avatar?.posterUrl,
        websiteAgentAvatarIntro: this.websiteAgentConfig?.avatar?.intro,
        websiteAgentPushToDemo: this.websiteAgentConfig?.pushToDemo?.enabled !== false,
        // FLO-514/519: floating card vs docked rail (+ its width). Init-time
        // override wins over the per-site config; both are passed through RAW —
        // WebsiteAgentView resolves them (unknown → 'floating' / 400) so a
        // hand-edited or stale config blob can never wreck a customer's layout.
        websiteAgentDisplayMode:
          this.config.websiteAgentDisplayMode ?? this.websiteAgentConfig?.displayMode,
        websiteAgentSidebarWidth:
          this.config.websiteAgentSidebarWidth ?? this.websiteAgentConfig?.sidebarWidth,
        // FLO-544: the resumed conversation's turns, when the server had any.
        // Passed by REFERENCE (not copied like transcriptHistory above): the
        // view's reconcile effect keys on this array's identity, so a fresh array
        // per render would re-run it on every animation frame the bot speaks in.
        websiteAgentResumedMessages: this.websiteAgentResumedMessages ?? undefined,
        websiteAgentResumeOutcome: this.websiteAgentResumeOutcome ?? undefined,
        // FLO-325: config-gated identity capture (anonymous-first — never gates
        // the first answer). The client card fires at `timing`; the submit rides
        // the existing prospect_prefill RTVI path → merges at session finalize.
        websiteAgentCapture:
          this.identityConsented() && this.websiteAgentConfig?.identityCapture?.enabled
            ? this.websiteAgentConfig.identityCapture
            : undefined,
        onRequestWebsiteAgentDemo: () => this.requestWebsiteAgentDemo(),
        onRequestWebsiteAgentIdentityPrompt: () =>
          this.requestWebsiteAgentIdentityPrompt(),
        // FLO-367: nudge telemetry — queue + beacon-flush to the analytics ingest.
        onWebsiteAgentNudgeEvent: (e: {
          type: string;
          nudgeId: string;
          page: string;
          match?: string;
          topic?: string;
        }) => this.recordNudgeEvent(e),
        onWebsiteAgentNavigated: () => this.loadRemainingNudgesOnNavigation(),
        // CDP Phase 1 (FLO-547): real open/close transitions of the website-
        // agent panel (the FLO-524 visual restore never fires these) + the
        // booking-CTA click proxy for the funnel's meetings stage.
        onWidgetOpened: (trigger: 'user' | 'nudge' | 'auto') => this.trackWidgetOpened(trigger),
        onWidgetClosed: () => this.trackWidgetClosed(),
        onBookingCtaClick: this.trackBookingCta,
        onResolveBookingIntent: this.resolveBookingIntentUrl,
        // A provider iframe callback is a UX hint only. Signed provider
        // webhooks remain the only source of MEETING_BOOKED truth.
        onBookingUxHint: (hint: BookingUxHint) =>
          this.emit('bookingCompletedHint', hint),
        websiteAgentCtaLabel: resolveUntaggedOperatorCopy(
          this.websiteAgentConfig?.pushToDemo?.ctaLabel?.trim() ||
            this.config.demoCtaLabel?.trim() ||
            this.siteCtaLabel.trim() ||
            undefined,
          this.selectedLanguage,
        ),
        // FLO-618: bumped by the bot's `show_calendar` push. The calendar URL
        // itself rides `demoCalendarLink` above — one resolution for both
        // agents.
        websiteAgentCalendarRequestId: this.websiteAgentCalendarRequestId,
        // The website→demo hand-over's two server edges. 0/0 is what an older
        // pipecat produces, and the surface then behaves exactly as it did
        // before: no card, stage mounted by the screencast alone.
        websiteAgentDemoStartingId: this.websiteAgentDemoStartingId,
        websiteAgentDemoVisibleId: this.websiteAgentDemoVisibleId,
        // Speaker (bot audio) mute. Controlled prop — the SDK owns the
        // mute state and the audio elements; DemoView just renders the
        // button + applies muted={true} to the avatar <video>.
        speakerMuted: this.speakerMuted,
        onToggleSpeaker: () => this.toggleSpeakerMute(),
        // Pre-supplied prospect email — skips the SDK form's email stage
        // (the wrapper page collected it before deciding to spawn pipecat,
        // so re-asking would be friction).
        prefilledEmail: this.config.prospectEmail,
        // ...and the rest of what the host already knows. userInfo is what a
        // /d/[token] demo link populates from its stored prospect context; it
        // was already reaching the BOT (it rides the session-start payload) but
        // never the FORM, so the recipient was asked to retype their own name.
        // Email is deliberately excluded — prefilledEmail above is the single
        // source for it, because that same value arms the cost gate.
        prefilledProspect: this.demoIntroPrefill(),
        // Fires when the prospect submits Stage 1 of the intro form
        // (valid email). This is the cost-gate trigger — it boots the
        // pipecat session that init() intentionally deferred.
        // Flags an obvious fake (test123@, throwaway domains) while the
        // prospect types, before the tap below spends a session on it.
        checkProspectEmail: this.prospectEmailCheck(),
        onProspectEmailCaptured: (email: string) => {
          this.startDemoSessionAfterEmail(email).catch((err) =>
            this.logger.error(
              '[OnboardingSDK] startDemoSessionAfterEmail failed:',
              err,
            ),
          );
        },
        // The SAME cost gate, minus the email — the Start tap on a deferred
        // policy, where nobody has been asked for one yet. Still a gesture, so
        // the mic is still acquired inline (FLO-109) and a headless link
        // scanner still can't spend a session by rendering the page.
        onStartDemoWithoutEmail: () => {
          this.startDemoSession().catch((err) =>
            this.logger.error('[OnboardingSDK] startDemoSession failed:', err),
          );
        },
        // Which intro surface to render — form vs fieldless Start card — and
        // which fields the deferred card asks for.
        demoCapture: this.resolvedDemoCapture(),
        identityAsk: this.identityConsented() ? this.identityAsk : null,
        onIdentityConfirm: (value: string) => this.confirmIdentityAsk(value),
        onIdentityReject: () => this.rejectIdentityAsk(),
        onRequestIdentityAskNow: (timing: string) => this.requestWebsiteAgentIdentityAsk(timing),

      })
    );
  }

  /**
   * Handle minimize from UI - keeps widget visible but collapsed
   */
  private handleMinimize() {
    this._isMinimized = true;
    this.planProgress = null;
    // CDP Phase 1: collapsing the onboarding panel is a real widget close.
    this.trackWidgetClosed();
    this.logger.log('[OnboardingSDK] UI minimized');
    this.renderReactUI();
    this.emit('minimized');
  }

  /**
   * Handle expand from UI
   */
  private handleExpand() {
    this._isMinimized = false;
    // CDP Phase 1: expanding from the orb is a user-triggered widget open.
    this.trackWidgetOpened('user');
    this.logger.log('[OnboardingSDK] UI expanded');
    this.renderReactUI();
    this.emit('expanded');
  }

  /**
   * Update connection progress during connection
   */
  private setConnectionProgress(stage: 'initializing' | 'authenticating' | 'connecting' | 'joining' | 'ready' | 'error', message: string, progress: number) {
    this.connectionProgress = { stage, message, progress };
    this.renderReactUI();
  }

  /**
   * Clear connection progress
   */
  private clearConnectionProgress() {
    this.connectionProgress = null;
    this.renderReactUI();
  }

  /**
   * Start a session from a user gesture (the orb / nudge click), idempotently.
   *
   * Separate from {@link handleToggleConnection} because the launcher fires on
   * every open, including re-opening a session that is merely minimized. A
   * toggle there would disconnect it — the user asked to SEE the agent, not to
   * hang up on it. No-ops when a session is live or a connect is already in
   * flight; otherwise runs the same connect path as before.
   */
  private startSessionFromGesture(): void {
    if (this.isConnected || this.connectInFlight) {
      this.logger.log('[OnboardingSDK] Session already live/connecting — open is a no-op');
      return;
    }
    void this.connectWithProgress().catch((error) => {
      // connectWithProgress already logs + emits; swallow so a React event
      // handler never leaves an unhandled rejection.
      this.logger.log('[OnboardingSDK] Connection error handled in startSessionFromGesture:', error);
    });
  }

  // FLO-51: `handleToggleConnection()` lived here. It was the one control that
  // meant "connect" or "hang up" depending on state, rendered next to a
  // near-identical Minimize — the exact ambiguity this ticket removed. Start is
  // now `startSessionFromGesture()` (above) and end is `disconnectAndMinimize()`
  // (below), each wired to its own labelled button.

  /**
   * Disconnect and minimize the UI (instead of removing it)
   */
  private async disconnectAndMinimize() {
    this.retirePlanRun();
    this.resetLanguageSwitchState();
    // Stop page tracking first
    this.pageTracker.stop();

    // Stop DOM Introspection Layer
    this.domIntrospector.stopCapturing();

    // Stop navigation detection
    if (this.navigationCompleteDetector) {
      this.navigationCompleteDetector.stop();
    }

    // Clear all overlays: cursor, highlights, tooltips
    this.overlay.cleanup();
    // Reset cursor state so next connect starts from bot icon
    this.overlay.resetCursorState();

    // Disconnect Pipecat client
    // FLO-960: so onDisconnected reports `client_requested`, not a bare drop.
    // Cleared in `finally` so a rejected disconnect cannot leak the flag.
    this.sdkRequestedTeardown = true;
    this.clientBeingTornDown = this.pipecatClient;
    try {
      if (this.pipecatClient) {
        await this.pipecatClient.disconnect();
        this.pipecatClient = null;
      }
    } finally {
      this.sdkRequestedTeardown = false;
      this.clientBeingTornDown = null;
    }

    // Clean up transport
    if (this.transport) {
      this.transport = null;
    }

    // Clean up audio elements
    if (this.audioElements) {
      this.audioElements.forEach(audio => {
        audio.pause();
        audio.remove();
      });
      this.audioElements = [];
    }

    // Clean up audio analyzers
    if (this.botAudioAnalyzer) {
      this.botAudioAnalyzer.destroy();
      this.botAudioAnalyzer = null;
    }
    if (this.userAudioAnalyzer) {
      this.userAudioAnalyzer.destroy();
      this.userAudioAnalyzer = null;
    }

    // Clean up user mic stream - invalidate any in-flight getUserMedia first
    this.userMicStreamRequestId++;
    if (this.userMicStream) {
      this.userMicStream.getTracks().forEach(track => track.stop());
      this.userMicStream = null;
    }
    this.stopPrimedMicStream();
    this.audioLevels = [];

    // Reset state but keep UI visible (minimized)
    this.isConnected = false;
    this.agentState = 'idle';
    this.resetBotSpeakingState();
    this._isMinimized = true; // Set to minimized state
    this.botTranscript = '';
    this.userTranscript = '';
    // CDP Phase 1: this path collapses the panel without going through
    // handleMinimize (voice already ended via onDisconnected).
    this.trackWidgetClosed();
    this.pendingCitationSources = null;

    // Re-render UI in minimized state (NOT remove it)
    this.renderReactUI();
    
    this.logger.log('[OnboardingSDK] Disconnected and minimized');
    this.emit('disconnected');
    this.emit('minimized');
  }

  /**
   * Connect with progress stages for better UX.
   * 
   * NOTE: This method is intended ONLY for post-onboarding reconnects (e.g., when a user
   * clicks the connect button after having already completed the initial onboarding flow).
   * As such, it intentionally skips onboarding checks that are performed in init():
   * - checkEndUserStatus() - not needed for reconnects
   * - shouldSkipOnboardingModal() - onboarding already completed
   * - welcome popup logic - user already onboarded
   * 
   * For initial connections, use init() instead, which includes all onboarding logic.
   */
  private async connectWithProgress() {
    // Set guard to prevent concurrent connect attempts
    if (this.connectInFlight) {
      this.logger.log('[OnboardingSDK] Connection already in progress, ignoring...');
      return;
    }
    this.connectInFlight = true;
    let connectingClient = this.pipecatClient;

    try {
      // Set connecting state
      this.agentState = 'connecting';
      this._isMinimized = false;
      this.fatalError = null; // Clear any prior init_error on a fresh connect attempt
      this.setConnectionProgress('initializing', getString('connection.initializing'), 10);
      
      // Create Daily transport for WebRTC
      this.transport = new DailyTransport();
      this.setConnectionProgress('authenticating', getString('connection.authenticating'), 30);
      
      // Initialize Pipecat client (same as init()).
      // In text mode, disable mic so initDevices() won't prompt for mic permission.
      connectingClient = this.pipecatClient = new PipecatClient({
        transport: this.transport,
        enableMic: this.micEnabled,
        enableCam: this.config.enableVideo || false,
        enableScreenShare: this.config.enableScreenCapture || false,
        callbacks: this.createPipecatCallbacks(),
      });
      if (!this.config.debug) {
        this.pipecatClient.setLogLevel(LogLevel.WARN);
      }

      // Navigation detection runs in both voice and text modes (same as init())
      this.setupNavigationDetection();

      // FLO-960: open this start's diagnostics thread before device init, so a
      // mic/start failure belongs to this attempt; reuse the id for the spawn.
      const sessionId = this.beginDiagnosticsTrackedSession();

      // Always initialize devices — required for transport ready state (same as init())
      // Falls back to text mode automatically if the browser denies mic access.
      const initializedClient = await this.initDevicesWithMicFallback(this.pipecatClient);
      if (this.pipecatClient !== connectingClient) return;
      connectingClient = this.pipecatClient = initializedClient;

      // Setup audio playback (bot audio tracks, analyzers) — same as init()
      this.setupAudioPlayback();

      this.setConnectionProgress('connecting', getString('connection.connecting'), 50);

      // Always use floe-api proxy endpoint for Pipecat session start
      // The backend handles routing to local Pipecat or Pipecat Cloud based on environment
      const proxyEndpoint = `${this.config.apiUrl}/sites/start-session`;

      this.logger.log('[OnboardingSDK] Starting session via floe-api proxy:', proxyEndpoint);

      // FLO-661: obtain the freshest bearer token before this user-started
      // session request. The helper checks support mode before touching the
      // callback and fails open, so proactive reconnects still start normally
      // and never read host authentication state.
      const mcp = await resolveSupportMcpForSession(
        this.config.mcp,
        this.runsInSupportMode(),
        (message) => this.logger.warn(message),
      );

      // Build request data matching init()'s shape with sessionData nested structure
      const requestData: Record<string, any> = {
        clientKey: this.config.clientKey,
        origin: window.location.origin,
        // FLO-534: this is THE support-mode path — the session a dormant agent
        // starts from the user's click. Without this stamp the Shepherd would
        // open with the onboarding pitch we just spent init() suppressing.
        activation: this.activationForSession(),
        ...(mcp && { mcp }),
        sessionData: {
          sessionId,
          createDailyRoom: true,
          dailyRoomProperties: { start_video_off: true },
        }
      };

      // Add user info if provided (matches init() structure, including its
      // C3 consent gate: pre-grant the spawn carries no userInfo at all).
      if (this.config.userInfo && this.identityConsented()) {
        requestData.sessionData.userInfo = this.config.userInfo;
      }

      this.setConnectionProgress('joining', getString('connection.joining'), 70);

      // Use startBotAndConnect() to match init()'s semantics. Pipecat's browser
      // transport retains the start options for diagnostics/reconnects, so
      // remove the one-shot credential from the shared object as soon as the
      // HTTP handshake has consumed it (success or failure).
      try {
        await this.pipecatClient.startBotAndConnect({
          endpoint: proxyEndpoint,
          requestData: requestData as any, // Type assertion needed for nested structure
        });
      } finally {
        if (mcp) {
          mcp.bearerToken = '';
          delete requestData.mcp;
        }
      }

      this.setConnectionProgress('ready', getString('connection.ready'), 100);
      
      // Clear progress after a brief delay
      setTimeout(() => {
        this.clearConnectionProgress();
      }, 500);

    } catch (error) {
      if (this.pipecatClient !== connectingClient) return;
      if (this.connectEndedByOwnTeardown(connectingClient)) {
        return;
      }
      this.retirePlanRun();
      this.logger.error('[OnboardingSDK] Connection failed:', error);
      this.sdkDiagnostics.noteConnectFailed(this.diagnosticFailureReason(error));
      this.setConnectionProgress('error', getString('connection.failed'), 0);
      this.agentState = 'idle';
      
      // Clear error state after a delay
      setTimeout(() => {
        if (this.pipecatClient !== connectingClient) return;
        this.clearConnectionProgress();
        this.renderReactUI();
      }, 3000);
      
      // Emit error event but don't rethrow - errors are handled locally
      // This prevents unhandled promise rejections when called from React event handlers
      this.emit('error', error);
      // Note: Error is NOT rethrown to avoid unhandled rejections in handleToggleConnection
    } finally {
      // Always clear the guard, even if connection succeeded or failed
      this.connectInFlight = false;
    }
  }

  /**
   * Create Pipecat client callbacks (extracted for reuse)
   * Contains the FULL callback logic matching init() to ensure all features work
   */
  private resetBotSpeakingState(): void {
    this.botIsSpeaking = false;
    this.botAudioSpeaking = false;
    if (this.botSpeakingEventOpen) {
      this.emit('botSpeaking', false);
    }
    this.botSpeakingEventOpen = false;
    this.botTurnInterrupted = false;
    this.llmCaptionTurn = false;
    this.llmTranscriptPending = false;
    this.llmTranscriptPendingIndex = null;
    this.captionTurnSource = null;
    this.captionTurnPlaybackExpected = false;
    this.captionEchoText = null;
    this.captionEchoHeard = '';
    this.openAssistantMessageId = null;
    this.supersededAssistantMessageId = null;
  }

  /**
   * Move the live caption onto the transcript as one assistant message.
   *
   * The single place a caption turn ends. It is what makes a late word frame
   * attachable: {@link openAssistantMessageId} names the entry this produced
   * for exactly as long as it is still the newest one.
   *
   * @param insertAt where the answer belongs — a deferred reply can be released
   *   after the visitor's next message has already been filed.
   */
  private commitAssistantTranscript(insertAt?: number | null): void {
    if (!this.botTranscript) return;
    const id = `assistant-${Date.now()}-${this.assistantMessageSeq++}`;
    const message = {
      id,
      role: 'assistant' as const,
      content: this.botTranscript,
      timestamp: Date.now(),
      // Drain any citation sources that arrived before this finalization.
      ...(this.pendingCitationSources ? { sources: this.pendingCitationSources } : {}),
    };
    const at = Math.max(0, Math.min(insertAt ?? this.transcriptHistory.length, this.transcriptHistory.length));
    this.transcriptHistory.splice(at, 0, message);
    this.pendingCitationSources = null;
    // A late frame can still complete this answer wherever it sits — the
    // visitor's next message is routinely filed below it, and a deferred reply
    // is spliced in above one on purpose. Only a NEWER assistant entry closes
    // it: past that, the conversation has moved on and the words are stale.
    this.openAssistantMessageId =
      this.transcriptHistory.slice(at + 1).some((entry) => entry.role === 'assistant') ? null : id;
    this.supersededAssistantMessageId = null;
    this.pruneTranscriptHistory();
  }

  /**
   * Release a reply that was held for playback, because its turn is over.
   *
   * A new LLM response is the proof the previous one ended: its own audio stop
   * may never arrive (the demo handoff moves the audio lifecycle to the
   * incoming turn). Without this the held text was either wiped by the next
   * LLM-owned start or inherited by the next TTS-owned live caption and
   * replayed inside that answer — the greeting repeated in answer one.
   */
  private releasePendingLlmTranscript(): void {
    this.commitAssistantTranscript(this.llmTranscriptPendingIndex);
    this.llmTranscriptPending = false;
    this.llmTranscriptPendingIndex = null;
    this.botTranscript = '';
    this.captionTurnSource = null;
    this.captionTurnPlaybackExpected = false;
    this.captionEchoText = null;
    this.captionEchoHeard = '';
  }

  /** Whitespace-insensitive comparison — LLM chunks and word frames space differently. */
  private static normalizeCaption(value: string): string {
    return value.replace(/\s+/g, ' ').trim();
  }

  /**
   * Handle a word frame while caption ownership is latched away from TTS.
   *
   * - `consumed` — the echo of text already rendered; stays silent.
   * - `released` — the words stopped matching, so this is a DIFFERENT
   *   utterance. Ownership is dropped and everything heard in this segment
   *   becomes the live caption; losing an utterance is worse than briefly
   *   double-rendering one.
   * - `ignored` — no echo is outstanding; the caller owns the frame.
   */
  private consumeSpokenEcho(text: string): 'consumed' | 'released' | 'ignored' {
    if (!this.captionEchoText) return 'ignored';
    const heard = appendCaptionFrame(this.captionEchoHeard, text);
    if (OnboardingSDK.normalizeCaption(this.captionEchoText)
      .includes(OnboardingSDK.normalizeCaption(heard))) {
      this.captionEchoHeard = heard;
      return 'consumed';
    }
    this.captionTurnSource = null;
    this.captionTurnPlaybackExpected = false;
    this.captionEchoText = null;
    this.captionEchoHeard = '';
    this.openAssistantMessageId = null;
    this.botTranscript = heard;
    return 'released';
  }

  /**
   * Hand the turn over: the entry the last one left open is closed for good.
   *
   * The id is remembered rather than forgotten so a frame still trailing that
   * turn is recognisably stale — dropping it is the whole point, and a plain
   * null would send it to the live buffer, into the mouth of the turn starting
   * here.
   */
  private supersedeOpenAssistantMessage(): void {
    if (this.openAssistantMessageId) {
      this.supersededAssistantMessageId = this.openAssistantMessageId;
      this.openAssistantMessageId = null;
    }
  }

  /**
   * Where a trailing word frame belongs.
   *
   * - a number — the owned entry's index, wherever it has ended up.
   * - `'stale'` — a newer assistant turn owns the conversation now, so these
   *   words belong to no entry the visitor is still reading.
   * - `'none'` — nothing is owned (or the entry aged out of the history), and
   *   nothing newer contradicts the frame: render it rather than lose it.
   */
  private locateOpenAssistantEntry(): number | 'stale' | 'none' {
    const owner = this.openAssistantMessageId;
    if (!owner) return this.supersededAssistantMessageId ? 'stale' : 'none';
    for (let index = this.transcriptHistory.length - 1; index >= 0; index--) {
      const entry = this.transcriptHistory[index];
      if (entry.id === owner) return index;
      // Something finalized an assistant entry below the owned one without
      // going through a turn start — a server-sent assistant_cue, or a prune
      // merge. The answer being completed is no longer the last word.
      if (entry.role === 'assistant') {
        this.supersededAssistantMessageId = owner;
        this.openAssistantMessageId = null;
        return 'stale';
      }
    }
    // Pruned out of the history entirely.
    this.openAssistantMessageId = null;
    return 'none';
  }

  /** Append a word frame to the caption turn it belongs to. */
  private appendTtsCaption(text: string): void {
    // No segment open and nothing live: this frame trails a turn that already
    // ended. Finish that answer rather than starting one nothing can commit.
    if (!this.botAudioSpeaking && !this.botTranscript) {
      const located = this.locateOpenAssistantEntry();
      if (typeof located === 'number') {
        const entry = this.transcriptHistory[located];
        this.transcriptHistory[located] = {
          ...entry,
          content: appendCaptionFrame(entry.content, text),
        };
        this.renderReactUI();
        return;
      }
      if (located === 'stale') {
        // Never into the live buffer: that buffer belongs to the turn now
        // speaking, so these words would either be wiped by its first segment
        // or committed inside an answer that never said them.
        this.logger.log('[OnboardingSDK] 🗑️ Dropped caption frame from a superseded turn:', text);
        return;
      }
    }
    // FLO-531: Cartesia frames are bare words with the spacing stripped
    // (Hindi included), so the seam space is re-inserted here. See
    // caption-text.ts for the one exception (Han/Kana).
    this.botTranscript = appendCaptionFrame(this.botTranscript, text);
    this.renderReactUI();
  }

  /**
   * A typed message or chip tap cut the bot off: close its turn as it stands.
   *
   * The counterpart of {@link discardInterruptedLlmTurn} for a KEYBOARD
   * interruption. A voice barge-in discards the abandoned caption; a typed one
   * commits it, because what the visitor has already read is the record of
   * what the bot said (the server commits the spoken prefix on its side). The
   * commit lands above the message that interrupted it — at
   * {@link llmTranscriptPendingIndex} when the reply was already held for
   * playback, else at the end, which is still before the visitor's message
   * since that is pushed after this. Every later edge of the interrupted turn
   * (its bot-llm-stopped if the End survives the flush, the bot-stopped-speaking
   * the interruption broadcasts, its trailing word frames) then finds no live
   * turn to act on: the LLM-owned latch is down so tokens and the LLM stop are
   * ignored, and a word frame still in flight attaches to the committed entry
   * exactly as a late frame does after any other stop edge. No-op while the bot
   * is idle, so an ordinary typed turn is unchanged.
   */
  private commitInterruptedBotTurn(): void {
    const turnLive =
      this.llmCaptionTurn || this.llmTranscriptPending || this.botAudioSpeaking || this.botIsSpeaking;
    if (!turnLive) return;
    if (this.botTranscript) {
      this.commitAssistantTranscript(this.llmTranscriptPending ? this.llmTranscriptPendingIndex : null);
    }
    this.botTurnInterrupted = false;
    this.llmCaptionTurn = false;
    this.llmTranscriptPending = false;
    this.llmTranscriptPendingIndex = null;
    this.captionTurnSource = null;
    this.captionTurnPlaybackExpected = false;
    this.captionEchoText = null;
    this.captionEchoHeard = '';
    this.agentState = 'listening';
    this.botIsSpeaking = false;
    this.botAudioSpeaking = false;
    this.botTranscript = '';
    this.renderReactUI();
    if (this.botSpeakingEventOpen) {
      this.botSpeakingEventOpen = false;
      this.emit('botSpeaking', false);
    }
  }

  private discardInterruptedLlmTurn(): void {
    this.botTurnInterrupted = false;
    this.llmCaptionTurn = false;
    this.llmTranscriptPending = false;
    this.llmTranscriptPendingIndex = null;
    this.captionTurnSource = null;
    this.captionTurnPlaybackExpected = false;
    this.captionEchoText = null;
    this.captionEchoHeard = '';
    this.openAssistantMessageId = null;
    this.supersededAssistantMessageId = null;
    this.agentState = 'listening';
    this.botIsSpeaking = false;
    this.botTranscript = '';
    this.pendingCitationSources = null;
    this.renderReactUI();
    if (this.botSpeakingEventOpen) {
      this.botSpeakingEventOpen = false;
      this.emit('botSpeaking', false);
    }
  }

  private createPipecatCallbacks() {
    // Every newly constructed client owns a callback set, including device
    // fallback clients and onboarding/support reconnects.
    // Retain retired run IDs: a replacement callback can still receive replayed
    // events for an old run, even though the old callbacks themselves are fenced.
    if (this.pipecatCallbackGeneration > 0) this.retirePlanRun();
    this.planSessionOpen = true;
    const capturedCallbackGeneration = ++this.pipecatCallbackGeneration;
    // FLO-390: stamp this callback set with the website-agent session it belongs
    // to (see the guard wrapper at the end of this method).
    const capturedGeneration = this.websiteAgentGeneration;
    const callbacks = {
      onConnected: () => {
        this.logger.log('[OnboardingSDK] Connected to Pipecat');
        this.resetBotSpeakingState();
        this.isConnected = true;
        this.agentState = 'listening';
        const desiredWebsiteAgentMic = this.config.websiteAgent
          ? this.websiteAgentMicPermissionReady && this.micEnabled
          : null;
        this.micEnabled = this.pipecatClient
          ? reconcileMicIntentOnConnect(
              this.pipecatClient,
              desiredWebsiteAgentMic,
            )
          : this.config.enableAudio !== false;
        this.logger.log('[OnboardingSDK] 🎤 Mic state on connect:', this.micEnabled ? 'ON' : 'MUTED');

        // Honour a mic-off preference on connect (FLO-51). The speaker half is
        // applied by syncAudioOutput() at bot-ready and by the trackStarted
        // handler, which both read this.speakerMuted.
        if (!this.config.websiteAgent && !this.micEnabled && this.pipecatClient) {
          this.pipecatClient.enableMic(false);
          this.logger.log('[OnboardingSDK] 🎤 Mic preference is OFF — muted on connect');
        }

        // CDP Phase 1: a session that joins with a LIVE mic (onboarding voice
        // mode) starts its VOICE span here; muted joins (website agent, text
        // mode) start theirs from the visitor's unmute in toggleMute().
        if (this.micEnabled) this.trackVoiceStarted();

        // FLO-960: separates "the spawn never happened" from "the join failed".
        this.sdkDiagnostics.noteStage('transport_connect');
        this.clearConnectionProgress();
        this.renderReactUI();
        this.emit('connected');
      },
      onDisconnected: () => {
        this.retirePlanRun();
        this.logger.log('[OnboardingSDK] Disconnected from Pipecat');
        // FLO-960: the one callback every mode reaches on transport death.
        this.sdkDiagnostics.noteTransportDisconnected(this.sdkRequestedTeardown);
        this.sdkDiagnostics.endSession(
          this.sdkRequestedTeardown ? 'client_requested' : 'transport_closed',
        );
        this.isConnected = false;
        this.isBotReady = false;
        // CDP Phase 1: the transport died — any open VOICE span ends with it.
        this.trackVoiceEnded();
        this.resetLanguageSwitchState();
        // Drop any queued messages — they belong to the prior session.
        if (this.pendingClientMessages.length > 0) {
          this.logger.warn(`[OnboardingSDK] Dropping ${this.pendingClientMessages.length} queued message(s) on disconnect`);
          this.pendingClientMessages = [];
        }
        this.agentState = 'idle';
        this.resetBotSpeakingState();

        // FLO-545: the avatar participant is gone with the session — drop its
        // stream so no surface keeps rendering a frozen last frame.
        // (botVideoStream is intentionally left: the demo error/wrap-up
        // surfaces read it after disconnect.)
        this.avatarVideoStream = null;
        // A hand-over cannot outlive its transport: with no server left to send
        // `demo_visible` and no screencast coming, a card still up here would
        // promise the product over a dead session until the give-up. Retiring
        // the edges leaves a stage that already has a picture to the stream
        // above, exactly as before the card existed.
        this.resetWebsiteAgentDemoHandover();

        // Stop page tracking
        this.pageTracker.stop();
        
        // Stop DOM Introspection Layer
        this.domIntrospector.stopCapturing();
        
        // Stop navigation detection
        if (this.navigationCompleteDetector) {
          this.navigationCompleteDetector.stop();
        }
        
        // Clear all overlays: cursor, highlights, tooltips and reset cursor state
        this.overlay.cleanup();
        this.overlay.resetCursorState();
        
        // Clean up audio elements
        if (this.audioElements) {
          this.audioElements.forEach(audio => {
            audio.pause();
            audio.remove();
          });
          this.audioElements = [];
        }

        // Clean up audio analyzers
        if (this.botAudioAnalyzer) {
          this.botAudioAnalyzer.destroy();
          this.botAudioAnalyzer = null;
        }
        if (this.userAudioAnalyzer) {
          this.userAudioAnalyzer.destroy();
          this.userAudioAnalyzer = null;
        }

        // Clean up user mic stream - CRITICAL: stops microphone input on disconnection
        // Invalidate any in-flight getUserMedia first to prevent race conditions
        this.userMicStreamRequestId++;
        if (this.userMicStream) {
          this.logger.log('[OnboardingSDK] 🎤 Stopping user microphone stream');
          this.userMicStream.getTracks().forEach(track => track.stop());
          this.userMicStream = null;
        }
        this.stopPrimedMicStream();
        this.audioLevels = [];
        
        this.renderReactUI();
        this.emit('disconnected');
      },
      onBotReady: () => {
        this.logger.log('[OnboardingSDK] Bot ready - transport is now in ready state');
        voiceStartupTimeline.mark('botReady');
        // FLO-960: disarms the startup watchdog.
        this.sdkDiagnostics.noteStage('bot_ready');
        this.isBotReady = true;

        // CRITICAL ORDERING — site_init MUST go before pendingClientMessages
        // are flushed. Without this, a text_message or prospect_prefill that
        // was queued before bot-ready lands on the server with no clientKey /
        // origin / userInfo / textMode metadata established yet, and the
        // server can't bind the session to a site / prospect.
        if (this.config.clientKey) {
          this.logger.log(`[OnboardingSDK] Sending site initialization with client key`);
          // Merge the stable anonymous id (FLO-325) so /end-users/resolve
          // can attribute the session even when the host configured no
          // userInfo — mirrors the start-session body, including its C3
          // consent gate: pre-grant the payload omits userInfo ENTIRELY
          // (configured name/email are identity keys too, and the getter
          // behind the anonymousId writes localStorage on first use).
          const siteInitUserInfo = this.consentedUserInfo();
          const siteInitPayload: any = {
            supportStepVersion: 1,
            supportStepCapabilities: ['destination-checks-v1', 'state-checked-click-v1', 'task-view-result-transition-v1', 'task-view-transition-evidence-v1', 'alternative-text-check-v1', 'verify-reveal-v1', 'plan-run-cancellation-v1', 'complete-collection-absence-v1', 'session-origin-v1', 'record-identity-v1', 'type-commit-v1', 'record-baseline-v1', 'record-read-evidence-v1', 'parent-scoped-check-v1', 'file-selection-v1', 'file-selection-host-consumes-input-v1'],
            clientKey: this.config.clientKey,
            origin: window.location.origin,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            locale: navigator.language,
            consent: this.consentDecision ?? this.consentMode,
            ...(siteInitUserInfo ? { userInfo: siteInitUserInfo } : {}),
            // FLO-51: two independent facts. `textMode` is still sent so an
            // older SERVER (which only understands the coupled flag) keeps
            // behaving sanely — it is true only when BOTH are, i.e. the one
            // case where the old meaning is actually correct.
            micMuted: !this.micEnabled,
            speakerMuted: this.speakerMuted,
            textMode: !this.micEnabled && this.speakerMuted,
          };
          if (this.pendingResumeSessionId) {
            siteInitPayload.resumeSessionId = this.pendingResumeSessionId;
            this.logger.log(`[OnboardingSDK] Resuming session: ${this.pendingResumeSessionId}`);
            this.pendingResumeSessionId = null;
          }
          this.pipecatClient?.sendClientMessage('site_init', siteInitPayload);
        }

        // FLO-348: sync the audio-output (TTS) state BEFORE the flush below. A
        // queued text_message runs an inference the moment it lands, so if this
        // went after the flush, a visitor who muted the speaker during "Connecting…"
        // and tapped an opener chip would hear that first answer spoken anyway —
        // skip_tts would arrive after the turn had already started. (The website
        // agent joins speaker-ON, so this normally just confirms TTS from turn 1.)
        this.syncAudioOutput();

        // Now flush any client messages queued before bot-ready. They arrive
        // AFTER site_init so the server already has clientKey/origin/etc.
        // Bot-ready can trail the connection while the server finishes
        // pipeline setup; forms / typed text submitted in that gap land in
        // pendingClientMessages.
        if (this.pendingClientMessages.length > 0) {
          this.logger.log(`[OnboardingSDK] 📤 Flushing ${this.pendingClientMessages.length} queued client message(s) (after site_init)`);
          const queued = this.pendingClientMessages.splice(0);
          for (const msg of queued) {
            try {
              this.pipecatClient?.sendClientMessage(msg.type, msg.data);
            } catch (e) {
              this.logger.error(`[OnboardingSDK] Failed to flush queued message ${msg.type}:`, e);
            }
          }
        }

        // ───── Host-page telemetry (FLO-451) ─────
        // All four wirings below stream the host page's DOM and the visitor's
        // interactions to the server. Only the onboarding agent acts on the
        // host page, so only it gets them — see agent-mode.ts. (This used to be
        // gated on `!demoMode`, which let the website agent through: it sent the
        // whole stack, and demo_bot.py's on_client_message dropped every byte.)
        if (this.drivesHostPage) {
          // Start DOM Introspection Layer - send accessible snapshots to server
          this.domIntrospector.startCapturing(1500, (snapshot) => {
            const accessibleSnap = this.accessibleSnapshot.capture(snapshot.captureReason);
            this.sendAccessibleSnapshot(accessibleSnap);
            this.sendDOMSnapshot(snapshot);
          });
          this.logger.log('[OnboardingSDK] DOM Introspection Layer started (with Accessible Snapshot)');

          // Wire up PageTracker with DOMIntrospector for sync captures
          this.pageTracker.setDOMIntrospector(this.domIntrospector);

          // No pageTracker.onNavigation() accessible-snapshot capture here.
          // PageTracker.sendPageMetadata already calls
          // domIntrospector.scheduleCapture('post-navigation') on the line above
          // its onNavigationCallback, and the callback registered here runs the
          // capture pipeline — which sends the accessible snapshot too. So this
          // was a second whole-document accessibility walk, on every navigation,
          // producing a snapshot the first one had already sent.
        } else {
          this.logger.log(
            `[OnboardingSDK] ${this.config.demoMode ? 'Demo' : 'Website-agent'} mode — ` +
            'skipping host-page telemetry (DOM + accessible snapshots, interaction events)'
          );
        }

        // Start tracking page events. Page context (page_metadata on start and
        // on every URL change) stays on in every mode; the interaction
        // listeners are onboarding-only.
        this.pageTracker.start({ interactions: this.drivesHostPage });
        this.logger.log('[OnboardingSDK] PageTracker started');
        
        // Note: Navigation detector and audio analyzers are initialized in setupAudioPlayback()
        // site_init is sent at the TOP of this handler (before flushing queued
        // messages) — see the ordering comment above.

        this.emit('botReady');
        
        // Note: Audio analyzers are initialized in setupAudioPlayback()
        
        // FLO-51: `_pendingDiscoveryStart` lived here. Its only writer was the
        // welcome modal's "Start Conversation" branch, so with the modal gone
        // there is nothing to defer. Hosts that want the discovery flow call
        // `startGuidedDiscovery()` (still public) themselves.
      },
      onUserTranscript: (data: any) => {
        this.logger.log('[OnboardingSDK] 🎤 User transcript:', data.text, data.final ? '(final)' : '(interim)');
        if (data.text) {
          this.userTranscript = data.text;
        }
        if (data.final && data.text) {
          this.lastUserTranscript = data.text;
        }
        this.emit('userTranscript', data);
        // No captureContext() here. It used to run on EVERY transcript — note the
        // missing `data.final` guard, three lines above one that has it — and it is
        // not a cheap call: captureWithRedaction clones the entire host document
        // into a live, non-sandboxed, full-viewport iframe and document.write()s the
        // app into it, images and stylesheets included. Every completed utterance
        // paid for a second copy of LeadRat rendering on top of itself.
        //
        // And the result was discarded: handleUserInput awaited it, dropped the
        // ContextData, and its own comment said "Context is automatically sent via
        // metadata tracking / No additional action needed here." The whole path had
        // no consumer, so it is removed rather than merely guarded.
        //
        // captureContext() itself is retained — it is public API.
        this.renderReactUI();
      },
      onBotTtsText: (data: any) => {
        // TTS text — fires in voice mode (standard + demo). Speech-synced, clean text.
        // Fires only when captions are TTS-paced (voice/demo). Not in 'llm' mode.
        if (!data.text) return;
        // The greeting has words. Marked here rather than on a "started" event
        // because this is the first frame the visitor can actually hear — the
        // TTS word frames are paced to the audio, so this IS "first bot
        // speech" on an audible turn. A keyboard-mode session runs with
        // skip_tts and produces no TTS frames at all; onBotLlmText carries the
        // mark for that case only.
        voiceStartupTimeline.mark('firstBotSpeech');
        // Ownership latched to 'llm' means these words may be the spoken echo of
        // an answer already on screen — but only for as long as they match it.
        if (this.activeCaptionSource !== 'tts') {
          // 'ignored' is an LLM-paced turn still streaming its own text — the
          // word frames of a turn the transcript is already rendering verbatim.
          if (this.consumeSpokenEcho(data.text) !== 'released') return;
          this.renderReactUI();
          return;
        }
        this.appendTtsCaption(data.text);
      },
      // NOTE: no onBotTranscript subscriber. The sentence-level botTranscript
      // event is deprecated in client-js (>=1.8.0 warns once when subscribed) and
      // we never used it for display — onBotTtsText (voice) / onBotLlmText (text)
      // carry the clean text. Subscribing to a no-op only re-introduced the
      // console deprecation warning, so the handler is intentionally omitted.
      onBotLlmText: (data: any) => {
        // Token-level LLM text — drives the transcript when captions are LLM-paced.
        // Appended VERBATIM: LLM stream chunks carry their own spacing and split
        // mid-word / before punctuation ("central|ize", "I|'d", "data|-driven"),
        // so injecting a seam space here mangled every Q&A bubble (FLO-505).
        // The injection lives only in onBotTtsText, whose per-word Cartesia
        // frames genuinely lack inter-frame spaces.
        //
        // `firstBotSpeech` from LLM text ONLY when no TTS will follow. On the
        // website agent the transcript is LLM-paced, so this token stream runs
        // AHEAD of the TTS word frames on an audible turn; marking here would
        // under-report the greeting by the LLM→audio lead. The condition is
        // the inverse of syncAudioOutput()'s `enabled`: speaker muted and no
        // demo/avatar video means the server runs skip_tts and onBotTtsText
        // never fires, so this text is the first (and only) greeting the
        // visitor gets.
        if (data.text && this.speakerMuted && !this.botVideoStream) {
          voiceStartupTimeline.mark('firstBotSpeech');
        }
        if (!this.llmCaptionTurn) return;
        if (data.text) {
          // TTS can be enabled while the reply is streaming. Only subsequent
          // text (or observed playback) makes this turn expect audio; a mute
          // toggle after its last text must not strand a silent turn's latch.
          if (!this.speakerMuted || this.botVideoStream) {
            this.captionTurnPlaybackExpected = true;
          }
          this.botTranscript += data.text;
          this.renderReactUI();
        }
      },
      onBotLlmStarted: () => {
        // LLM-paced captions: LLM lifecycle owns the transcript. Latch ownership
        // for the whole turn (captionSource can flip mid-stream if start_demo
        // attaches the demo video) so onBotLlmStopped still finalizes this turn.
        // Otherwise onBotStartedSpeaking (the TTS lifecycle) handles it.
        //
        // A new response is the proof the previous one ended. Release anything
        // still held for playback FIRST: its own audio stop may never arrive,
        // and whichever source owns this turn would otherwise wipe that text or
        // replay it inside this answer.
        if (this.llmTranscriptPending && !this.llmCaptionTurn) this.releasePendingLlmTranscript();
        // Whatever this turn says belongs to this turn, not to the last one.
        this.supersedeOpenAssistantMessage();
        this.captionEchoText = null;
        this.captionEchoHeard = '';
        this.captionTurnSource = this.captionSource;
        this.captionTurnPlaybackExpected = !this.speakerMuted || !!this.botVideoStream;
        this.llmCaptionTurn = this.captionTurnSource === 'llm';
        if (!this.llmCaptionTurn) return;
        this.agentState = 'speaking';
        this.botIsSpeaking = true;
        this.botTranscript = '';
        this.renderReactUI();
      },
      onBotLlmStopped: () => {
        // LLM-paced captions: finalize the transcript when the LLM finishes,
        // keyed on the LATCHED ownership (not the live getter, which may have
        // flipped). Otherwise onBotStoppedSpeaking (the TTS lifecycle) handles it.
        if (!this.llmCaptionTurn) return;
        // A visitor can interrupt before the avatar emits its first audio frame.
        // In that case no later BotStopped callback exists to release this turn.
        if (this.botTurnInterrupted && !this.botAudioSpeaking) {
          this.discardInterruptedLlmTurn();
          return;
        }
        this.llmCaptionTurn = false;
        const audioStillPlaying = this.captionTurnPlaybackExpected && this.botAudioSpeaking;
        if (!this.captionTurnPlaybackExpected) this.captionTurnSource = null;
        this.llmTranscriptPending = audioStillPlaying;
        this.llmTranscriptPendingIndex = audioStillPlaying ? this.transcriptHistory.length : null;
        if (audioStillPlaying) {
          // Keep the live transcript intact until the matching playback stop.
          // Moving it to history here makes one spoken answer jump between two
          // visual sections and splits tool-result continuations into bubbles.
          this.renderReactUI();
          return;
        }
        this.agentState = 'listening';
        this.botIsSpeaking = false;
        this.botTurnInterrupted = false;
        const rendered = this.botTranscript;
        this.commitAssistantTranscript();
        // Playback of THIS answer is still expected, so ownership stays latched
        // and its spoken echo must not render a second time. Suppression now
        // lasts only while the incoming words match what was just committed —
        // and with nothing rendered there is no echo to suppress, only the next
        // spoken cue to swallow.
        this.captionEchoHeard = '';
        if (this.captionTurnPlaybackExpected && rendered) {
          this.captionEchoText = rendered;
        } else {
          this.captionEchoText = null;
          this.captionTurnSource = null;
          this.captionTurnPlaybackExpected = false;
        }
        this.llmTranscriptPending = false;
        this.botTranscript = '';
        this.renderReactUI();
        // If audio ended before the final LLM frame, its stop was deferred so
        // event consumers would not clear the still-streaming transcript.
        if (!this.botAudioSpeaking && this.botSpeakingEventOpen) {
          this.botSpeakingEventOpen = false;
          this.emit('botSpeaking', false);
        }
      },
      onUserStartedSpeaking: () => {
        this.logger.log('[OnboardingSDK] 🎤 User started speaking');
        if (this.botIsSpeaking || this.botAudioSpeaking || this.llmCaptionTurn) {
          this.botTurnInterrupted = true;
        }
        // Before playback starts (or between avatar audio segments), an
        // interruption may produce neither an LLM stop nor another audio stop.
        // The visitor speaking is the terminal edge for that abandoned turn.
        if (this.llmCaptionTurn && !this.botAudioSpeaking) {
          this.discardInterruptedLlmTurn();
        }
        this.agentState = 'listening';
        this.userIsSpeaking = true;
        // FLO-307: a spoken turn clears any opener chips (transient, turn-1 only).
        this.suggestedOptions = [];
        this.suggestedOptionsMetadata = null;
        this.renderReactUI();
        this.emit('userSpeaking', true);
      },
      onUserStoppedSpeaking: () => {
        this.logger.log('[OnboardingSDK] 🛑 User stopped speaking');
        this.agentState = 'thinking';
        this.userIsSpeaking = false;
        if (this.lastUserTranscript) {
          this.transcriptHistory.push({
            id: `user-${Date.now()}`,
            role: 'user',
            content: this.lastUserTranscript,
            timestamp: Date.now(),
          });
          this.pruneTranscriptHistory();
          this.logger.log('[OnboardingSDK] 📝 Added user message to history:', this.lastUserTranscript);
          this.lastUserTranscript = '';
        }
        this.userTranscript = '';
        this.renderReactUI();
        this.emit('userSpeaking', false);
      },
      onBotStartedSpeaking: () => {
        this.logger.log('[OnboardingSDK] 🔊 Bot started speaking');
        if (this.llmCaptionTurn) this.captionTurnPlaybackExpected = true;
        this.botAudioSpeaking = true;
        // FLO-960: the bot's first word is the other half of "audible audio".
        // A track that has been playing since before the greeting only counts
        // now, and only if the visitor can actually hear it.
        this.maybeReportFirstAudio();
        this.agentState = 'speaking';
        this.botIsSpeaking = true;
        this.userTranscript = '';
        // Reset the transcript buffer only when the TTS lifecycle owns it. In
        // 'llm' mode onBotLlmStarted already reset it, and TTS can start
        // mid-LLM-stream — resetting here would drop tokens. The latch guards the
        // race where captionSource flipped 'llm'→'tts' before the LLM turn
        // finalized: don't clobber the still-buffering LLM transcript. FLO-379.
        if (
          this.activeCaptionSource === 'tts'
          && !this.llmCaptionTurn
          && !this.llmTranscriptPending
        ) this.botTranscript = '';
        // A new segment's words belong to it, not to the answer before it.
        this.supersedeOpenAssistantMessage();
        this.renderReactUI();
        if (!this.botSpeakingEventOpen) {
          this.botSpeakingEventOpen = true;
          this.emit('botSpeaking', true);
        }
      },
      onBotStoppedSpeaking: () => {
        this.logger.log('[OnboardingSDK] 🔇 Bot stopped speaking');
        this.botAudioSpeaking = false;
        // A barge-in can cancel the LLM queue before RTVI observes its final
        // response frame. The avatar bridge still sends the physical audio-stop,
        // so close and discard that abandoned live caption here rather than
        // stranding the speaking state into the visitor's next turn.
        if (
          this.botTurnInterrupted
          && (this.llmCaptionTurn || this.llmTranscriptPending)
        ) {
          this.discardInterruptedLlmTurn();
          return;
        }
        // The audio lifecycle can stop between streamed LLM segments (the
        // avatar bridge, in particular, has its own TTS sequence boundaries).
        // While the LLM lifecycle owns this turn, it also owns the visible
        // speaking state and transcript buffer; onBotLlmStopped finalizes both.
        // Clearing either here cuts the current answer and makes the next token
        // look like a separate response.
        if (this.llmCaptionTurn) {
          return;
        }
        this.botTurnInterrupted = false;
        this.agentState = 'listening';
        this.botIsSpeaking = false;
        // LLM-paced captions stay live through avatar playback and are committed
        // here. TTS-paced captions have always used this same audio boundary.
        if (
          (this.llmTranscriptPending || this.activeCaptionSource === 'tts')
          && this.botTranscript
        ) {
          // Citations buffered from a bot_citation message that arrived between
          // bot_started_speaking and now (rare but possible if the RTVI server
          // message races the audio frame stream) are drained by the commit.
          // Normal path: the citation arrives AFTER bot_stopped_speaking and is
          // attached via handleBotCitation's backwards scan.
          this.logger.log('[OnboardingSDK] 📝 Added assistant message to history:', this.botTranscript.substring(0, 50) + '...');
          this.commitAssistantTranscript(this.llmTranscriptPending ? this.llmTranscriptPendingIndex : null);
        }
        this.llmTranscriptPending = false;
        this.llmTranscriptPendingIndex = null;
        this.botTranscript = '';
        this.captionTurnSource = null;
        this.captionTurnPlaybackExpected = false;
        this.captionEchoText = null;
        this.captionEchoHeard = '';
        this.renderReactUI();
        if (this.botSpeakingEventOpen) {
          this.botSpeakingEventOpen = false;
          this.emit('botSpeaking', false);
        }
      },
      onServerMessage: (msg: any) => {
        this.logger.log('[OnboardingSDK] Server message:', msg);
        // handleServerMessage handles all message types including session_paused
        this.handleServerMessage(msg);
      },
      onError: (error: any) => {
        this.logger.error('[OnboardingSDK] Error:', error);
        this.emit('error', error);
      },
      // Note: Track handling (onTrackStarted/onTrackStopped) is done via event listeners in setupAudioPlayback()
    };
    // Wrap every callback so a superseded client's disconnect or delayed plan
    // message cannot revoke or replace the current session's browser actions.
    // FLO-390 also guards website-agent restarts before their client is created.
    // A late frame must not change the fresh session after a re-tap bumps that
    // generation, even while the old transport is still disconnecting.
    const guarded = {} as typeof callbacks;
    for (const [name, fn] of Object.entries(callbacks)) {
      (guarded as Record<string, (...args: any[]) => unknown>)[name] = (...args: any[]) => {
        if (!this.isCurrentPipecatRun(capturedCallbackGeneration, capturedGeneration)) return undefined;
        return (fn as (...a: any[]) => unknown)(...args);
      };
    }
    return guarded;
  }

  /**
   * Resume a paused session
   * Always forces a full reconnect since the bot's pipeline was cancelled during pause
   */
  async resumeSession(sessionId: string): Promise<void> {
    this.logger.log(`[OnboardingSDK] Resuming session: ${sessionId}`);
    this.retirePlanRun();

    // Set explicit resume intent (preserved through disconnect/init flow)
    // This is separate from pausedSessionId which is for UI state only
    this.pendingResumeSessionId = sessionId;

    // Reset paused state
    this.agentState = 'idle';

    // Always force a full reconnect when resuming a paused session
    // The bot's pipeline was cancelled during pause, so we need a fresh connection
    this.logger.log('[OnboardingSDK] Forcing full reconnect for resume (bot pipeline was cancelled)');

    // Disconnect first to ensure clean state
    if (this.isConnected) {
      await this.disconnect();
    }

    // Small delay to ensure disconnect completes
    await new Promise(resolve => setTimeout(resolve, 100));

    // Now init() will run completely and send site_init with resumeSessionId via onBotReady
    // onBotReady will consume and clear pendingResumeSessionId after use
    await this.init();
  }

  /**
   * Stop/end a paused session (disconnect and clear paused state)
   */
  async stopSession(): Promise<void> {
    this.logger.log(`[OnboardingSDK] Stopping paused session`);
    this.retirePlanRun();

    // Clear paused state
    this.agentState = 'idle';
    this.pausedSessionId = null;
    this.pendingResumeSessionId = null;
    this.planProgress = null;

    // Disconnect and minimize (keep Floe in the DOM as a collapsed orb)
    if (this.isConnected) {
      await this.disconnectAndMinimize();
    } else {
      // Not connected — just minimize
      this._isMinimized = true;
      this.renderReactUI();
    }

    this.emit('sessionStopped');
  }

  /**
   * Handle screen share toggle from UI
   */
  private async handleToggleScreenShare() {
    if (!this.pipecatClient) {
      this.logger.warn('[OnboardingSDK] Cannot toggle screen share: client not initialized');
      return;
    }

    try {
      if (this.screenShareEnabled) {
        this.logger.log('[OnboardingSDK] Stopping screen share');
        await this.pipecatClient.enableScreenShare(false);
        this.screenShareEnabled = false;
        
        // Notify server to deactivate vision scheduler and pipeline
        this.sendMetadata({ type: 'screen_share_status', active: false });
      } else {
        this.logger.log('[OnboardingSDK] Starting screen share');
        await this.pipecatClient.enableScreenShare(true);
        this.screenShareEnabled = true;
        
        // Notify server that screen share is active
        this.sendMetadata({ type: 'screen_share_status', active: true });
      }

      // Re-render UI to reflect the change
      this.renderReactUI();
    } catch (error) {
      this.logger.error('[OnboardingSDK] Failed to toggle screen share:', error);
      this.emit('error', error);
    }
  }




  /**
   * Send metadata to bot via Pipecat
   * Data should include a 'type' field (e.g., 'click', 'page_metadata', 'form_input')
   */
  sendMetadata(data: any) {
    if (this.pipecatClient && this.isConnected) {
      const metadataType = data.type || 'metadata';
      this.logger.log(`[OnboardingSDK] Sending metadata: ${metadataType}`, data);

      // Send with the event type as the message type for proper routing
      this.pipecatClient.sendClientMessage(metadataType, data);
    } else {
      this.logger.warn('[OnboardingSDK] Cannot send metadata - not connected');
    }
  }

  /**
   * Send DOM snapshot to server for DOM Introspection Layer
   * Server will use this for accurate element targeting via vision + DOM correlation
   */
  private sendDOMSnapshot(snapshot: DOMSnapshot) {
    if (this.pipecatClient && this.isConnected) {
      // Only log occasionally to reduce noise
      if (Math.random() < 0.2) {
        this.logger.log(`[OnboardingSDK] Sending DOM snapshot: ${snapshot.totalElements} elements`);
      }

      this.pipecatClient.sendClientMessage('dom_snapshot', {
        type: 'dom_snapshot',
        ...snapshot
      });
    }
  }

  /**
   * Send accessible snapshot to server (improved LLM-friendly format)
   * 
   * Key improvements over raw DOM snapshot:
   * - Only interactive elements (~90% reduction in noise)
   * - Computed accessible names (W3C accname algorithm)
   * - Semantic roles and states (selected, checked, disabled)
   * - Context awareness (row context, section headings)
   * - Unique refs for easy element identification (B1, C2, T3)
   */
  private sendAccessibleSnapshot(snapshot: AccessibleSnapshot) {
    if (this.pipecatClient && this.isConnected) {
      // Log more frequently for accessible snapshot since it's the primary one
      if (Math.random() < 0.3) {
        this.logger.log(`[OnboardingSDK] Sending accessible snapshot: ${snapshot.elementCount} elements (reduced from DOM)`);
        if (this.config.debug) {
          this.logger.log('[OnboardingSDK] Accessible elements:',
            snapshot.elements.slice(0, 5).map(e => `${e.ref}: ${e.role} "${e.name}"`).join(', '),
            snapshot.elements.length > 5 ? '...' : ''
          );
        }
      }

      this.pipecatClient.sendClientMessage('accessible_snapshot', {
        type: 'accessible_snapshot',
        ...snapshot
      });
    }
  }

  /**
   * Handle server messages from bot (via Pipecat ServerMessage events)
   * Supports both legacy selector-based commands and new hybrid target format
   * 
  /**
   * Prune transcript history to stay within the configured cap.
   * When the cap is reached:
   * 1. Merge rapid sequential same-role messages within MERGE_WINDOW_MS
   * 2. If still over cap, keep the most recent messages
   */
  private pruneTranscriptHistory(): void {
    const maxMessages = (this.config as any).maxTranscriptMessages ?? DEFAULT_MAX_TRANSCRIPT_MESSAGES;

    if (this.transcriptHistory.length <= maxMessages) {
      return;
    }

    // Step 1: Merge rapid sequential same-role messages within 30s
    const merged: typeof this.transcriptHistory = [];
    for (let i = 0; i < this.transcriptHistory.length; i++) {
      const current = this.transcriptHistory[i];
      const prev = merged[merged.length - 1];

      if (
        prev &&
        prev.role === current.role &&
        (current.timestamp - prev.timestamp) < MERGE_WINDOW_MS
      ) {
        // Merge: append content to previous message
        prev.content = prev.content + ' ' + current.content;
        prev.timestamp = current.timestamp; // Use latest timestamp
      } else {
        merged.push({ ...current });
      }
    }

    // Step 2: If still over cap, keep the most recent messages
    if (merged.length > maxMessages) {
      this.transcriptHistory = merged.slice(-maxMessages);
    } else {
      this.transcriptHistory = merged;
    }
  }

  /**
   * Message formats supported:
   * 1. RTVIServerMessageFrame: { data: { type: 'ui_action', action: '...', target: {...} } }
   * 2. Direct ui_action: { type: 'ui_action', action: '...', target: {...} }
   * 3. Direct action: { action: '...', target: {...} }
   * 4. Legacy: { type: 'highlight', selector: '...' }
   */
  private handleServerMessage(message: any) {
    if (this.logger.isDebug) this.logger.log('[OnboardingSDK] 📥 Server message received:', message);

    // Unwrap RTVIServerMessageFrame if needed (comes as { data: {...} })
    let payload = message;
    if (message.data && typeof message.data === 'object') {
      this.logger.log('[OnboardingSDK] Unwrapping RTVIServerMessageFrame data');
      payload = message.data;
    }

    // Handle ui_action type from HybridActionProcessor
    if (payload.type === 'ui_action' && payload.action && payload.target) {
      this.logger.log('[OnboardingSDK] 🎯 Received UI action from bot:', payload.action);
      if (this.logger.isDebug) this.logger.log('[OnboardingSDK] Target:', payload.target);
      this.handleHybridAction({
        action: payload.action,
        target: payload.target,
        value: payload.value,
        description: payload.description,
        precondition: payload.precondition,  // For check_precondition action
        actionId: payload.actionId,
        supportContract: payload.supportContract,
        planRunId: payload.planRunId,
      } as HybridUIAction);
      return;
    }

    // Handle hybrid UI actions (direct format from LLM)
    if (payload.action && payload.target) {
      this.logger.log('[OnboardingSDK] 🎯 Received direct hybrid action:', payload.action);
      this.handleHybridAction(payload as HybridUIAction);
      return;
    }

    // Handle legacy highlight/tooltip commands
    if (payload.type === 'highlight') {
      this.logger.log('[OnboardingSDK] Handling highlight command');
      if (payload.target) {
        // New hybrid format
        this.overlay.highlightTarget(payload.target, payload.options);
      } else if (payload.selector) {
        // Legacy selector format
        this.overlay.highlight(payload.selector, payload.options);
      }
    } else if (payload.type === 'tooltip' && payload.text) {
      this.overlay.showTooltip(payload.text, payload.options);
    } else if (payload.type === 'action') {
      if (payload.target) {
        // New hybrid format
        this.handleHybridAction(payload);
      } else {
        // Legacy format
        this.actionExecutor.executeWithConsent(payload.action);
      }
    } else if (payload.type === 'clear_highlight') {
      this.overlay.clearHighlight();
    } else if (payload.type === 'language_changed') {
      // User-requested acknowledgement or server auto-detection. The reducer
      // rejects stale request ids and languages outside this site's policy.
      this.handleLanguageChanged(payload);
    } else if (payload.type === 'language_change_failed') {
      // The selected pack never changed optimistically, so failure is a clean
      // rollback: release the pending control and keep the prior locale.
      this.handleLanguageChangeFailed(payload);
    } else if (payload.type === 'batched_navigation') {
      // Fast Execution: Handle batched navigation from server
      this.handleBatchedNavigation(payload);
    } else if (payload.type === 'fast_action') {
      // Fast Execution: Single fast action (no cursor animation)
      this.executeActionFast(payload as HybridUIAction);
    } else if (payload.type === 'execute_api' || payload.action === 'execute_api') {
      // Direct API Execution: Bot wants us to make an API call
      // This is part of the Capability-Based Guidance system
      this.handleExecuteApi(payload);
    } else if (payload.type === 'session_paused') {
      this.retirePlanRun();
      // Handle session paused message from bot
      this.logger.log('[OnboardingSDK] Session paused:', payload);
      this.agentState = 'paused';
      this.pausedSessionId = payload.session_id || null;
      // Clear all overlay artifacts when pausing
      this.overlay.cleanup();
      this.renderReactUI();
      this.emit('sessionPaused', payload);
    } else if (payload.type === 'plan_progress') {
      if (!this.acceptPlanLifecycle(payload.planRunId, true)) return;
      if (payload.supportRecords !== undefined) this.actionExecutor.supportRecords.configure(payload.planRunId, payload.supportRecords);
      // Handle plan progress update from bot during guided execution
      this.logger.log('[OnboardingSDK] Plan progress:', payload);
      this.planProgress = {
        planName: payload.planName || '',
        currentStep: payload.currentStep || 0,
        totalSteps: payload.totalSteps || 0,
        progressPercent: payload.progressPercent || 0,
        currentTaskTitle: payload.currentTaskTitle || '',
      };
      this.renderReactUI();
      this.emit('planProgress', this.planProgress);
    } else if (payload.type === 'plan_started') {
      if (!this.acceptPlanLifecycle(payload.planRunId, true)) return;
      if (payload.supportRecords !== undefined) this.actionExecutor.supportRecords.configure(payload.planRunId, payload.supportRecords);
      // Handle plan started notification from bot
      this.logger.log('[OnboardingSDK] Plan started:', payload);
      // planProgress will be set by the subsequent plan_progress message
      this.emit('planStarted', { planName: payload.planName, totalSteps: payload.totalSteps });
    } else if (payload.type === 'plan_complete' || payload.type === 'plan_stopped') {
      if (!this.acceptPlanLifecycle(payload.planRunId, false)) return;
      // Handle plan completion notification from bot
      this.logger.log('[OnboardingSDK] Plan complete:', payload);
      this.planProgress = null;
      // Clear all overlay artifacts when a plan/task is complete
      this.overlay.cleanup();
      this.renderReactUI();
      if (payload.type === 'plan_complete') this.emit('planComplete', { planName: payload.planName });
    } else if (payload.type === 'bot_citation' && Array.isArray(payload.sources)) {
      // Citation sources for the most-recent assistant message.
      // Emitted by pipecat-server's BotCitationEmitter when the bot finishes
      // its TTS turn AFTER a recall_knowledge tool call stashed sources.
      // We attach the sources to the most-recent finalized assistant message
      // in transcriptHistory. If the bot is still mid-stream (no finalized
      // message yet), buffer the sources and attach on next finalization.
      this.handleBotCitation(payload.sources);
    } else if (payload.type === 'suggested_options') {
      // FLO-307: deterministic opener chips pushed by the server at kickoff.
      // Server-authored (never model-emitted), so they're guaranteed on turn 1
      // before any bot speech. Validate as string[] defensively (trim, drop
      // blanks, cap at 6) even though floe-api/resolveDiscovery already capped
      // them — a malformed payload must never blow the chip row. `transient`
      // in the payload is advisory; the transience is enforced by our clear
      // logic (next user turn / demo_ended), not a stored flag.
      const options = Array.isArray(payload.options)
        ? (payload.options as unknown[])
            .filter((o): o is string => typeof o === 'string')
            .map((o) => o.trim())
            .filter((o) => o.length > 0)
            .slice(0, 6)
        : [];
      const optionLanguage = isSupportedLanguage(payload.language)
        ? payload.language
        : undefined;
      // A row from the language that just lost a switch race must not repaint
      // the new locale. Untagged direct-demo deterministic rows are handled by
      // visibleSuggestedOptions() as English-owned and replaced by pack copy.
      if (optionLanguage && optionLanguage !== this.selectedLanguage) {
        this.logger.warn(
          `[OnboardingSDK] Ignoring stale ${optionLanguage} suggested options in ${this.selectedLanguage} session`,
        );
        return;
      }
      this.suggestedOptions = options;
      this.suggestedOptionsMetadata = options.length
        ? {
            language: optionLanguage,
            source: typeof payload.source === 'string' ? payload.source : undefined,
          }
        : null;
      this.logger.log(`[OnboardingSDK] Suggested options: ${options.length} chip(s)`);
      this.renderReactUI();
    } else if (payload.type === 'assistant_cue' && typeof payload.text === 'string') {
      // A server-owned control cue uses this path only when speaker output is
      // muted. It is an assistant message, never a fabricated visitor turn.
      const text = payload.text.trim().slice(0, 240);
      if (!text) return;
      this.transcriptHistory.push({
        id: `assistant-cue-${Date.now()}`,
        role: 'assistant',
        content: text,
        timestamp: Date.now(),
      });
      this.pruneTranscriptHistory();
      this.renderReactUI();
    } else if (payload.type === 'identity_ask') {
      if (!this.isConnected || this.demoEnded || !this.identityConsented()) return;
      const next = reduceIdentityAsk(this.identityAsk, payload);
      if (next !== this.identityAsk) {
        // Only accepted terminal revisions may replace booking identity. A
        // proposed transcription is not a confirmed email, even if it parses.
        if (payload.field === 'email' && payload.status === 'resolved') {
          this.bookingEmail = payload.outcome === 'confirmed' && typeof payload.value === 'string'
            ? payload.value : '';
        }
        // An acknowledgement or a replacement ask permits a new submission.
        // Late updates to another field's revision history do not.
        if (next?.askId !== this.identityAsk?.askId || next?.status !== this.identityAsk?.status) {
          this.identitySubmissionPending = null;
        }
        this.identityAsk = next;
        this.renderReactUI();
        if (next) { const { revisions, ...event } = next; this.emit('identityAsk', event); }
      }
    } else if (payload.type === 'capture_identity') {
      // Legacy card request — ignored.
    } else if (payload.type === 'show_calendar') {
      // FLO-618: the bot's `offer_booking` tool fired — the visitor asked to
      // talk to a human / book a call, and the website agent should put the
      // calendar on screen. Server-authored (the model reaches it through the
      // tool handler, which owns the "is a calendar even configured" gate), so
      // this handler only has to decide whether THIS surface can render one.
      //
      // Demo mode is refused rather than forwarded. The demo agent's calendar
      // is its POST-demo state — showing it mid-walkthrough would replace the
      // screencast the prospect is watching, i.e. end the demo without saying
      // so. The server gates on entry_phase for the same reason, and this is
      // the client-side belt (mirrors `capture_identity`'s demoMode check,
      // which fails the other way for the same structural reason).
      if (!this.config.websiteAgent) {
        this.logger.warn(
          '[OnboardingSDK] show_calendar outside website-agent mode — ignoring',
        );
      } else if (!hasCalendar(this.getResolvedCalendarUrl())) {
        // Nothing USABLE configured. The server believed otherwise (stale
        // config, or a site whose URL was cleared mid-session), so say so in the
        // log rather than opening an empty view the visitor has to back out of.
        //
        // Review follow-up: this was a raw truthiness check, which passes for an
        // `http://` or malformed URL. The view has its own `hasCalendar` belt so
        // nothing would have rendered — but this branch would have logged a
        // success and emitted `calendarRequested` for a booking that could never
        // appear, which is a lying event for anyone listening on it. Both sides
        // now read the one resolver.
        this.logger.warn(
          '[OnboardingSDK] show_calendar requested but no usable booking URL is configured — ignoring',
        );
      } else {
        // A COUNTER, not a flag: a visitor who closes the calendar and asks
        // again must get it back, and re-setting `true` is not a state change
        // the view can react to.
        this.websiteAgentCalendarRequestId += 1;
        this.logger.log(
          `[OnboardingSDK] Calendar requested by bot (#${this.websiteAgentCalendarRequestId})`,
        );
        this.renderReactUI();
        this.emit('calendarRequested', { requestId: this.websiteAgentCalendarRequestId });
      }
    } else if (payload.type === 'demo_starting') {
      // The website agent is handing the visitor to a live demo. The server
      // sends this once, only for a transition that genuinely begins — never on
      // direct-demo entry and never on a refused start — so there is no state
      // to validate here beyond the surface.
      //
      // Gated on the website-agent surface POSITIVELY (as `show_calendar` is,
      // and for the reason agent-mode.ts spells out): the demo agent's stage is
      // never in a hand-over, and a `!demoMode` gate here would hand this card
      // to the onboarding agent, which has no stage at all.
      if (!this.config.websiteAgent) {
        this.logger.warn(
          '[OnboardingSDK] demo_starting outside website-agent mode — ignoring',
        );
      } else {
        this.websiteAgentDemoStartingId += 1;
        this.logger.log(
          `[OnboardingSDK] 🎬 Demo hand-over starting (#${this.websiteAgentDemoStartingId})`,
        );
        this.renderReactUI();
      }
    } else if (payload.type === 'demo_visible') {
      // The first post-transition browser action finished: the picture is worth
      // looking at. Ignored unless a hand-over is in flight — the view's reducer
      // enforces that too, but a stray message must not bump a counter that a
      // later START would then have to climb past.
      if (!this.config.websiteAgent) {
        this.logger.warn(
          '[OnboardingSDK] demo_visible outside website-agent mode — ignoring',
        );
      } else if (!this.websiteAgentDemoStartingId) {
        this.logger.warn(
          '[OnboardingSDK] demo_visible with no hand-over in flight — ignoring',
        );
      } else {
        this.websiteAgentDemoVisibleId += 1;
        this.logger.log(
          `[OnboardingSDK] 🎬 Demo hand-over visible (#${this.websiteAgentDemoVisibleId})`,
        );
        this.renderReactUI();
      }
    } else if (payload.type === 'demo_ended') {
      this.retirePlanRun();
      // The hand-over cannot outlive the demo it introduced: a card still on
      // screen here would sit over a finished session.
      this.resetWebsiteAgentDemoHandover();
      // End-of-demo payload from the bot's end_demo tool. Powers the
      // post-demo screen (features list + summary + next step) instead of
      // an abrupt disconnect. Shape: { summary, features_shown,
      // next_step, buying_signals, duration_seconds }.
      this.demoEnded = true;
      // FLO-307: drop any opener chips still showing — the demo is over.
      this.suggestedOptions = [];
      this.suggestedOptionsMetadata = null;
      this.demoSummary = typeof payload.summary === 'string' ? payload.summary : null;
      this.demoFeaturesShown = Array.isArray(payload.features_shown)
        ? payload.features_shown.filter((f: unknown) => typeof f === 'string')
        : [];
      this.demoNextStep = typeof payload.next_step === 'string' ? payload.next_step : null;
      this.logger.log(
        `[OnboardingSDK] Demo ended: ${this.demoFeaturesShown.length} features, summary=${(this.demoSummary || '').slice(0, 60)}`,
      );
      this.renderReactUI();
      this.emit('demoEnded', {
        summary: this.demoSummary,
        featuresShown: this.demoFeaturesShown,
        nextStep: this.demoNextStep,
      });
    } else if (payload.type === 'init_error') {
      this.retirePlanRun();
      // The server couldn't load this site's product context, so it refused to
      // greet with missing/incorrect info. Show the error instead of waiting on
      // a bot that will never speak.
      const message = typeof payload.message === 'string' && payload.message
        ? payload.message
        : "I couldn't load this site's setup just now. Please refresh and try again.";
      this.logger.error('[OnboardingSDK] Init error from server:', payload.code, message);
      this.fatalError = message;
      this.agentState = 'idle';
      this.overlay.cleanup();
      this.renderReactUI();
      this.emit('error', { type: 'init_error', code: payload.code, message });
    } else {
      this.logger.log('[OnboardingSDK] Unhandled server message type:', payload.type || 'unknown');
    }
  }

  /**
   * Retire this page session's hand-over edges.
   *
   * Zeroing the START counter is what the view reads as "no transition": its
   * reducer returns to `idle`, the card unmounts, and the stage goes back to
   * being owned by the video track alone. Both halves are reset together — a
   * surviving VISIBLE id would be an END edge waiting for the next hand-over's
   * card to arm.
   */
  private resetWebsiteAgentDemoHandover() {
    this.websiteAgentDemoStartingId = 0;
    this.websiteAgentDemoVisibleId = 0;
  }

  /**
   * Attach citation sources to the most-recent assistant message in
   * transcriptHistory. If no assistant message has been finalized yet
   * (citation arrived before content stream completed), buffer the sources
   * in `pendingCitationSources` so the next finalized assistant message
   * picks them up.
   *
   * Defensive shape check: each source must have a string `name` and a
   * numeric `confidence`. Anything else is dropped silently — we'd rather
   * render zero chips than render a broken chip on camera.
   */
  private handleBotCitation(sources: any[]) {
    type SourceShape = NonNullable<typeof this.pendingCitationSources>[number];
    const mergeSources = (
      existing: SourceShape[],
      incoming: SourceShape[],
    ): SourceShape[] => {
      const byKey = new Map<string, SourceShape>();
      for (const source of [...existing, ...incoming]) {
        const key = (source.url || source.name).trim().toLowerCase();
        const prior = byKey.get(key);
        if (!prior || source.confidence > prior.confidence) byKey.set(key, source);
      }
      return [...byKey.values()].sort((a, b) => b.confidence - a.confidence);
    };
    const validated: SourceShape[] = sources
      .filter((s): s is { name: string; confidence: number; snippet?: string | null; url?: string | null; source_type?: string } => {
        return typeof s?.name === 'string'
          && typeof s?.confidence === 'number'
          && s.name.length > 0;
      })
      .map((s): SourceShape => ({
        name: s.name,
        snippet: typeof s.snippet === 'string' ? s.snippet : null,
        url: typeof s.url === 'string' ? s.url : null,
        confidence: s.confidence,
        source_type:
          s.source_type === 'knowledge' ? 'knowledge'
          : s.source_type === 'plan' ? 'plan'
          : 'content',
      }));

    if (validated.length === 0) {
      this.logger.log('[OnboardingSDK] bot_citation arrived with no valid sources; skipping');
      return;
    }

    // A citation received while the current assistant turn is still live must
    // not attach to an older history bubble. Avatar playback deliberately keeps
    // an LLM-complete transcript pending until its audio boundary.
    if (
      this.llmCaptionTurn
      || this.llmTranscriptPending
      || this.botIsSpeaking
      || Boolean(this.botTranscript)
    ) {
      this.pendingCitationSources = mergeSources(
        this.pendingCitationSources ?? [],
        validated,
      );
      this.logger.log(
        `[OnboardingSDK] 📚 Buffered ${validated.length} citation source(s) `
          + `for current assistant turn (now ${this.pendingCitationSources.length} total)`,
      );
      return;
    }

    // Find the most-recent finalized assistant message.
    let attached = false;
    for (let i = this.transcriptHistory.length - 1; i >= 0; i--) {
      const msg = this.transcriptHistory[i];
      if (msg.role === 'assistant' && !msg.isPartial) {
        // If sources already exist (an earlier tool call in the same turn
        // produced citations too), MERGE and dedupe instead of skipping.
        // Dedupe key prefers `url` when present, else `name` — both are
        // stable per source. Sort by confidence so the strongest stays
        // first regardless of which tool fired.
        const existing = msg.sources ?? [];
        const merged = mergeSources(existing, validated);
        msg.sources = merged;
        attached = true;
        this.logger.log(
          `[OnboardingSDK] 📚 ${existing.length ? 'Merged' : 'Attached'} ` +
            `${validated.length} new citation source(s) on message ${msg.id} ` +
            `(now ${merged.length} total)`,
        );
        break;
      }
    }

    if (!attached) {
      // No finalized assistant message yet — buffer and attach on next
      // finalization (in the bot transcript flush path).
      this.pendingCitationSources = mergeSources(
        this.pendingCitationSources ?? [],
        validated,
      );
      this.logger.log(`[OnboardingSDK] 📚 Buffered ${validated.length} citation source(s) for next assistant message`);
      return;
    }

    this.pendingCitationSources = null;
    this.renderReactUI();
  }

  /** Revoke this session's pending and delayed actions before transport cleanup. */
  private retirePlanRun(): void {
    this.actionExecutor.supportRecords.retire();
    if (this.activePlanRunId) this.retiredPlanRunIds.add(this.activePlanRunId);
    this.activePlanRunId = null;
    this.legacyPlanActionsAllowed = false;
    this.planSessionOpen = false;
    this.pendingTypeAction?.abort();
  }

  /** Correlate lifecycle messages before changing browser execution authority. */
  private acceptPlanLifecycle(runId: unknown, active: boolean): boolean {
    // A pending lookup can produce an unseen run ID after termination. Keep
    // activation closed until a new client owns the session's callbacks.
    if (active && !this.planSessionOpen) return false;
    if (typeof runId !== 'string' || !runId) {
      // Old runtimes can resume their legacy actions with a new plan event.
      // Once identities are in use, uncorrelated events cannot revoke a new run.
      if (this.identifiedPlanRuns) return false;
      this.legacyPlanActionsAllowed = active;
      if (!active) this.pendingTypeAction?.abort();
      return true;
    }
    this.identifiedPlanRuns = true;
    if (active) {
      if (this.retiredPlanRunIds.has(runId)) return false;
      if (this.activePlanRunId !== runId) {
        this.actionExecutor.supportRecords.retire();
        if (this.activePlanRunId) this.retiredPlanRunIds.add(this.activePlanRunId);
        this.pendingTypeAction?.abort();
        this.activePlanRunId = runId;
      }
      return true;
    }
    this.retiredPlanRunIds.add(runId);
    if (this.activePlanRunId !== runId) return false;
    this.actionExecutor.supportRecords.retire();
    this.activePlanRunId = null;
    this.pendingTypeAction?.abort();
    return true;
  }

  /** Handle hybrid UI actions, including identity-bound guarded support steps. */
  private async handleHybridAction(action: HybridUIAction & { planRunId?: string }) {
    if (action.supportContract) {
      if (!action.actionId) return;
      const step = action.supportContract;
      const currentRun = action.planRunId
        ? action.planRunId === this.activePlanRunId && !this.retiredPlanRunIds.has(action.planRunId)
        : !this.identifiedPlanRuns && this.legacyPlanActionsAllowed;
      if (!currentRun) {
        // Reject before aborting: a late action from an old run must not cancel
        // a valid replacement action. This does not undo an already saved change.
        this.sendMetadata({
          type: 'bot_action_complete', action: step.action, actionId: action.actionId,
          success: false, target: 'support task',
          supportStep: {planId: step.planId, planVersion: step.planVersion, taskId: step.taskId},
          error: 'This workflow execution is stopped, replaced, or has not started.',
          timestamp: Date.now(),
        });
        return;
      }
      // All guarded mutations share cancellation with field entry. A newer
      // task, pause, minimize, or disconnect invalidates delayed actions.
      this.pendingTypeAction?.abort();
      const controller = new AbortController();
      this.pendingTypeAction = controller;
      this._botActionDepth++;
      let success = false;
      try {
        success = await this.actionExecutor.executeHybridAction(action, false, controller.signal);
      } finally {
        this._botActionDepth--;
        if (this.pendingTypeAction === controller) this.pendingTypeAction = null;
      }
      const recordReceipt = this.actionExecutor.supportRecords.takeReceipt(action.actionId);
      const baselineReceipt = this.actionExecutor.supportRecords.takeBaselineReceipt(action.actionId);
      const recordReadEvidence = this.actionExecutor.takeSupportRecordReadEvidence(action.actionId);
      const fileSelectionEvidence = this.actionExecutor.takeSupportFileSelectionEvidence(action.actionId);
      this.sendMetadata({
        type: 'bot_action_complete', action: step.action, actionId: action.actionId,
        success, target: 'support task',
        supportStep: {planId: step.planId, planVersion: step.planVersion, taskId: step.taskId},
        ...(success && recordReceipt ? {recordReceipt} : {}),
        ...(success && baselineReceipt ? {baselineReceipt} : {}),
        ...(success && recordReadEvidence ? {recordReadEvidence: {text: recordReadEvidence, url: getSanitizedUrl()}} : {}),
        ...(success && fileSelectionEvidence ? {fileSelectionEvidence} : {}),
        ...(success && this.actionExecutor.lastSupportTransitionEvidence ? {resultTransitionEvidence: this.actionExecutor.lastSupportTransitionEvidence, resultTransitionPageUrl: getSanitizedUrl()} : {}),
        ...(!success ? {error: this.actionExecutor.lastError || 'Support task was not verified'} : {}),
        timestamp: Date.now(),
      });
      this.domIntrospector.scheduleCapture('post-support-step');
      return;
    }
    this.logger.log('[OnboardingSDK] 🎯 Handling hybrid action:', action.action);
    this.logger.log('[OnboardingSDK] Target strategies:', action.target.strategy || 'auto-detect');
    this.logger.log('[OnboardingSDK] Target text:', action.target.text);
    this.logger.log('[OnboardingSDK] Target selector:', action.target.selector);
    this.logger.log('[OnboardingSDK] Target tag:', action.target.tag);

    // NOTE: Bot action depth is incremented RIGHT BEFORE actual DOM interaction
    // This avoids race conditions during animation delays

    switch (action.action) {
      case 'highlight':
        this.logger.log('[OnboardingSDK] 💡 Executing HIGHLIGHT action');
        // Highlight doesn't generate click events - no need to track

        const highlightSuccess = this.overlay.highlightTarget(action.target, {
          color: 'rgba(0, 170, 255, 0.7)',  // Bright blue for visibility
          padding: 10
        });

        if (highlightSuccess) {
          this.logger.log('[OnboardingSDK] ✅ Element highlighted successfully');
          if (action.description) {
            // Show tooltip near the highlighted element
            this.overlay.showTooltip(action.description, {});
          }
          // Emit event for UI feedback
          this.emit('elementHighlighted', { target: action.target, description: action.description });
        } else {
          this.logger.warn('[OnboardingSDK] ⚠️ Failed to find element for highlight');
          // Try again with extended matching after a small delay (for dynamic content)
          setTimeout(() => {
            const retrySuccess = this.overlay.highlightTarget(action.target, {
              color: 'rgba(255, 193, 7, 0.7)',  // Amber for retry
              padding: 10
            });
            if (retrySuccess) {
              this.logger.log('[OnboardingSDK] ✅ Element found on retry');
              if (action.description) {
                this.overlay.showTooltip(action.description, {});
              }
            } else {
              this.logger.error('[OnboardingSDK] ❌ Element not found even after retry:', action.target);
            }
          }, 500);
        }
        break;

      case 'click':
        this.logger.log('[OnboardingSDK] 👆 Executing CLICK action with Floe cursor');

        // Capture URL before click for sanity checking on server
        // If clicking accidentally navigates (e.g., clicking logo), server can detect it
        const urlBeforeClick = getSanitizedUrl();

        // SPECIAL HANDLING: Dropdown option selection (optionText)
        // When optionText is specified, we need to:
        // 1. Open the dropdown if not already open
        // 2. Wait for options to appear
        // 3. Find and click the actual option
        if (action.target.optionText) {
          this.logger.log('[OnboardingSDK] 🎯 Dropdown option selection detected:', action.target.optionText);
          await this.handleDropdownOptionClick(action);
          break;
        }

        // Resolve element first
        let clickElement = HybridTargetResolver.resolveTarget(action.target);
        if (!clickElement) {
          this.logger.warn('[OnboardingSDK] ⚠️ Click target not found');
          // Clear all overlay artifacts on error - stale highlight should not remain
          this.overlay.cleanup();
          // Report failure to server so it doesn't think the action succeeded
          this.sendMetadata({
            type: 'bot_action_complete',
            action: 'click',
            success: false,
            target: action.target.text || action.target.selector || 'unknown',
            error: 'Element not found',
            timestamp: Date.now(),
          });
          // No need to clear bot action flag - we haven't set it yet
          // (flag is only set right before actual DOM interaction)
          break;
        }

        // Scroll into view
        HybridTargetResolver.scrollIntoView(clickElement);

        // Wait for scroll, then animate Floe cursor
        setTimeout(async () => {
          // Check if element is in a collapsed sidebar
          const collapsedSidebar = this.findCollapsedSidebar(clickElement!);

          if (collapsedSidebar) {
            this.logger.log('[OnboardingSDK] 🔍 Element is in collapsed sidebar, expanding first...');

            // Animate Floe cursor to sidebar first
            const sidebarRect = collapsedSidebar.getBoundingClientRect();
            await this.overlay.animateFloeCursorTo(
              sidebarRect.left + sidebarRect.width / 2,
              sidebarRect.top + sidebarRect.height / 3,
              400
            );

            // Trigger hover on sidebar to expand
            collapsedSidebar.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
            collapsedSidebar.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));

            // Wait for expansion animation
            await new Promise(r => setTimeout(r, 450));

            this.logger.log('[OnboardingSDK] 📂 Sidebar expanded');
          }

          // CRITICAL: Wait for element position to stabilize after scroll/animation
          // This prevents clicking wrong elements during sidebar expansion or scroll
          const stableRect = await HybridTargetResolver.waitForPositionStability(clickElement!, 800);
          if (!stableRect) {
            this.logger.error('[OnboardingSDK] ❌ Element disappeared during stability wait');
            // Clear all overlay artifacts on error
            this.overlay.cleanup();
            this.sendMetadata({
              type: 'bot_action_complete',
              action: 'click',
              success: false,
              target: action.target.text || action.target.selector || 'unknown',
              error: 'Element disappeared during animation',
              timestamp: Date.now(),
            });
            return;
          }

          // CRITICAL: Re-resolve element AFTER stability wait
          // The original element reference may point to wrong position after animations
          const freshElement = HybridTargetResolver.reResolveAndVerify(action.target, clickElement!);
          if (freshElement) {
            clickElement = freshElement;
            this.logger.log('[OnboardingSDK] 🔄 Re-resolved element after stability wait');
          } else {
            this.logger.warn('[OnboardingSDK] ⚠️ Could not re-resolve element, using original');
          }

          // Now highlight the resolved clickable element
          // this.overlay.highlightResolvedElement(clickElement!, {
          //   color: 'rgba(124, 58, 237, 0.5)',  // Purple to match Floe brand
          //   padding: 8
          // });

          // Animate Floe cursor to the element
          await this.overlay.moveToElementAndClick(clickElement!);

          // Small pause after cursor arrives
          await new Promise(r => setTimeout(r, 150));

          // Mark bot action RIGHT BEFORE the actual DOM interaction
          // This ensures click events during animation are tagged as 'user'
          this._botActionDepth++;

          // CRITICAL FIX: Create a modified action with position-independent target
          // The original action.target has stale domIndex/boundingBox from server's snapshot
          // After scroll/animation, those coordinates point to wrong elements
          const positionIndependentAction: HybridUIAction = {
            ...action,
            target: {
              text: action.target.text,
              selector: action.target.selector,
              selectorFallbacks: action.target.selectorFallbacks,
              tag: action.target.tag,
              ariaLabel: action.target.ariaLabel,
              placeholder: action.target.placeholder,
              role: action.target.role,
              // EXCLUDE: domIndex, boundingBox, confidence - stale after page changes
              // Cast to handle server-sent strategy values that may include position-dependent types
              strategy: action.target.strategy?.filter(s =>
                (s as string) !== 'boundingBox' && (s as string) !== 'domIndex'
              )
            }
          };

          // Execute the actual click with position-independent target
          let clickSuccess = false;
          try {
            clickSuccess = await this.actionExecutor.executeHybridAction(positionIndependentAction, false);
          } finally {
            // Always decrement depth after click, regardless of success
            this._botActionDepth--;
          }

          if (clickSuccess) {
            this.logger.log('[OnboardingSDK] ✅ Click executed successfully');

            // CRITICAL: Validate that we clicked the right element
            // Compare the expected target text with what was actually clicked
            const expectedText = (action.target.text || '').toLowerCase().trim();
            const clickedElementText = clickElement?.textContent?.toLowerCase().trim() || '';
            const clickedElementTag = clickElement?.tagName.toLowerCase() || 'unknown';

            // Check for element mismatch
            let clickMismatch = false;
            let mismatchDetails = '';

            if (expectedText && clickedElementText) {
              // The clicked element should contain the expected text
              if (!clickedElementText.includes(expectedText) && !expectedText.includes(clickedElementText)) {
                clickMismatch = true;
                mismatchDetails = `Expected "${action.target.text}", but clicked "${clickedElementText.slice(0, 50)}"`;
                this.logger.error(`[OnboardingSDK] ❌ CLICK MISMATCH: ${mismatchDetails}`);
              }
            }

            // CRITICAL: Trigger immediate DOM capture after bot click
            // This ensures server has fresh DOM state for next action
            if (this.domIntrospector) {
              // Wait a bit for React to update DOM, then capture
              await new Promise(r => setTimeout(r, 150));
              this.domIntrospector.scheduleCapture('post-bot-click');
              this.logger.log('[OnboardingSDK] 📸 DOM snapshot sent after bot click');
            }

            // Send bot_action_complete for auto-advance (for clicks that don't change URL)
            // This enables fast execution for CLICK tasks without navigation
            this.sendMetadata({
              type: 'bot_action_complete',
              action: 'click',
              success: !clickMismatch,  // Mark as failure if mismatch detected
              target: action.target.text || action.target.selector || 'unknown',
              actualClicked: clickMismatch ? clickedElementText.slice(0, 100) : undefined,
              clickedTag: clickedElementTag,
              mismatch: clickMismatch,
              mismatchDetails: mismatchDetails || undefined,
              previousUrl: urlBeforeClick,  // For server-side URL sanity check
              currentUrl: getSanitizedUrl(),  // Current URL after click
              timestamp: Date.now(),
            });

            if (clickMismatch) {
              this.logger.warn('[OnboardingSDK] ⚠️ Click mismatch reported to server');
            } else {
              this.logger.log('[OnboardingSDK] 📤 Sent bot_action_complete for auto-advance');
            }
          } else {
            const errorReason = this.actionExecutor.lastError || 'unknown';
            this.logger.warn('[OnboardingSDK] ⚠️ Click action failed:', errorReason);
            this.sendMetadata({
              type: 'bot_action_complete',
              action: 'click',
              success: false,
              target: action.target.text || action.target.selector || 'unknown',
              error: errorReason,
              timestamp: Date.now(),
            });
          }

          // Wait a bit for animations to complete
          await new Promise(r => setTimeout(r, 100));

          // Clear all overlay artifacts after action
          setTimeout(() => {
            this.overlay.cleanup();
          }, 800);
        }, 350);
        break;

      case 'type': {
        this.pendingTypeAction?.abort();
        const typeController = new AbortController();
        this.pendingTypeAction = typeController;
        this.logger.log('[OnboardingSDK] ⌨️ Executing TYPE action');

        // Resolve element first
        const typeElement = HybridTargetResolver.resolveTarget(action.target);
        if (typeElement) {
          // Highlight the resolved input field (handles hidden placeholders)
          this.overlay.highlightResolvedElement(typeElement, {
            color: 'rgba(76, 175, 80, 0.6)',  // Green for input
            padding: 4
          });
        }
        // Execute after brief delay
        await new Promise(r => setTimeout(r, 300));
        this._botActionDepth++;
        let typeSuccess = false;
        try {
          typeSuccess = await this.actionExecutor.executeHybridAction(action, false, typeController.signal);
        } finally {
          this._botActionDepth--;
        }
        // A request ID opts into this protocol. Older runtimes may otherwise
        // consume an unsolicited type acknowledgment as their next click result.
        if (action.actionId) {
          this.sendMetadata({
            type: 'bot_action_complete',
            action: 'type',
            actionId: action.actionId,
            success: typeSuccess,
            target: action.target.text || action.target.selector || 'input',
            ...(!typeSuccess ? { error: this.actionExecutor.lastError || 'Field entry failed' } : {}),
            timestamp: Date.now(),
          });
        }
        this.domIntrospector?.scheduleCapture('post-bot-type');
        setTimeout(() => this.overlay.clearHighlight(), 1500);
        break;
      }

      case 'focus':
        this.logger.log('[OnboardingSDK] 🎯 Executing FOCUS action');
        // Focus doesn't generate click events
        await this.actionExecutor.executeHybridAction(action, false);
        break;

      case 'hover':
        this.logger.log('[OnboardingSDK] 🖱️ Executing HOVER action');
        // Hover doesn't generate click events
        await this.actionExecutor.executeHybridAction(action, false);
        break;

      case 'scroll':
        this.logger.log('[OnboardingSDK] 📜 Executing SCROLL action');
        // Scroll doesn't generate click events
        await this.actionExecutor.executeHybridAction(action, false);
        break;

      case 'multi_select':
        this.logger.log('[OnboardingSDK] 📋 Executing MULTI_SELECT action');
        await this.handleMultiSelectAction(action);
        break;

      case 'navigate':
        this.logger.log('[OnboardingSDK] 🧭 Executing NAVIGATE action to URL:', action.value);
        // Clear all overlay artifacts before navigation - they relate to the current page
        this.overlay.cleanup();
        {
          const targetUrl = action.value;
          if (!targetUrl) {
            this.logger.warn('[OnboardingSDK] ⚠️ Navigate action missing URL');
            this.sendMetadata({
              type: 'bot_action_complete',
              action: 'navigate',
              success: false,
              error: 'Missing target URL',
              timestamp: Date.now(),
            });
            break;
          }

          // Resolve against the origin we are actually running on. The host stored in
          // a plan is where it was RECORDED, not where it replays — see
          // resolveNavigationTarget for why that host is always discarded.
          const target = resolveNavigationTarget(targetUrl, window.location.href);
          if (!target.ok) {
            const error = (target as { ok: false; error: string }).error;
            this.logger.warn(`[OnboardingSDK] ⚠️ Navigate blocked — ${error}`);
            this.sendMetadata({
              type: 'bot_action_complete',
              action: 'navigate',
              success: false,
              // Never echo the raw target — it may carry tokens.
              error: `Blocked: ${error}`,
              timestamp: Date.now(),
            });
            break;
          }
          if (target.hostRewritten) {
            // Expected on any multi-tenant product that gives each customer its own
            // subdomain. Logged rather than silent so a genuinely wrong plan URL is
            // still visible.
            this.logger.log(
              '[OnboardingSDK] 🧭 Plan URL recorded on another host — using this origin',
            );
          }
          const resolvedUrl: string = target.url;

          const beforeUrl = window.location.href;
          const navTimestamp = Date.now();

          // Persist a pending navigation record BEFORE triggering the navigation.
          // Full-page navigations destroy the current JS context, so sendMetadata
          // may never execute.  sessionStorage survives same-origin navigations
          // and is read back by _finalizePendingNavigation() on the next page load.
          const pendingNav = {
            resolvedUrl: sanitizeUrl(resolvedUrl),
            beforeUrl: sanitizeUrl(beforeUrl),
            timestamp: navTimestamp,
          };
          try {
            sessionStorage.setItem(
              'floe_pending_navigation',
              JSON.stringify(pendingNav),
            );
          } catch {
            // sessionStorage unavailable (private browsing, quota) — best-effort
          }

          // (CDP Phase 1 / FLO-547 cleanup: this used to register a pagehide
          // handler that beaconed a bot_action_complete to /api/v1/events/beacon
          // — an endpoint floe-api never had a handler for, so every byte died
          // on arrival. Deleted with its CORS entry. The unload case is fully
          // covered by the floe_pending_navigation record above, which
          // _finalizePendingNavigation() reports on the next page load; visitor
          // analytics now flow through the EventQueue instead.)

          // SPA-friendly navigation first — it preserves the app's state, which is
          // what makes a guided walkthrough feel continuous. But it can be REFUSED:
          // the host router may not have a route for this URL. Verify rather than
          // assume, because the URL alone proves nothing once we have pushed it
          // ourselves (see spa-navigation.ts).
          let spaResult: SpaNavigationResult = {
            accepted: false,
            reason: 'pushState unavailable',
          };
          try {
            spaResult = await attemptSpaNavigation(
              createBrowserNavigationAdapter(),
              resolvedUrl,
            );
          } catch (error) {
            spaResult = {
              accepted: false,
              // Not necessarily pushState — the attempt also notifies the router,
              // waits, and may restore the URL. This string is reported to the server
              // as the failure cause, so it should not name a step it cannot know threw.
              reason: `the SPA navigation attempt threw: ${error instanceof Error ? error.message : 'unknown'}`,
            };
          }

          if (!spaResult.accepted) {
            // The app would not route there. A full load always arrives; it costs the
            // in-app state, which is strictly better than the walkthrough continuing
            // on a page it never reached.
            this.logger.warn(
              `[OnboardingSDK] ↪️ SPA navigation refused (${spaResult.reason}) — falling back to a full page load`,
            );

            // Assigning a URL that differs only by fragment is a SAME-DOCUMENT
            // navigation: no request, no unload, nothing rendered. The fallback would
            // silently do nothing. reload() forces the document to actually load.
            const fragmentOnly =
              new URL(resolvedUrl).href.split('#')[0] ===
              window.location.href.split('#')[0];
            window.location.href = resolvedUrl;
            if (fragmentOnly) window.location.reload();

            // Normally this context dies here and the next page load's
            // _finalizePendingNavigation reports it. But a full load is not
            // guaranteed to unload — a cancelled beforeunload prompt, or a
            // target that responds with Content-Disposition: attachment, both
            // leave us running. Without this the pending record is never
            // cleared, and the server waits out its 10s timeout with no
            // bot_action_complete at all.
            await new Promise((r) => setTimeout(r, FULL_LOAD_GRACE_MS));

            try { sessionStorage.removeItem('floe_pending_navigation'); } catch { /* noop */ }
            this.sendMetadata({
              type: 'bot_action_complete',
              action: 'navigate',
              target: sanitizeUrl(resolvedUrl),
              success: false,
              previousUrl: sanitizeUrl(beforeUrl),
              currentUrl: getSanitizedUrl(),
              error: `SPA navigation refused (${spaResult.reason}) and the page did not load`,
              timestamp: navTimestamp,
            });
            break;
          }

          // Accepted. waitForNavigation is now a formality (the URL already matches),
          // but it also covers a router that finishes asynchronously after settling.
          const navigationSucceeded = await this.waitForNavigation(resolvedUrl, 10000);

          // Navigation completed without a full page teardown — clean up.
          try { sessionStorage.removeItem('floe_pending_navigation'); } catch { /* noop */ }

          this.sendMetadata({
            type: 'bot_action_complete',
            action: 'navigate',
            target: sanitizeUrl(resolvedUrl),
            success: navigationSucceeded,
            previousUrl: sanitizeUrl(beforeUrl),
            currentUrl: getSanitizedUrl(),
            ...(navigationSucceeded ? {} : { error: 'Navigation timed out waiting for URL change' }),
            timestamp: navTimestamp,
          });
        }
        break;

      case 'check_precondition':
        this.logger.log('[OnboardingSDK] 🔍 Checking precondition');
        await this.handleCheckPrecondition(action);
        break;

      default:
        this.logger.warn('[OnboardingSDK] ⚠️ Unknown hybrid action:', action.action);
    }
  }

  /**
   * Wait for navigation to complete by polling the current URL.
   * Compares normalized URL components (origin, pathname, search, hash)
   * to avoid false positives from substring matching.
   * 
   * @param expectedUrl - Absolute URL we're navigating to (already resolved)
   * @param timeoutMs - Maximum time to wait in milliseconds
   * @returns true if navigation was observed, false if timed out
   */
  private async waitForNavigation(expectedUrl: string, timeoutMs: number): Promise<boolean> {
    const startTime = Date.now();
    const pollInterval = 200; // Check every 200ms

    // Normalize a pathname by stripping a single trailing slash (but keep "/" as-is)
    const normalizePath = (p: string): string => (p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p);

    // Pre-parse the expected URL once (caller passes an absolute URL)
    let expected: URL;
    try {
      expected = new URL(expectedUrl);
    } catch {
      // If the expected URL can't be parsed, fall back to exact string match
      this.logger.warn('[OnboardingSDK] ⚠️ waitForNavigation: could not parse expectedUrl, using exact match');
      return new Promise<boolean>((resolve) => {
        const fallback = () => {
          if (window.location.href === expectedUrl) { resolve(true); return; }
          if (Date.now() - startTime >= timeoutMs) { resolve(false); return; }
          setTimeout(fallback, pollInterval);
        };
        fallback();
      });
    }

    const expectedOrigin = expected.origin;
    const expectedPath = normalizePath(expected.pathname);
    const expectedSearch = expected.search;
    const expectedHash = expected.hash;

    return new Promise<boolean>((resolve) => {
      const check = () => {
        const elapsed = Date.now() - startTime;

        try {
          const current = new URL(window.location.href);
          // Origin and pathname must always match.
          // Search and hash are only enforced when the expected URL explicitly
          // included them; otherwise the destination is free to add its own
          // query params or fragments (analytics, SPA state, etc.).
          const matches =
            current.origin === expectedOrigin &&
            normalizePath(current.pathname) === expectedPath &&
            (!expectedSearch || current.search === expectedSearch) &&
            (!expectedHash || current.hash === expectedHash);

          if (matches) {
            this.logger.log(`[OnboardingSDK] ✅ Navigation completed to: ${window.location.href}`);
            resolve(true);
            return;
          }
        } catch {
          // window.location.href couldn't be parsed — keep polling
        }

        // Timeout
        if (elapsed >= timeoutMs) {
          this.logger.warn(`[OnboardingSDK] ⏱️ Navigation timeout after ${timeoutMs}ms. Current: ${window.location.href}, Expected: ${expectedUrl}`);
          resolve(false);
          return;
        }

        // Keep polling
        setTimeout(check, pollInterval);
      };

      check();
    });
  }

  /**
   * Finalize any pending bot navigation that was interrupted by a full-page load.
   *
   * When navigate sets `window.location.href` for a cross-origin or MPA target the
   * entire JS context is destroyed, so the optimistic sendMetadata never fires.
   * The navigate handler persists a record to sessionStorage before triggering
   * the navigation.  On the next page load this method reads it back, validates
   * that we actually arrived at the expected destination, and sends the final
   * bot_action_complete metadata so the server can auto-advance the task.
   *
   * Called once during init() — safe to call before the WebRTC transport is
   * connected because sendMetadata already guards on `this.isConnected`.
   * We retry briefly after init to cover the case where the transport
   * connects a moment later.
   */
  private _finalizePendingNavigation(): void {
    let raw: string | null;
    try {
      raw = sessionStorage.getItem('floe_pending_navigation');
    } catch {
      return; // sessionStorage unavailable
    }
    if (!raw) return;

    let pending: { resolvedUrl: string; beforeUrl: string; timestamp: number };
    try {
      pending = JSON.parse(raw);
      if (!pending.resolvedUrl || !pending.timestamp) return;
    } catch {
      // Corrupt record — discard
      try { sessionStorage.removeItem('floe_pending_navigation'); } catch { /* noop */ }
      return;
    }

    // Discard stale records (older than 60 s — navigation should be instant)
    if (Date.now() - pending.timestamp > 60_000) {
      try { sessionStorage.removeItem('floe_pending_navigation'); } catch { /* noop */ }
      return;
    }

    // Validate: did we actually arrive at the expected destination?
    const currentSanitized = getSanitizedUrl();
    const success = currentSanitized === pending.resolvedUrl;

    const metadata = {
      type: 'bot_action_complete',
      action: 'navigate',
      target: pending.resolvedUrl,
      success,
      previousUrl: pending.beforeUrl,
      currentUrl: currentSanitized,
      ...(!success ? { error: 'Full-page navigation did not reach expected URL' } : {}),
      timestamp: pending.timestamp,
      finalized: true, // Marks this as a post-reload finalization
    };

    // Clear the record so we don't send it twice
    try { sessionStorage.removeItem('floe_pending_navigation'); } catch { /* noop */ }

    // Attempt to send immediately (may succeed if transport is already connected)
    this.sendMetadata(metadata);

    // Also retry after a short delay to cover the case where the WebRTC
    // transport hasn't connected yet when init() runs
    setTimeout(() => {
      if (this.isConnected) {
        this.sendMetadata(metadata);
      }
    }, 3000);

    this.logger.log(
      `[OnboardingSDK] 🔄 Finalized pending navigation: ${pending.resolvedUrl} (success=${success})`,
    );
  }

  /**
   * Check a precondition and report the result to the server.
   * Used for conditional task execution (e.g., check if an option exists before selecting it).
   */
  private async handleCheckPrecondition(action: HybridUIAction): Promise<void> {
    const precondition = action.precondition;

    if (!precondition) {
      this.logger.warn('[OnboardingSDK] ⚠️ No precondition specified');
      this.sendMetadata({
        type: 'precondition_result',
        preconditionType: 'unknown',
        optionText: '',
        result: false,
        details: 'No precondition specified in action',
        timestamp: Date.now(),
      });
      return;
    }

    const { type, optionText, selector } = precondition;
    let result = false;
    let details = '';

    switch (type) {
      case 'option_exists':
        {

          // Check if an option exists in a dropdown
          if (!optionText) {
            details = 'No optionText provided for option_exists';
            break;
          }
          // First try to find the dropdown and open it
          const dropdownTrigger = this.findDropdownTrigger(action.target);

          if (dropdownTrigger) {
            this.logger.log('[OnboardingSDK] 🔍 Opening dropdown to check for option:', optionText);

            // Open dropdown
            this._botActionDepth++;
            try {
              this.actionExecutor.openDropdown(dropdownTrigger);
            } finally {
              this._botActionDepth--;
            }

            // Wait for dropdown to open
            await new Promise(r => setTimeout(r, 400));

            // Look for the option
            const option = this.findVisibleDropdownOption(optionText);
            result = !!option;

            // Also try scrolling to find it
            if (!result) {
              const scrolledOption = await this.scrollToDropdownOption(optionText);
              result = !!scrolledOption;
            }

            details = result
              ? `Option "${optionText}" found in dropdown`
              : `Option "${optionText}" NOT found in dropdown`;

            // Close dropdown properly - wrap in _botActionDepth so PageTracker doesn't record as user action
            this._botActionDepth++;
            try {
              await new Promise(r => setTimeout(r, 100));
              // Dispatch on activeElement or body to ensure target is always an HTMLElement
              // (dispatching on document causes PageTracker to crash when e.target is Document)
              const escapeTarget = document.activeElement instanceof HTMLElement
                ? document.activeElement
                : document.body;
              if (escapeTarget) {
                escapeTarget.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
              }
              await new Promise(r => setTimeout(r, 100));
              // Also click body as backup
              document.body.dispatchEvent(new MouseEvent('click', { bubbles: true }));
            } finally {
              this._botActionDepth--;
            }

            // Clear any highlight
            this.overlay.clearHighlight();
          } else {
            details = 'Could not find dropdown trigger';
          }
        }
        break;

      case 'element_visible':
        // Check if an element is visible on the page
        {
          const usedSelector = selector || action.target.selector;
          if (!usedSelector) {
            details = 'No selector provided for element_visible';
            break;
          }
          let el: HTMLElement | null = null;
          try {
            el = document.querySelector(usedSelector) as HTMLElement | null;
          } catch (e) {
            details = `Invalid selector for element_visible: "${usedSelector}"`;
            break;
          }
          result = !!(el && HybridTargetResolver.isVisible(el));
          details = result
            ? `Element "${usedSelector}" is visible`
            : `Element "${usedSelector}" is NOT visible or not found`;
        }
        break;

      case 'element_enabled':
        // Check if an element is enabled (not disabled)
        {
          const usedSelector = selector || action.target.selector;
          if (!usedSelector) {
            details = 'No selector provided for element_enabled';
            break;
          }
          let el: HTMLElement | null = null;
          try {
            el = document.querySelector(usedSelector) as HTMLElement | null;
          } catch (e) {
            details = `Invalid selector for element_enabled: "${usedSelector}"`;
            break;
          }
          if (el) {
            const isDisabled = el.hasAttribute('disabled') ||
              el.getAttribute('aria-disabled') === 'true' ||
              el.classList.contains('disabled') ||
              ((el as any).disabled === true);
            result = !isDisabled;
            details = result
              ? `Element "${usedSelector}" is enabled`
              : `Element "${usedSelector}" is disabled`;
          } else {
            details = `Element "${usedSelector}" not found`;
          }
        }
        break;

      default:
        details = `Unknown precondition type: ${type}`;
    }

    this.logger.log(`[OnboardingSDK] 🔍 Precondition check result: ${result} - ${details}`);

    this.sendMetadata({
      type: 'precondition_result',
      preconditionType: type,
      optionText: optionText,
      result,
      details,
      timestamp: Date.now(),
    });
  }

  /**
   * Handle selecting multiple options from a dropdown.
   * This handles the complex flow of:
   * 1. Opening the dropdown
   * 2. Clicking each value in selectedValues
   * 3. Closing the dropdown
   */
  private async handleMultiSelectAction(action: HybridUIAction): Promise<void> {
    const selectedValues = action.target.selectedValues || [];
    this.logger.log('[OnboardingSDK] 📋 handleMultiSelectAction for:', selectedValues);

    if (selectedValues.length === 0) {
      this.logger.warn('[OnboardingSDK] ⚠️ No values to select in multi_select action');
      this.sendMetadata({
        type: 'bot_action_complete',
        action: 'multi_select',
        success: false,
        target: action.target.text || action.target.selector || 'unknown',
        error: 'No values provided for multi-select',
        timestamp: Date.now(),
      });
      return;
    }

    // Step 1: Find the dropdown trigger
    const dropdownTrigger = this.findDropdownTrigger(action.target);
    if (!dropdownTrigger) {
      this.logger.error('[OnboardingSDK] ❌ Could not find dropdown trigger for multi-select');
      this.sendMetadata({
        type: 'bot_action_complete',
        action: 'multi_select',
        success: false,
        target: action.target.text || action.target.selector || 'unknown',
        error: 'Dropdown trigger not found',
        timestamp: Date.now(),
      });
      return;
    }

    // Step 2: Open the dropdown
    HybridTargetResolver.scrollIntoView(dropdownTrigger);
    await new Promise(r => setTimeout(r, 200));

    // this.overlay.highlightResolvedElement(dropdownTrigger, {
    //   color: 'rgba(124, 58, 237, 0.5)',
    //   padding: 8
    // });
    await this.overlay.moveToElementAndClick(dropdownTrigger);

    this.logger.log('[OnboardingSDK] 📂 Opening multi-select dropdown...');
    this._botActionDepth++;
    try {
      this.actionExecutor.openDropdown(dropdownTrigger);
    } finally {
      this._botActionDepth--;
    }

    // Wait for dropdown to open
    await new Promise(r => setTimeout(r, 300));

    // Step 3: Click each value
    const successfulSelections: string[] = [];
    const failedSelections: string[] = [];

    for (let i = 0; i < selectedValues.length; i++) {
      const value = selectedValues[i];
      this.logger.log(`[OnboardingSDK] 📋 Selecting option ${i + 1}/${selectedValues.length}: "${value}"`);

      // Find the option (may need to wait if dropdown closed after previous selection)
      let optionElement = this.findVisibleDropdownOption(value);

      // If not found, dropdown might have closed - try to reopen
      if (!optionElement) {
        this.logger.log(`[OnboardingSDK] ⚠️ Option not visible, checking if dropdown closed...`);

        // Try reopening the dropdown
        this._botActionDepth++;
        try {
          this.actionExecutor.openDropdown(dropdownTrigger);
        } finally {
          this._botActionDepth--;
        }

        // Wait for dropdown to open and try again
        await new Promise(r => setTimeout(r, 300));
        optionElement = this.findVisibleDropdownOption(value);

        // Still not found? Try scrolling within dropdown
        if (!optionElement) {
          const scrolledOption = await this.scrollToDropdownOption(value);
          if (scrolledOption) {
            optionElement = scrolledOption;
          }
        }
      }

      if (optionElement) {
        // Check if already selected (for multi-select, might be toggled)
        const isAlreadySelected =
          optionElement.getAttribute('aria-selected') === 'true' ||
          optionElement.classList.contains('selected') ||
          optionElement.closest('[aria-selected="true"]') !== null;

        if (!isAlreadySelected) {
          // Click to select - simulate proper click events for React compatibility
          this._botActionDepth++;
          try {
            const rect = optionElement.getBoundingClientRect();
            const clickX = rect.left + rect.width / 2;
            const clickY = rect.top + rect.height / 2;

            // Dispatch mousedown, mouseup, click sequence for React/Vue compatibility
            const commonOptions = {
              bubbles: true,
              cancelable: true,
              view: window,
              clientX: clickX,
              clientY: clickY,
            };

            optionElement.dispatchEvent(new MouseEvent('mousedown', commonOptions));
            optionElement.dispatchEvent(new MouseEvent('mouseup', commonOptions));
            optionElement.dispatchEvent(new MouseEvent('click', commonOptions));

            await new Promise(r => setTimeout(r, 200)); // Wait between selections
            successfulSelections.push(value);
            this.logger.log(`[OnboardingSDK] ✅ Selected: "${value}"`);
          } finally {
            this._botActionDepth--;
          }
        } else {
          successfulSelections.push(value);
          this.logger.log(`[OnboardingSDK] ✅ Already selected: "${value}"`);
        }
      } else {
        failedSelections.push(value);
        this.logger.warn(`[OnboardingSDK] ⚠️ Option not found after retries: "${value}"`);
      }
    }

    // Step 4: Close the dropdown - wrap in _botActionDepth so PageTracker doesn't record as user action
    await new Promise(r => setTimeout(r, 200));

    this._botActionDepth++;
    try {
      // Try multiple methods to close dropdown
      // Method 1: Press Escape
      // Dispatch on activeElement or body to ensure target is always an HTMLElement
      // (dispatching on document causes PageTracker to crash when e.target is Document)
      const escapeTarget = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : document.body;
      if (escapeTarget) {
        escapeTarget.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      }
      await new Promise(r => setTimeout(r, 100));

      // Method 2: Click outside (on an overlay if exists, or body)
      const overlay = document.querySelector('[class*="backdrop"], [class*="overlay"], .MuiBackdrop-root');
      if (overlay && overlay instanceof HTMLElement) {
        overlay.click();
      } else {
        // Click on body at a safe position
        document.body.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      }
    } finally {
      this._botActionDepth--;
    }

    // Clear highlight
    this.overlay.clearHighlight();

    // Report result
    const success = successfulSelections.length > 0;
    this.logger.log(
      `[OnboardingSDK] 📋 Multi-select complete: ` +
      `${successfulSelections.length} selected, ${failedSelections.length} failed`
    );

    this.sendMetadata({
      type: 'bot_action_complete',
      action: 'multi_select',
      success,
      target: action.target.text || action.target.selector || 'unknown',
      selectedValues: successfulSelections,
      failedValues: failedSelections,
      timestamp: Date.now(),
    });
  }

  /**
   * Handle clicking a dropdown option when optionText is specified.
   * This handles the complex flow of:
   * 1. Opening the dropdown if not already open
   * 2. Waiting for options to appear
   * 3. Finding and clicking the actual option
   */
  private async handleDropdownOptionClick(action: HybridUIAction): Promise<void> {
    const optionText = action.target.optionText!;
    this.logger.log('[OnboardingSDK] 🎯 handleDropdownOptionClick for:', optionText);

    // Step 1: Try to find the option directly (dropdown might already be open)
    let optionElement = this.findVisibleDropdownOption(optionText);

    if (optionElement) {
      this.logger.log('[OnboardingSDK] ✅ Option already visible, clicking directly');
      await this.clickDropdownOption(optionElement, action);
      return;
    }

    // Step 2: Option not found — check if dropdown is already open
    const dropdownAlreadyOpen = this.isDropdownCurrentlyOpen();
    this.logger.log('[OnboardingSDK] 🔍 Option not visible, dropdown already open:', dropdownAlreadyOpen);

    // Find the dropdown trigger (combobox input or select control)
    const dropdownTrigger = this.findDropdownTrigger(action.target);
    if (!dropdownTrigger) {
      this.logger.error('[OnboardingSDK] ❌ Could not find dropdown trigger');
      this.sendMetadata({
        type: 'bot_action_complete',
        action: 'click',
        success: false,
        target: optionText,
        error: 'Dropdown trigger not found',
        timestamp: Date.now(),
      });
      return;
    }

    // Step 2a: If dropdown is already open, try scrolling to the option first
    // (it may be outside the virtual scroll render window)
    if (dropdownAlreadyOpen) {
      this.logger.log('[OnboardingSDK] 📜 Dropdown open but option not visible — trying scroll first');
      const scrolledOption = await this.scrollToDropdownOption(optionText);
      if (scrolledOption) {
        this.logger.log('[OnboardingSDK] ✅ Found option after scrolling');
        await this.clickDropdownOption(scrolledOption, action);
        return;
      }
    }

    // Step 2b: Try typing into the search input to filter the list
    // This is critical for virtual-scrolled dropdowns where only visible items are in DOM
    const searchInput = this.findDropdownSearchInput(dropdownTrigger);
    if (searchInput) {
      this.logger.log('[OnboardingSDK] ⌨️ Typing to filter dropdown:', optionText);
      optionElement = await this.typeToFilterDropdown(searchInput, optionText);
      if (optionElement) {
        this.logger.log('[OnboardingSDK] ✅ Found option after typing to filter');
        await this.clickDropdownOption(optionElement, action);
        return;
      }
      this.logger.log('[OnboardingSDK] ⚠️ Typing to filter did not reveal the option');
    }

    // Step 3: Dropdown is NOT open — open it, then search
    if (!dropdownAlreadyOpen) {
      // Highlight and animate cursor to dropdown trigger
      HybridTargetResolver.scrollIntoView(dropdownTrigger);
      await new Promise(r => setTimeout(r, 200));

      await this.overlay.moveToElementAndClick(dropdownTrigger);

      // Click to open dropdown — safe because dropdown is confirmed closed
      this.logger.log('[OnboardingSDK] 📂 Opening dropdown...');
      this._botActionDepth++;
      try {
        this.actionExecutor.openDropdown(dropdownTrigger);
      } finally {
        this._botActionDepth--;
      }

      // Wait for dropdown animation
      await new Promise(r => setTimeout(r, 300));

      // After opening, first try direct find
      optionElement = this.findVisibleDropdownOption(optionText);
      if (optionElement) {
        this.logger.log('[OnboardingSDK] ✅ Option visible after opening dropdown');
        await this.clickDropdownOption(optionElement, action);
        return;
      }

      // Try typing to filter after opening
      const newSearchInput = this.findDropdownSearchInput(dropdownTrigger);
      if (newSearchInput) {
        this.logger.log('[OnboardingSDK] ⌨️ Typing to filter after opening dropdown:', optionText);
        optionElement = await this.typeToFilterDropdown(newSearchInput, optionText);
        if (optionElement) {
          this.logger.log('[OnboardingSDK] ✅ Found option after typing to filter');
          await this.clickDropdownOption(optionElement, action);
          return;
        }
      }

      // Try scrolling as last resort
      const scrolledOption = await this.scrollToDropdownOption(optionText);
      if (scrolledOption) {
        this.logger.log('[OnboardingSDK] ✅ Found option after scrolling');
        await this.clickDropdownOption(scrolledOption, action);
        return;
      }
    }

    // Step 4: All strategies failed — poll briefly as final attempt
    const maxWaitMs = 1500;
    const pollInterval = 150;
    let waited = 0;

    while (waited < maxWaitMs) {
      optionElement = this.findVisibleDropdownOption(optionText);
      if (optionElement) {
        this.logger.log('[OnboardingSDK] ✅ Option appeared after', waited, 'ms (final poll)');
        break;
      }

      await new Promise(r => setTimeout(r, pollInterval));
      waited += pollInterval;
    }

    if (!optionElement) {
      this.logger.error('[OnboardingSDK] ❌ All strategies failed for option:', optionText);

      // Log what options ARE visible for debugging
      const visibleOptions = document.querySelectorAll('[role="option"], [class*="option"], nz-option-item, .ant-select-item-option');
      const optionTexts = Array.from(visibleOptions)
        .slice(0, 10)
        .map(el => el.textContent?.trim().slice(0, 50))
        .filter(Boolean);
      if (optionTexts.length > 0) {
        this.logger.log('[OnboardingSDK] Available options:', optionTexts);
      } else {
        this.logger.log('[OnboardingSDK] No options visible - dropdown may not have opened');
      }

      this.sendMetadata({
        type: 'bot_action_complete',
        action: 'click',
        success: false,
        target: optionText,
        error: `Option "${optionText}" not found in dropdown`,
        timestamp: Date.now(),
      });

      this.overlay.clearHighlight();
      return;
    }

    // Click the found option
    await this.clickDropdownOption(optionElement, action);
  }

  /**
   * Check if a dropdown/listbox is currently open on the page.
   * Delegates to shared dropdown-helpers module.
   */
  private isDropdownCurrentlyOpen(): boolean {
    return _isDropdownCurrentlyOpen(HybridTargetResolver.isVisible);
  }

  /**
   * Find the search input within or associated with a dropdown.
   * Delegates to shared dropdown-helpers module.
   */
  private findDropdownSearchInput(trigger: HTMLElement): HTMLInputElement | null {
    return _findDropdownSearchInput(trigger, HybridTargetResolver.isVisible);
  }

  /**
   * Type text into a dropdown search input to filter options, then find the target option.
   * Delegates to shared dropdown-helpers module.
   */
  private async typeToFilterDropdown(searchInput: HTMLInputElement, optionText: string): Promise<HTMLElement | null> {
    return _typeToFilterDropdown(searchInput, optionText, HybridTargetResolver.isVisible);
  }


  /**
   * Find a visible dropdown option by text.
   * Delegates to shared dropdown-helpers module.
   */
  private findVisibleDropdownOption(optionText: string): HTMLElement | null {
    return _findVisibleDropdownOption(optionText, HybridTargetResolver.isVisible);
  }

  /**
   * Try to scroll within the dropdown to find an option that might be off-screen.
   * Returns the element if found after scrolling, null otherwise.
   */
  private async scrollToDropdownOption(optionText: string): Promise<HTMLElement | null> {
    const normalizedText = optionText.toLowerCase().trim();
    this.logger.log(`[OnboardingSDK] 🔍 Scrolling dropdown to find: "${optionText}"`);

    // Find the scrollable dropdown container (including CDK virtual scroll)
    // IMPORTANT: For Angular CDK virtual scroll, the scrollable element is
    // cdk-virtual-scroll-viewport, NOT nz-option-container (the outer shell)
    // or cdk-virtual-scroll-content-wrapper (the inner content holder).
    // Only scrolling the viewport triggers CDK to re-render items.
    const scrollableSelectors = [
      'cdk-virtual-scroll-viewport',                    // Angular CDK virtual scroll (tag)
      '.cdk-virtual-scroll-viewport',                   // CDK virtual scroll (class)
      '[role="listbox"]',                               // Generic listbox
      '[class*="-menu"] [class*="menu-list"]',          // React Select menu list
      '[class*="options-container"]',                    // Generic options container
      '[class*="virtual-list"]',                        // Virtual list
    ];

    for (const selector of scrollableSelectors) {
      let containers: NodeListOf<Element>;
      try {
        containers = document.querySelectorAll(selector);
      } catch {
        continue;
      }

      for (const container of containers) {
        const scrollEl = container as HTMLElement;

        // Skip if not visible
        if (!HybridTargetResolver.isVisible(scrollEl)) continue;

        // Skip if not actually scrollable (scrollHeight must exceed clientHeight)
        if (scrollEl.scrollHeight <= scrollEl.clientHeight + 5) continue;

        // Verify this is a real scroll container, not just a wrapper
        const computedOverflow = window.getComputedStyle(scrollEl).overflowY;
        if (computedOverflow === 'visible' || computedOverflow === 'hidden') {
          this.logger.log(`[OnboardingSDK] Skipping non-scrollable container (${selector}), overflow-y: ${computedOverflow}`);
          continue;
        }

        this.logger.log(`[OnboardingSDK] Found scrollable container (${selector}), scrollHeight: ${scrollEl.scrollHeight}, clientHeight: ${scrollEl.clientHeight}, overflow-y: ${computedOverflow}`);

        // Scroll down in increments, checking for the option each time
        // Use smaller increments for virtual scroll (items may be small)
        const scrollIncrement = 150;
        const maxScrolls = Math.ceil(scrollEl.scrollHeight / scrollIncrement) + 2;

        // Start from current position, scroll down
        const startScroll = scrollEl.scrollTop;
        for (let i = 0; i < maxScrolls; i++) {
          scrollEl.scrollTop = i * scrollIncrement;

          // For virtual scroll, we need to wait for new items to render
          await new Promise(r => setTimeout(r, 120));

          // Check for the option in the currently rendered items
          const optionSelectors = '[role="option"], [class*="option"]:not([class*="singleValue"]), nz-option-item, .ant-select-item-option, .ant-select-item';
          const options = scrollEl.querySelectorAll(optionSelectors);
          for (const option of options) {
            const optEl = option as HTMLElement;
            const optRect = optEl.getBoundingClientRect();
            // Skip zero-size elements (not actually rendered)
            if (optRect.height === 0 || optRect.width === 0) continue;

            const text = optEl.textContent?.trim().toLowerCase() || '';
            if (text === normalizedText || text.includes(normalizedText)) {
              this.logger.log(`[OnboardingSDK] ✅ Found option after scrolling to position ${scrollEl.scrollTop}: "${optionText}"`);
              // Ensure it's in view
              optEl.scrollIntoView({ block: 'center' });
              await new Promise(r => setTimeout(r, 100));
              return optEl;
            }
          }
        }

        // Restore scroll position if we didn't find it
        scrollEl.scrollTop = startScroll;
      }
    }

    this.logger.log(`[OnboardingSDK] Option not found after scrolling: "${optionText}"`);
    return null;
  }

  /**
   * Validate that an element is a valid dropdown element (not SDK UI, visible, clickable)
   * 
   * @param el - The element to validate
   * @param requireDropdownAncestor - If true, requires element to have a dropdown-like ancestor (for Strategy 5)
   * @returns true if the element is valid for dropdown interaction
   */
  private isValidDropdownElement(el: HTMLElement | null, requireDropdownAncestor: boolean = false): boolean {
    // 1. Reject null
    if (!el) {
      this.logger.warn('[OnboardingSDK] ⚠️ Dropdown validation: element is null');
      return false;
    }

    // 2. Exclude SDK widget elements (overlays, cursor, highlights, etc.)
    if (isSDKWidget(el)) {
      this.logger.warn('[OnboardingSDK] ⚠️ Dropdown validation: element is inside SDK widget', {
        id: el.id,
        className: el.className,
        tagName: el.tagName
      });
      return false;
    }

    // 3. Confirm visibility/clickability
    // Check aria-hidden on element and ancestors
    if (el.getAttribute('aria-hidden') === 'true' || el.closest('[aria-hidden="true"]')) {
      this.logger.warn('[OnboardingSDK] ⚠️ Dropdown validation: element or ancestor has aria-hidden="true"', {
        id: el.id,
        className: el.className
      });
      return false;
    }

    // Check computed styles
    const computedStyle = window.getComputedStyle(el);
    if (computedStyle.display === 'none') {
      this.logger.warn('[OnboardingSDK] ⚠️ Dropdown validation: element has display:none', {
        id: el.id,
        className: el.className
      });
      return false;
    }

    if (computedStyle.visibility === 'hidden') {
      this.logger.warn('[OnboardingSDK] ⚠️ Dropdown validation: element has visibility:hidden', {
        id: el.id,
        className: el.className
      });
      return false;
    }

    // Check pointer-events (elements with pointer-events:none cannot be clicked)
    if (computedStyle.pointerEvents === 'none') {
      this.logger.warn('[OnboardingSDK] ⚠️ Dropdown validation: element has pointer-events:none', {
        id: el.id,
        className: el.className
      });
      return false;
    }

    // Check opacity (elements with opacity 0 are not visible)
    if (computedStyle.opacity === '0') {
      this.logger.warn('[OnboardingSDK] ⚠️ Dropdown validation: element has opacity:0', {
        id: el.id,
        className: el.className
      });
      return false;
    }

    // Check offsetParent (null means not rendered, except for fixed/sticky positioned elements)
    const position = computedStyle.position;
    if (el.offsetParent === null && position !== 'fixed' && position !== 'sticky') {
      // Additional check: element might be in a fixed/sticky ancestor
      let parent = el.parentElement;
      let hasFixedAncestor = false;
      while (parent) {
        const parentStyle = window.getComputedStyle(parent);
        if (parentStyle.position === 'fixed' || parentStyle.position === 'sticky') {
          hasFixedAncestor = true;
          break;
        }
        parent = parent.parentElement;
      }

      if (!hasFixedAncestor) {
        this.logger.warn('[OnboardingSDK] ⚠️ Dropdown validation: element has null offsetParent (not rendered)', {
          id: el.id,
          className: el.className
        });
        return false;
      }
    }

    // Check bounding rect dimensions
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      this.logger.warn('[OnboardingSDK] ⚠️ Dropdown validation: element has zero dimensions', {
        id: el.id,
        className: el.className,
        width: rect.width,
        height: rect.height
      });
      return false;
    }

    // 4. For Strategy 5: require dropdown-like ancestor to reduce false positives
    if (requireDropdownAncestor) {
      const hasDropdownAncestor =
        el.closest('[role="combobox"]') !== null ||
        el.closest('[aria-haspopup="listbox"]') !== null ||
        el.closest('[aria-haspopup="true"]') !== null ||
        el.closest('[class*="select"]') !== null ||
        el.closest('[class*="Select"]') !== null ||
        el.closest('[class*="dropdown"]') !== null ||
        el.closest('[class*="Dropdown"]') !== null ||
        el.closest('select') !== null;

      if (!hasDropdownAncestor) {
        this.logger.warn('[OnboardingSDK] ⚠️ Dropdown validation (Strategy 5): element lacks dropdown-like ancestor', {
          id: el.id,
          className: el.className,
          tagName: el.tagName
        });
        return false;
      }
    }

    return true;
  }

  /**
   * Find the dropdown trigger element (combobox input or select control)
   * 
   * Priority:
   * 1. dropdownTriggerSelector - captured CSS selector for the trigger
   * 2. labelText - find dropdown by its form label (label, div, span, etc.)
   * 3. Find currently open/focused dropdown on page
   * 4. Fallback strategies
   */
  private findDropdownTrigger(target: HybridTarget): HTMLElement | null {
    // STRATEGY 1: Use captured dropdown trigger selector (HIGHEST PRIORITY)
    if (target.dropdownTriggerSelector) {
      this.logger.log('[OnboardingSDK] 🎯 Using dropdownTriggerSelector:', target.dropdownTriggerSelector);
      try {
        const el = querySelectorWithAngularFallback(target.dropdownTriggerSelector);
        if (el) {
          const container = this.getClickableDropdownContainer(el);
          if (this.isValidDropdownElement(container)) {
            this.logger.log('[OnboardingSDK] ✅ Found trigger via dropdownTriggerSelector');
            return container;
          }
          this.logger.warn('[OnboardingSDK] ⚠️ Strategy 1: dropdownTriggerSelector element failed validation');
        }
      } catch (e) {
        this.logger.warn('[OnboardingSDK] Invalid dropdownTriggerSelector:', e);
      }
    }

    // STRATEGY 2: Find by labelText (works with label, div, span - not just <label>)
    if (target.labelText) {
      this.logger.log('[OnboardingSDK] 🔍 Finding dropdown by label:', target.labelText);
      const trigger = this.findDropdownByLabel(target.labelText);
      if (trigger) {
        this.logger.log('[OnboardingSDK] ✅ Found trigger via labelText');
        return trigger;
      }
    }

    // STRATEGY 3: Find currently focused/open dropdown on the page
    // React Select keeps focus on input when menu is open
    const focusedCombobox = document.querySelector('input[role="combobox"]:focus') as HTMLElement;
    if (focusedCombobox) {
      const focusedContainer = this.getClickableDropdownContainer(focusedCombobox);
      if (this.isValidDropdownElement(focusedContainer)) {
        this.logger.log('[OnboardingSDK] ✅ Found focused combobox');
        return focusedContainer;
      }
      this.logger.warn('[OnboardingSDK] ⚠️ Strategy 3: focused combobox failed validation');
    }

    const openCombobox = document.querySelector('input[role="combobox"][aria-expanded="true"]') as HTMLElement;
    if (openCombobox) {
      const openContainer = this.getClickableDropdownContainer(openCombobox);
      if (this.isValidDropdownElement(openContainer)) {
        this.logger.log('[OnboardingSDK] ✅ Found expanded combobox');
        return openContainer;
      }
      this.logger.warn('[OnboardingSDK] ⚠️ Strategy 3: expanded combobox failed validation');
    }

    // STRATEGY 4: Try selector if provided (strip virtual attributes)
    if (target.selector) {
      // First normalize escaped characters from CSS.escape/JSON serialization
      const normalizedSelector = target.selector
        .replace(/\\"/g, '"')      // Unescape quotes: \" -> "
        .replace(/\\ /g, ' ')      // Unescape spaces: \  -> (space)
        .replace(/\\\\/g, '\\');   // Unescape backslashes: \\ -> \

      const cleanSelector = normalizedSelector
        .replace(/\[data-option-text="[^"]*"\]/g, '')
        .replace(/\[data-label="[^"]*"\]/g, '')
        .replace(/\[role="option"\]/g, '')  // Don't transform to combobox - it's unreliable
        .trim();

      // Only try if we have a meaningful selector left
      if (cleanSelector && cleanSelector !== '') {
        try {
          const el = document.querySelector(cleanSelector) as HTMLElement;
          if (el) {
            const selectorContainer = this.getClickableDropdownContainer(el);
            if (this.isValidDropdownElement(selectorContainer)) {
              this.logger.log('[OnboardingSDK] ✅ Found trigger via cleaned selector');
              return selectorContainer;
            }
            this.logger.warn('[OnboardingSDK] ⚠️ Strategy 4: selector element failed validation');
          }
        } catch (e) {
          this.logger.warn('[OnboardingSDK] Invalid selector:', cleanSelector);
        }
      }
    }

    // STRATEGY 5: Search for display value text in dropdown controls
    // Uses stricter validation requiring dropdown-like ancestor to reduce false positives
    if (target.text || target.optionText) {
      const searchText = (target.text || target.optionText || '').toLowerCase();
      // Look in common dropdown display value containers
      const displayValueSelectors = [
        '[class*="singleValue"]',
        '[class*="single-value"]',
        '[class*="select__single-value"]',
        '.MuiSelect-select',
        '[class*="placeholder"]'
      ];

      for (const selector of displayValueSelectors) {
        const elements = document.querySelectorAll(selector);
        for (const el of elements) {
          const text = el.textContent?.trim().toLowerCase() || '';
          if (text === searchText || text.includes(searchText)) {
            const container = this.getClickableDropdownContainer(el as HTMLElement);
            // Strategy 5 uses stricter validation: requireDropdownAncestor = true
            if (this.isValidDropdownElement(container, true)) {
              this.logger.log('[OnboardingSDK] ✅ Found trigger via display value match');
              return container;
            }
            this.logger.warn('[OnboardingSDK] ⚠️ Strategy 5: display value match element failed validation', {
              searchText,
              selector,
              elementText: text
            });
          }
        }
      }
    }

    this.logger.warn('[OnboardingSDK] ❌ Could not find dropdown trigger');
    return null;
  }

  /**
   * Find dropdown by its label text (searches label, div, span elements)
   * Works with proper <label> elements AND improper div/span "labels"
   */
  private findDropdownByLabel(labelText: string): HTMLElement | null {
    const normalizedLabel = labelText.toLowerCase().trim();

    // Search for text in various label-like elements
    const labelSelectors = [
      'label',
      '[class*="label"]',
      '[class*="Label"]',
      '[class*="form-label"]',
      '.MuiFormLabel-root',
      '.MuiInputLabel-root',
    ];

    for (const selector of labelSelectors) {
      const labels = document.querySelectorAll(selector);
      for (const label of labels) {
        const text = label.textContent?.trim().toLowerCase().replace(/\*$/, '').trim() || '';
        if (text === normalizedLabel || text.includes(normalizedLabel)) {
          // Found label, now find the associated dropdown
          const labelEl = label as HTMLElement;

          // Check for htmlFor attribute
          if (labelEl.tagName === 'LABEL' && (labelEl as HTMLLabelElement).htmlFor) {
            const input = document.getElementById((labelEl as HTMLLabelElement).htmlFor);
            if (input) {
              return this.getClickableDropdownContainer(input as HTMLElement);
            }
          }

          // Check for aria-labelledby relationship
          const labelId = labelEl.id;
          if (labelId) {
            const dropdown = document.querySelector(`[aria-labelledby="${labelId}"]`) as HTMLElement;
            if (dropdown) {
              return this.getClickableDropdownContainer(dropdown);
            }
          }

          // Look for sibling or child dropdown
          const container = labelEl.closest('[class*="field"], [class*="form-group"], [class*="FormControl"]') || labelEl.parentElement;
          if (container) {
            const dropdownInContainer = container.querySelector(
              'input[role="combobox"], select, [aria-haspopup="listbox"], [class*="control"]'
            ) as HTMLElement;
            if (dropdownInContainer) {
              return this.getClickableDropdownContainer(dropdownInContainer);
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Get the clickable container for a dropdown input
   * (The input itself may not be clickable, but its container is)
   */
  private getClickableDropdownContainer(element: HTMLElement): HTMLElement {
    // Common patterns for clickable dropdown containers
    const container = element.closest('[class*="-control"]') ||   // React Select
      element.closest('[class*="control"]') ||
      element.closest('[class*="trigger"]') ||
      element.closest('[aria-haspopup]') ||
      element.closest('.MuiSelect-select')?.parentElement ||
      element.closest('[class*="select"]');

    return (container as HTMLElement) || element;
  }

  /**
   * Click a dropdown option with proper animation and feedback
   */
  private async clickDropdownOption(optionElement: HTMLElement, action: HybridUIAction): Promise<void> {
    // Highlight and animate cursor to option
    // this.overlay.highlightResolvedElement(optionElement, {
    //   color: 'rgba(124, 58, 237, 0.5)',
    //   padding: 4
    // });
    await this.overlay.moveToElementAndClick(optionElement);
    await new Promise(r => setTimeout(r, 150));

    // Click the option
    this.logger.log('[OnboardingSDK] 👆 Clicking dropdown option:', optionElement.textContent?.trim());

    // Mark bot action RIGHT BEFORE the click
    this._botActionDepth++;

    try {
      // Use React onClick if available, otherwise DOM events
      const hasReactHandler = this.actionExecutor.triggerReactOnClickPublic(optionElement);
      if (hasReactHandler) {
        this.logger.log('[OnboardingSDK] ✅ React onClick triggered for option');
      } else {
        // Fallback to DOM events
        const rect = optionElement.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const eventOptions: MouseEventInit = {
          bubbles: true,
          cancelable: true,
          view: window,
          clientX: centerX,
          clientY: centerY,
        };

        optionElement.dispatchEvent(new MouseEvent('mousedown', eventOptions));
        optionElement.dispatchEvent(new MouseEvent('mouseup', eventOptions));
        optionElement.dispatchEvent(new MouseEvent('click', eventOptions));
      }
    } finally {
      // Always decrement after click
      this._botActionDepth--;
    }

    this.logger.log('[OnboardingSDK] ✅ Dropdown option clicked successfully');

    // CRITICAL: Capture DOM after dropdown selection
    // Dropdowns often change the UI significantly (close menu, show selected value)
    if (this.domIntrospector) {
      await new Promise(r => setTimeout(r, 150)); // Wait for dropdown to close
      this.domIntrospector.scheduleCapture('post-dropdown-select');
      this.logger.log('[OnboardingSDK] 📸 DOM snapshot sent after dropdown selection');
    }

    // Send success event
    this.sendMetadata({
      type: 'bot_action_complete',
      action: 'click',
      success: true,
      target: action.target.optionText || action.target.text || 'dropdown option',
      timestamp: Date.now(),
    });
    this.logger.log('[OnboardingSDK] 📤 Sent bot_action_complete for auto-advance');

    // Clear all overlay artifacts after delay
    await new Promise(r => setTimeout(r, 200));
    setTimeout(() => {
      this.overlay.cleanup();
    }, 600);
  }

  /**
   * Find collapsed sidebar container that contains the element
   * Returns the sidebar element if found, null otherwise
   */
  private findCollapsedSidebar(element: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = element.parentElement;

    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const width = current.getBoundingClientRect().width;
      const height = current.getBoundingClientRect().height;

      // Check for sidebar-like characteristics:
      // - Narrow width (< 80px is typically collapsed)
      // - Has transition property (for expand animation)
      // - Tall element (sidebar typically spans most of viewport)
      const hasTransition = style.transition.includes('width') ||
        style.transitionProperty.includes('width') ||
        style.transitionProperty === 'all';
      const isNarrow = width < 80;
      const isTall = height > window.innerHeight * 0.5;
      const currentClassName = typeof current.className === 'string' ? current.className : '';
      const hasSidebarClasses = currentClassName.includes('sidebar') ||
        currentClassName.includes('nav') ||
        currentClassName.includes('menu');

      // Look for w-12 class (common Tailwind collapsed sidebar)
      const hasTailwindCollapsed = currentClassName.includes('w-12') ||
        currentClassName.includes('w-14') ||
        currentClassName.includes('w-16');

      if ((isNarrow && isTall && (hasTransition || hasSidebarClasses)) || hasTailwindCollapsed) {
        this.logger.log('[OnboardingSDK] Detected collapsed sidebar:', {
          width,
          height,
          hasTransition,
          hasTailwindCollapsed,
          classes: current.className.substring(0, 100)
        });
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  // ============================================================================
  // FAST EXECUTION METHODS
  // ============================================================================

  /**
   * Execute a single action quickly without cursor animation
   * Used for fast mode where we want minimal visual overhead
   * 
   * @param action The hybrid UI action to execute
   * @returns true if action succeeded
   */
  private async executeActionFast(action: HybridUIAction): Promise<boolean> {
    this.logger.log('[OnboardingSDK] ⚡ Fast executing action:', action.action);

    // Resolve element
    let element = HybridTargetResolver.resolveTarget(action.target);

    if (!element) {
      // Quick retry for dynamic content
      await new Promise(r => setTimeout(r, 150));
      element = HybridTargetResolver.resolveTarget(action.target);
      if (!element) {
        this.logger.warn('[OnboardingSDK] ⚠️ Fast action target not found');
        return false;
      }
    }

    // Fast scroll (no smooth animation)
    element.scrollIntoView({ behavior: 'auto', block: 'center' });
    await new Promise(r => setTimeout(r, 50));

    // Minimal highlight (no cursor)
    this.overlay.highlightResolvedElement(element, {
      color: 'rgba(124, 58, 237, 0.4)',
      padding: 4
    });
    await new Promise(r => setTimeout(r, 80));

    // Mark bot action RIGHT BEFORE the actual DOM interaction
    this._botActionDepth++;
    let success = false;
    try {
      // Execute action
      success = await this.actionExecutor.executeHybridAction(action, false);
    } finally {
      this._botActionDepth--;
    }

    // Quick cleanup
    await new Promise(r => setTimeout(r, 50));
    this.overlay.clearHighlight();

    return success;
  }

  /**
   * Handle direct API execution request from server
   * Part of Capability-Based Guidance - allows bot to execute APIs directly
   * 
   * The SDK makes the fetch() call with the user's session/cookies,
   * then reports the result back to the bot.
   */
  private async handleExecuteApi(payload: any): Promise<void> {
    this.logger.log('[OnboardingSDK] 🔌 Executing API call:', payload.method, payload.url);

    const {
      method = 'GET',
      url,
      base_url,
      body,
      query_params,
      success_indicator,
      result_extractor,
      error_message_path,
    } = payload;

    // Build full URL
    let fullUrl = url;
    if (base_url) {
      fullUrl = `${base_url.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
    }

    // Add query params for GET requests
    if (query_params && method === 'GET') {
      const params = new URLSearchParams(query_params);
      fullUrl = `${fullUrl}?${params.toString()}`;
    }

    // Validate origin — only allow same-origin requests
    try {
      const resolvedUrl = new URL(fullUrl, window.location.href);
      if (resolvedUrl.origin !== window.location.origin) {
        this.logger.error('[OnboardingSDK] ❌ Cross-origin API request blocked:', resolvedUrl.origin);
        this.sendMetadata({
          type: 'api_execution_result',
          success: false,
          error: `Cross-origin request blocked: ${resolvedUrl.origin} does not match ${window.location.origin}`,
          url: fullUrl,
          method,
          timestamp: Date.now(),
        });
        return;
      }
    } catch {
      this.logger.error('[OnboardingSDK] ❌ Invalid URL:', fullUrl);
      this.sendMetadata({
        type: 'api_execution_result',
        success: false,
        error: `Invalid URL: ${fullUrl}`,
        url: fullUrl,
        method,
        timestamp: Date.now(),
      });
      return;
    }

    // Validate HTTP method
    const ALLOWED_METHODS = ['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE'];
    if (!ALLOWED_METHODS.includes(method.toUpperCase())) {
      this.logger.error('[OnboardingSDK] ❌ HTTP method not allowed:', method);
      this.sendMetadata({
        type: 'api_execution_result',
        success: false,
        error: `HTTP method not allowed: ${method}`,
        url: fullUrl,
        method,
        timestamp: Date.now(),
      });
      return;
    }

    try {
      // Build fetch options - user's cookies are included automatically (same-origin validated above)
      const options: RequestInit = {
        method: method.toUpperCase(),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include', // Safe — same-origin validated above
      };

      if (body && method !== 'GET') {
        options.body = JSON.stringify(body);
      }

      this.logger.log('[OnboardingSDK] 📤 Making API request:', fullUrl);
      const response = await fetch(fullUrl, options);
      
      let data: any;
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }

      this.logger.log('[OnboardingSDK] 📥 API response:', response.status, data);

      // Check success
      const success = response.ok && this.evaluateJsonPathCondition(data, success_indicator);

      // Extract result for bot to report
      const result_text = this.extractJsonPath(data, result_extractor);

      // Extract error message if failed
      let error: string | undefined;
      if (!success) {
        error = this.extractJsonPath(data, error_message_path) || 
                data.error?.message ||
                data.message ||
                `Request failed with status ${response.status}`;
      }

      // Report result back to bot
      this.sendMetadata({
        type: 'api_execution_result',
        success,
        status_code: response.status,
        data,
        result_text,
        error,
        url: fullUrl,
        method,
        timestamp: Date.now(),
      });

      this.logger.log(`[OnboardingSDK] ${success ? '✅' : '❌'} API execution ${success ? 'succeeded' : 'failed'}:`, result_text || error);

    } catch (err) {
      this.logger.error('[OnboardingSDK] ❌ API execution error:', err);
      
      // Report error back to bot
      this.sendMetadata({
        type: 'api_execution_result',
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
        url: fullUrl,
        method,
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Evaluate a JSONPath-style condition (simple implementation)
   * Supports: $.path.to.value == "expected" or just $.path.to.value (truthy check)
   */
  private evaluateJsonPathCondition(data: any, condition?: string): boolean {
    if (!condition) return true;
    
    try {
      if (condition.includes(' == ')) {
        const [path, expected] = condition.split(' == ');
        const actual = this.extractJsonPath(data, path.trim());
        const expectedValue = expected.trim().replace(/^["']|["']$/g, '');
        return String(actual) === expectedValue;
      }
      // Just check if path exists and is truthy
      return !!this.extractJsonPath(data, condition);
    } catch {
      return true; // Assume success if can't evaluate
    }
  }

  /**
   * Extract value from object using simple JSONPath
   * Supports: $.path.to.value or just path.to.value
   */
  private extractJsonPath(data: any, path?: string): any {
    if (!path || !data) return undefined;
    
    const cleanPath = path.replace(/^\$\.?/, '');
    const parts = cleanPath.split('.');
    
    let current = data;
    for (const part of parts) {
      if (current === null || current === undefined) return undefined;
      current = current[part];
    }
    
    return current;
  }

  /**
   * Handle batched navigation request from server
   * Executes multiple navigation steps quickly without LLM round-trips
   */
  private async handleBatchedNavigation(payload: any): Promise<void> {
    this.logger.log('[OnboardingSDK] ⚡ Received batched navigation request');

    if (!this.batchedActionExecutor) {
      this.logger.error('[OnboardingSDK] BatchedActionExecutor not initialized');
      return;
    }

    const steps: BatchStep[] = (payload.steps || []).map((step: any) => {
      // Build target if any targeting info is provided
      // Support both flat (step.text) and nested (step.target.text) formats for compatibility
      const targetData = step.target || step;  // Use nested target if present, else use step directly
      const hasTarget = targetData.text || targetData.selector || targetData.tag;
      const target: HybridTarget | undefined = hasTarget ? {
        text: targetData.text,
        tag: targetData.tag,
        selector: targetData.selector,
        ariaLabel: targetData.aria_label || targetData.ariaLabel,
        role: targetData.role,
        // Rich targeting fields for disambiguation
        labelText: targetData.label_text || targetData.labelText,
        // Fallback selectors - critical for resilient element targeting
        selectorFallbacks: targetData.selector_fallbacks || targetData.selectorFallbacks,
      } : undefined;

      return {
        action: step.action,
        target,
        condition: step.condition,
        waitMs: step.wait_ms || step.waitMs,
      };
    });

    if (steps.length === 0) {
      this.logger.warn('[OnboardingSDK] No steps in batched navigation request');
      return;
    }

    // Enable fast mode during batch execution
    this.fastExecutionMode = true;
    // Mark bot action in progress so click events are tagged correctly
    // For batched navigation, keep depth elevated throughout since all clicks are bot-initiated
    this._botActionDepth++;

    try {
      this.logger.log('[OnboardingSDK] ⚡ Executing batched navigation with steps:',
        steps.map(s => `${s.action}${s.target?.text ? ` "${s.target.text}"` : ''}`).join(' → '));

      const result = await this.batchedActionExecutor.executeBatch({
        steps,
        // Note: We handle completion via return value, not callback
      });

      // CRITICAL: Capture DOM after batched navigation completes
      // This ensures server has fresh DOM state regardless of success/failure
      if (this.domIntrospector) {
        await new Promise(r => setTimeout(r, 200)); // Wait for UI to settle
        this.domIntrospector.scheduleCapture('post-batched-navigation');
        this.logger.log('[OnboardingSDK] 📸 DOM snapshot sent after batched navigation');
      }

      if (result.success) {
        // Send success message back to server
        this.logger.log('[OnboardingSDK] ✅ Batched navigation complete, sending success');
        this.sendMetadata({
          type: 'batched_navigation_complete',
          success: true,
          stepCount: steps.length,
          completedSteps: result.completedSteps,
          timestamp: Date.now(),
        });
      } else {
        this.logger.log(`[OnboardingSDK] ❌ Batched navigation failed at step ${result.completedSteps + 1}, sending failure`);
        this.sendMetadata({
          type: 'batched_navigation_complete',
          success: false,
          error: `Step ${result.completedSteps + 1} failed - check console for details`,
          stepCount: steps.length,
          completedSteps: result.completedSteps,
          timestamp: Date.now(),
        });
      }
    } finally {
      this.fastExecutionMode = false;
      this._botActionDepth--;  // Decrement bot action depth
    }
  }

  /**
   * Start navigation complete detection
   * Call this when you want to monitor for page navigation completion
   */
  public startNavigationDetection(onComplete: (url: string) => void): void {
    if (this.navigationCompleteDetector) {
      this.navigationCompleteDetector.start((url) => {
        this.logger.log('[OnboardingSDK] Navigation complete detected:', url);
        // Send to server
        this.sendMetadata({
          type: 'navigation_complete',
          url,
          timestamp: Date.now(),
        });
        // Call callback
        onComplete(url);
      });
    }
  }

  /**
   * Stop navigation complete detection
   */
  public stopNavigationDetection(): void {
    if (this.navigationCompleteDetector) {
      this.navigationCompleteDetector.stop();
    }
  }

  /**
   * Check if currently in fast execution mode
   */
  public isInFastMode(): boolean {
    return this.fastExecutionMode;
  }

  /**
   * Capture current context
   */
  async captureContext(): Promise<ContextData> {
    const screenshot = this.config.enableScreenCapture
      ? await this.capture.captureWithRedaction()
      : undefined;

    const structure = this.extractPageStructure();

    return {
      screenshot,
      url: getSanitizedUrl(),
      structure,
      timestamp: Date.now()
    };
  }

  /**
   * Extract page structure
   */
  private extractPageStructure(): PageStructure {
    const elements: ElementInfo[] = [];

    // Find interactive elements
    const selectors = [
      'button',
      'a',
      'input',
      'select',
      'textarea',
      '[role="button"]',
      '[onclick]'
    ];

    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach((el) => {
        const rect = el.getBoundingClientRect();
        const visible = rect.width > 0 && rect.height > 0 &&
          rect.top < window.innerHeight &&
          rect.bottom > 0;

        if (visible) {
          elements.push({
            selector: this.getSelector(el),
            type: el.tagName.toLowerCase(),
            text: (el.textContent || '').trim().substring(0, 100),
            visible,
            position: rect,
            attributes: this.getAttributes(el)
          });
        }
      });
    });

    return {
      title: document.title,
      url: getSanitizedUrl(),
      elements,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }

  /**
   * Get unique selector for element
   */
  private getSelector(element: Element): string {
    if (element.id) {
      return `#${element.id}`;
    }

    const path: string[] = [];
    let current: Element | null = element;

    while (current && current !== document.body) {
      let selector = current.tagName.toLowerCase();

      if (current.className) {
        const classes = Array.from(current.classList).slice(0, 2).join('.');
        if (classes) {
          selector += `.${classes}`;
        }
      }

      path.unshift(selector);
      current = current.parentElement;
    }

    return path.join(' > ');
  }

  /**
   * Get element attributes
   */
  private getAttributes(element: Element): Record<string, string> {
    const attributes: Record<string, string> = {};
    const allowedAttrs = ['href', 'type', 'name', 'placeholder', 'aria-label', 'role'];

    allowedAttrs.forEach(attr => {
      const value = element.getAttribute(attr);
      if (value) {
        attributes[attr] = value;
      }
    });

    return attributes;
  }


  /**
   * Public API Methods
   */

  /**
   * Send text message (low-level)
   */
  async sendText(text: string) {
    if (this.pipecatClient && this.isConnected) {
      await this.pipecatClient.sendText(text);
    }
  }

  /**
   * Send a text message with optimistic UI update.
   * Uses custom 'text_message' RTVI message type (Approach B) instead of
   * Pipecat's native send-text, so the server-side handler can run the full
   * processing pipeline (state machine, system prompt refresh, activity
   * recording, goal extraction, transcript recording) — not just LLM injection.
   *
   * In text mode, TTS is skipped (audio_response: false).
   */
  /**
   * Mark the demo intro form as submitted. Called by AgentUI when the form is submitted.
   */
  markDemoIntroFormSubmitted() {
    this.demoIntroFormSubmitted = true;
    // FLO-109 safety net for a session that connected mic-less because it was
    // started outside a user gesture (voicePendingGesture). THIS call runs
    // synchronously from the prospect's "Start demo" tap — a real user
    // activation — which is that session's one chance at the iOS mic prompt.
    //
    // Inert on every shipped path today: all of them start the session FROM a
    // gesture, so the mic is primed by ensureMicPermission() inside
    // startDemoSessionAfterEmail and voicePendingGesture is already false.
    if (this.voicePendingGesture) {
      void this.primeMicFromGesture();
    }
    this.renderReactUI();
  }

  /**
   * Acquire the mic for a "voice pending" demo from a confirmed user gesture
   * (FLO-109). Used by the prefilled-email auto-start flow, where the session
   * connected at init (not a gesture) with the mic deferred. Mirrors the
   * voice-enable half of toggleMute(): prompt from the gesture; on grant,
   * unmute + wire up visualization; on denial ensureMicPermission() has already
   * reverted us to text mode (notify + persist + re-render), so we just stop.
   * Must be called synchronously from the gesture handler — do not await
   * anything before it, or iOS Safari drops the transient activation.
   */
  private async primeMicFromGesture(): Promise<void> {
    if (!this.voicePendingGesture) return;
    this.voicePendingGesture = false;
    // skipPermissionPrecheck: we're synchronously inside the "Start demo" tap;
    // skip the awaited Permissions API query so getUserMedia fires while the
    // transient activation is still valid (iOS Safari).
    const granted = await this.ensureMicPermission(true);
    if (!granted) return; // ensureMicPermission() already fell back to text mode.
    this.pipecatClient?.enableMic(true);
    this.micEnabled = true;
    // CDP Phase 1: open the VOICE_STARTED span, same as toggleMute() /
    // retryMicAfterFailedProbe() — otherwise the eventual trackVoiceEnded()
    // has nothing to close. (Guarded: a no-op if a span is already open.)
    this.trackVoiceStarted();
    // Reuses the stream primed by ensureMicPermission() above (no second prompt).
    this.setupUserMicAnalysis();
    if (this.drivesHostPage) writeMicPref(true);
    this.renderReactUI();
  }

  /**
   * Retry the demo session after an error.
   * Tears down the current SDK instance and re-initializes from scratch.
   */
  private async retryDemo() {
    this.demoError = null;

    // Clean up the current instance. Suppress `closed`: this is a rebuild, not
    // a teardown — emitting it would make the host wrapper navigate away (e.g.
    // DemoShell's router.push('/')) before the fresh init() below mounts.
    try { await this.disconnect(true); } catch {}

    // Remove the React UI container so init() can create a fresh one.
    // Unmount the React root FIRST: dropping it with only .remove() skips
    // React's effect cleanups, so DemoView's scroll-lock unlock never runs and
    // <body> stays pinned (position: fixed) — the host page is left stuck,
    // scrolled to the top and unscrollable, after a retried demo closes.
    if (this.reactContainer) {
      this.reactRoot?.unmount();
      this.reactContainer.remove();
      this.reactContainer = null as any;
      this.reactRoot = null as any;
    }

    // Reset state for a fresh session — including end-of-demo wrap-up and
    // any unattached citation buffer. Without these resets a retried
    // session inherits the prior "That's the demo." card or stale citation
    // chips on its first message.
    this.isInitialized = false;
    this.isConnected = false;
    // Clear any messages queued during the failed attempt (e.g. a prospect_prefill
    // that queued while !isConnected) and reset bot-ready. The transport's
    // onDisconnected normally does this, but it doesn't fire when the failed
    // attempt never fully connected — without this, stale messages would flush
    // into the fresh session on its onBotReady.
    this.pendingClientMessages = [];
    this.isBotReady = false;
    this.botTranscript = '';
    this.userTranscript = '';
    this.transcriptHistory = [];
    this.resetBotSpeakingState();
    this.demoIntroFormSubmitted = false;
    // A retry IS a fresh session, so the idempotency latch from the dead one
    // has to clear with everything else. Left set, the re-shown intro form
    // renders fine and its submit silently no-ops in
    // startDemoSessionAfterEmail — a retry button that spins forever. Only the
    // start-failed path cleared this before (in that catch block); a session
    // that connected and THEN died kept it, and retrying was already dead.
    this.demoSessionStarted = false;
    // Same reasoning as the latch above: a retry IS a fresh session, so the
    // deferred-capture one-shot has to clear with it. Left set, a session that
    // had already been asked (or dismissed) could never be asked again, and one
    // where the server had requested but the prospect hadn't answered would
    // raise the card immediately in the new session — before the demo has
    // reached the moment the operator configured.
    this.resetIdentityAskSession();
    this.botVideoStream = null;
    // A retry IS a fresh session here too: the dead session's hand-over edges
    // would otherwise keep its stage mounted over the new one's boot.
    this.resetWebsiteAgentDemoHandover();
    this.avatarVideoStream = null;
    this.demoEnded = false;
    this.demoSummary = null;
    this.demoFeaturesShown = [];
    this.demoNextStep = null;
    this.pendingCitationSources = null;

    // Re-initialize (creates new Daily room, new pipecat session)
    try {
      await this.init();
    } catch (err) {
      this.logger.error('[OnboardingSDK] Retry failed:', err);
    }
  }

  /**
   * Send a client→server RTVI message, or queue it if the transport hasn't
   * reached its "ready" state yet (Pipecat throws on early sends).
   *
   * Race motivation: there are two pre-ready windows now:
   *   1. Demo mode: init() no longer auto-connects — startBotAndConnect
   *      doesn't run until startDemoSessionAfterEmail() fires (after the
   *      prospect submits Stage 1 of the intro form). If the user types
   *      Stage 2 fast, the prospect_prefill lands while pipecat is still
   *      mid-handshake (isConnected=false, isBotReady=false).
   *   2. Even on the post-connect path, bot-ready can trail isConnected
   *      while the server finishes pipeline setup (and, with an avatar
   *      enabled, its session spin-up). Stage 2 can submit in that gap too.
   *
   * Both cases must QUEUE — dropping these messages is what caused the
   * "initial payload missing" bug after the deferred-start refactor.
   * onBotReady flushes the queue in FIFO order, AFTER it sends site_init,
   * so the server always sees clientKey/origin/userInfo first.
   *
   * Only drop when there's no pipecatClient at all (init crashed) — at
   * that point there's nothing to retry on.
   */
  private sendOrQueueClientMessage(type: string, data: any) {
    if (!this.pipecatClient) {
      // FLO-382: during a website-agent boot the client doesn't exist yet
      // (startWebsiteAgentSession sets websiteAgentSessionStarted synchronously,
      // then awaits probeMicPermission BEFORE creating the client).
      // A message sent in that gap — e.g. a suggested-question tap that renders
      // its bubble immediately — must be QUEUED (flushed by onBotReady), not
      // dropped, or the visitor's first question silently vanishes. Genuine
      // init-failure / torn-down sends (no active session) still drop, and
      // onDisconnected purges the queue so this stays session-scoped.
      if (this.websiteAgentSessionStarted) {
        this.logger.log(`[OnboardingSDK] ⏳ Queuing ${type} — website-agent session booting (no client yet)`);
        this.pendingClientMessages.push({ type, data });
        return;
      }
      this.logger.warn(`[OnboardingSDK] Dropping ${type} — no pipecat client (init failed or torn down)`);
      return;
    }
    if (!this.isConnected || !this.isBotReady) {
      this.logger.log(
        `[OnboardingSDK] ⏳ Queuing ${type} ` +
          `(connected=${this.isConnected}, botReady=${this.isBotReady}) — ` +
          `will flush on onBotReady`,
      );
      this.pendingClientMessages.push({ type, data });
      return;
    }
    try {
      this.pipecatClient.sendClientMessage(type, data);
    } catch (e) {
      // Defensive: if Pipecat's internal state disagrees with ours, queue
      // and let onBotReady retry rather than crashing the caller.
      this.logger.warn(`[OnboardingSDK] sendClientMessage(${type}) threw — queuing for retry:`, e);
      this.pendingClientMessages.push({ type, data });
    }
  }

  /**
   * Send prospect prefill data from intro form as a structured RTVI message.
   * This updates the prospect profile directly and tells Shepherd to skip discovery.
   *
   * Keeps only the email in memory for explicitly configured booking templates.
   */
  sendProspectPrefill(
    data: Record<string, string>,
    options?: { messageKind?: 'identity_capture' },
  ) {
    const isIdentityCapture = options?.messageKind === 'identity_capture';
    if (isIdentityCapture && !this.identityConsented()) {
      this.logger.log(
        '[OnboardingSDK] Consent pending/denied — dropping website-agent identity capture',
      );
      return;
    }

    const payload = isIdentityCapture ? withAnonymousId(data) : data;
    if (this.identityConsented() && typeof data.email === 'string') {
      this.bookingEmail = data.email;
      this.renderReactUI();
    }
    this.sendOrQueueClientMessage('prospect_prefill', {
      ...payload,
      ...(options?.messageKind ? { messageKind: options.messageKind } : {}),
    });
  }

  /** Start the live product without fabricating a visible visitor message. */
  requestWebsiteAgentDemo() {
    this.suggestedOptions = [];
    this.suggestedOptionsMetadata = null;
    this.renderReactUI();
    this.sendOrQueueClientMessage('start_demo', {});
  }

  /** Ask for the current identity detail through one server-owned spoken cue. */
  requestWebsiteAgentIdentityPrompt() {
    if (!this.identityConsented()) return;
    this.suggestedOptions = [];
    this.suggestedOptionsMetadata = null;
    this.renderReactUI();
    this.sendOrQueueClientMessage('demo_identity_prompt', {});
  }

  private resetIdentityAskSession() {
    this.identityAsk = null;
    this.identitySubmissionPending = null;
    this.websiteIdentityAskRequested = false;
  }

  confirmIdentityAsk(value: string) {
    const ask = this.identityAsk;
    if (!ask || ask.status === 'resolved' || !this.isConnected || this.demoEnded || !this.identityConsented()) return false;
    const key = `${ask.askId}:${ask.revision}`;
    // Refused submissions leave the composer's edited draft intact.
    if (!value.trim() || value.trim().length > 320 || this.identitySubmissionPending === key) return false;
    this.identitySubmissionPending = key;
    this.suggestedOptions = [];
    this.suggestedOptionsMetadata = null;
    this.transcriptHistory.push({ id: `user-${Date.now()}`, role: 'user', content: value.trim(), timestamp: Date.now() });
    this.pruneTranscriptHistory();
    this.renderReactUI();
    this.sendOrQueueClientMessage('identity_confirm', { askId: ask.askId, field: ask.field, revision: ask.revision, action: 'confirm', value: value.trim() });
    return true;
  }

  rejectIdentityAsk() {
    const ask = this.identityAsk;
    if (!ask || ask.status !== 'proposed' || !this.isConnected || this.demoEnded || !this.identityConsented()) return;
    const key = `${ask.askId}:${ask.revision}`;
    if (this.identitySubmissionPending === key) return;
    this.identitySubmissionPending = key;
    this.sendOrQueueClientMessage('identity_confirm', { askId: ask.askId, field: ask.field, revision: ask.revision, action: 'reject' });
  }

  requestWebsiteAgentIdentityAsk(timing: string) {
    if (!this.identityConsented() || !this.isConnected || this.demoEnded || this.websiteIdentityAskRequested) return;
    this.websiteIdentityAskRequested = true;
    this.sendOrQueueClientMessage('identity_ask_now', { timing });
  }

  // ── FLO-367: nudge telemetry (batched + beacon-flushed) ──────────────────
  // The queue/debounce/beacon machinery that used to live inline here was
  // extracted into the shared EventQueue (event-queue.ts) so nudge telemetry
  // and the CDP visitor-event stream (FLO-547) ride ONE delivery path.
  // Endpoint + body shape are unchanged, except the batch now carries the
  // visitor token when one exists — the controller has always accepted it.
  private nudgeEventQueue = new EventQueue<{
    type: string;
    nudgeId: string;
    page: string;
    match?: string;
    topic?: string;
  }>({
    // Lazy closures: field initializers run before the constructor body sets
    // this.config, but url/buildBody are only ever read at flush time.
    url: () => `${this.config.apiUrl}/sites/web-agent/nudge-events`,
    buildBody: (events) => {
      const token = getStoredVisitorToken();
      return {
        clientKey: this.config.clientKey,
        // CDP Phase 1: join nudge telemetry to the canonical anonymous person
        // key (WebVisitor) so NudgeEvent reads resolve to the same visitor as
        // VisitorEvent reads.
        ...(token ? { visitorToken: token } : {}),
        events,
      };
    },
  });

  /** Queue a nudge event; flushed on a 3s debounce and on page-hide (keepalive). */
  private recordNudgeEvent(e: {
    type: string;
    nudgeId: string;
    page: string;
    match?: string;
    topic?: string;
  }): void {
    if (!this.config.clientKey) return;
    // CDP Phase 1 (C5): the consent kill switch from validate-client-key —
    // the same flag the visitor-event stream honors. The nudge still renders;
    // only its telemetry stops (the server 200-drops these batches too).
    if (!this.eventCollectionEnabled) return;
    // Per-visitor consent: pre-grant under 'pending' the queue stays inert —
    // not queued-for-later, never enqueued (mirrors the visitor-event stream,
    // whose un-bootstrapped track() drops rather than buffers).
    if (!this.identityConsented()) return;
    this.nudgeEventQueue.enqueue(e);
  }

  // ── CDP Phase 1 (FLO-547): SDK-side visitor-event emitters ───────────────
  // Thin, guarded wrappers over VisitorEventsClient.track. The open/voice
  // pairs are span-shaped (STARTED/OPENED stamps a timestamp, the closer
  // derives the duration) and self-guarding: a close with no matching open in
  // THIS page load is silently dropped — which is exactly what makes the
  // FLO-524 sidebar restore not count as an open.

  /** WIDGET_OPENED {trigger} — a real open, never the visual restore. */
  private trackWidgetOpened(trigger: 'user' | 'nudge' | 'auto'): void {
    // A pre-consent open is not buffered. Keep the span unset as well as
    // dropping the event, otherwise a later grant followed by Close would emit
    // an unmatched duration that started before collection was allowed.
    if (!this.identityConsented()) return;
    if (this.widgetOpenedAtMs !== null) return;
    this.widgetOpenedAtMs = Date.now();
    this.visitorEvents.track('WIDGET_OPENED', { trigger });
  }

  /** WIDGET_CLOSED {openDurationMs} — inert without a tracked open. */
  private trackWidgetClosed(): void {
    if (this.widgetOpenedAtMs === null) return;
    const openDurationMs = Date.now() - this.widgetOpenedAtMs;
    this.widgetOpenedAtMs = null;
    this.visitorEvents.track('WIDGET_CLOSED', { openDurationMs });
  }

  /** VOICE_STARTED — the visitor's mic went live. */
  private trackVoiceStarted(): void {
    // As with widget spans, consent applies prospectively: never retain a local
    // start timestamp for voice that began while event collection was gated.
    if (!this.identityConsented()) return;
    if (this.voiceStartedAtMs !== null) return;
    this.voiceStartedAtMs = Date.now();
    this.visitorEvents.track('VOICE_STARTED');
  }

  /** VOICE_ENDED {durationMs} — mic muted or the session died with it open. */
  private trackVoiceEnded(): void {
    if (this.voiceStartedAtMs === null) return;
    const durationMs = Date.now() - this.voiceStartedAtMs;
    this.voiceStartedAtMs = null;
    this.visitorEvents.track('VOICE_ENDED', { durationMs });
  }

  /** BOOKING_CTA_CLICKED {provider, url}. The URL is stripped to origin+path
   *  so customer-configured provider query values never enter telemetry. */
  private trackBookingCta = (url: string): Promise<string> => {
    if (!url) return Promise.resolve('');
    this.visitorEvents.track('BOOKING_CTA_CLICKED', {
      provider: inferBookingProvider(url),
      url: stripUrlForEvent(url),
    });
    // Mint correlation only after the visitor signals booking intent. Ordinary
    // page loads and conversation opens must not consume attribution budgets.
    return this.resolveBookingIntentUrl(url);
  };

  /**
   * Resolve the booking URL shared by both agents. Priority:
   *   1. Explicit config.demoCalendarLink (customer-set in their snippet)
   *   2. siteCalBookingUrl (configured in Floe dashboard, fetched at
   *      validateClientKey time)
   *   3. Empty — DemoView falls back to no iframe + no Book button.
   *
   * Ordinary URLs never receive profile fields. An explicit {email} query
   * value opts into email handoff, subject to the host's consent setting.
   */
  private getResolvedCalendarUrl(): string {
    const base = (this.config.demoCalendarLink || this.siteCalBookingUrl)?.trim();
    if (!base) return '';
    const destination = this.resolveConfiguredBookingUrl(base);
    if (!destination || hasBookingEmailTemplate(base)) return destination;
    return this.getCachedBookingAttributionUrl() || destination;
  }

  private resolveConfiguredBookingUrl(base: string): string {
    const email = this.identityConsented()
      ? this.bookingEmail ?? this.config.prospectEmail ?? this.config.userInfo?.email
      : undefined;
    return resolveBookingUrl(base, email);
  }

  /**
   * Best-effort intent-time lookup for direct SDK CTAs. The server re-resolves
   * the visitor and booking route; only an opaque provider reference returns.
   */
  private resolveBookingIntentUrl = (requestedUrl: string): Promise<string> => {
    const configuredUrl = (this.config.demoCalendarLink || this.siteCalBookingUrl)?.trim();
    const originalUrl = this.resolveConfiguredBookingUrl(configuredUrl || '');
    const fallbackUrl = originalUrl || requestedUrl;
    // Custom templates are link-only. Provider attribution must not remove
    // their required routing input or replace a per-prospect destination.
    if (configuredUrl && hasBookingEmailTemplate(configuredUrl)) return Promise.resolve(originalUrl);
    if (!this.identityConsented()) return Promise.resolve(fallbackUrl);
    const cachedUrl = this.getCachedBookingAttributionUrl();
    if (cachedUrl) {
      return Promise.resolve(cachedUrl);
    }
    if (this.bookingAttributionRequest) return this.bookingAttributionRequest;

    const visitorToken = getStoredVisitorToken();
    if (!visitorToken || !originalUrl) return Promise.resolve(fallbackUrl);

    const generation = this.bookingAttributionGeneration;
    let request: Promise<string>;
    request = fetchAttributedBookingUrl({
      apiUrl: this.config.apiUrl || '',
      clientKey: this.config.clientKey,
      visitorToken,
      originalUrl,
      conversationId: this.websiteAgentConversationId,
    })
      .then((destinationUrl) => {
        if (generation !== this.bookingAttributionGeneration) return fallbackUrl;
        if (destinationUrl && destinationUrl !== this.attributedBookingUrl) {
          this.attributedBookingUrl = destinationUrl;
          this.attributedBookingUrlExpiresAtMs =
            Date.now() + BOOKING_ATTRIBUTION_CACHE_TTL_MS;
          this.renderReactUI();
        }
        return destinationUrl || fallbackUrl;
      })
      .catch(() => fallbackUrl)
      .finally(() => {
        if (this.bookingAttributionRequest === request) {
          this.bookingAttributionRequest = null;
        }
      });
    this.bookingAttributionRequest = request;
    return request;
  };

  private getCachedBookingAttributionUrl(): string {
    if (
      this.attributedBookingUrl &&
      isBookingAttributionCacheFresh(this.attributedBookingUrlExpiresAtMs)
    ) {
      return this.attributedBookingUrl;
    }
    this.clearBookingAttributionCache();
    return '';
  }

  private clearBookingAttributionCache(): void {
    this.attributedBookingUrl = '';
    this.attributedBookingUrlExpiresAtMs = 0;
  }

  /**
   * @param options.messageKind FLO-531: a structured marker for messages the
   * server has to recognise by ROLE rather than by wording. pipecat's demo
   * kickoff used to sniff `text.startswith("my name is")`, which no translated
   * intro form can satisfy; it now keys on `messageKind === 'intro_form'` and
   * keeps the prefix match only for older SDKs.
   */
  async sendTextMessage(text: string, options?: { messageKind?: 'intro_form' }) {
    if (!text.trim()) return;

    // FLO-307: any user-sent text (typed, or a tapped opener chip routed
    // through onSelectOption) is a turn — clear the transient opener chips.
    this.suggestedOptions = [];
    this.suggestedOptionsMetadata = null;

    // A typed message or chip tap while the bot is still answering is a
    // REDIRECTION: the server cuts the bot's speech and in-flight response on
    // this message (the same InterruptionFrame a voice barge-in sends), so the
    // answer on screen is over. Commit it as it stands — cut, never left open,
    // never wiped by the reply to this message — and file it ABOVE this
    // message, where the conversation actually happened. The intro form is a
    // kickoff, not a redirection, and keeps its ordering.
    if (options?.messageKind !== 'intro_form') this.commitInterruptedBotTurn();

    // Add to transcript history immediately (optimistic UI)
    this.transcriptHistory.push({
      id: `user-${Date.now()}`,
      role: 'user',
      content: text.trim(),
      timestamp: Date.now(),
    });
    this.pruneTranscriptHistory();
    this.renderReactUI();
    // Send as custom RTVI message — routes to on_client_message handler
    // which runs the full pipeline (state machine, prompt refresh, etc.)
    this.sendOrQueueClientMessage('text_message', {
      text: text.trim(),
      // FLO-51: typing does NOT imply silence. The only thing that suppresses
      // the spoken reply is the speaker being explicitly muted.
      audio_response: !this.speakerMuted,
      ...(options?.messageKind ? { messageKind: options.messageKind } : {}),
    });
  }

  /**
   * Toggle microphone mute.
   *
   * FLO-706: a Website Agent text session has intentionally never probed the
   * microphone. Its first explicit mic click requests permission and enables
   * voice in one action, even if the transport client is still being built.
   * A real denied/unavailable verdict also re-requests rather than rendering a
   * lying "mic on" state. Returns false while an async request is in flight.
   */
  toggleMute(): boolean {
    if (!this.pipecatClient) {
      if (this.config.websiteAgent && this.websiteAgentSessionStarted) {
        if (!this.websiteAgentMicPermissionReady) {
          // No await before this call: beginWebsiteAgentMicRequest reaches
          // getUserMedia synchronously inside the visitor's click gesture.
          void this.enableWebsiteAgentMicFromGesture();
          return false;
        }

        // Permission may resolve while conversation resume is still pending,
        // before the client exists. Keep accepting mic clicks as desired-state
        // changes so a second click can mute the upcoming join.
        this.updateWebsiteAgentMicIntent({ type: 'toggle' });
        this.logger.log(
          '[OnboardingSDK] 🎤 Pending mic toggled:',
          this.micEnabled ? 'ON' : 'MUTED',
        );
        this.renderReactUI();
        return this.micEnabled;
      }
      return false;
    }

    const currentlyEnabled = micStateForToggle({
      websiteAgent: !!this.config.websiteAgent,
      connected: this.isConnected,
      desiredState: this.micEnabled,
      transportState: this.pipecatClient.isMicEnabled,
    });
    const newState = !currentlyEnabled;

    if (
      newState &&
      this.config.websiteAgent &&
      (!this.websiteAgentMicPermissionReady || this.micProbeFailed || this.micPermissionDenied)
    ) {
      void this.enableWebsiteAgentMicFromGesture();
      return false;
    }

    if (newState && (this.micProbeFailed || this.micPermissionDenied)) {
      // Re-ask from this click (a real user gesture, so the prompt is allowed to
      // appear). A browser that remembers the denial rejects immediately and we
      // simply stay muted — bot audio and the text input are unaffected either way.
      // Gated on micProbeFailed too: a machine with no usable mic never sets
      // micPermissionDenied, and would otherwise sail straight into enableMic(true).
      void this.retryMicAfterFailedProbe();
      return false;
    }

    this.pipecatClient.enableMic(newState);
    this.micEnabled = newState;
    // CDP Phase 1: a mic session is a VOICE_STARTED/VOICE_ENDED span.
    if (newState) this.trackVoiceStarted();
    else this.trackVoiceEnded();
    // FLO-51: a remembered preference — for the onboarding agent only (see
    // the constructor: the other two own their own opening posture).
    if (this.drivesHostPage) writeMicPref(newState);
    this.logger.log('[OnboardingSDK] 🎤 Mic toggled:', newState ? 'ON' : 'MUTED');
    // Re-render UI to reflect mute state change
    this.renderReactUI();
    return newState;
  }

  /**
   * Complete the Website Agent's explicit voice opt-in.
   *
   * This supports both sides of the connect boundary: if the permission result
   * arrives before PipecatClient exists, the start path observes
   * websiteAgentMicPermissionReady and joins enabled; if a text session is
   * already live, this enables that client immediately. Denial is text-safe and
   * only updates the blocked-mic explanation.
   */
  private async enableWebsiteAgentMicFromGesture(): Promise<void> {
    const generation = this.websiteAgentGeneration;
    const result = await this.beginWebsiteAgentMicRequest();

    if (this.websiteAgentMicRequestOutlivedSession(generation)) return;

    this.applyMicProbeResult(result);
    this.updateWebsiteAgentMicIntent({ type: 'permission-resolved', granted: result.granted });
    if (!result.granted) {
      this.logger.warn('[OnboardingSDK] 🎤 Website Agent mic unavailable — staying in text-safe mode');
      this.renderReactUI();
      return;
    }

    // The client may still be null while conversation resume is pending. In
    // that case startWebsiteAgentSession reads permission plus the desired state
    // and constructs accordingly; otherwise switch the live client now. A
    // connected text-first session did not pass through onConnected with a live
    // mic, so open its CDP voice span here. During an in-progress connect,
    // onConnected remains the authoritative start and avoids counting join time.
    if (this.pipecatClient) {
      this.pipecatClient.enableMic(true);
      if (this.isConnected) this.trackVoiceStarted();
    }
    this.logger.log('[OnboardingSDK] 🎤 Website Agent voice enabled by explicit mic action');
    this.renderReactUI();
  }

  /**
   * Did the Website Agent session that started a mic request go away while the
   * browser sat on the prompt? Repeated verbatim at three await points before
   * this existed — and every one of them exists to stop a dead session's
   * verdict from being written over a live replacement's UI.
   */
  private websiteAgentMicRequestOutlivedSession(generation: number): boolean {
    const stale =
      !this.config.websiteAgent
      || !this.websiteAgentSessionStarted
      || this.websiteAgentGeneration !== generation;
    if (stale) {
      this.logger.log('[OnboardingSDK] 🎤 Mic request outlived its session — discarding the result');
    }
    return stale;
  }

  /**
   * FLO-786: finish the voice launcher's opt-in without a second tap.
   *
   * The visitor already asked to talk — that is what "Talk to me" IS — so the
   * only thing left to collect is the browser's permission. Interrupting the
   * agent's opening line with a prompt would talk over the welcome, and the
   * small in-panel "Turn on mic" card that replaced it is easy to miss, so the
   * request goes out the moment the welcome finishes instead.
   *
   * Called ONLY from the surface's post-welcome handoff, and only for a session
   * the visitor opened with a voice launcher — a keyboard start is an explicit
   * text choice and never reaches this (see WebsiteAgentView's `voiceEntry`).
   *
   * That timing costs the transient user activation, so unlike every other mic
   * path here this one can be refused without a prompt ever being drawn
   * (WebKit). `classifyAutoMicProbe` keeps that case OFF the record: nothing is
   * committed, `false` comes back, and the surface falls back to the tap
   * invitation — which carries a real gesture and works everywhere.
   *
   * Resolves true when voice is settled (granted, or already resolved by an
   * earlier gesture); false when the surface should ask with its own card.
   *
   * PRIVATE on purpose: it is reached only through the render callback below,
   * which is built inside this class. It is not part of the SDK's public API —
   * nothing outside our own surface has any business asking for a microphone
   * with no gesture behind it. (TypeScript privacy is compile-time only, so
   * this documents and narrows the supported surface; it is not a runtime
   * boundary. A script that can run on the host page can already call
   * getUserMedia itself, so this is hygiene, not a security control.)
   */
  private async requestWebsiteAgentMicAfterWelcome(): Promise<boolean> {
    if (!this.config.websiteAgent || !this.websiteAgentSessionStarted) return false;
    // An earlier gesture already settled this — granted (nothing to ask) or
    // refused (the control is already explaining it). Either way, don't re-ask.
    if (this.websiteAgentMicPermissionReady) return true;
    if (this.micProbeFailed || this.micPermissionDenied) return false;
    // One automatic ask per session — see the field. A remount around the
    // handoff gets `false` and the tap invitation, not a second prompt.
    if (this.websiteAgentAutoMicRequested) return false;
    this.websiteAgentAutoMicRequested = true;

    const generation = this.websiteAgentGeneration;
    const result = await this.beginWebsiteAgentMicRequest();

    if (this.websiteAgentMicRequestOutlivedSession(generation)) return false;

    if (!result.granted) {
      // A second suspension point on a path the guard above exists to protect:
      // the prompt can sit open for as long as the visitor ignores it, and the
      // query itself is another turn of the loop.
      const permission = await this.queryMicPermissionState();
      if (this.websiteAgentMicRequestOutlivedSession(generation)) return false;
      if (classifyAutoMicProbe(result, permission) === 'inconclusive') {
        this.logger.log(
          '[OnboardingSDK] 🎤 Automatic mic request was refused without a prompt (no user activation) — leaving it to the tap invitation',
        );
        return false;
      }
      this.applyMicProbeResult(result);
      this.updateWebsiteAgentMicIntent({ type: 'permission-resolved', granted: false });
      this.logger.warn('[OnboardingSDK] 🎤 Automatic mic request denied/unavailable — staying text-safe');
      this.renderReactUI();
      return false;
    }

    this.applyMicProbeResult(result);
    this.updateWebsiteAgentMicIntent({ type: 'permission-resolved', granted: true });
    // Mirrors enableWebsiteAgentMicFromGesture: the client may still be null
    // while conversation resume is pending, in which case the start path reads
    // the intent above and joins enabled.
    if (this.pipecatClient) {
      this.pipecatClient.enableMic(true);
      if (this.isConnected) this.trackVoiceStarted();
    }
    this.logger.log('[OnboardingSDK] 🎤 Website Agent voice enabled automatically after the spoken welcome');
    this.renderReactUI();
    return true;
  }

  /**
   * Re-request the mic after an earlier probe failed, then unmute if this one
   * succeeds. probeMicPermission() swallows its own failures and mutates
   * nothing, so nothing rejects out of the click handler that called
   * toggleMute() — and a verdict that arrives too late is simply dropped.
   */
  private async retryMicAfterFailedProbe(): Promise<void> {
    this.logger.log('[OnboardingSDK] 🎤 Unmute requested after a failed probe — re-asking');

    // The permission prompt can sit open indefinitely. Pin the session this
    // request belongs to BEFORE awaiting: the visitor can end the chat (which
    // nulls pipecatClient and bumps the generation) and re-tap the pill while
    // it's up, and this continuation would otherwise unmute the REPLACEMENT
    // session's mic and stamp a dead session's verdict over the live one's.
    const client = this.pipecatClient;
    const generation = this.websiteAgentGeneration;

    const result = await this.probeMicPermission();

    if (this.pipecatClient !== client || this.websiteAgentGeneration !== generation) {
      this.logger.log('[OnboardingSDK] 🎤 Mic re-request outlived its session — discarding the result');
      return;
    }

    this.applyMicProbeResult(result);
    if (!result.granted || !client) {
      this.logger.warn('[OnboardingSDK] 🎤 Mic still unavailable — staying muted');
      this.renderReactUI(); // surface the refreshed denied/unavailable state
      return;
    }
    client.enableMic(true);
    this.micEnabled = true;
    this.trackVoiceStarted();
    if (this.drivesHostPage) writeMicPref(true);
    this.logger.log('[OnboardingSDK] 🎤 Mic toggled: ON (after re-request)');
    this.renderReactUI();
  }

  /**
   * Toggle bot audio output (speaker mute). Single source of truth for the
   * "I don't want to hear the bot right now" state — applies to the
   * <audio> elements the SDK mounted in trackStarted (which cover the avatar
   * participant's voice track too, when one is in the room) AND, when
   * reachable, the bot audio track's `enabled` flag so the receiver-side
   * gate also fires. The avatar's <video> element is muted separately by the
   * render layer (AvatarPiP) via the `speakerMuted` prop, so it can never
   * become a second audio surface.
   */
  toggleSpeakerMute(): boolean {
    const next = !this.speakerMuted;
    this.speakerMuted = next;
    // (1) Audio elements we mounted for the bot's audio track.
    this.audioElements.forEach((el) => { el.muted = next; });
    // (2) The bot track itself, when the transport exposes it. tracks()
    //     can throw before connection — guarded so an early click while
    //     "Connecting…" doesn't blow up.
    try {
      const tracks = this.pipecatClient?.tracks();
      const botAudio = tracks?.bot?.audio;
      if (botAudio) botAudio.enabled = !next;
    } catch (err) {
      this.logger.warn('[OnboardingSDK] toggleSpeakerMute: tracks() unavailable yet', err);
    }
    // FLO-51: remembered across page loads, independently of the mic
    // (onboarding only — see the constructor).
    if (this.drivesHostPage) writeSpeakerPref(next);
    this.logger.log('[OnboardingSDK] 🔊 Speaker', next ? 'MUTED' : 'ON');
    // FLO-348: tell the server to stop/resume TTS generation with the speaker.
    this.syncAudioOutput();
    // FLO-960: an unmute can be the moment the bot's audio first becomes
    // audible. The playback edge that already happened is remembered, so this
    // re-check is what turns it into the one honest first_audio report.
    this.maybeReportFirstAudio();
    this.renderReactUI();
    return next;
  }

  /**
   * FLO-348: tell the server whether to GENERATE TTS. We pay for Cartesia only
   * when someone can hear it — the speaker is ON, OR a live demo is on screen
   * (the demo's word-synced captions are sourced from TTS text, so TTS must stay
   * on there even when the audio is muted client-side). The server maps this to
   * LLMConfigureOutputFrame(skip_tts), which is sticky, so it holds across turns
   * until the state changes.
   *
   * FLO-51: no longer gated to the demo pipeline. Onboarding used to "manage TTS
   * via its own text-mode path", which is precisely the coupling this ticket
   * removed — and it left the onboarding speaker button as a client-side mute
   * that still paid Cartesia for every word nobody heard. `bot_refactored.py`
   * now has the matching `set_audio_output` handler.
   */
  private syncAudioOutput(): void {
    const enabled = !this.speakerMuted || !!this.botVideoStream;
    this.sendOrQueueClientMessage('set_audio_output', { enabled });
  }

  /**
   * Highlight an element
   */
  highlight(selector: string, options?: any) {
    this.overlay.highlight(selector, options);
  }

  /**
   * Clear highlights
   */
  clearHighlight() {
    this.overlay.clearHighlight();
  }

  /**
   * Show tooltip
   */
  showTooltip(text: string, options?: any) {
    this.overlay.showTooltip(text, options);
  }

  /**
   * Hide tooltip
   */
  hideTooltip() {
    this.overlay.hideTooltip();
  }

  /**
   * Execute an action
   */
  async executeAction(action: UIAction): Promise<boolean> {
    return this.actionExecutor.executeWithConsent(action);
  }

  /**
   * Take a screenshot
   */
  async takeScreenshot(options?: ScreenshotOptions): Promise<string> {
    return this.capture.captureWithRedaction(options);
  }

  /** Marketing agents can show passive exit intent; onboarding/support cannot. */
  private supportsExitIntent(): boolean {
    if (this.config.websiteAgent) return true;
    return !!this.config.demoMode && this.config.embedMode !== 'fullscreen';
  }

  private stopExitIntentDetector(): void {
    this.exitIntentDetector?.disable();
    this.exitIntentDetector = null;
  }

  private armConfiguredExitIntent(): void {
    const options = resolveExitIntentConfig(this.config.exitIntent);
    if (options) this.enableExitIntent(options);
  }

  /**
   * Suppress an automatic prompt once the visitor has already chosen to engage.
   * If exit intent was what surfaced the agent, expose that conversion to hosts.
   */
  private markExitIntentEngaged(): void {
    if (this.exitIntentEngaged) return;
    this.exitIntentEngaged = true;
    this.stopExitIntentDetector();
    if (this.exitIntentShown) {
      this.emit('exitIntentEngaged', {
        agentMode: this.config.websiteAgent ? 'website_agent' : 'demo_agent',
      });
    }
  }

  /**
   * Enable one-shot desktop exit detection for the mounted marketing agent.
   *
   * Calling this before `ready` is safe: the options are remembered and the
   * listener is installed once validation and the passive UI have completed.
   * Returns false only when this agent mode cannot provide an exit surface.
   */
  enableExitIntent(options: ExitIntentOptions = {}): boolean {
    if (!this.supportsExitIntent()) {
      this.logger.warn(
        '[OnboardingSDK] Exit intent requires websiteAgent or a docked demo embed',
      );
      return false;
    }

    const normalized = normalizeExitIntentOptions(options);
    this.config.exitIntent = normalized;
    this.stopExitIntentDetector();

    if (!this.isInitialized || this.exitIntentShown || this.exitIntentEngaged) {
      return true;
    }
    if (typeof document === 'undefined') return false;

    this.exitIntentDetector = new ExitIntentDetector(
      () => this.showExitIntent({ message: normalized.message }),
      normalized,
      document,
    );
    this.exitIntentDetector.enable();
    return true;
  }

  /** Disable automatic detection. A later manual `showExitIntent()` still works. */
  disableExitIntent(): void {
    this.config.exitIntent = false;
    this.stopExitIntentDetector();
  }

  /**
   * Reveal the marketing agent as a passive, one-shot exit invitation.
   *
   * This never connects Pipecat, probes the microphone, or sends a message on
   * the visitor's behalf. Website-agent mode opens its dormant panel; docked
   * demo mode expands the existing intro pill. Returns false when no eligible
   * mounted surface claimed the request (or the one shot was already spent).
   */
  showExitIntent(options: ExitIntentSurfaceOptions = {}): boolean {
    if (
      !this.supportsExitIntent() ||
      !this.isInitialized ||
      this.exitIntentShown ||
      this.exitIntentEngaged
    ) {
      return false;
    }

    // A manual/mobile trigger should reuse declarative or runtime-configured
    // copy when the caller does not override it for this particular signal.
    const configured = resolveExitIntentConfig(this.config.exitIntent);
    const message = normalizeExitIntentMessage(options.message) ?? configured?.message;
    if (!surfaceExitIntent({ message })) return false;

    this.exitIntentShown = true;
    this.stopExitIntentDetector();
    this.emit('exitIntentShown', {
      agentMode: this.config.websiteAgent ? 'website_agent' : 'demo_agent',
      customMessage: !!message,
    });
    return true;
  }

  /**
   * Open the demo from the host page's own UI — a "See it live" button in a
   * hero, nav or footer that sits OUTSIDE the agent.
   *
   * Public API. Drives whichever agent is already mounted rather than spawning a
   * second overlay, and deliberately hides which one that is:
   *
   *   - website agent — sends the demo request; a presenter on the page takes
   *                     it in its dock, otherwise the panel opens
   *   - demo agent    — expands and focuses the docked pill
   *   - onboarding    — no demo to open; resolves `false` immediately
   *
   * Resolves `true` only if the agent actually took the request. **Branch on
   * it.** A caller that assumes success is the exact bug this returns a value to
   * prevent: the CTA's only listener once lived in a component that stopped
   * mounting when the site switched agents, and every button on floe.so became a
   * silent no-op with nothing to show for it.
   *
   * `false` means no mounted agent claimed it — the site has no demo available,
   * the SDK never booted, or the agent is a build too old to listen. Fall back
   * to whatever you'd do without Floe (usually: book a call).
   *
   * ```js
   * button.addEventListener('click', async () => {
   *   if (!(await floe.requestDemo())) window.open('/book-a-call', '_blank');
   * });
   * ```
   *
   * Waits up to `timeoutMs` (default 1500) because a click landing before the
   * agent has mounted is indistinguishable from one with nothing listening.
   * Keep it well under ~5s if you fall back with `window.open`, or the browser
   * will have dropped the user activation and blocked the popup.
   */
  async requestDemo(options: { timeoutMs?: number } = {}): Promise<boolean> {
    // The onboarding agent has no demo surface, so there is nothing to wait for
    // — answer immediately instead of burning the caller's whole budget.
    if (drivesHostPage(this.config)) {
      this.logger.warn(
        '[OnboardingSDK] requestDemo() has no effect for the onboarding agent — ' +
          'it is for demoMode / websiteAgent embeds.',
      );
      return false;
    }
    return surfaceDemo(options);
  }

  /** The one predicate every identity-bearing call site asks. True means the
   *  SDK may write/attach identity keys (anonymousId, visitor token), enqueue
   *  visitor/nudge events, and open the server-side conversation. */
  private identityConsented(): boolean {
    return identityAllowed(this.consentMode, this.consentDecision);
  }

  /**
   * FLO-1096: the open page's activity so far, as a `sessionData` fragment for
   * a Website or Demo Agent session start. The page a visitor opens the agent
   * on has not been left yet, so its summary has not been sent; this carries
   * it with the request that starts the session, where floe-api cleans it as
   * it cleans the event. Empty unless the page is being watched, which already
   * requires the site switch, the visitor's consent and a marketing-site agent
   * (`pageEngagementAllowed`).
   */
  private currentPageForSession(): { currentPage?: CurrentPageSummary } {
    const currentPage = this.visitorEvents.currentPageSummary();
    return currentPage ? { currentPage } : {};
  }

  /**
   * C3 (PR #620 r6): the identity-bearing `userInfo` for a marketing-surface
   * spawn/site_init payload — or `undefined` pre-grant, meaning the KEY itself
   * is omitted. ONE builder on purpose: a call site that gated only the
   * anonymousId while still spreading `config.userInfo` (name/email — identity
   * just as much) was the exact shape of the pre-r6 leak, and a shared seam is
   * what keeps a fourth session sender from reintroducing it. The general
   * session-payload mint lives here; the website identity-card helpers have
   * their own explicit identityConsented() gates. getOrCreateAnonymousId()
   * WRITES localStorage on first use, so an ungated call is a consent leak.
   */
  private consentedUserInfo(): Record<string, unknown> | undefined {
    if (!this.identityConsented()) return undefined;
    return {
      ...(this.config.userInfo || {}),
      anonymousId: getOrCreateAnonymousId(),
    };
  }

  /**
   * Record the visitor's consent decision (public SDK surface, like
   * {@link requestDemo}: a method on the instance `Floe()` returns).
   *
   * Only meaningful with `consentMode: 'pending'` (or the per-site default) —
   * under the default 'not-required' mode identity already flows and this is
   * a no-op. `'granted'` releases the deferred bootstrap: the visitor token is
   * minted and the boot events (WIDGET_LOADED + PAGE_VIEWED) go out; sessions
   * started from then on carry identity keys. `'denied'` keeps everything
   * parked and is STICKY for the page load — a later 'granted' is ignored.
   *
   * ```js
   * const floe = Floe({ clientKey, websiteAgent: true, consentMode: 'pending' });
   * cookieBanner.onAccept(() => floe.consent('granted'));
   * cookieBanner.onReject(() => floe.consent('denied'));
   * ```
   */
  consent(decision: 'granted' | 'denied'): void {
    const next = nextConsentDecision(this.consentDecision, decision);
    if (next === this.consentDecision) {
      if (decision !== this.consentDecision) {
        this.logger.warn(
          `[OnboardingSDK] consent(${JSON.stringify(decision)}) ignored — ` +
            (this.consentDecision === 'denied'
              ? 'denied is sticky for this page load'
              : 'expected "granted" or "denied"'),
        );
      }
      return;
    }
    this.consentDecision = next;
    if (this.isConnected) this.sendOrQueueClientMessage('consent_changed', { consent: next });
    if (!this.identityConsented()) {
      this.identityAsk = null;
      this.bookingEmail = '';
    }
    this.logger.log(`[OnboardingSDK] Visitor consent: ${next}`);
    // Identity capture is a render-time capability. Re-render an already
    // mounted surface so grant enables the configured card and denial removes
    // it immediately; renderReactUI() is inert before the UI mounts.
    if (next === 'denied') {
      // Honored in EVERY mode, including 'not-required' where the event
      // machinery may already be armed: stop the stream the same way the C5
      // kill switch does — queued events dropped UNSENT, URL watch unbound,
      // nudge queue cleared then disposed (clear FIRST — dispose flushes).
      this.visitorEvents.setCollectionEnabled(false);
      // FLO-960: a withdrawal is a hard local cutoff for diagnostics too.
      this.sdkDiagnostics.setCollectionEnabled(false);
      this.nudgeEventQueue.clear();
      this.nudgeEventQueue.dispose();
      // A span opened while consent was valid must not leak a later Close/End
      // after revocation, and a pre-consent span is never allowed to survive a
      // decision boundary. Revocation is a hard local analytics cutoff.
      this.widgetOpenedAtMs = null;
      this.voiceStartedAtMs = null;
      this.bookingAttributionGeneration += 1;
      this.bookingAttributionRequest = null;
      this.clearBookingAttributionCache();
      this.renderReactUI();
      return;
    }
    this.renderReactUI();
    if (next !== 'granted') return;
    // Run the bootstrap init deferred, exactly once. identityConsented() now
    // answers true, so sessions/events from here on carry identity keys; the
    // pre-grant queues were never armed, so nothing collected pre-consent is
    // retroactively sent (there is nothing to send).
    if (this.consentBootstrapDeferred) {
      this.consentBootstrapDeferred = false;
      void this.visitorEvents.bootstrap();
      this.visitorEvents.startUrlWatch();
    }
  }

  /**
   * Get session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      initialized: this.isInitialized,
      connected: this.isConnected,
      sessionId: this.sessionId,
      microphoneMuted: this.pipecatClient ? !this.pipecatClient.isMicEnabled : false
    };
  }

  // ========================================================================
  // Smart Discovery Mode - Guided Onboarding
  // ========================================================================

  /**
   * Start guided discovery mode.
   * 
   * Sends a message to the bot to begin the discovery flow, which will
   * ask structured questions to determine the best onboarding plan for the user.
   * 
   * @returns true if the message was sent successfully
   */
  startGuidedDiscovery(): boolean {
    if (!this.pipecatClient || !this.isConnected) {
      this.logger.warn('[OnboardingSDK] Cannot start discovery - not connected');
      return false;
    }

    this.logger.log('[OnboardingSDK] 🔍 Starting guided discovery mode');

    this.pipecatClient.sendClientMessage('start_discovery', {
      type: 'start_discovery',
      timestamp: Date.now(),
    });

    // Hide welcome popup if shown
    this.hideWelcomePopup();

    return true;
  }

  /**
   * Skip discovery and let the user state their goal directly.
   * 
   * This closes the welcome popup and lets the bot handle the conversation
   * in the standard greeting flow.
   */
  skipDiscovery(): void {
    this.logger.log('[OnboardingSDK] Skipping discovery - standard greeting flow');
    this.hideWelcomePopup();
  }

  /**
   * Show the welcome popup and wait for user choice.
   * Returns a Promise that resolves to true if user wants discovery.
   * 
   * Uses Raycast-style dark theme to match other modals.
   */
  showWelcomePopupAsync(): Promise<boolean> {
    return new Promise((resolve) => {
      // Check if popup already exists
      if (document.getElementById('floe-welcome-popup')) {
        resolve(false);
        return;
      }

      const popup = document.createElement('div');
      popup.id = 'floe-welcome-popup';
      popup.setAttribute('data-floe-widget', 'true');

      // Raycast-style dark theme matching screen share modal
      popup.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        backdrop-filter: blur(12px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2147483647;
        pointer-events: auto;
        animation: welcomeFadeIn 0.2s ease-out;
        font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif;
      `;

      const content = document.createElement('div');
      content.style.cssText = `
        background: linear-gradient(180deg, rgba(32, 32, 36, 0.98) 0%, rgba(24, 24, 28, 0.99) 100%);
        border-radius: 20px;
        padding: 32px;
        max-width: 420px;
        width: 90%;
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3), 0 24px 48px rgba(0, 0, 0, 0.5), 0 12px 24px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
        animation: welcomeSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      `;

      content.innerHTML = `
        <style>
          @keyframes welcomeFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes welcomeSlideUp {
            from { transform: translateY(20px) scale(0.98); opacity: 0; }
            to { transform: translateY(0) scale(1); opacity: 1; }
          }
          @keyframes welcomeFadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
        </style>
        
        <!-- Icon with live indicator -->
        <div style="display: flex; justify-content: center; margin-bottom: 24px;">
          <div style="position: relative;">
            <div style="
              width: 64px;
              height: 64px;
              border-radius: 16px;
              background: linear-gradient(135deg, rgba(40, 40, 45, 0.9) 0%, rgba(30, 30, 35, 0.95) 100%);
              border: 1px solid rgba(255, 255, 255, 0.1);
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
            ">
              <svg width="36" height="32" viewBox="0 0 159 138" fill="none">
                <!-- Floe logo -->
                <path fill="rgba(255, 255, 255, 0.9)" d="M64 31.5c-23.5 0-34 18-64 48.5C0 31.5 26.5 1 54 1s105-1 105-1c-2 11.5-18.5 31.5-29.5 31.5z"/>
                <path fill="rgba(255, 255, 255, 0.9)" d="M64.5 89.5c-23.5 0-34 18-64 48.5 0-48.5 26.5-79 54-79s75-1 75-1c-2 11.5-18.5 31.5-29.5 31.5z"/>
              </svg>
            </div>
            <!-- Live pulse indicator -->
            <div style="
              position: absolute;
              top: -2px;
              right: -2px;
              width: 14px;
              height: 14px;
              background: #10b981;
              border-radius: 50%;
              border: 2px solid rgba(24, 24, 28, 0.99);
              animation: livePulse 2s ease-in-out infinite;
            "></div>
          </div>
        </div>
        
        <style>
          @keyframes livePulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
            50% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
          }
          #floe-start-guided, #floe-skip-guided {
            cursor: pointer !important;
          }
          #floe-start-guided:hover, #floe-skip-guided:hover {
            cursor: pointer !important;
          }
        </style>
        
        <!-- Title -->
        <h2 style="margin: 0 0 12px 0; font-size: 20px; font-weight: 600; color: rgba(255, 255, 255, 0.95); text-align: center; letter-spacing: -0.02em;">
          Your AI guide is ready
        </h2>
        
        <!-- Description -->
        <p style="margin: 0 0 8px 0; color: rgba(255, 255, 255, 0.6); font-size: 14px; line-height: 1.6; text-align: center;">
          Start a live voice conversation to get personalized help setting up your account.
        </p>
        
        <!-- Live badge -->
        <div style="display: flex; justify-content: center; margin-bottom: 28px;">
          <div style="
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 12px;
            background: rgba(16, 185, 129, 0.1);
            border: 1px solid rgba(16, 185, 129, 0.2);
            border-radius: 20px;
          ">
            <div style="width: 6px; height: 6px; background: #10b981; border-radius: 50%;"></div>
            <span style="font-size: 12px; font-weight: 500; color: #10b981;">Live voice assistant</span>
          </div>
        </div>
        
        <!-- Buttons -->
        <div style="display: flex; gap: 12px;">
          <button type="button" id="floe-start-guided" style="
            flex: 1;
            padding: 14px 20px;
            border-radius: 12px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer !important;
            border: none;
            background: rgba(255, 255, 255, 0.95);
            color: rgba(0, 0, 0, 0.9);
            transition: all 0.2s ease;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          ">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
            Start Conversation
          </button>
        </div>
        
        <button type="button" id="floe-skip-guided" style="
          width: 100%;
          margin-top: 12px;
          padding: 12px 20px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer !important;
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.4);
          transition: all 0.2s ease;
        ">
          I'll explore on my own
        </button>
      `;

      popup.appendChild(content);
      document.body.appendChild(popup);

      // Add hover effects matching ScreenShareModal style
      const primaryBtn = popup.querySelector('#floe-start-guided') as HTMLButtonElement;
      const secondaryBtn = popup.querySelector('#floe-skip-guided') as HTMLButtonElement;

      if (primaryBtn) {
        primaryBtn.onmouseenter = () => {
          primaryBtn.style.background = 'rgba(255, 255, 255, 1)';
          primaryBtn.style.transform = 'translateY(-1px)';
          primaryBtn.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
        };
        primaryBtn.onmouseleave = () => {
          primaryBtn.style.background = 'rgba(255, 255, 255, 0.95)';
          primaryBtn.style.transform = 'translateY(0)';
          primaryBtn.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
        };
        primaryBtn.onclick = () => {
          popup.style.animation = 'welcomeFadeOut 0.2s ease';
          setTimeout(() => {
            popup.remove();
            resolve(true);
          }, 200);
        };
      }

      if (secondaryBtn) {
        secondaryBtn.onmouseenter = () => {
          secondaryBtn.style.color = 'rgba(255, 255, 255, 0.8)';
        };
        secondaryBtn.onmouseleave = () => {
          secondaryBtn.style.color = 'rgba(255, 255, 255, 0.4)';
        };
        secondaryBtn.onclick = () => {
          popup.style.animation = 'welcomeFadeOut 0.2s ease';
          setTimeout(() => {
            popup.remove();
            resolve(false);
          }, 200);
        };
      }

      this.logger.log('[OnboardingSDK] Welcome popup shown (async)');
    });
  }

  /**
   * Show the welcome popup for new users (legacy sync version).
   * 
   * Displays a popup with options to start guided discovery or
   * proceed with the standard flow.
   * @deprecated Use showWelcomePopupAsync instead
   */
  showWelcomePopup(): void {
    this.showWelcomePopupAsync().then((startDiscovery) => {
      if (startDiscovery) {
        this.startGuidedDiscovery();
      } else {
        this.skipDiscovery();
      }
    });
  }

  /**
   * Hide the welcome popup.
   */
  hideWelcomePopup(): void {
    const popup = document.getElementById('floe-welcome-popup');

    if (popup) {
      popup.style.animation = 'welcomeFadeOut 0.2s ease';
      setTimeout(() => popup.remove(), 200);
    }

    this.logger.log('[OnboardingSDK] Welcome popup hidden');
  }

  /**
   * Check if discovery mode should be shown for this user.
   * 
   * Can be called after connection to determine if the welcome popup
   * should be displayed. Returns true for new users or users who haven't
   * completed onboarding.
   */
  shouldShowDiscoveryPrompt(): boolean {
    // Check localStorage for previous visits
    const hasVisited = localStorage.getItem('floe_has_visited');
    const hasCompletedOnboarding = localStorage.getItem('floe_onboarding_complete');

    // Show popup for first-time visitors or those who haven't completed onboarding
    return !hasVisited || !hasCompletedOnboarding;
  }

  /**
   * Check exact-visitor status from API to determine if onboarding should be
   * skipped. The caller supplies a pre-existing, consented visitor token; this
   * method never acquires one and never selects state by email/externalId.
   */
  private async checkEndUserStatus(visitorToken: string): Promise<void> {
    try {
      // apiUrl is guaranteed to be set from constructor (via __FLOE_API_URL__ build-time constant)
      const url = `${this.config.apiUrl}/end-users/check-status`;

      const payload: EndUserStatusRequest = {
        clientKey: this.config.clientKey,
        origin: window.location.origin,
        visitorToken,
      };

      if (this.config.debug) {
        // Never log the continuity bearer, even redacted: a fixed message is
        // sufficient to diagnose whether the bootstrap ran.
        this.logger.log('[OnboardingSDK] Checking exact-visitor status...');
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        // Anonymous (clientKey + Origin) — no cookies on the public SDK bucket.
        credentials: 'omit',
      });

      if (!response.ok) {
        this.logger.warn(`[OnboardingSDK] EndUser status check failed: ${response.status}`);
        return;
      }

      this.endUserStatus = await response.json();

      this.logger.log(`[OnboardingSDK] EndUser status: found=${this.endUserStatus?.found}, isNewUser=${this.endUserStatus?.isNewUser}, hasPreferences=${this.endUserStatus?.hasPreferences}`);
    } catch (error) {
      this.logger.warn('[OnboardingSDK] Error checking EndUser status:', error);
      // Don't throw - fall back to localStorage detection
    }
  }

  /**
   * Get EndUser preferences (for qualification pre-fill).
   * Returns preferences from API if available.
   */
  getEndUserPreferences(): Record<string, any> | null {
    return this.endUserStatus?.preferences || null;
  }

  /**
   * FLO-534: is this the onboarding agent running in support mode?
   *
   * Support mode is an ONBOARDING-agent posture, and `FloeConfig.activation`
   * documents that the demo and website agents ignore it. That has to be
   * enforced here, not assumed from `init()`'s ordering: the website-agent
   * branch does return before the support-mode gate, but the DEMO branch comes
   * after it. Without `drivesHostPage`, `Floe({ demoMode: true, activation:
   * 'on_demand' })` would take the dormant path and the demo would never start.
   *
   * `drivesHostPage` is already exactly "is this the onboarding agent"
   * (!demoMode && !websiteAgent) and is derived once in the constructor, so it
   * is the right gate rather than a second hand-rolled mode check.
   *
   * Delegates to the shared resolver rather than comparing the literal inline,
   * so an SDK constructed outside the Floe() boundary — which is where the
   * value is normalised — still lands on 'proactive' instead of matching
   * nothing and going dormant by accident.
   *
   * Named `runsInSupportMode` rather than `isSupportMode` only to keep the
   * imported predicate reachable by its own name in this file.
   */
  private runsInSupportMode(): boolean {
    return this.drivesHostPage && isSupportMode(this.config.activation);
  }

  /**
   * FLO-534: the activation value to stamp on /sites/start-session.
   *
   * Derived from the SAME predicate that decides whether init() goes dormant,
   * rather than re-reading config. The two must never disagree: a client that
   * stays dormant but reports 'proactive' gets a Shepherd that opens with the
   * onboarding pitch the moment the user clicks — the exact failure support
   * mode exists to prevent.
   */
  private activationForSession(): 'proactive' | 'on_demand' {
    return this.runsInSupportMode() ? 'on_demand' : 'proactive';
  }

  /**
   * FLO-534: should the orb slide out a nudge on its own? See the predicate in
   * agent-mode.ts for why support mode flips this default.
   *
   * Resolved here (rather than left undefined for ControlHub's own `= true`
   * default) because that default would otherwise win for a support-mode embed
   * that passes no nudge config at all — which is most of them.
   */
  private nudgeAutoShow(): boolean {
    return resolveNudgeAutoShow(this.config.nudge?.autoShow, this.config.activation);
  }

  /**
   * Determine if onboarding modal should be skipped.
   * Priority: explicit config > API EndUser.isNewUser > localStorage fallback
   */
  private shouldSkipOnboardingModal(): boolean {
    // Demo mode: always skip — the demo starts immediately, no onboarding welcome
    if (this.config.demoMode) {
      this.logger.log('[OnboardingSDK] Skipping modal: demoMode is enabled');
      return true;
    }

    // Explicit override takes priority (useful for dev/testing)
    if (typeof this.config.skipOnboardingModal === 'boolean') {
      this.logger.log(`[OnboardingSDK] skipOnboardingModal explicitly set to: ${this.config.skipOnboardingModal}`);
      return this.config.skipOnboardingModal;
    }

    // API is the source of truth for new user detection
    // If API explicitly says this is a new user, show the modal (don't skip)
    if (this.endUserStatus?.isNewUser === true) {
      this.logger.log('[OnboardingSDK] Showing modal: API indicates isNewUser=true');
      return false;
    }

    // Check API response: skip if EndUser exists and is NOT new
    if (this.endUserStatus?.found && this.endUserStatus.isNewUser === false) {
      this.logger.log('[OnboardingSDK] Skipping modal: existing EndUser (isNewUser=false)');
      return true;
    }

    // Fallback only when API doesn't give us definitive info about user status
    // This handles cases where API call failed or returned inconclusive results
    const hasVisited = localStorage.getItem('floe_has_visited') === 'true';
    const hasCompletedOnboarding = localStorage.getItem('floe_onboarding_complete') === 'true';

    const shouldSkip = hasVisited && hasCompletedOnboarding;
    this.logger.log(`[OnboardingSDK] Fallback to localStorage: skip=${shouldSkip} (hasVisited: ${hasVisited}, hasCompletedOnboarding: ${hasCompletedOnboarding})`);

    return shouldSkip;
  }

  /**
   * Mark that the user has visited (used to track new vs returning users).
   */
  markUserVisited(): void {
    localStorage.setItem('floe_has_visited', 'true');
  }

  /**
   * Mark onboarding as complete.
   */
  markOnboardingComplete(): void {
    localStorage.setItem('floe_onboarding_complete', 'true');
  }

  /**
   * Disconnect and cleanup
   */
  /**
   * End the live demo into the in-place wrap-up state WITHOUT tearing the
   * overlay down or signalling 'closed'.
   *
   * The demo End-call modal ("Book Demo" / "End Call") routes here instead of
   * disconnect(): we stop the voice + screencast session and flip `demoEnded`
   * so DemoView swaps to its post-demo overlay (the Cal/Calendly calendar for
   * "Book Demo", the goodbye card for "End Call") — rendered IN PLACE. Full
   * teardown — disconnect(), which unmounts React and emits 'closed' (the host
   * page navigates away) — is reserved for the prospect actually leaving.
   *
   * Bug this fixes: onEndDemo used to call disconnect() directly, so clicking
   * "Book Demo" unmounted the whole UI and emitted 'closed' → the host page's
   * `closed` handler did router.push('/'), bouncing the prospect to the
   * homepage before the calendar could ever render.
   */
  async endDemoForWrapUp() {
    this.retirePlanRun();
    // Flip to the post-demo state and re-render FIRST so the wrap-up overlay
    // (calendar / goodbye) appears instantly. KEEP the React root mounted and do
    // NOT emit 'closed' — DemoView renders in place. The teardown below then runs
    // without the prospect staring at a frozen live-call UI while the socket closes.
    this.isConnected = false;
    this.resetLanguageSwitchState();
    this.agentState = 'idle';
    this.demoEnded = true;
    this.renderReactUI();

    // Stop the live voice/video session (ends the bot + the screencast track).
    // FLO-960: deliberate wrap-up — onDisconnected reports `client_requested`.
    this.sdkRequestedTeardown = true;
    this.clientBeingTornDown = this.pipecatClient;
    try {
      if (this.pipecatClient) {
        await this.pipecatClient.disconnect();
        this.pipecatClient = null;
      }
    } catch (err) {
      this.logger.warn('[OnboardingSDK] endDemoForWrapUp: pipecat disconnect failed', err);
    } finally {
      this.sdkRequestedTeardown = false;
      this.clientBeingTornDown = null;
    }
    this.transport = null;

    // Halt the runtime trackers. The live session is over (the bot is gone), so
    // nothing should keep listening — leaving these running fires stale page /
    // DOM / navigation events into a disconnected pipeline. Mirror disconnect().
    this.pageTracker.stop();
    this.domIntrospector.stopCapturing();
    if (this.navigationCompleteDetector) {
      this.navigationCompleteDetector.stop();
    }

    // Silence + release audio/mic so nothing keeps running behind the overlay.
    if (this.audioElements) {
      this.audioElements.forEach(audio => { audio.pause(); audio.remove(); });
      this.audioElements = [];
    }
    this.userMicStreamRequestId++;
    if (this.userMicStream) {
      this.userMicStream.getTracks().forEach(track => track.stop());
      this.userMicStream = null;
    }
    this.stopPrimedMicStream();
    if (this.botAudioAnalyzer) { this.botAudioAnalyzer.destroy(); this.botAudioAnalyzer = null; }
    if (this.userAudioAnalyzer) { this.userAudioAnalyzer.destroy(); this.userAudioAnalyzer = null; }
    this.audioLevels = [];

    this.logger.log('[OnboardingSDK] Demo ended into wrap-up (overlay kept mounted)');
  }

  async disconnect(suppressClosedEvent = false) {
    // An init() still validating must not mount after this: it compares the
    // count, and a wait before a retry ends now rather than when it is due.
    this.teardownCount += 1;
    this.cancelValidationRetry?.();
    this.bookingEmail = undefined;
    this.resetIdentityAskSession();
    this.retirePlanRun();
    this.resetLanguageSwitchState();
    // FLO-786: unbind the permission listener before anything awaited, so a
    // change landing mid-teardown cannot re-enable a mic on a dying session.
    this.stopWatchingMicPermission();
    // Drop the top-edge listener/timer before any awaited teardown so a visitor
    // leaving mid-disconnect cannot re-open UI that is being unmounted.
    this.stopExitIntentDetector();
    // Stop page tracking first
    this.pageTracker.stop();

    // Stop DOM Introspection Layer
    this.domIntrospector.stopCapturing();

    // Stop navigation detection (restores history methods)
    if (this.navigationCompleteDetector) {
      this.navigationCompleteDetector.stop();
    }

    // Clear all overlays: cursor, highlights, tooltips before removing
    this.overlay.cleanup();

    // FLO-960: set before the await so a mid-teardown onDisconnected reports
    // `client_requested` rather than a bare transport close.
    this.sdkRequestedTeardown = true;
    this.clientBeingTornDown = this.pipecatClient;
    try {
      if (this.pipecatClient) {
        await this.pipecatClient.disconnect();
        this.pipecatClient = null;
      }
    } catch (err) {
      // Catch-and-log (mirrors endWebsiteAgentSession / endDemoForWrapUp) so a
      // rejected transport disconnect can't abort disconnect() — the full
      // teardown below (transport, audio, mic, UI unmount, state reset,
      // disconnected/closed events) must still run.
      this.logger.warn('[OnboardingSDK] disconnect: pipecat disconnect failed', err);
    } finally {
      this.clientBeingTornDown = null;
      // FLO-367 review: flush any queued telemetry, then clear the debounce
      // timers + unbind the pagehide/visibilitychange listeners so the torn-down
      // SDK isn't retained by window/document and no timer fires after
      // disconnect. In a `finally` so it still runs even when the transport
      // disconnect above rejects. dispose() = final beacon flush + full unbind
      // for both queues (nudge telemetry + CDP visitor events).
      this.trackVoiceEnded();
      this.trackWidgetClosed();
      this.nudgeEventQueue.dispose();
      this.visitorEvents.dispose();
      // FLO-960: close the thread and flush it, then detach from the
      // module-level timeline so this SDK is not retained by the closure.
      this.sdkDiagnostics.dispose();
      this.voiceStartupMarkUnsubscribe?.();
      this.voiceStartupMarkUnsubscribe = null;
      this.bookingAttributionGeneration += 1;
      this.bookingAttributionRequest = null;
      this.clearBookingAttributionCache();
    }

    // FLO-320: clear the website-agent single-session guard so a re-opened
    // launcher (after a full disconnect / SDK re-init) can start a fresh
    // session. Within a live session the guard still blocks a double-tap.
    this.websiteAgentSessionStarted = false;

    // Clean up transport
    if (this.transport) {
      this.transport = null;
    }

    // Clean up audio elements
    if (this.audioElements) {
      this.audioElements.forEach(audio => {
        audio.pause();
        audio.remove();
      });
      this.audioElements = [];
    }

    // Clean up audio analyzers
    if (this.botAudioAnalyzer) {
      this.botAudioAnalyzer.destroy();
      this.botAudioAnalyzer = null;
    }
    if (this.userAudioAnalyzer) {
      this.userAudioAnalyzer.destroy();
      this.userAudioAnalyzer = null;
    }

    // Clean up user mic stream - invalidate any in-flight getUserMedia first
    this.userMicStreamRequestId++;
    if (this.userMicStream) {
      this.userMicStream.getTracks().forEach(track => track.stop());
      this.userMicStream = null;
    }
    this.stopPrimedMicStream();
    this.audioLevels = [];

    // Clean up React UI
    try {
      if (this.reactRoot) {
        this.reactRoot.unmount();
        this.reactRoot = null;
      }
    } finally {
      if (this.ownsUiStrings) {
        releaseUiStrings(this);
        this.ownsUiStrings = false;
      }
    }

    // Unbind the prefetch intent listeners with the container they live on,
    // and cancel a boot-time warm that has not fired yet.
    this.voiceStartupPrefetchAbort?.abort();
    this.voiceStartupPrefetchAbort = null;
    this.voiceStartupPrefetchCancel?.();
    this.voiceStartupPrefetchCancel = null;
    this.cancelRemainingNudgesLoad();
    // The warm registry is page-scoped, not per-instance: with an approach
    // intro it holds a dozen buffered media elements, and nothing but teardown
    // would ever let go of them.
    releaseWarmedMedia();
    if (this.reactContainer) {
      this.reactContainer.remove();
      this.reactContainer = null;
    }

    // FLO-519: give the host page its width back. The unmount above normally
    // does this (WebsiteAgentView's effect cleanup releases the squeeze), but a
    // missed release would leave <html> with `margin-right: 400px` and no widget
    // left to undo it — unrecoverable without a reload. Unconditional + cheap:
    // a no-op when nothing was ever squeezed.
    forceReleaseSqueeze();

    // FLO-523: and un-patch the host's globals / drop the containment harness.
    // Unconditional for the same reason as the line above — a page left with a
    // patched `window.innerWidth`, a body scroller and no widget is a customer
    // site we broke until they reload. A no-op when nothing was applied.
    releaseSqueezeCompat();

    // Remove overlay (destroys DOM elements and resets cursor state)
    this.overlay.remove();
    this.isInitialized = false;
    this.isConnected = false;
    this.agentState = 'idle';
    this.resetBotSpeakingState();

    // FLO-960: the teardown is over; a later transport drop is not ours.
    this.sdkRequestedTeardown = false;
    this.logger.log('[OnboardingSDK] Disconnected');
    // `disconnected` for backward compat with consumers that just want a
    // generic "the call dropped" signal. `closed` is the new explicit
    // "user fully tore the SDK down" signal — only fires from this method,
    // NOT from the transport's onDisconnected callback. Use this to route
    // your wrapper page elsewhere (e.g. back to a landing) without
    // collapsing the transport-drop case (which leaves the error UI mounted
    // so the user can hit Retry).
    this.emit('disconnected');
    // suppressClosedEvent: a retry tears the session down only to immediately
    // re-init (retryDemo). Firing `closed` there makes the host wrapper route
    // away (e.g. DemoShell's router.push('/')) before the fresh demo mounts —
    // `closed` must mean "the prospect is done", not "we're rebuilding".
    if (!suppressClosedEvent) {
      this.emit('closed');
    }
  }
}

// Export for browser console usage
if (typeof window !== 'undefined') {
  (window as any).OnboardingSDK = OnboardingSDK;
}
